// src/common/pagination.ts
import { z } from "zod";
var MAX_PER_PAGE = 200;
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(MAX_PER_PAGE).default(25)
});
var sortableQuerySchema = paginationQuerySchema.extend({
  /** `field:asc,other:desc` — validated per resource against an allowlist. */
  sort: z.string().optional(),
  q: z.string().trim().max(200).optional()
});
function pageMeta(page, perPage, total) {
  return { page, perPage, total, totalPages: Math.max(1, Math.ceil(total / perPage)) };
}
function parseSort(sort, allowed, fallback) {
  if (!sort) return fallback;
  const parsed = sort.split(",").map((part) => part.trim()).filter(Boolean).map((part) => {
    const [field, dir] = part.split(":");
    return { field: field ?? "", dir: dir === "desc" ? "desc" : "asc" };
  }).filter((s) => allowed.includes(s.field));
  return parsed.length > 0 ? parsed : fallback;
}

// src/common/error.ts
var ERROR_CODES = {
  VALIDATION: "VALIDATION",
  UNAUTHORIZED: "UNAUTHORIZED",
  MFA_REQUIRED: "MFA_REQUIRED",
  MFA_INVALID: "MFA_INVALID",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  ACCOUNT_LOCKED: "ACCOUNT_LOCKED",
  ACCOUNT_DISABLED: "ACCOUNT_DISABLED",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  TOKEN_REUSE_DETECTED: "TOKEN_REUSE_DETECTED",
  SCOPE_MISSING: "SCOPE_MISSING",
  /**
   * The card already has a login, or the login already has a card. Its own code
   * because the frontend has something useful to offer here — show the account
   * that holds the link — and cannot tell that from a bare CONFLICT.
   */
  ALREADY_LINKED: "ALREADY_LINKED",
  /**
   * Aynı belge numarası zaten kayıtlı — ENGELLENDİ. Gerçek, düzenleyene göre
   * tekil bir numara iki kez girilemez (fatura, e-fatura/e-arşiv ödemesi).
   * `details.eslesmeler` kapsam içindeki çakışan kaydı taşır.
   */
  DUPLICATE_DOCUMENT: "DUPLICATE_DOCUMENT",
  /**
   * Tekerrür OLABİLİR — kayıt yapılmadı, gerekçe bekleniyor. İstek aynı gövde
   * ve bir `duplicateReason` ile tekrarlanırsa kabul edilir ve gerekçe kaydın
   * geçmişine yazılır. Ayrı kod, çünkü ekranın burada sunacağı şey bir hata
   * mesajı değil, bir soru: "yine de devam etmek istiyor musunuz, neden?"
   */
  DUPLICATE_SUSPECTED: "DUPLICATE_SUSPECTED",
  /** A card missing its TCKN may not be used for leave. See EmployeeCompleteness. */
  EMPLOYEE_PROFILE_INCOMPLETE: "EMPLOYEE_PROFILE_INCOMPLETE",
  LEAVE_BALANCE_INSUFFICIENT: "LEAVE_BALANCE_INSUFFICIENT",
  LEAVE_OVERLAP: "LEAVE_OVERLAP",
  LEAVE_CALENDAR_CHANGED: "LEAVE_CALENDAR_CHANGED",
  POLICY_NOT_FOUND: "POLICY_NOT_FOUND",
  STATE_CONFLICT: "STATE_CONFLICT",
  INTEGRATION_DISABLED: "INTEGRATION_DISABLED",
  UNIT_MISMATCH: "UNIT_MISMATCH",
  TIMESTAMP_SKEW: "TIMESTAMP_SKEW",
  REPLAYED_NONCE: "REPLAYED_NONCE",
  PASSWORD_CHANGE_REQUIRED: "PASSWORD_CHANGE_REQUIRED",
  INTERNAL: "INTERNAL"
};
function isApiErrorBody(value) {
  if (typeof value !== "object" || value === null) return false;
  const err = value.error;
  return typeof err === "object" && err !== null && typeof err.code === "string";
}

// src/common/money.ts
import { z as z2 } from "zod";
var TR_STRICT = /^\d{1,3}(\.\d{3})*(,\d{1,2})?$/;
var TR_PLAIN = /^\d+(,\d{1,2})?$/;
var EN_STRICT = /^\d{1,3}(,\d{3})*(\.\d{1,2})?$/;
var EN_PLAIN = /^\d+(\.\d{1,2})?$/;
var decimalSeparatorOf = (s) => {
  const m = /[.,](\d{1,2})$/.exec(s);
  if (!m) return null;
  return s[s.length - m[0].length] === "," ? "," : ".";
};
function parseMoney(raw, format = "tr") {
  const s = raw.trim().replace(/\s+/g, "").replace(/^[+]/, "");
  if (s === "") return { ok: false, value: null, reason: "Tutar bo\u015F." };
  if (s.startsWith("-"))
    return { ok: false, value: null, reason: "Tutar negatif olamaz; y\xF6n ayr\u0131 alan." };
  if (!/^[\d.,]+$/.test(s))
    return { ok: false, value: null, reason: "Tutar yaln\u0131zca rakam, nokta ve virg\xFCl i\xE7erebilir." };
  const decimal = decimalSeparatorOf(s);
  const expected = format === "tr" ? "," : ".";
  if (decimal && decimal !== expected) {
    return {
      ok: false,
      value: null,
      reason: format === "tr" ? `"${raw.trim()}" \u0130ngilizce bi\xE7imde g\xF6r\xFCn\xFCyor (ondal\u0131k nokta). Bi\xE7imi de\u011Fi\u015Ftirin veya sat\u0131r\u0131 d\xFCzeltin.` : `"${raw.trim()}" T\xFCrk\xE7e bi\xE7imde g\xF6r\xFCn\xFCyor (ondal\u0131k virg\xFCl). Bi\xE7imi de\u011Fi\u015Ftirin veya sat\u0131r\u0131 d\xFCzeltin.`
    };
  }
  const strict = format === "tr" ? TR_STRICT : EN_STRICT;
  const plain = format === "tr" ? TR_PLAIN : EN_PLAIN;
  if (!strict.test(s) && !plain.test(s)) {
    return { ok: false, value: null, reason: `"${raw.trim()}" okunamad\u0131.` };
  }
  const normalised = format === "tr" ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
  const value = Number(normalised);
  if (!Number.isFinite(value))
    return { ok: false, value: null, reason: `"${raw.trim()}" okunamad\u0131.` };
  return { ok: true, value };
}
var formatMoney = (n, currency2) => `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency2 ? ` ${currency2}` : ""}`;
function paymentAmountFrom(input) {
  const parsed = parseMoney(input.amountText, input.amountFormat ?? "tr");
  if (!parsed.ok || parsed.value === null) {
    return { ok: false, value: null, reason: parsed.reason ?? "Tutar okunamad\u0131." };
  }
  if (input.amount !== void 0 && Math.abs(input.amount - parsed.value) > 5e-3) {
    return {
      ok: false,
      value: null,
      reason: `Girilen tutar ("${input.amountText}") ile g\xF6nderilen say\u0131 (${input.amount}) uyu\u015Fmuyor. Sayfay\u0131 yenileyip tekrar deneyin.`
    };
  }
  return { ok: true, value: parsed.value };
}
var moneyText = (opts = {}) => z2.string().min(1, "Tutar bo\u015F.").max(32, "Tutar \xE7ok uzun \u2014 en fazla 32 karakter yaz\u0131labilir.").transform((raw, ctx) => {
  const trimmed = raw.trim();
  const negative = opts.allowNegative && trimmed.startsWith("-");
  const parsed = parseMoney(negative ? trimmed.slice(1) : trimmed, "tr");
  if (!parsed.ok || parsed.value === null) {
    ctx.addIssue({ code: "custom", message: parsed.reason ?? "Tutar okunamad\u0131." });
    return z2.NEVER;
  }
  const value = negative ? -parsed.value : parsed.value;
  if (opts.min !== void 0 && value < opts.min) {
    ctx.addIssue({ code: "custom", message: `Tutar en az ${formatMoney(opts.min)} olabilir.` });
    return z2.NEVER;
  }
  if (opts.max !== void 0 && value > opts.max) {
    ctx.addIssue({
      code: "custom",
      message: `Tutar en fazla ${formatMoney(opts.max)} olabilir.`
    });
    return z2.NEVER;
  }
  return value;
});

// src/enums/permissions.ts
var PERMISSIONS = {
  // --- identity & administration -----------------------------------------
  USER_READ: "user.read",
  USER_MANAGE: "user.manage",
  ROLE_READ: "role.read",
  ROLE_MANAGE: "role.manage",
  SETTINGS_MANAGE: "settings.manage",
  JOB_READ: "job.read",
  JOB_MANAGE: "job.manage",
  AUDIT_READ: "audit.read",
  /**
   * Veri Düzeltme Merkezi.
   *
   * HİÇBİR ROLE VERİLMİYOR ve bu kasıtlı: bu kodları bugün yalnız Super Admin
   * taşıyor, çünkü o rol kataloğun tamamını kod gereği alıyor. Rol matrisinden
   * başka birine vermek mümkün ve o ayrı, görünür bir karar.
   */
  CORRECTION_READ: "correction.read",
  CORRECTION_MANAGE: "correction.manage",
  SECURITY_READ: "security.read",
  /**
   * Kimin oturumu açık, en son ne zaman görüldü, bugün içeride mi.
   *
   * Deliberately NOT audit.read, though every number on that screen is derived
   * from data audit.read already exposes. The two answer different questions
   * and belong to different people: audit.read is "reconstruct what happened to
   * this record", a forensic power; this is "is Ayşe reachable right now", an
   * operational one. Folding the second into the first would mean handing the
   * forensic trail to whoever needs to know if someone is at their desk.
   *
   * It grants no detail either — a heading and a timestamp, never the changed
   * fields. Anyone who needs to know WHAT was done still needs audit.read.
   */
  ACTIVITY_READ: "activity.read",
  /**
   * WHICH SCREEN somebody is on — a third power, not a detail of the second.
   *
   * `activity.read` answers "can I reach Ayşe". This answers "what is Ayşe
   * looking at right now", and the gap between them is the same size as the gap
   * between activity.read and audit.read one level up: a person who needs to
   * know whether to phone a colleague does not need to watch their screen.
   *
   * The label itself is already narrowed at the source — the units send a page
   * NAME and never a path, so no record id, customer or aircraft crosses the
   * boundary — and nothing is retained. This permission decides who sees even
   * that. Without it the column renders blank rather than being absent, so the
   * screen does not pretend the information does not exist.
   */
  ACTIVITY_PAGE_READ: "activity.page_read",
  // --- organisation -------------------------------------------------------
  COMPANY_READ: "company.read",
  COMPANY_MANAGE: "company.manage",
  ORG_UNIT_READ: "org_unit.read",
  ORG_UNIT_MANAGE: "org_unit.manage",
  // --- employees ----------------------------------------------------------
  EMPLOYEE_READ_SELF: "employee.read_self",
  EMPLOYEE_READ: "employee.read",
  /**
   * Salary, TCKN, IBAN and special-category documents.
   * Deliberately NOT held by Sistem yöneticisi: the person who maintains the
   * system must not automatically be able to read personnel salaries (KVKK).
   */
  EMPLOYEE_READ_SENSITIVE: "employee.read_sensitive",
  EMPLOYEE_WRITE: "employee.write",
  /**
   * Binding a personnel card to a login, and unbinding it.
   *
   * Deliberately neither of the two permissions it looks like. Not `user.manage`:
   * sistem_yoneticisi holds that and cannot see an employee at all, so it must
   * not be the authority that decides which card an account belongs to. Not
   * plain `employee.write` either: every hand that edits a card holds that, and
   * a wrong link is not a wrong field — it hands one person's leave balance,
   * approval chain and dashboard to another.
   */
  EMPLOYEE_LINK_USER: "employee.link_user",
  /**
   * Zimmet — şirkete ait eşyanın kişide olduğu kayıt (DST-00065).
   *
   * Okuma ve yönetme ayrı: "kimde ne var" bir yönetim sorusu ve birim
   * müdürünün de cevabına ihtiyacı var; kütüğe yazmak ve eşyayı teslim etmek
   * İK'nın işi. Zimmet vermek bir karardır, listeyi görmek değil.
   *
   * ASSET_MANAGE artık YALNIZ KÜTÜK: varlık kartı açmak ve düzeltmek. Teslim
   * etmek, iade almak, iptal etmek ve hasar yazmak ayrı kapılar — farklı
   * kararlar ve aynı kişide toplanmaları gerekmiyor. Bugün ASSET_MANAGE
   * taşıyan roller yeni kodları göçte aldı, kimse yetki kaybetmedi.
   */
  ASSET_READ: "asset.read",
  ASSET_MANAGE: "asset.manage",
  /**
   * Kendi zimmetini görmek bir yönetim yetkisi değil, kişinin kendi kaydına
   * erişimi — ve ASSET_READ'in "başkalarınınkini oku" anlamı korunsun diye
   * ayrı bir kod. Ev düzeninde her X_READ başkasınınkini okumak demek.
   */
  ASSET_READ_OWN: "asset.read_own",
  ASSET_ASSIGN: "asset.assign",
  /**
   * Handing THIS company's property to someone on ANOTHER company's payroll.
   *
   * Its own code, and not a detail of ASSET_MANAGE, because the two are not the
   * same power. ASSET_MANAGE is the register: open the card, fix the serial
   * number, retire the item — every one of those acts stays inside one legal
   * entity's own books and answers only to that entity. Binding another legal
   * entity's employee to this entity's property does something else: it creates
   * an obligation one company will enforce against a person it does not employ,
   * cannot discipline and cannot run an exit checklist on. That is a crossing of
   * exactly the boundary the whole tenancy model exists to draw — the same
   * boundary `app.can_see_company()` and the company-scoped RLS policies spend
   * every query defending.
   *
   * The failure this separation prevents, concretely: fold the power into
   * ASSET_MANAGE and every warehouse desk that keeps a register acquires it by
   * inheritance, silently, without anyone having decided it. `depo_sorumlusu`
   * carries ASSET_MANAGE today (roles.ts) — so on the day the restructuring left
   * the assets in one company and the people in another, the warehouse desk
   * would have started reaching across companies and nobody would have known
   * which decision made that legal.
   *
   * A separate code makes it a decision someone has to take, in the role matrix,
   * where it is visible and audited. It is a DEFAULT-BREAKER, in the same family
   * as ASSET_CALIBRATION_OVERRIDE and ASSET_EXIT_OVERRIDE: the same-company rule
   * stays in force for everyone, this code is the named, narrow exception, and
   * like the other two it demands a written reason that lands in the record
   * (`hr_asset_custody.cross_company_reason`) rather than in someone's memory.
   *
   * It does NOT replace ASSET_ASSIGN — it is only meaningful next to it. Someone
   * who cannot issue a zimmet at all cannot issue a cross-company one either.
   */
  ASSET_ASSIGN_CROSS_COMPANY: "asset.assign.cross_company",
  ASSET_RETURN: "asset.return",
  ASSET_CANCEL: "asset.cancel",
  ASSET_INCIDENT: "asset.incident",
  /**
   * Alış maliyeti, zarar bedeli ve bakım tutarı. Zimmet listesini görebilen
   * herkesin şirketin neye ne ödediğini görmesi gerekmiyor; sütun bu kod
   * yoksa sunucuda hiç doldurulmuyor, istemcide gizlenmiyor.
   */
  ASSET_COST_READ: "asset.cost_read",
  ASSET_REPORT: "asset.report",
  /**
   * Kuralı DELEN iki kod, bilerek dar.
   *
   * Kalibrasyonu geçmiş bir ekipmanı yine de zimmetlemek ve açık zimmetli
   * birinin çıkışını yine de tamamlamak — ikisi de gerekçe ister ve gerekçe
   * `hr_asset_exception`e silinemez biçimde düşer. Bunları zimmet verebilen
   * herkese dağıtmak, kuralı hiç yazmamakla aynı kapıya çıkardı.
   */
  ASSET_CALIBRATION_OVERRIDE: "asset.calibration_override",
  ASSET_EXIT_OVERRIDE: "asset.exit_override",
  /**
   * Confirming a match the matcher PROPOSED between a unit-ERP user and a card.
   *
   * Kept apart from link_user because they are different acts: one is "I know
   * this person", the other is "I vouch for a guess about this person". The
   * second is rarer and belongs higher up.
   */
  EMPLOYEE_IDENTITY_CONFIRM: "employee.identity_confirm",
  DOCUMENT_READ_OWN: "document.read_own",
  DOCUMENT_READ: "document.read",
  DOCUMENT_WRITE: "document.write",
  /**
   * Kendi bordrosunu görmek — DST-00092.
   *
   * `PAYSLIP_MANAGE`'den ayrı, çünkü ev düzeninde her X_READ "başkasınınkini
   * oku" demek ve bordroda o fark bu modülün tamamı. `ASSET_READ_OWN` ile aynı
   * gerekçe, aynı yer: kişinin kendi kaydına erişimi bir yönetim yetkisi değil.
   *
   * `DOCUMENT_READ_OWN`'a yamanmadı ve bu bilinçli: o kod bugün ÖLÜ — hiçbir uç
   * onu istemiyor, "kendi belgelerim" ekranı hiç yazılmadı. Ölü bir kodun
   * üzerine canlı bir uç kurmak, iki yıl sonra "bu izin neyi açıyor" sorusuna
   * iki farklı cevap üretir. Ayrıca özlük belgesi ile maaş pusulası aynı veri
   * sınıfı değil.
   */
  PAYSLIP_READ_OWN: "payslip.read_own",
  /**
   * Başkasının bordrosunu yüklemek, görmek ve silmek.
   *
   * `EMPLOYEE_READ_SENSITIVE` ile birleştirilmedi: o izin maaş TUTARINI açıyor
   * (`compensation` modülü, RESTRICTIVE RLS). Bordro dağıtımı ise bir IK
   * OPERASYONU — bordroyu yükleyen kişinin maaş tablosunu sorgulayabilmesi
   * gerekmiyor, ve tersi de doğru değil.
   */
  PAYSLIP_MANAGE: "payslip.manage",
  CREDENTIAL_READ: "credential.read",
  CREDENTIAL_MANAGE: "credential.manage",
  // --- leave --------------------------------------------------------------
  LEAVE_REQUEST_CREATE: "leave_request.create",
  LEAVE_REQUEST_CREATE_FOR_OTHERS: "leave_request.create_for_others",
  LEAVE_REQUEST_READ: "leave_request.read",
  LEAVE_REQUEST_APPROVE: "leave_request.approve",
  LEAVE_REQUEST_CANCEL_OWN: "leave_request.cancel_own",
  LEAVE_REQUEST_CANCEL: "leave_request.cancel",
  /** Manual ledger correction. Always requires a reason and is audited. */
  LEAVE_LEDGER_ADJUST: "leave_ledger.adjust",
  LEAVE_POLICY_MANAGE: "leave_policy.manage",
  HOLIDAY_MANAGE: "holiday.manage",
  // --- dashboards & reporting --------------------------------------------
  DASHBOARD_SELF: "dashboard.self",
  DASHBOARD_TEAM: "dashboard.team",
  DASHBOARD_GROUP: "dashboard.group",
  REPORT_COMPANY: "report.company",
  REPORT_GROUP: "report.group",
  // --- attendance ---------------------------------------------------------
  /** Recording one's own or another's clock events. */
  ATTENDANCE_RECORD: "attendance.record",
  ATTENDANCE_READ: "attendance.read",
  /** Recompute, and turn an unrecorded day into an absence. */
  ATTENDANCE_MANAGE: "attendance.manage",
  /**
   * Rewriting the RULES — the md. 68 break ladder, the tolerances, md. 41's
   * weekly line.
   *
   * Separate from ATTENDANCE_MANAGE for the reason LEAVE_POLICY_MANAGE is
   * separate from approving a request: resolving one person's day is a decision
   * about that day, while moving a break threshold silently re-derives every
   * employee's worked minutes the next time anyone recomputes.
   */
  ATTENDANCE_POLICY_MANAGE: "attendance.policy.manage",
  // --- compensation -------------------------------------------------------
  /**
   * Entering and changing salary, bonuses, deductions and advances.
   *
   * Separate from EMPLOYEE_READ_SENSITIVE, which only grants sight. Reading a
   * salary and setting one are different powers, and the second is rarer.
   */
  COMPENSATION_MANAGE: "compensation.manage",
  // --- integration --------------------------------------------------------
  INTEGRATION_READ: "integration.read",
  INTEGRATION_ADMIN: "integration.admin",
  // --- weekly payment table -----------------------------------------------
  /** Filing a payment request into the week's table. */
  PAYMENT_REQUEST_CREATE: "payment_request.create",
  /**
   * Acil ödeme talebi açmak — normal zinciri ATLAYAN talep.
   *
   * Ayrı bir kod, çünkü `payment_request.create` SELF_PERMISSIONS içinde:
   * ödeme talebi açmak self servis, çünkü onu durduran şey kapılardır. Acil
   * talep ise tam olarak o kapıları atlıyor, dolayısıyla "kim talep açabilir"
   * ile "kim kapıları atlayan bir talep açabilir" aynı soru değil.
   */
  PAYMENT_URGENT_CREATE: "payment_request.urgent",
  PAYMENT_READ: "payment.read",
  /**
   * Acting on the gate of the approval chain one has been assigned to.
   *
   * A coarse gate on purpose. It says "this person may take part in payment
   * approvals at all"; WHICH gate, and whether it is their turn, is decided by
   * the chain configuration in `pay_approval_assignee`, not by a permission
   * code. Holding this and being assigned to nothing approves nothing.
   *
   * It replaces the two codes of the two-gate era, `payment.approve_manager`
   * and `payment.approve_executive`. One permission per gate cannot survive a
   * chain whose gates are data: the fifth stage someone adds on a Tuesday
   * afternoon would need a code that only a deploy can create.
   *
   * It also gates the BYTES of a restricted attachment — today the signed card
   * payment instruction form, which carries a full card number. Uploading and
   * listing stay on PAYMENT_REQUEST_CREATE, which sits in SELF_PERMISSIONS and
   * therefore means everyone; reading those particular bytes does not. This is
   * not a second authorisation model: the route still asks Permission × Scope,
   * the row only selects WHICH permission applies. Deliberately not narrowed
   * further to `payment.execute` — that code sits on the finance manager alone,
   * and the three finance specialists who file these payments would have been
   * locked out of a form they uploaded themselves.
   */
  PAYMENT_APPROVE: "payment.approve",
  /**
   * Defining the gates of the chain and naming who sits in them.
   *
   * BOTH chains, despite the `payment.` prefix: the invoice chain uses the same
   * gates, the same seats and the same screen, and a separate
   * `invoice.approval_chain_manage` would have bought nothing but a second code.
   * The cost of a new code is not theoretical here — every session whose
   * contracts are not rebuilt refuses to boot on an unknown permission, and
   * `seed:core` stops without writing anything. The name is the price of not
   * paying that twice; the comment is here so nobody has to guess.
   */
  PAYMENT_APPROVAL_CHAIN_MANAGE: "payment.approval_chain_manage",
  /** Locking the week and releasing the list to finance. */
  PAYMENT_WEEK_MANAGE: "payment.week_manage",
  /** Making the payment and recording the receipt. A ROLE, not a person. */
  PAYMENT_EXECUTE: "payment.execute",
  /**
   * Ödenmiş talebi, dekontu yüklüyken «Ödendi» işaretlemek (DST-00116).
   * Adı tarihî: önce yalnız kart; 21.10'dan beri havale de (#504).
   *
   * `PAYMENT_EXECUTE`'un dar kardeşi, ve dar olması bütün mesele. Kartla
   * ödemeyi çoğu zaman finans uzmanı kendisi yapıyor ve dekont onun elinde;
   * ama «Ödendi»yi yalnız execute sahibi koyabiliyordu — 90 günde kapanan 97
   * ödemenin 97'sini tek kişi kapattı, ve onaylı ödemeler o kişiyi bekledi.
   *
   * Execute'u uzmana vermek dekontsuz kapatmayı da açardı. Bu kod yalnız
   * DEKONT YÜKLÜYKEN açıyor — kapatmanın kanıtı kapatmadan önce kayıtta;
   * bankada havaleyi yapan yine execute sahibi. Kural tek yerde:
   * `odemeKapatmaKarari` (dto/payment.ts).
   */
  PAYMENT_CARD_SETTLE: "payment.card_settle",
  /**
   * Onaylı ödemeyi sonraki haftaya bırakmak (DST-00061, DST-00144).
   *
   * Execute'un içindeydi: «bu hafta ödeyemiyoruz» parayı gönderenin kararı
   * sayılıyordu. Ama tabloyu haftadan haftaya taşıyan masa finans uzmanı, ve
   * Ağustos'tan beri üç kez «ödenmeyeni taşıyamıyorum» dedi. Taşımak onayı
   * bozmuyor, para hareketi değil: talep yalnız sıradaki açık haftanın
   * tablosuna geçiyor ve kim taşıdığı geçmişe yazılıyor. Execute'u vermek
   * havaleyi de açardı; bu kod yalnız taşımayı açıyor.
   */
  PAYMENT_DEFER: "payment.defer",
  /** Preparing the bank's transfer form. Also a role, for the same reason. */
  PAYMENT_TRANSFER_FORM: "payment.transfer_form",
  /**
   * Maintaining the payee register — the counterparties money is sent TO.
   *
   * Only the WRITE side needs a permission of its own. Reading the register is
   * gated on PAYMENT_REQUEST_CREATE, which everyone filing a request already
   * holds: they type the same payee, bank and IBAN by hand today, so a list to
   * pick from discloses nothing new. Writing is different — an edited IBAN
   * redirects every future payment to that counterparty at once, which is why
   * it sits with finance rather than with whoever is buying something.
   */
  PAYEE_MANAGE: "payee.manage",
  /**
   * Faturanın kendisi — kaydeden ve karara bağlayan, iki ayrı yetki.
   *
   * READ kasten AYRI bir kod, ve sebebi cari ekranının kapısında: Cari Kayıtları
   * `payment_request.create` ile açılıyor ve o izin SELF_PERMISSIONS içinde,
   * yani her çalışanda. Onay bekleyen faturaların tutarını o ekrana bu iznin
   * altına koymadan eklemek, bordro dışı bütün gider akışını herkese açardı.
   *
   * APPROVE ile READ'i ayırmanın sebebi de aynı sınıftan: faturayı SİSTEME
   * GİREN muhasebeci ile onu ONAYLAYAN yönetici aynı kişi değil, ve bir belgeyi
   * kaydedebilmek onu kabul edebilmek anlamına gelmemeli.
   *
   * Bu iki kod tek seferde eklendi. Katalog kuralı gereği yeni bir izin kodu
   * gören her süreç açılmayı reddediyor; ikisini ayrı ayrı eklemek bütün
   * oturumların API'sini iki kez durdururdu.
   */
  INVOICE_READ: "invoice.read",
  INVOICE_APPROVE: "invoice.approve",
  // --- personel talebi ------------------------------------------------------
  /**
   * Personelin bir ihtiyacı için talep açması.
   *
   * SELF_PERMISSIONS içinde: bir çalışanın "buna ihtiyacım var" diyebilmesi
   * bir ayrıcalık değil. Talebin kendisi hiçbir şey satın almıyor — parayı
   * hareket ettiren, onaydan sonra açılan ödeme talebi, ve onun kapıları ayrı.
   *
   * Ödeme talebini doğrudan açmakla (`payment_request.create`) karıştırılmasın:
   * o da herkeste, ama cari, IBAN, belge ve kesin tutar istiyor. Sahada
   * çalışan biri bunların hiçbirini bilmiyor; bildiği tek şey neye ihtiyacı
   * olduğu. İki uç kapı bu yüzden ayrı.
   */
  STAFF_REQUEST_CREATE: "staff_request.create",
  /**
   * Başkasının talebini görmek.
   *
   * Katalogdaki her `X_READ` gibi: kendi talebini görmek için gerekmiyor, o
   * CREATE ile geliyor. Bu kod, listeyi başkalarının satırlarına açan şey —
   * kimin neyi istediği, bir birimin bütün ihtiyaç listesi demek.
   */
  STAFF_REQUEST_READ: "staff_request.read",
  /**
   * Zincirde kendisine verilmiş kapıda karar vermek.
   *
   * `payment.approve` ile aynı gerekçeyle kaba: hangi kapı, sıra kimde ve
   * yeter sayı — hepsi `pay_approval_assignee` verisinde. Bu kod yalnız
   * "bu kişi personel taleplerinin onayına hiç girebilir mi" diyor.
   */
  STAFF_REQUEST_APPROVE: "staff_request.approve",
  /**
   * Onaylanmış talebi satın alıp ödeme talebine dönüştürmek.
   *
   * ONAYDAN AYRI, ve bu ayrım kasıtlı: onaylayan "bu alınsın" diyor, satın
   * alan cariyi, gerçekleşen tutarı ve belgeyi giriyor. İkincisi haftalık
   * tabloya para hareketi yazan adım, ve o yüzden finansta duruyor.
   */
  STAFF_REQUEST_PURCHASE: "staff_request.purchase",
  // --- finance ------------------------------------------------------------
  /** Unit profitability, budgets, cost centres. */
  FINANCE_READ: "finance.read",
  FINANCE_MANAGE: "finance.manage",
  /**
   * Proje / operasyon boyutu.
   *
   * FINANCE_READ'in ALTINA girmiyor ve bu bilerek: faturaya proje seçen kişi
   * grubun bütün mali tablosunu görmek zorunda değil. Ayrı bir kod olması,
   * seçiciyi açmak için finans yetkisi dağıtmayı gereksiz kılıyor.
   *
   * MANAGE proje açar/düzenler/pasife alır; READ yalnız görür ve seçer.
   */
  PROJECT_READ: "project.read",
  PROJECT_MANAGE: "project.manage",
  /** Filing one's own expense claim. Everyone who spends money needs it. */
  EXPENSE_CREATE: "expense.create",
  EXPENSE_APPROVE: "expense.approve",
  /**
   * The SECOND gate on an expense claim — the unit manager's, above the amount
   * threshold in policy.
   *
   * Its own code rather than a reuse, for two reasons. The step used to require
   * `finance.read`, and a READ permission standing in for a decision is a
   * category error that reads as deliberate long after it was an accident:
   * everyone who could see a budget could sign off a claim. Reusing
   * `expense.approve` instead is the opposite error — nothing separates the two
   * gates, and expense has no "same person twice" rule, so one line manager
   * could pass both and the escalation the amount threshold buys would be
   * decorative. `fixed_expense.escalation_approve` exists for exactly this
   * shape of question.
   */
  EXPENSE_APPROVE_UNIT: "expense.approve_unit",
  /** Closing a period, which freezes a month's profit. */
  FINANCE_PERIOD_CLOSE: "finance.period_close",
  // --- bütçe ----------------------------------------------------------------
  /**
   * Opening a budget version, typing its rows, revising it.
   *
   * Separate from finance.manage because the people who prepare a budget are
   * not the people who record actuals — a unit's own planner should be able to
   * draft next year without also being able to post an entry into last month.
   */
  BUDGET_MANAGE: "budget.manage",
  /**
   * Putting a version into force, and locking it.
   *
   * The same reasoning as finance.period_close: once a budget is the one every
   * variance report measures against, changing which one that is has to be a
   * rarer power than filling in the numbers. A planner who can also silently
   * swap the baseline can make any overspend disappear.
   */
  BUDGET_LOCK: "budget.lock",
  // --- kasa ve banka --------------------------------------------------------
  /** Account list and balances. Separate from finance.read on purpose: the
   *  accrual reports say what a month cost, this says what the group HAS, and
   *  the second is a smaller circle of people. */
  TREASURY_READ: "treasury.read",
  /** Opening an account, closing one, setting the opening balance. */
  TREASURY_ACCOUNT_MANAGE: "treasury.account_manage",
  /** Recording a movement or a transfer. Held by whoever counts the cash box
   *  and whoever operates the bank, which is rarely the same person who reads
   *  the reports. */
  TREASURY_RECORD: "treasury.record",
  /** Importing a bank statement and matching lines to movements. */
  TREASURY_RECONCILE: "treasury.reconcile",
  // --- sabit giderler -------------------------------------------------------
  /** Sözleşmeler, vade takvimi, kredi bakiyeleri, sabit gider raporları. */
  FIXED_EXPENSE_READ: "fixed_expense.read",
  /**
   * Sözleşme tanımlamak, tutarını ve banka bilgisini değiştirmek, yürürlüğe
   * almak, feshetmek.
   *
   * Aylık işleyişten (fatura girmek, ödendi işaretlemek) ayrı tutuldu: kirayı
   * her ay kaydeden kişi ile kira sözleşmesini değiştirebilen kişi aynı olmak
   * zorunda değil, ve ikincisi tek bir düzenlemeyle yıllık taahhüdü büyütür.
   */
  FIXED_EXPENSE_MANAGE: "fixed_expense.manage",
  /** Gelen faturayı girmek, dönemi ödemeye hazırlamak, atlamak, ödendi işlemek. */
  FIXED_EXPENSE_RECORD: "fixed_expense.record",
  /**
   * Zam önerisini onaylamak.
   *
   * Kaydetmekten ayrı, çünkü zam öneriyi giren kişinin kendi kendini onaylaması
   * hâlinde kira artışının hiçbir kapıdan geçmemesi demektir — ve artış, bu
   * modüldeki tek kalıcı taahhüt değişikliğidir.
   */
  FIXED_EXPENSE_ESCALATION_APPROVE: "fixed_expense.escalation_approve",
  // --- kiralık uçak ---------------------------------------------------------
  /** Filo, kira sözleşmeleri, garanti uçuş saatleri ve ödeme planı. */
  LEASED_AIRCRAFT_READ: "leased_aircraft.read",
  /**
   * Uçak kartı açmak, kira sözleşmesi tanımlamak, saat ücretini ve resmî /
   * gayriresmî ayrımı değiştirmek, yürürlüğe almak, feshetmek.
   *
   * Aylık işleyişten ayrı tutuldu, sabit giderdekiyle aynı gerekçeyle: uçuş
   * saatini her ay giren kişi ile garanti saati ya da saat ücretini
   * değiştirebilen kişi aynı olmak zorunda değil, ve ikincisi tek bir
   * düzenlemeyle yıllık taahhüdü büyütür.
   */
  LEASED_AIRCRAFT_MANAGE: "leased_aircraft.manage",
  /** Uçuş saati girmek, ödeme talebi açmak, ödendi işlemek. */
  LEASED_AIRCRAFT_RECORD: "leased_aircraft.record",
  /**
   * Bir dönemin uçuş saatini KESİNLEŞTİRMEK.
   *
   * Girmekten ayrı, çünkü kesinleştirme bu modüldeki para kararının ta kendisi:
   * saat kesinleştiği anda garanti mahsubu hesaplanıyor ve ödeme ayakları
   * oluşuyor. Saati yazan kişinin kendi rakamını kendi onaylaması, kiraya
   * verene ne ödendiğini hiçbir kapıdan geçirmemek demek olurdu.
   */
  LEASED_AIRCRAFT_HOURS_CONFIRM: "leased_aircraft.hours_confirm",
  // --- uçuş primi -----------------------------------------------------------
  /**
   * Pilot listesini, uçuş kayıtlarını, prim oranlarını ve aylık primi görmek.
   *
   * Bu kod aynı zamanda `FLIGHT_PREMIUM` veri sınıfını açan anahtar
   * (`ScopeService.resolve`): prim tutarlarını taşıyan tablolar KISITLAYICI bir
   * RLS politikasının arkasında ve o politika bu sınıfı soruyor. Maaşın
   * `SENSITIVE` sınıfından AYRI tutuldu — prim listesini okuyabilen birinin
   * maaş bordrosunu da okuyabilmesi için hiçbir sebep yok, ve tek bir sınıf
   * kullanmak tam olarak bunu yapardı.
   */
  FLIGHT_PREMIUM_READ: "flight_premium.read",
  /**
   * Uçuş kaydı girmek, düzeltmek, dosyadan aktarmak; pilot listesini yönetmek.
   *
   * Ölçümün yetkisi. Fiyatı belirlemekten (aşağıdaki oran yetkisi) ayrı, çünkü
   * saati giren kişi ile saatin kaç lira ettiğini söyleyen kişi aynı olmak
   * zorunda değil — ve ikisini tek elde toplamak, primi tek başına belirleyen
   * bir masa demektir.
   */
  FLIGHT_PREMIUM_RECORD: "flight_premium.record",
  /**
   * Pilotun saatlik prim oranını tanımlamak ve değiştirmek.
   *
   * En dar yetki: buradaki tek bir satır, o pilotun bütün gelecek aylarını
   * yeniden fiyatlandırır.
   */
  FLIGHT_PREMIUM_RATE_MANAGE: "flight_premium.rate_manage",
  /**
   * Hesaplanmış ayı ödeme tablosuna yazmak.
   *
   * Ayrı, çünkü bu adım geri alınamaz bir şey başlatıyor: talep zincire girer,
   * ve zincirin diğer ucunda para vardır. Hesaplamak bir ölçüm, göndermek bir
   * karardır.
   */
  FLIGHT_PREMIUM_FILE_PAYMENT: "flight_premium.file_payment",
  // --- vergi ----------------------------------------------------------------
  /** Mükellefler, mükellefiyetler, vergi takvimi ve kapsam denetimi. */
  TAX_READ: "tax.read",
  /**
   * Mükellef kartı açmak ve düzenlemek; bir mükellefi bir vergiye tabi kılmak
   * ya da kapsam dışı işaretlemek.
   *
   * Kataloğu yönetmekten ayrı: hangi şirketin hangi vergiden sorumlu olduğu
   * finansın günlük bilgisidir, verginin mevzuattaki tarihi ise değil.
   */
  TAX_MANAGE: "tax.manage",
  /**
   * Yükümlülük türlerini ve mevzuat tarihlerini yönetmek.
   *
   * En dar yetki, çünkü buradaki tek bir gün değişikliği BÜTÜN mükelleflerin
   * bütün dönemlerinin tarihini kaydırır — bir mükellefiyet kaydı yalnız
   * kendini ilgilendirir, bir katalog satırı hepsini.
   */
  TAX_CALENDAR_MANAGE: "tax.calendar_manage",
  // --- konaklama (misafirhane) --------------------------------------------
  /** Odalar, yataklar, doluluk, rezervasyonlar, bakiyeler, temizlik, eksikler. */
  KONAKLAMA_READ: "konaklama.read",
  /** Oda ve yatak tanımlamak, ücret tarifesi girmek. */
  KONAKLAMA_MANAGE: "konaklama.manage",
  KONAKLAMA_RESERVATION_MANAGE: "konaklama.reservation_manage",
  /** Öğrenciden para almak. */
  KONAKLAMA_COLLECT: "konaklama.collect",
  /**
   * Bakiyeye indirim, silme veya düzeltme işlemek.
   *
   * Tahsilat almaktan ayrı tutuldu: parayı almak ile borcu silmek aynı güç
   * değil, ve ikincisi çok daha nadir kullanılır.
   */
  KONAKLAMA_LEDGER_ADJUST: "konaklama.ledger_adjust",
  /** Misafirhane giderlerini kaydetmek. */
  KONAKLAMA_EXPENSE_MANAGE: "konaklama.expense_manage",
  KONAKLAMA_HOUSEKEEPING: "konaklama.housekeeping",
  KONAKLAMA_ISSUE_CREATE: "konaklama.issue_create",
  /** Eksiği atamak, çözmek, yatağı kullanıma kapatmak. */
  KONAKLAMA_ISSUE_MANAGE: "konaklama.issue_manage",
  /**
   * Misafirin TCKN'si, telefonu ve veli bilgisi.
   * EMPLOYEE_READ_SENSITIVE ile aynı gerekçeyle sistem yöneticisine verilmez.
   */
  KONAKLAMA_GUEST_READ_SENSITIVE: "konaklama.guest_read_sensitive",
  /**
   * Misafirhane kaydını KALICI olarak silmek.
   *
   * Ayrı bir kod, çünkü sildiği şey yönetmekten farklı: `konaklama.manage` bir
   * odanın adını düzeltir, bu kod odayı yok eder. Aynı koda bağlansaydı, oda
   * tanımlayabilen herkes silebilir olurdu ve yetki matrisinde bu ayrımı
   * yapacak yer kalmazdı.
   *
   * Günlük işletme rolünde DEĞİL, bilerek. Ledger düzeltmesinin o rolden
   * tutulmasıyla aynı gerekçe: tahsilatı alan kişi kaydı da silebiliyorsa,
   * tahsil edilmemiş bir parayı kapatmak tek bir tıklama olur.
   */
  KONAKLAMA_DELETE: "konaklama.delete",
  // --- okul tahsilatı (DST-00120) ------------------------------------------
  /** Sınav ücreti ve eğitim materyali ödemelerini ve dekontlarını görmek. */
  OKUL_TAHSILAT_READ: "okul_tahsilat.read",
  /**
   * Ödemeyi dekontuyla kaydetmek, ek dekont yüklemek, yanlış kaydı iptal etmek.
   *
   * İptal ayrı bir kod değil: kayıt silinmiyor, gerekçesiyle iptal ediliyor ve
   * iz kalıyor. Misafirhanedeki silme/düzeltme ayrımının sebebi — bakiyeyi
   * kapatan tek tıklama — burada yok, çünkü burada borç tutulmuyor.
   */
  OKUL_TAHSILAT_RECORD: "okul_tahsilat.record",
  /**
   * Fiyat listesini ve ödemenin yattığı hesapları yönetmek.
   *
   * Kayıttan ayrı: birim fiyatı değiştiren kişi, her yeni dekontun "beklenen
   * tutar"ını değiştirmiş olur. Bugün ikisi aynı ellerde; ayrı kod, o günü
   * yarın değiştirmenin yolunu açık bırakıyor.
   */
  OKUL_TAHSILAT_CATALOG: "okul_tahsilat.catalog",
  // --- destek -------------------------------------------------------------
  /**
   * Hata bildirmek ve geliştirme istemek. HERKESTE olması beklenir.
   *
   * Bir hatayı bildirmek için izin istemek, hatanın bildirilmemesiyle
   * sonuçlanır — ve bildirilmeyen hata, kimsenin bilmediği hatadır. Bu kod yine
   * de var, çünkü servis hesaplarının ve entegrasyon kullanıcılarının kayıt
   * açmasının bir anlamı yok.
   */
  SUPPORT_CREATE: "support.create",
  /** Kendi açtığı kayıtları ve onlara gelen cevapları görmek. */
  SUPPORT_READ_OWN: "support.read_own",
  /**
   * BAŞKALARININ kayıtlarını da görmek — destek/BT ekibi.
   *
   * READ_OWN'dan ayrı, çünkü bir hata kaydının gövdesinde ekran görüntüsü,
   * personel adı, tutar geçer. "Herkes her şeyi görsün" bu modülde şeffaflık
   * değil, kimsenin fark etmediği bir veri sızıntısıdır.
   */
  SUPPORT_READ: "support.read",
  /** Durum değiştirmek, atamak, önceliklendirmek, kapatmak. */
  SUPPORT_MANAGE: "support.manage",
  // --- doküman kütüphanesi ------------------------------------------------
  /**
   * Grubun yazılı belgelerini (yönetmelik, prosedür, form, resmî yazı) okumak.
   *
   * DOCUMENT_READ ile karıştırılmamalı ve ondan ayrı olması kasıtlı: o, bir
   * PERSONELİN özlük evrakını okuma yetkisidir — sağlık raporu, diploma, adli
   * sicil. Buradaki belgelerin sahibi yok. İkisini tek koda bağlamak, ortak bir
   * prosedürü okuyabilsin diye birine bütün personelin özlük dosyasını açmak
   * demek olurdu.
   */
  DOCUMENT_LIBRARY_READ: "document_library.read",
  /**
   * Klasör açmak, dosya yüklemek, yeni sürüm koymak, silmek.
   *
   * Klasör başına kilit ayrıca var (`doc_folder.required_permission`) ve bu
   * kodun yerine geçmez: bu kod kütüphaneye yazabilmek, o ise tek bir klasörü
   * görebilmek. Grup düzeyi bir klasöre yazmak ayrıca GRUP KAPSAMI ister —
   * kapsam yetkinin yerini tutmaz, ikisi birlikte karar verir.
   */
  DOCUMENT_LIBRARY_MANAGE: "document_library.manage",
  // --- ajanda -------------------------------------------------------------
  /**
   * Kendi ajandası: not, hatırlatma ve görev yazmak, kendine düşenleri görmek.
   *
   * HERKESTE olması beklenir, SUPPORT_CREATE ile aynı gerekçeyle. Kod yine de
   * var, çünkü servis hesaplarının ve entegrasyon kullanıcılarının ajandası
   * yoktur — ve bir sonraki modülün "herkeste var" diye yetkisiz açtığı bir uç,
   * bu depodaki "varsayılan kapalı" kuralının tek istisnası olurdu.
   */
  AGENDA_USE: "agenda.use",
  /**
   * BAŞKASININ ajandasına iş düşürmek — görevlendirme.
   *
   * Kendi ajandasını tutmaktan ayrı, çünkü bunlar farklı iki şey: biri kendi
   * gününü düzenlemek, diğeri bir başkasının gününü. İkincisi bir yetki
   * ilişkisidir ve herkeste olmamalı — aksi hâlde bu modül, sisteme yerleşmiş
   * bir "birbirine iş yazma" kanalına dönüşür.
   */
  AGENDA_ASSIGN: "agenda.assign",
  /**
   * Kapsamındaki BÜTÜN görevlendirmeleri görmek — kimde ne var.
   *
   * Dikkat: bu, "başkasının ajandasını okumak" DEĞİL ve öyle bir yetki bu
   * katalogda bilerek yok. Bir ajandada doktor randevusu, mülakat notu ve
   * "avukatı ara" yazar; onları okumak, işin nerede olduğunu bilmek için
   * gereken şeyin çok ötesindedir.
   *
   * Bu yüzden yetkinin gördüğü küme veriyle sınırlanıyor, ekranla değil:
   * yalnızca BAŞKASINA VERİLMİŞ kayıtlar (`assignee <> created_by`). Kişinin
   * kendine yazdığı hiçbir satır, bu yetkiyle hiçbir yerde görünmez.
   */
  AGENDA_OVERSEE: "agenda.oversee",
  // --- duyuru ve memorandum -------------------------------------------------
  /**
   * Kendisine tebliğ edilen duyuru ve memorandumları okumak, görüntülemesini
   * damgalamak, beyan vermek.
   *
   * HERKESTE, AGENDA_USE ve SUPPORT_CREATE ile aynı gerekçeyle: kendi
   * işvereninin tebliğini okumak için izin istemek, okunmayan tebliğ üretir.
   * Kod yine de var — servis ve entegrasyon hesaplarının memorandumu yoktur.
   */
  ANNOUNCEMENT_READ: "announcement.read",
  /**
   * Yayımlamak: taslak, hedef, yayım (metni ve muhatap listesini donduran an),
   * geri çekme, yeniden tebliğ, hatırlatma.
   *
   * Erişim alanını RÜTBE değil KAPSAM sınırlıyor: grup geneli bir yayım GRUP
   * kapsamı ister ve bunu hem servis (okunur 403 için) hem RLS yazma
   * politikası (dayanak için) uyguluyor.
   */
  ANNOUNCEMENT_PUBLISH: "announcement.publish",
  /**
   * Makbuzları AD AD görmek: kim, ne zaman, saat kaçta.
   *
   * Yayımlamaktan ayrı bir kod, ve bu ayrım kasıtlı: yayımlamak şirket adına
   * yapılan bir iş, makbuz listesi ise bir meslektaşın dakika dakika okuma
   * davranışı. İmza kovalayan kişi çoğu zaman metni yazan kişi değildir —
   * activity.read ile audit.read arasındaki ayrımın aynısı.
   *
   * Yayımcı kendi yayımının SAYILARINI bu kod olmadan da görür; adları görmez.
   */
  ANNOUNCEMENT_RECEIPT_READ: "announcement.receipt_read",
  // --- kurumsal iletişim: mesajlaşma ---------------------------------------
  /**
   * Kendi konuşmalarını okumak ve mesaj göndermek.
   *
   * HERKESTE, ANNOUNCEMENT_READ ile aynı gerekçeyle: bir meslektaşına yazmak
   * için izin istemek, sistemin dışında yazışan bir şirket üretir — ve o
   * yazışmanın kurumsal saklama politikası olmaz. Kod yine de var, çünkü
   * servis ve entegrasyon hesaplarının konuşması yoktur.
   *
   * Bu kod BİR KONUŞMAYI AÇMAYA yetmez: her uç ayrıca ÜYELİK sorar ve üyesi
   * olmadığın bir konuşma 404 döner. İzin "mesajlaşabilir misin"i, üyelik
   * "hangi mesajlaşmayı"yı cevaplar.
   */
  MESSAGE_USE: "message.use",
  /**
   * Birden fazla kişiyle grup konuşması açmak.
   *
   * MESSAGE_USE'dan ayrı, çünkü grup açmak bir DAĞITIM işidir: kurulan grup
   * üyelerine bildirim üretir ve kimin kiminle konuştuğunu belirler. Birebir
   * yazışma kişinin kendi işi, grup kurmak organizasyonun işi.
   */
  MESSAGE_GROUP_CREATE: "message.group_create",
  /**
   * Departman ve konu kanallarını açmak, adlandırmak, üyeliğini yönetmek.
   *
   * Gruptan ayrı bir kod, çünkü kanal KALICI bir yapıdır ve departman kanalı
   * organizasyon şemasını takip eder: yanlış kurulmuş bir kanal, yıllarca
   * yanlış insanların okuduğu bir yer olur. Grup geçicidir, kanal kurumsaldır.
   */
  MESSAGE_CHANNEL_MANAGE: "message.channel_manage",
  /**
   * Başkasının mesajını kurumsal saklama politikasına uygun biçimde kaldırmak
   * ve kanal üyeliğine müdahale etmek.
   *
   * Kaldırma bir SİLME DEĞİL: gövde yerinde kalır, satır "kaldırıldı" damgası
   * ve gerekçeyle işaretlenir, denetim kaydına düşer. Gerçek silme hiçbir
   * yetkiyle mümkün değil — bir yazışmayı yok edebilen yetki, o yazışmanın
   * delil değerini bütünüyle bitirir.
   */
  MESSAGE_MODERATE: "message.moderate",
  /**
   * Bütün iletişim kayıtlarının denetim izini okumak.
   *
   * AUDIT_READ'in altına GİRMİYOR ve MESSAGE_MODERATE'ten de ayrı, ikisi de
   * kasıtlı: moderatör bir mesaja müdahale eder, bu kod ise kimin kiminle
   * yazıştığının tarihçesini okur. announcement.receipt_read'in
   * announcement.publish'ten ayrılmasıyla aynı ayrım — müdahale yetkisi,
   * gözetleme yetkisi değildir.
   */
  MESSAGE_AUDIT_READ: "message.audit_read",
  /**
   * Duyuru/memorandum KATEGORİ sözlüğünü yönetmek: satır açmak, adlandırmak,
   * sıralamak, pasife almak.
   *
   * Yayımlamaktan AYRI, çünkü kategori listesi kurumun ortak sözlüğü — bir
   * yayımcının kendi yazısı değil. Yayımlayan herkes listeye satır
   * ekleyebilseydi sözlük birkaç ayda birbirinin eşanlamlısı yirmi satıra
   * dönerdi ve "geçen yıl kaç prosedür tebliğ ettik" sorusu kod bazında
   * cevaplanamaz olurdu.
   *
   * SİLME YETKİSİ DEĞİL: bu kod pasife almayı açıyor, silmeyi değil. Geçmiş
   * kayıtlar kodu saklıyor ve silinen bir kategori, dün tebliğ edilmiş bir
   * memorandumun künyesindeki ibareyi bugün ortadan kaldırırdı.
   */
  ANNOUNCEMENT_CATEGORY_MANAGE: "announcement.category_manage",
  /**
   * Bir memorandum taslağını onay zincirine SOKMAK.
   *
   * Yayımlamaktan ayrı bir kod, çünkü onaya sunmak yayımın kendisi değil:
   * sunan kişi metni artık düzenleyemez ve karar sırası kapıdakilere geçer.
   * Aynı sebeple `announcement.approve`'tan da ayrı — taslağı sunan ile
   * kapıda karar veren aynı kişi olduğunda zincir hiç kurulmamış demektir.
   */
  ANNOUNCEMENT_SUBMIT: "announcement.submit",
  /**
   * Onay zincirinin bir kapısında KARAR VERMEK: onaylamak, reddetmek ya da
   * revizyon istemek.
   *
   * ALAN BAŞINA TEK KOD, ve bu kural motorunun tanıdığı tek şekil: kapıları
   * birbirinden KOLTUK ayırıyor, izin değil. Kapı başına ayrı bir izin
   * yazmak, masraf zincirinin aylarca geçilemeyen ikinci kapısını üretmişti
   * (DST-00060) — izinle koltuğun kesişimi boş kalır ve bunu hiçbir ekran
   * söylemez.
   *
   * Bu kod TEK BAŞINA bir memorandumu onaylamaya yetmez: kişinin ayrıca o
   * kapıda bir koltuğu (ya da geçerli bir vekâleti) olmalı. İzin "onay
   * verebilir misin"i, koltuk "bu kapıda mısın"ı cevaplıyor.
   */
  ANNOUNCEMENT_APPROVE: "announcement.approve",
  /**
   * Yayımlanmış bir duyuru ya da memorandumu TUTANAK olarak dışa aktarmak
   * (PDF): künye, gövde, ekler, onaylayanlar ve dağıtım listesi tek sayfada.
   *
   * Okumaktan AYRI bir kod, ve ayrım okuyanın yetkisiyle değil belgenin
   * gideceği yerle ilgili: ekrandaki metin bu sistemin içinde durur ve her
   * açılışı kayda geçer, dışa aktarılan dosya ise e-postayla dolaşır, basılır
   * ve bir dosya dolabında kalır. Aynı sebeple her dışa aktarma
   * `sys_data_access_log`'a bir EXPORT satırı yazıyor — `payment.execute`'un
   * haftalık tabloyu PDF'e verirken yazdığının aynısı.
   *
   * Yayımlamaktan da ayrı: tutanağı isteyen kişi çoğu zaman metni yazan kişi
   * değil, imzaları dosyalayan kişidir (`announcement.receipt_read`'in
   * `announcement.publish`'ten ayrılmasıyla aynı ayrım). Rol matrisinden
   * verilecek bir karar olarak duruyor.
   */
  ANNOUNCEMENT_EXPORT: "announcement.export",
  /**
   * Birim ERP'lerinden gelen, tamamlanmış bakım işlerini görmek.
   *
   * `FINANCE_READ`'in altına GİRMİYOR ve bu bilerek: bu ekran bir mali tablo
   * değil, bir takip listesidir — "iş bitti, faturası hâlâ kesilmedi". Onu
   * `FINANCE_READ`'e bağlamak, faturayı kovalaması gereken kişiye grubun bütün
   * finans verisini açmak olurdu; tersi de doğru, finansı okuyan herkesin bu
   * listeyi izlemesi gerekmiyor.
   */
  MAINTENANCE_READ: "maintenance.read",
  /**
   * Bir bakım işini "faturalanmayacak" diye işaretlemek (garanti, grup içi iş,
   * kendi filomuz) ve bu işareti geri almak.
   *
   * Okumaktan ayrı, çünkü bu işaret bir satırı takip listesinden DÜŞÜRÜR: aksi
   * hâlde listeyi izleyen herkesin elinde, izlediği şeyi görünmez kılan bir
   * düğme olurdu. Ölçüm satırına dokunmaz — hüküm ayrı satırda durur, kim ve ne
   * zaman dediği yazılır, geri alınması da denetim kaydına düşer.
   */
  MAINTENANCE_BILLING_EXCLUDE: "maintenance.billing_exclude"
};
var ALL_PERMISSIONS = Object.values(PERMISSIONS);
var PERMISSION_LABELS = {
  "user.read": "Kullan\u0131c\u0131lar\u0131 g\xF6r\xFCnt\xFCle",
  "user.manage": "Kullan\u0131c\u0131 y\xF6net",
  "role.read": "Rolleri g\xF6r\xFCnt\xFCle",
  "role.manage": "Rol ve yetki y\xF6net",
  "settings.manage": "Sistem ayarlar\u0131n\u0131 y\xF6net",
  "job.read": "Otomatik i\u015Fleri g\xF6r\xFCnt\xFCle",
  "job.manage": "Otomatik i\u015Fleri y\xF6net",
  "audit.read": "Denetim kayd\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "correction.read": "Veri d\xFCzeltme analizlerini g\xF6r\xFCnt\xFCle",
  "correction.manage": "Veri d\xFCzeltme i\u015Flemi y\xFCr\xFCt",
  "security.read": "G\xFCvenlik olaylar\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "activity.read": "Personel aktivitesi (kim \xE7evrimi\xE7i, en son ne zaman g\xF6r\xFCld\xFC)",
  "activity.page_read": "Ki\u015Finin o an hangi ekranda oldu\u011Funu g\xF6rme",
  "company.read": "\u015Eirketleri g\xF6r\xFCnt\xFCle",
  "company.manage": "\u015Eirket y\xF6net",
  "org_unit.read": "Departmanlar\u0131 g\xF6r\xFCnt\xFCle",
  "org_unit.manage": "Departman y\xF6net",
  "employee.read_self": "Kendi personel kart\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "employee.read": "Personel kartlar\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "employee.read_sensitive": "Hassas personel verisi (maa\u015F, TCKN, IBAN)",
  "employee.write": "Personel kart\u0131 d\xFCzenle",
  "employee.link_user": "Personel kart\u0131n\u0131 kullan\u0131c\u0131 hesab\u0131na ba\u011Fla",
  "asset.read": "Zimmet kay\u0131tlar\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "asset.manage": "Varl\u0131k kart\u0131 olu\u015Ftur ve d\xFCzenle",
  "asset.read_own": "Kendi zimmetlerini g\xF6r\xFCnt\xFCle",
  "asset.assign": "Zimmet ver (tekli ve toplu)",
  "asset.assign.cross_company": "\u015Eirketler aras\u0131 zimmet verebilir",
  "asset.return": "\u0130ade al ve devret",
  "asset.cancel": "Zimmet kayd\u0131n\u0131 iptal et (ters kay\u0131t)",
  "asset.incident": "Hasar, kay\u0131p, bak\u0131m ve kalibrasyon kayd\u0131 gir",
  "asset.cost_read": "Varl\u0131k maliyet ve zarar tutarlar\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "asset.report": "Zimmet raporlar\u0131n\u0131 g\xF6r\xFCnt\xFCle ve d\u0131\u015Fa aktar",
  "asset.calibration_override": "Kalibrasyonu ge\xE7mi\u015F ekipmana istisna uygula",
  "asset.exit_override": "A\xE7\u0131k zimmete ra\u011Fmen personel \xE7\u0131k\u0131\u015F\u0131na izin ver",
  "employee.identity_confirm": "Birim ERP e\u015Fle\u015Fmesini onayla",
  "document.read_own": "Kendi belgelerini g\xF6r\xFCnt\xFCle",
  "payslip.read_own": "Kendi bordrolar\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "payslip.manage": "Bordro y\xFCkle ve y\xF6net",
  "document.read": "\xD6zl\xFCk belgelerini g\xF6r\xFCnt\xFCle",
  "document.write": "\xD6zl\xFCk belgesi y\xFCkle/d\xFCzenle",
  "credential.read": "Sertifika ve lisanslar\u0131 g\xF6r\xFCnt\xFCle",
  "credential.manage": "Sertifika ve lisans y\xF6net",
  "leave_request.create": "Kendine izin talebi olu\u015Ftur",
  "leave_request.create_for_others": "Ba\u015Fkas\u0131 ad\u0131na izin talebi olu\u015Ftur",
  "leave_request.read": "\u0130zin taleplerini g\xF6r\xFCnt\xFCle",
  "leave_request.approve": "\u0130zin talebi onayla/reddet",
  "leave_request.cancel_own": "Kendi talebini iptal et",
  "leave_request.cancel": "Talep iptal et",
  "leave_ledger.adjust": "\u0130zin bakiyesi d\xFCzeltmesi yap",
  "leave_policy.manage": "\u0130zin politikalar\u0131n\u0131 y\xF6net",
  "holiday.manage": "Resm\xEE tatil takvimini y\xF6net",
  "dashboard.self": "Ki\u015Fisel panel",
  "dashboard.team": "Ekip paneli",
  "dashboard.group": "Grup paneli",
  "report.company": "\u015Eirket raporlar\u0131",
  "report.group": "Grup raporlar\u0131",
  "attendance.record": "Giri\u015F/\xE7\u0131k\u0131\u015F kayd\u0131 olu\u015Ftur",
  "attendance.read": "Puantaj g\xF6r\xFCnt\xFCle",
  "attendance.manage": "Puantaj y\xF6net ve karara ba\u011Fla",
  "attendance.policy.manage": "Puantaj politikas\u0131 y\xF6net",
  "compensation.manage": "Maa\u015F, prim ve avans y\xF6net",
  "integration.read": "Entegrasyon durumunu g\xF6r\xFCnt\xFCle",
  "integration.admin": "Entegrasyon y\xF6net",
  "payment_request.create": "\xD6deme talebi olu\u015Ftur",
  "payment_request.urgent": "Acil \xF6deme talebi a\xE7 (normal zinciri atlar)",
  "payment.read": "\xD6deme tablosunu g\xF6r\xFCnt\xFCle",
  "payment.approve": "\xD6deme onay zincirinde kendi a\u015Famas\u0131n\u0131 onayla",
  "payment.approval_chain_manage": "\xD6deme onay zincirini ve onaylayanlar\u0131 tan\u0131mla",
  "payment.week_manage": "Haftal\u0131k \xF6deme tablosunu y\xF6net",
  "payment.execute": "\xD6demeyi ger\xE7ekle\u015Ftir",
  "payment.card_settle": "Dekontu y\xFCkl\xFC \xF6demeyi (kart ya da havale) \xAB\xD6dendi\xBB i\u015Faretle",
  "payment.defer": "Onayl\u0131 \xF6demeyi sonraki haftaya b\u0131rak",
  "payment.transfer_form": "Transfer formu haz\u0131rla",
  "payee.manage": "Cari (tedarik\xE7i) kayd\u0131 tan\u0131mla ve d\xFCzenle",
  "invoice.read": "Fatura kay\u0131tlar\u0131n\u0131 ve onay durumunu g\xF6r\xFCnt\xFCle",
  "invoice.approve": "Faturay\u0131 onayla veya gerek\xE7esiyle reddet",
  "staff_request.create": "Kendi ihtiya\xE7 talebini a\xE7",
  "staff_request.read": "Ba\u015Fkalar\u0131n\u0131n ihtiya\xE7 taleplerini g\xF6r\xFCnt\xFCle",
  "staff_request.approve": "\u0130htiya\xE7 talebini onayla veya gerek\xE7esiyle reddet",
  "staff_request.purchase": "Onaylanan talebi sat\u0131n al ve \xF6deme talebine d\xF6n\xFC\u015Ft\xFCr",
  "expense.create": "Masraf talebi olu\u015Ftur",
  "expense.approve": "Masraf talebi onayla",
  "expense.approve_unit": "Masraf talebine \xFCst kademe onay\u0131 ver",
  "treasury.read": "Kasa ve banka bakiyelerini g\xF6r\xFCnt\xFCle",
  "treasury.account_manage": "Kasa/banka hesab\u0131 a\xE7, kapat, a\xE7\u0131l\u0131\u015F bakiyesi gir",
  "treasury.record": "Para hareketi ve virman kaydet",
  "treasury.reconcile": "Banka ekstresi y\xFCkle ve mutabakat yap",
  "fixed_expense.read": "Sabit giderleri g\xF6r\xFCnt\xFCle",
  "fixed_expense.manage": "Sabit gider s\xF6zle\u015Fmesi tan\u0131mla ve y\xF6net",
  "fixed_expense.record": "Sabit gider faturas\u0131 ve \xF6demesi i\u015Fle",
  "fixed_expense.escalation_approve": "Sabit gider zamm\u0131n\u0131 onayla",
  "leased_aircraft.read": "Kiral\u0131k u\xE7aklar\u0131 ve s\xF6zle\u015Fmeleri g\xF6r\xFCnt\xFCle",
  "leased_aircraft.manage": "Kiral\u0131k u\xE7ak ve kira s\xF6zle\u015Fmesi tan\u0131mla ve y\xF6net",
  "leased_aircraft.record": "U\xE7u\u015F saati gir, kira \xF6demesi i\u015Fle",
  "leased_aircraft.hours_confirm": "D\xF6nemin u\xE7u\u015F saatini kesinle\u015Ftir",
  "flight_premium.read": "U\xE7u\u015F primlerini g\xF6r\xFCnt\xFCle",
  "flight_premium.record": "U\xE7u\u015F kayd\u0131 gir ve pilot listesini y\xF6net",
  "flight_premium.rate_manage": "U\xE7u\u015F prim oran\u0131 belirle",
  "flight_premium.file_payment": "Ayl\u0131k u\xE7u\u015F primini \xF6demeye g\xF6nder",
  "tax.read": "Vergi takvimini ve m\xFCkellefleri g\xF6r\xFCnt\xFCle",
  "tax.manage": "M\xFCkellef ve m\xFCkellefiyet y\xF6net",
  "tax.calendar_manage": "Vergi t\xFCrlerini ve mevzuat tarihlerini y\xF6net",
  "finance.period_close": "Mali d\xF6nem kapat",
  "finance.read": "Finans verilerini g\xF6r\xFCnt\xFCle",
  "finance.manage": "Finans y\xF6net",
  "project.read": "Proje / operasyonlar\u0131 g\xF6r\xFCnt\xFCle",
  "project.manage": "Proje / operasyon y\xF6net",
  "budget.manage": "B\xFCt\xE7e versiyonu haz\u0131rla ve sat\u0131r gir",
  "budget.lock": "B\xFCt\xE7e versiyonunu y\xFCr\xFCrl\xFC\u011Fe al ve kilitle",
  "konaklama.read": "Misafirhane kay\u0131tlar\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "konaklama.manage": "Oda, yatak ve \xFCcret tarifesi y\xF6net",
  "konaklama.reservation_manage": "Rezervasyon, giri\u015F ve \xE7\u0131k\u0131\u015F i\u015Fle",
  "konaklama.collect": "Konaklama tahsilat\u0131 i\u015Fle",
  "konaklama.ledger_adjust": "Konaklama bakiyesi d\xFCzeltmesi yap",
  "konaklama.expense_manage": "Misafirhane gideri kaydet",
  "konaklama.delete": "Misafirhane kayd\u0131n\u0131 kal\u0131c\u0131 olarak sil",
  "konaklama.housekeeping": "Temizlik kayd\u0131 gir",
  "konaklama.issue_create": "Ar\u0131za ve eksik bildir",
  "konaklama.issue_manage": "Ar\u0131za ve eksik kayd\u0131n\u0131 y\xF6net",
  "konaklama.guest_read_sensitive": "Hassas misafir verisi (TCKN, telefon, veli)",
  "okul_tahsilat.read": "S\u0131nav ve materyal tahsilatlar\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "okul_tahsilat.record": "S\u0131nav ve materyal tahsilat\u0131 kaydet (dekontla)",
  "okul_tahsilat.catalog": "Okul tahsilat fiyat listesi ve hesaplar\u0131n\u0131 y\xF6net",
  "support.create": "Hata bildir ve geli\u015Ftirme iste",
  "support.read_own": "Kendi destek kay\u0131tlar\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "support.read": "T\xFCm destek kay\u0131tlar\u0131n\u0131 g\xF6r\xFCnt\xFCle",
  "support.manage": "Destek kayd\u0131n\u0131 y\xF6net (durum, atama, \xF6ncelik)",
  "document_library.read": "Dok\xFCman k\xFCt\xFCphanesini g\xF6r\xFCnt\xFCle",
  "document_library.manage": "Dok\xFCman k\xFCt\xFCphanesine klas\xF6r a\xE7 ve dosya y\xFCkle",
  "agenda.use": "Kendi ajandas\u0131n\u0131 kullan (not, hat\u0131rlatma, g\xF6rev)",
  "agenda.assign": "Ba\u015Fkas\u0131na g\xF6rev ver (g\xF6revlendirme)",
  "agenda.oversee": "Kapsam\u0131ndaki g\xF6revlendirmeleri izle",
  "announcement.read": "Duyuru ve memorandumlar\u0131 g\xF6r\xFCnt\xFCle",
  "announcement.publish": "Duyuru ve memorandum yay\u0131mla",
  "announcement.receipt_read": "Duyuru okuma kay\u0131tlar\u0131n\u0131 (kim, ne zaman) g\xF6r\xFCnt\xFCle",
  "message.use": "Mesajla\u015Fmay\u0131 kullan",
  "message.group_create": "Grup konu\u015Fmas\u0131 a\xE7",
  "message.channel_manage": "Kanal a\xE7 ve y\xF6net",
  "message.moderate": "Mesaj moderasyonu",
  "message.audit_read": "\u0130leti\u015Fim denetim izini g\xF6r\xFCnt\xFCle",
  "announcement.category_manage": "Duyuru ve memorandum kategorilerini y\xF6net",
  "announcement.submit": "Memorandumu onaya sun",
  "announcement.approve": "Memorandum onay kap\u0131s\u0131nda karar ver",
  "announcement.export": "Duyuru/memorandum tutana\u011F\u0131n\u0131 PDF d\u0131\u015Fa aktar",
  "maintenance.read": "Birimlerin tamamlanan bak\u0131m i\u015Flerini g\xF6r\xFCnt\xFCle",
  "maintenance.billing_exclude": "Bak\u0131m i\u015Fini faturalanmayacak olarak i\u015Faretle"
};

// src/enums/scopes.ts
import { z as z3 } from "zod";
var SCOPE_TYPES = {
  GROUP: "GROUP",
  COMPANY: "COMPANY",
  DEPARTMENT: "DEPARTMENT",
  SELF: "SELF"
};
var scopeSchema = z3.object({
  type: z3.enum([SCOPE_TYPES.GROUP, SCOPE_TYPES.COMPANY, SCOPE_TYPES.DEPARTMENT, SCOPE_TYPES.SELF]),
  /** companyId for COMPANY, orgUnitId for DEPARTMENT, null for GROUP/SELF. */
  targetId: z3.uuid().nullable()
});
var SCOPE_LABELS = {
  GROUP: "Grup geneli",
  COMPANY: "\u015Eirket",
  DEPARTMENT: "Departman (alt birimler d\xE2hil)",
  SELF: "Yaln\u0131zca kendisi"
};

// src/enums/roles.ts
var ROLES = {
  PERSONEL: "personel",
  DEPARTMAN_YONETICISI: "departman_yoneticisi",
  BIRIM_MUDURU: "birim_muduru",
  IK_UZMANI: "ik_uzmani",
  IK_YONETICISI: "ik_yoneticisi",
  MISAFIRHANE_SORUMLUSU: "misafirhane_sorumlusu",
  DEPO_SORUMLUSU: "depo_sorumlusu",
  FINANS_UZMANI: "finans_uzmani",
  HAZINE_HESAP_SORUMLUSU: "hazine_hesap_sorumlusu",
  FINANS_YONETICISI: "finans_yoneticisi",
  GRUP_YONETICISI: "grup_yoneticisi",
  ORTAK: "ortak",
  SISTEM_YONETICISI: "sistem_yoneticisi",
  SUPER_ADMIN: "super_admin"
};
var P = PERMISSIONS;
var SELF_PERMISSIONS = [
  P.EMPLOYEE_READ_SELF,
  P.DOCUMENT_READ_OWN,
  // Kendi bordrosunu görmek bir ayrıcalık değil — DST-00092'nin tamamı bu
  // cümle. Burada olması, rol matrisinde tek tek dağıtılmasına gerek
  // bırakmıyor: SELF_PERMISSIONS'ı yayan sekiz rolün hepsi bunu alıyor.
  P.PAYSLIP_READ_OWN,
  P.LEAVE_REQUEST_CREATE,
  P.LEAVE_REQUEST_CANCEL_OWN,
  // Clocking oneself in is not a privilege; everyone has it.
  P.ATTENDANCE_RECORD,
  // Nor is claiming back money one has already spent.
  P.EXPENSE_CREATE,
  // Anyone who buys something for the company files the payment request; the
  // gates are what control it, not who may ask.
  //
  // PAYMENT_READ is deliberately NOT here, and used to be. Every X_READ in this
  // catalogue means "read other people's X" and is granted to the roles that
  // need it — LEAVE_REQUEST_READ sits on the group manager, not in this list.
  // PAYMENT_READ broke that pattern, and because it gated the list endpoint
  // while the query bounded rows by company scope alone, every employee could
  // read the whole company's payment table: payee, amount, IBAN last four and
  // the free-text reason. Filing a request is self-service; reading everyone
  // else's is not.
  P.PAYMENT_REQUEST_CREATE,
  // İhtiyacını söyleyebilmek de öyle — ve bu, ödeme talebi açmaktan DAHA
  // küçük bir yetki: talep hiçbir carinin hesabına, hiçbir tutara ve hiçbir
  // belgeye dokunmuyor. Yalnız "buna ihtiyacım var" diyor; parayı hareket
  // ettiren adım onaydan sonra, başka bir masada açılıyor.
  P.STAFF_REQUEST_CREATE,
  P.DASHBOARD_SELF,
  // Bir hatayı bildirmek için izin istemek, hatanın bildirilmemesiyle
  // sonuçlanır. Kendi bildirdiğini takip edebilmek de aynı pakete ait: cevabı
  // görülemeyen bir bildirim, ikinci kez bildirilir.
  P.SUPPORT_CREATE,
  P.SUPPORT_READ_OWN,
  // Kütüphaneyi OKUMAK herkesin. Bir yönetmeliği okuyabilmek için izin istemek,
  // yönetmeliğin okunmamasıyla sonuçlanır — ve bu modülün tamamı, yazılmış ama
  // bulunamayan bir prosedürü engellemek için var. Gizli kalması gereken evrak
  // klasör başına kilitleniyor (doc_folder.required_permission), kütüphanenin
  // kapısında değil: kapıya konan kilit, yanlış tarafta duran kilittir.
  P.DOCUMENT_LIBRARY_READ,
  // Kendi gününü yazmak da bir ayrıcalık değil — ve bu satır olmadan modül,
  // asıl kullanıcısı için hiç açılmaz. AGENDA_ASSIGN bilerek burada DEĞİL:
  // başkasının gününe iş düşürmek bir yetki ilişkisidir, herkeste olursa
  // modül bir "birbirine iş yazma" kanalına döner.
  P.AGENDA_USE,
  // Kendi işvereninin tebliğini okumak için izin istemek, okunmayan tebliğ
  // üretir. Yayımlamak (announcement.publish) ve makbuzları ad ad görmek
  // (announcement.receipt_read) bilerek burada DEĞİL.
  P.ANNOUNCEMENT_READ,
  // Bir meslektaşına yazmak için izin istemek, sistemin DIŞINDA yazışan bir
  // şirket üretir — ve o yazışmanın kurumsal saklama politikası olmaz.
  // Grup açmak da burada: ekip kurmak günlük işin kendisi, ayrıcalık değil.
  // Kalıcı KANAL açmak (message.channel_manage) bilerek DEĞİL — kanal
  // organizasyon şemasını takip eder ve yanlış kurulanı yıllarca yaşar.
  P.MESSAGE_USE,
  P.MESSAGE_GROUP_CREATE,
  // Üzerine ne zimmetlendiğini görmek de öyle — ve bu satır olmadan zimmet
  // modülü, kaydın MUHATABI için hiç açılmaz. Kabul beyanını verecek kişi de
  // bu; beyanı görmediği bir tutanağa veremez. ASSET_READ bilerek DEĞİL:
  // ev düzeninde her X_READ "başkasınınkini oku" demek.
  P.ASSET_READ_OWN
];
var MANAGER_PERMISSIONS = [
  // Zimmet YALNIZ OKUMA: "ekibimde kimde ne var" bir yönetim sorusu; kütüğe
  // yazmak ve eşyayı teslim etmek İK'nın işi (DST-00065). Raporu da görür —
  // gecikmiş iadeyi kovalayacak kişi yöneticidir, ama maliyeti görmez.
  P.ASSET_READ,
  P.ASSET_REPORT,
  // Departman ve konu kanalları: kimin hangi kanalı açacağına karar veren
  // kişi, ekibin kimlerden oluştuğunu bilen kişidir.
  P.MESSAGE_CHANNEL_MANAGE,
  ...SELF_PERMISSIONS,
  P.EMPLOYEE_READ,
  P.ORG_UNIT_READ,
  P.COMPANY_READ,
  P.LEAVE_REQUEST_READ,
  P.LEAVE_REQUEST_APPROVE,
  P.CREDENTIAL_READ,
  // Seeing the team's working time is what a line manager is for.
  P.ATTENDANCE_READ,
  P.EXPENSE_APPROVE,
  // A department manager decides what their department buys — but only where
  // the chain gives them a seat. The permission opens the door; the assignment
  // is what puts a request in front of them.
  P.PAYMENT_APPROVE,
  // Ekibinin ne istediğini görmek ve buna karar vermek: talebin ilk kapısı
  // neredeyse her zaman burası. Görme ve karar ayrı kodlar, çünkü bir birimin
  // ihtiyaç listesini okumak ile o listeye "alınsın" demek ayrı işler.
  P.STAFF_REQUEST_READ,
  P.STAFF_REQUEST_APPROVE,
  P.DASHBOARD_TEAM,
  // İş vermek ve verdiği işin nerede olduğunu görmek: yöneticiliğin tanımı bu.
  // İkisi tek pakette, çünkü verdiği işi takip edemeyen bir yönetici, aynı işi
  // ikinci kez sorar.
  P.AGENDA_ASSIGN,
  P.AGENDA_OVERSEE
];
var HR_SPECIALIST_PERMISSIONS = [
  // Zimmet: eşya kişiye bağlanıyor ve personel kaydı bu masada (DST-00065).
  // Günlük işin tamamı burada — kütük, teslim, iade, hasar. İSTİSNA yetkileri
  // bilerek yok: kuralı delen işlem uzmanın değil, yöneticinin kararı.
  P.ASSET_READ,
  P.ASSET_MANAGE,
  P.ASSET_ASSIGN,
  P.ASSET_RETURN,
  P.ASSET_CANCEL,
  P.ASSET_INCIDENT,
  P.ASSET_COST_READ,
  ...MANAGER_PERMISSIONS,
  P.EMPLOYEE_WRITE,
  // Opening a personnel card from an existing login is the İK specialist's
  // everyday route, so the authority sits with the specialist and not only with
  // the İK manager. What keeps it safe is the scope, not the rank: the linkable
  // list is filtered by the caller's own visibility, so a company-scoped
  // specialist never even sees a group-scoped holding administrator.
  P.EMPLOYEE_LINK_USER,
  P.DOCUMENT_READ,
  P.DOCUMENT_WRITE,
  // Bordro dağıtımı özlük işinin parçası ve uzmanda duruyor: aynı masa zaten
  // iş sözleşmesini yüklüyor (DOCUMENT_WRITE), ve o belge de ücreti gösteriyor.
  // Bordroyu bir kademe yukarı koymak, aynı bilgiyi iki farklı rütbeye bağlamak
  // olurdu. Maaş TABLOSU ayrı ve yukarıda kalıyor — o EMPLOYEE_READ_SENSITIVE.
  P.PAYSLIP_MANAGE,
  // Yönetmeliği, prosedürü ve formu yayımlayan taraf İK. Kendi şirketinin
  // klasörlerini açar ve doldurur; grup düzeyi bir klasör açmak ayrıca grup
  // kapsamı ister, o da şirket kapsamlı uzmanda yok — yani bu satır uzmanı
  // grubun tamamına yazan biri yapmıyor.
  P.DOCUMENT_LIBRARY_MANAGE,
  P.CREDENTIAL_MANAGE,
  P.ORG_UNIT_MANAGE,
  P.LEAVE_REQUEST_CREATE_FOR_OTHERS,
  P.LEAVE_REQUEST_CANCEL,
  P.REPORT_COMPANY,
  P.INTEGRATION_READ,
  // İK, personelin ve öğrencinin nerede kaldığını görmek zorunda; ama
  // rezervasyonu açan, parayı alan ve odayı yöneten ayrı roller.
  P.KONAKLAMA_READ,
  // İK hem yayımlar hem imza kovalar: yönetmelik tebliği bu masadan çıkar.
  P.ANNOUNCEMENT_PUBLISH,
  P.ANNOUNCEMENT_RECEIPT_READ,
  // Tutanak da bu masada: imzaları toplayan kişi, onları dosyalayan kişidir.
  // Bir memorandumun kâğıt karşılığını üretmek İK'nın günlük işi ve ayrı bir
  // koda bağlı olmasının sebebi rütbe değil, dosyanın SİSTEMDEN ÇIKMASI —
  // dışa aktarılan her tutanak `sys_data_access_log`'a bir EXPORT satırı
  // yazıyor. Diğer rollere (birim müdürü, ortak, grup yöneticisi) buradan
  // dağıtılmadı: kimin tutanak alabileceği rol matrisi ekranından verilecek
  // bir karar ve o kararın görünür olması iyidir.
  P.ANNOUNCEMENT_EXPORT,
  // Kategori sözlüğü de burada, çünkü sözlüğü kullanan masa burası; bir liste
  // en iyi onu her gün seçen kişi tarafından bakılır. `sistem_yoneticisi`ne
  // BİLEREK verilmedi: yayımlayabiliyor olması sözlüğün sahibi olmasını
  // gerektirmiyor, ve rol matrisi ekrandan düzenlenebilir — gerekirse orada
  // verilir ve o kararın görünür olması iyidir.
  P.ANNOUNCEMENT_CATEGORY_MANAGE,
  // Onay zincirinin iki kodu da burada, çünkü memorandumu yazan da onu onaya
  // sokan da bu masa; ve İK yöneticisi, uzmanın yazdığının ilk kapısıdır.
  // İkisinin aynı listede olması bir çelişki DEĞİL: kural motoru kendi
  // sunduğunu onaylamayı zaten reddediyor (`selfApproval`), yani uzman kendi
  // taslağını değil meslektaşınınkini onaylayabiliyor.
  //
  // Bir kapıya oturtulacak DİĞER roller (birim müdürü, ortak) bu kodu rol
  // matrisi ekranından alır. Buradan dağıtmak, kimsenin bakmadığı bir yetki
  // genişlemesi olurdu — ve bir izin satırı, o rolde insan yoksa yalnızca
  // geçilemeyen bir kapı üretir.
  P.ANNOUNCEMENT_SUBMIT,
  P.ANNOUNCEMENT_APPROVE,
  // Uçuş primi: ÖLÇÜMÜ İK tutuyor. Pilotun ayda kaç saat uçtuğu bir puantaj
  // sorusudur ve bu masanın işi; saatin kaç lira ettiği ile ayın ödemeye
  // gönderilmesi ayrı yetkilerde ve uzmanda YOK — biri fiyatı, diğeri parayı
  // başlatır.
  P.FLIGHT_PREMIUM_READ,
  P.FLIGHT_PREMIUM_RECORD
];
var GUESTHOUSE_PERMISSIONS = [
  P.KONAKLAMA_READ,
  P.KONAKLAMA_MANAGE,
  P.KONAKLAMA_RESERVATION_MANAGE,
  P.KONAKLAMA_COLLECT,
  P.KONAKLAMA_EXPENSE_MANAGE,
  P.KONAKLAMA_HOUSEKEEPING,
  P.KONAKLAMA_ISSUE_CREATE,
  P.KONAKLAMA_ISSUE_MANAGE,
  P.KONAKLAMA_GUEST_READ_SENSITIVE,
  // Sınav ücreti ve eğitim materyali tahsilatı — DST-00120. Aynı masa: öğrenciden
  // misafirhane parasını alan kişi sınav ve materyal parasının dekontunu da
  // tutuyor (canlıda bu rolde üç kişi var, üçü de okulun idari kadrosu).
  P.OKUL_TAHSILAT_READ,
  P.OKUL_TAHSILAT_RECORD,
  P.OKUL_TAHSILAT_CATALOG
];
var ROLE_DEFINITIONS = [
  {
    code: ROLES.PERSONEL,
    name: "Personel",
    description: "Kendi izin taleplerini ve \xF6zl\xFCk bilgilerini g\xF6r\xFCnt\xFCler.",
    defaultScopeType: SCOPE_TYPES.SELF,
    mfaRequired: false,
    sortOrder: 10,
    permissions: SELF_PERMISSIONS
  },
  {
    code: ROLES.DEPARTMAN_YONETICISI,
    name: "Departman y\xF6neticisi",
    description: "Kendi departman\u0131 ve alt departmanlar\u0131n\u0131n personelini g\xF6r\xFCr, izin onaylar.",
    defaultScopeType: SCOPE_TYPES.DEPARTMENT,
    mfaRequired: false,
    sortOrder: 20,
    permissions: [
      ...MANAGER_PERMISSIONS,
      /**
       * FATURA: birim müdürü fatura zincirinin bir kapısında oturabildiği için.
       *
       * `MANAGER_PERMISSIONS`'a DEĞİL bu role konuyor. O paketi `birim_muduru`
       * ve İK rolleri de devralıyor; faturayı oraya yazmak, bir birim müdürü
       * kapısı kurarken İK uzmanına fatura onayı vermek olurdu. Göçün
       * fikstürü o üç rolü bilerek yetkisiz kurup ölçüyor.
       *
       * İKİ KOD BİRDEN, çünkü tek başına `invoice.approve` işi yaptırmıyor:
       * kapının bildirimi faturayı adıyla haber veriyor ve bağlantı
       * `invoice.read` istiyor. Açamadığı bir faturayı onaylaması istenen
       * müdür, kararı imzalamıyor — bilmediği bir şeye evet diyor.
       *
       * Fatura görünürlüğü BUGÜN ŞİRKET DÜZEYİNDE (`p_fin_invoice_scope` →
       * `app.can_see_company`), yani bu iki kod müdüre yalnız kendi biriminin
       * değil şirketin fatura listesini açıyor. Daraltma ayrı bir iş: birim
       * süzgeci `orgUnitWhere()`de hazır duruyor ama yalnız DEPARTMENT
       * kapsamlı kullanıcıda ısırıyor, bu rolün sahipleri COMPANY kapsamlı.
       *
       * Yetkinin tek başına kimseye fatura onaylatmadığını da not etmek
       * gerekiyor: `invoice.service.ts:withChain` kararı `canDecide &&
       * approval.myTurn` yapıyor — izin kapıyı açar, KOLTUK faturayı önüne
       * koyar.
       *
       * Defteri `20261002130500_birim_muduru_fatura_yetkisi` yazıyor; var olan
       * bir rolde bu satır tek başına ETKİSİZDİR (seed yalnız öksüz kodları
       * dağıtır) ve `check-role-grant-migration` bunu kapıda söylüyor.
       */
      P.INVOICE_READ,
      P.INVOICE_APPROVE
    ]
  },
  {
    code: ROLES.BIRIM_MUDURU,
    name: "Birim m\xFCd\xFCr\xFC",
    description: "Kendi \u015Firketinin tamam\u0131n\u0131 g\xF6r\xFCr, \xFCst kademe izin onay\u0131 verir.",
    defaultScopeType: SCOPE_TYPES.COMPANY,
    mfaRequired: false,
    sortOrder: 30,
    permissions: [
      ...MANAGER_PERMISSIONS,
      P.EMPLOYEE_WRITE,
      P.REPORT_COMPANY,
      // Masraf zincirinin İKİNCİ kapısı, ve adı bu rolün adı: eşik üstü bir
      // masrafı bölüm yöneticisinden sonra birim müdürü onaylar. Kapı zaten
      // vardı ama `finance.read` istiyordu — bu rolde olmayan bir izin — ve
      // uç ayrıca `expense.approve` şart koşuyordu; ikisinin kesişimi yalnız
      // sistem yöneticisiydi, dolayısıyla eşiği aşan her masraf orada durdu
      // (DST-00060). Yetkiyi zincirin adımı veriyor artık, ve adım bu kodu
      // istiyor.
      P.EXPENSE_APPROVE_UNIT,
      P.KONAKLAMA_READ,
      P.KONAKLAMA_ISSUE_CREATE
    ]
  },
  {
    code: ROLES.IK_UZMANI,
    name: "\u0130nsan kaynaklar\u0131 uzman\u0131",
    description: "Atand\u0131\u011F\u0131 \u015Firketin \xF6zl\xFCk, belge ve izin i\u015Flemlerini y\xFCr\xFCt\xFCr.",
    defaultScopeType: SCOPE_TYPES.COMPANY,
    mfaRequired: false,
    sortOrder: 40,
    permissions: HR_SPECIALIST_PERMISSIONS
  },
  {
    code: ROLES.IK_YONETICISI,
    name: "\u0130nsan kaynaklar\u0131 y\xF6neticisi",
    description: "Grup genelinde \u0130K; izin politikas\u0131 ve hassas personel verisi yetkisi.",
    defaultScopeType: SCOPE_TYPES.GROUP,
    mfaRequired: false,
    sortOrder: 50,
    permissions: [
      // Zimmet: İK uzmanının yaptığı işin üstü (DST-00065). Kuralı DELEN iki
      // kod burada durur ve aşağı inmez — kalibrasyonu geçmiş bir ekipmanı
      // yine de vermek, ve açık zimmetli birinin çıkışını yine de tamamlamak.
      // İkisi de gerekçe ister ve gerekçe silinemez biçimde kayda düşer.
      P.ASSET_READ,
      P.ASSET_MANAGE,
      P.ASSET_CALIBRATION_OVERRIDE,
      P.ASSET_EXIT_OVERRIDE,
      ...HR_SPECIALIST_PERMISSIONS,
      P.EMPLOYEE_READ_SENSITIVE,
      /**
       * Kullanıcı yönetimi — DST-00081, açık iş kararı.
       *
       * İşe alım İK'da başlıyor ve yeni personelin girişini açmak için sistem
       * yöneticisini beklemek, onboarding'i bir kişiye bağlıyordu. Talep de
       * tam bunu söylüyor: "Kullanıcılar kısmında kullanıcı ekle seçeneği
       * çıkmıyor."
       *
       * BEDELİ AÇIKÇA YAZILI: `user.manage` rol atamayı da açıyor, yani bu
       * rolü taşıyan biri kendisine de başkasına da her rolü verebilir.
       * Kabul edilebilir olmasının sebebi, yetki veren bir değişikliğin artık
       * gerekçe istemesi ve denetim izine düşmesi (bkz. yetki-yukseltme.ts) —
       * kapı açık ama sessiz değil.
       *
       * ⚠️ Bu satır YALNIZ taze kuruluma ulaşır. `seed:core` var olan bir role
       * yalnız kataloğa YENİ giren izinleri dağıtıyor ve `user.manage` yeni
       * değil; mevcut ortamlarda yetki Roller ekranından verilmeli.
       */
      P.USER_MANAGE,
      // Vouching for a machine's guess about who a unit-ERP user is stops here.
      // An unconfirmed match cannot debit a leave balance, so confirming one is
      // what releases it — a heavier act than making a link one already knows.
      P.EMPLOYEE_IDENTITY_CONFIRM,
      P.COMPENSATION_MANAGE,
      // Primin fiyatı ve ayın ödemeye gönderilmesi burada, uzmanda değil:
      // ikisi de ücret kararıdır ve bu rol zaten COMPENSATION_MANAGE taşıyor.
      P.FLIGHT_PREMIUM_RATE_MANAGE,
      P.FLIGHT_PREMIUM_FILE_PAYMENT,
      P.ATTENDANCE_MANAGE,
      P.ATTENDANCE_POLICY_MANAGE,
      P.LEAVE_POLICY_MANAGE,
      P.LEAVE_LEDGER_ADJUST,
      P.HOLIDAY_MANAGE,
      P.REPORT_GROUP,
      P.DASHBOARD_GROUP,
      P.AUDIT_READ,
      P.KONAKLAMA_MANAGE,
      P.KONAKLAMA_GUEST_READ_SENSITIVE,
      /**
       * Opening the login for somebody HR has just hired.
       *
       * This is a deliberate exception to a separation this catalogue otherwise
       * keeps: `user.manage` sat only on Sistem yöneticisi, who is kept away
       * from `employee.read_sensitive` on purpose, so that the hand which
       * maintains accounts is not also the hand that reads salaries. HR is the
       * other side of that line, and holding both closes it.
       *
       * It was granted anyway, by the owner, because onboarding is HR's job and
       * routing every new hire through the system administrator made the first
       * day of employment wait on somebody with no reason to be involved.
       *
       * Know what it carries: `createUserSchema` and `updateUserSchema` both
       * take `scopes`, so this permission assigns roles and scopes as well as
       * creating the account. A holder can open an account with any role in the
       * catalogue. The control that remains is the audit trail, not the gate.
       */
      /**
       * Ve ekranı AÇABİLMESİ için `user.read`.
       *
       * DST-00059 yalnız `user.manage` verdi, ama Kullanıcılar rotası
       * `USER_READ` istiyor (`App.tsx`) — yani bu tanım, kullanıcı açma
       * yetkisi olan ama o ekranı hiç açamayan bir rol tarif ediyordu. Üretimde
       * fark edilmedi çünkü oradaki `ik_yoneticisi` rolü `user.read`'i rol ilk
       * yaratıldığında almıştı ve seed var olan matrisi korur; yani üretim ile
       * bu dosya birbirinin TAM ZIDDI hâle gelmişti — orada okuma var yazma
       * yok, burada yazma var okuma yok. İkisi de tek başına işi yaptırmıyor.
       */
      P.USER_READ,
      /**
       * Ve rolleri ATAYABİLMESİ için `role.read`.
       *
       * `user.manage` bir hesabı açar ve ona ROL verir — `createUserSchema`
       * `roles`'ı alıyor. Ama atanacak rollerin listesi ekrana `GET /roles`'tan
       * geliyor ve o uç `role.read` istiyor. Yetkisiz kalınca liste boş
       * dönüyor, ve üç ekran üç ayrı biçimde bozuluyor: «Kullanıcı ekle»
       * diyaloğunda Rol açılır listesi boş çiziliyor ama form yine
       * gönderiliyor — durumun başlangıç değeri `'personel'` — yani İK'nın
       * açtığı HER hesap sessizce personel oluyor; «Başvurular» sekmesinde
       * onay diyaloğunun Rol alanı boş kaldığı için «Onayla ve hesabı aç»
       * düğmesi hiç etkinleşmiyor; ve listedeki Roller sütunu rol adları
       * yerine ham kodları yazıyor.
       *
       * Bu dosyanın kendi denetimi (`features/users/api.ts`) `company.read` ve
       * `org_unit.read` için «bu ekranı açabilen herkeste var» diye
       * yazılmıştı; `role.read` o denetime hiç alınmamıştı. DST-00081'i
       * `user.manage` ile kapatmak, çıkan ama işe yaramayan bir düğme
       * bırakırdı — kapalı bir düğmeden kötüsü.
       *
       * OKUMA, yönetme değil: rol matrisini DEĞİŞTİREN `role.manage` yerinde
       * duruyor ve `sistem_yoneticisi`'nin. Zaten rol atayabilen birine
       * rollerin listesini göstermek, verilenden daha az bir şey.
       */
      P.ROLE_READ,
      P.USER_MANAGE
    ]
  },
  {
    code: ROLES.MISAFIRHANE_SORUMLUSU,
    name: "Misafirhane sorumlusu",
    description: "Misafirhaneyi i\u015Fletir: oda ve yatak tan\u0131mlar, rezervasyon a\xE7ar, tahsilat i\u015Fler, temizlik ve eksik kayd\u0131 tutar; okulun s\u0131nav ve materyal tahsilatlar\u0131n\u0131n dekontunu tutar.",
    defaultScopeType: SCOPE_TYPES.COMPANY,
    mfaRequired: false,
    sortOrder: 55,
    permissions: [...SELF_PERMISSIONS, P.COMPANY_READ, P.EMPLOYEE_READ, ...GUESTHOUSE_PERMISSIONS]
  },
  {
    /**
     * Depo sorumlusu — fatura zincirinin malzeme kapısı.
     *
     * Tek bir soruyu cevaplamak için var: "bu malzeme depomuzda zaten var mı".
     * Fatura onaylamak bu rolün işi DEĞİL, ama `invoice.approve` taşımak
     * zorunda — çünkü bu sistemde bir kapıyı geçmenin yolu o izinden geçiyor.
     *
     * Ve bu, göründüğü kadar geniş bir yetki değil: izin tek başına hiçbir
     * faturayı onaylatmıyor. `canDecide` kişinin O ANKİ kapıda OTURUYOR olmasını
     * da istiyor, dolayısıyla bu rol yalnızca oturtulduğu depo kapısında karar
     * verebilir. Kapı, yetkinin sınırı; izin yalnızca ön şart.
     *
     * `invoice.read` de burada: göremediği bir faturayı işaretlemek anlamsız.
     *
     * Kapsam COMPANY, ve bu bilinçli: depo şirkete ait. Uçak Bakım'ın deposunu
     * bilen kişi Uçuş Okulu'nun faturasına bakamamalı.
     */
    code: ROLES.DEPO_SORUMLUSU,
    name: "Depo sorumlusu",
    description: "Faturadaki malzemenin depoda olup olmad\u0131\u011F\u0131n\u0131 teyit eder. Fatura onay zincirinin depo kap\u0131s\u0131nda oturur; oturmad\u0131\u011F\u0131 hi\xE7bir kap\u0131da karar veremez.",
    defaultScopeType: SCOPE_TYPES.COMPANY,
    mfaRequired: false,
    sortOrder: 56,
    permissions: [
      ...SELF_PERMISSIONS,
      P.COMPANY_READ,
      P.INVOICE_READ,
      P.INVOICE_APPROVE,
      // Malzemenin depoda olup olmadığını teyit eden masa, kütüğü de tutar:
      // "elimizde ne var" sorusunun cevabı zaten bu kişide. MALİYET yok —
      // depo sorumlusu neyin nerede olduğunu bilir, şirketin ona ne ödediğini
      // değil — ve istisna yetkisi de yok.
      P.ASSET_READ,
      P.ASSET_MANAGE,
      P.ASSET_ASSIGN,
      P.ASSET_RETURN,
      P.ASSET_INCIDENT,
      P.ASSET_REPORT
    ]
  },
  {
    code: ROLES.FINANS_UZMANI,
    name: "Finans uzman\u0131",
    description: "\u015Eirketinin kasa ve banka hareketlerini i\u015Fler, ekstre mutabakat\u0131 yapar, b\xFCt\xE7e tasla\u011F\u0131 haz\u0131rlar.",
    defaultScopeType: SCOPE_TYPES.COMPANY,
    mfaRequired: false,
    sortOrder: 60,
    permissions: [
      ...SELF_PERMISSIONS,
      P.COMPANY_READ,
      P.FINANCE_READ,
      P.PROJECT_READ,
      // Parayı sayan ve bankayı işleten kişi bu. Hesap AÇMAK ve açılış bakiyesi
      // girmek bilerek yok: açılış bakiyesi denetlenemeyen tek rakamdır, çünkü
      // arkasında bu sistemde bir hareket yoktur.
      P.TREASURY_READ,
      P.TREASURY_RECORD,
      P.TREASURY_RECONCILE,
      // Bütçeyi hazırlayan, bütçeyi yürürlüğe alan değildir.
      P.BUDGET_MANAGE,
      // Ödeme onay zincirinin ilk kapısı. Belgeyi, IBAN'ı ve tutarı ilk
      // inceleyen kişi bu; zincirin üstüne çıkmadan önce evrakın doğru
      // olduğunu söyleyecek biri olmalı.
      P.PAYMENT_APPROVE,
      // Onaylayacağı tabloyu görmesi gerekiyor; artık ayrıca veriliyor.
      P.PAYMENT_READ,
      // Kartla ödemeyi çoğu zaman bu masa yapıyor ve dekont onda (DST-00116).
      // Execute DEĞİL: bankada havaleyi yapan yine yönetici; bu kod ödemeyi
      // (kart ya da havale) yalnız dekontu yüklüyken kapatıyor.
      P.PAYMENT_CARD_SETTLE,
      // Ödenmeyeni sonraki haftaya taşımak da bu masada (DST-00144): tabloyu
      // haftadan haftaya o yürütüyor. Para hareketi değil, onayı bozmuyor.
      P.PAYMENT_DEFER,
      // Acil ödeme: normal zinciri atlayan talebi AÇABİLİR, ama onaylayan
      // yine ortaklar. Talebi bir gün önceden bilen masa burası — akaryakıt
      // planı finansa geliyor, personele değil. Bu yüzden ayrı bir koda
      // bağlandı: talep açmak herkeste, kapıları atlayan talep açmak değil.
      P.PAYMENT_URGENT_CREATE,
      P.INVOICE_READ,
      // Faturayı ilk elden denetleyen masa burası, ve zincirin ilk kapısı bu
      // role kurulu. Yetki eksikti: `payment.approve` bu roldeydi ama
      // `invoice.approve` değildi, dolayısıyla FATURA zincirinin "Finans
      // uzmanı" kapısına oturan üç kişinin üçü de o kapıyı GEÇEMİYORDU. Her
      // fatura ilk kapıda süresiz bekledi ve ekran kimseye bir şey söylemedi —
      // üç ayrı destek kaydı (DST-00013, DST-00033, DST-00048) bunun izidir.
      //
      // Ödeme ile fatura arasındaki bu asimetri bir karar değil, gözden
      // kaçmaydı: aynı masa aynı belgeyi ödeme tarafında onaylıyordu.
      P.INVOICE_APPROVE,
      P.PAYEE_MANAGE,
      // Onaylanmış talebi satın alıp ödeme talebine çeviren masa bu: cariyi,
      // gerçekleşen tutarı ve belgeyi bilen taraf. Talebi ONAYLAMIYOR — neyin
      // alınacağına karar veren ile alan aynı kişi olursa, onay kapısı satın
      // alma masasının kendi içinde kapanmış olur.
      P.STAFF_REQUEST_READ,
      P.STAFF_REQUEST_PURCHASE,
      // Sabit giderlerin aylık işleyişi: gelen faturayı girer, dönemi ödemeye
      // hazırlar. Sözleşmenin kendisini DEĞİŞTİREMEZ ve zammı ONAYLAYAMAZ —
      // kirayı her ay kaydeden kişi kira tutarını da belirleyebiliyorsa, artış
      // hiçbir kapıdan geçmemiş olur.
      P.FIXED_EXPENSE_READ,
      P.FIXED_EXPENSE_RECORD,
      // Uçuş saatini giren ve kira ödemesini işleyen kişi. Saati
      // KESİNLEŞTİRMEK yöneticide: kesinleştirme, kiraya verene ne ödendiğine
      // karar veren adım.
      P.LEASED_AIRCRAFT_READ,
      P.LEASED_AIRCRAFT_RECORD,
      // Vergi takvimini görür. Mükellef kartı açmak ve bir şirketi bir vergiye
      // tabi kılmak bu rolde DEĞİL: yükümlülüğün kapsamını belirlemek, aylık
      // işleyişi yürütmekten farklı bir karar.
      P.TAX_READ,
      P.KONAKLAMA_READ,
      P.OKUL_TAHSILAT_READ,
      P.KONAKLAMA_COLLECT,
      P.KONAKLAMA_EXPENSE_MANAGE,
      // Faturası kesilmemiş bakım işlerini KOVALAYAN kişi bu — listeyi okuması
      // işin kendisi. İşaretleme (faturalanmayacak) bilerek burada değil:
      // kovalamakla yükümlü olan, kovalayacağı satırı listeden düşürebilmemeli.
      P.MAINTENANCE_READ
    ]
  },
  {
    /**
     * Hazine hesap sorumlusu — bir EK rol, tek başına bir masa değil (DST-00030).
     *
     * Hesap açmak ve açılış bakiyesi girmek finans uzmanında bilerek yok (bkz.
     * yukarıdaki yorum): açılış bakiyesi, arkasında hareket olmayan tek
     * rakamdır, o yüzden hesabı AÇAN ile hareketi YAZAN ayrı tutuldu. Kurum
     * 2026-10-02'de bu ayrımı BİR kişi için kaldırmaya karar verdi — kasa
     * hesaplarını işleten finans uzmanı kendi hesaplarını da açacak.
     *
     * Rolün kendisine değil kişiye verilmesi istendiği için yetki
     * `finans_uzmani`'ye eklenmedi: o rolde üç kişi var ve karar yalnız birini
     * kapsıyor. Bu rol, o kişinin finans uzmanı rolünün YANINA atanır; yetki
     * kümesi bilerek dar — hesap tanımlamak ve onu görebilmek.
     *
     * Yeni bir rol olduğu için `seed:core` onu ilk dağıtımda yetkileriyle
     * birlikte yaratır (`if (!existing)`); göç gerekmez. Atamayı Kullanıcılar
     * ekranından bir yönetici yapar.
     */
    code: ROLES.HAZINE_HESAP_SORUMLUSU,
    name: "Hazine hesap sorumlusu",
    description: "Ek rol: kasa ve banka hesab\u0131 a\xE7ar, ad\u0131n\u0131 ve bankas\u0131n\u0131 d\xFCzeltir, kullan\u0131lmayan\u0131 kapat\u0131r. Finans uzman\u0131 gibi hareket giren bir rol\xFCn yan\u0131na verilir.",
    defaultScopeType: SCOPE_TYPES.COMPANY,
    mfaRequired: false,
    sortOrder: 62,
    permissions: [P.TREASURY_READ, P.TREASURY_ACCOUNT_MANAGE]
  },
  {
    code: ROLES.FINANS_YONETICISI,
    name: "Finans y\xF6neticisi",
    description: "Grup genelinde finans: k\xE2rl\u0131l\u0131k, nakit pozisyonu, b\xFCt\xE7e ve \xF6deme tablosu. D\xF6nemi ve b\xFCt\xE7eyi kapatan ki\u015Fi.",
    defaultScopeType: SCOPE_TYPES.GROUP,
    mfaRequired: false,
    sortOrder: 70,
    permissions: [
      ...SELF_PERMISSIONS,
      P.COMPANY_READ,
      P.EMPLOYEE_READ,
      P.EMPLOYEE_READ_SENSITIVE,
      P.FINANCE_READ,
      P.PROJECT_READ,
      P.FINANCE_MANAGE,
      P.PROJECT_MANAGE,
      // Masrafın finans kapısı bu rolde ve bugüne kadar `finance.manage` ile
      // geçiliyordu. Zincir veriye taşınınca kapının izni alan başına TEK bir
      // koda düştü (`expense.approve`), ve bu satır olmasaydı finans kapısı
      // ilk günden geçilemez olurdu — fatura zincirinin aylarca kapalı kalan
      // ilk kapısı tam olarak buydu. Yeni bir yetki değil: aynı yetkinin yeni
      // sözcüğü.
      P.EXPENSE_APPROVE,
      // Closing a month freezes its profit, so it sits with the finance
      // manager rather than with anyone who can record an entry.
      P.FINANCE_PERIOD_CLOSE,
      // Kasa ve bankanın tamamı. Hesap açmak ve açılış bakiyesi girmek burada
      // durur, uzmanda değil.
      P.TREASURY_READ,
      P.TREASURY_ACCOUNT_MANAGE,
      P.TREASURY_RECORD,
      P.TREASURY_RECONCILE,
      // Hazırlamak ve yürürlüğe almak aynı elde toplanıyor; ayrı tutulmalarının
      // sebebi uzmanın ikincisine sahip olmaması, yöneticinin birincisinden
      // mahrum kalması değil.
      P.BUDGET_MANAGE,
      P.BUDGET_LOCK,
      // Finance runs the week and makes the payments. Both are roles the
      // specification named after people; naming them after people is how a
      // process stops the day someone takes leave.
      P.PAYMENT_APPROVE,
      P.PAYMENT_URGENT_CREATE,
      P.PAYMENT_WEEK_MANAGE,
      P.PAYMENT_EXECUTE,
      P.PAYMENT_DEFER,
      P.PAYMENT_TRANSFER_FORM,
      P.PAYMENT_READ,
      // Cari kaydı finansa ait: IBAN'ı değiştirmek, o cariye giden her ödemeyi
      // aynı anda yönlendirmek demek.
      P.INVOICE_READ,
      P.INVOICE_APPROVE,
      P.PAYEE_MANAGE,
      // Grup genelinde ne istendiğini görür ve satın alma masası boşsa kendisi
      // kapatır. Onay kapısı yine ayrı: zincirdeki koltuk verisi neyse o.
      P.STAFF_REQUEST_READ,
      P.STAFF_REQUEST_PURCHASE,
      P.REPORT_GROUP,
      P.KONAKLAMA_READ,
      P.OKUL_TAHSILAT_READ,
      P.KONAKLAMA_COLLECT,
      P.KONAKLAMA_EXPENSE_MANAGE,
      // Borcu silmek parayı almaktan ayrı bir güç; burada durur.
      P.KONAKLAMA_LEDGER_ADJUST,
      // Sabit giderlerin tamamı. Sözleşme tanımlamak ve zammı onaylamak burada
      // durur: ikisi de yıllık taahhüdü kalıcı olarak büyüten kararlar.
      P.FIXED_EXPENSE_READ,
      P.FIXED_EXPENSE_MANAGE,
      P.FIXED_EXPENSE_RECORD,
      P.FIXED_EXPENSE_ESCALATION_APPROVE,
      P.LEASED_AIRCRAFT_READ,
      P.LEASED_AIRCRAFT_MANAGE,
      P.LEASED_AIRCRAFT_RECORD,
      P.LEASED_AIRCRAFT_HOURS_CONFIRM,
      // Verginin tamamı. Katalog da burada: mevzuat tarihi değiştiğinde onu
      // girecek kişi, sonucuna katlanacak bütçenin de sahibi.
      P.TAX_READ,
      P.TAX_MANAGE,
      P.TAX_CALENDAR_MANAGE,
      // Bu rolün altında çalışan bir uzman var; onun rolü MANAGER_PERMISSIONS'tan
      // gelmediği için görevlendirme yetkisi de gelmiyor, burada ayrıca veriliyor.
      P.AGENDA_ASSIGN,
      P.AGENDA_OVERSEE,
      // Bakım takibi ve tek hüküm yetkisi burada birleşiyor. "Bu iş
      // faturalanmayacak" demek — garanti, grup içi iş, kendi filomuz — bir
      // gelirden vazgeçme kararıdır; kovalayan uzmanda değil, vazgeçmeye yetkili
      // olanda durur.
      P.MAINTENANCE_READ,
      P.MAINTENANCE_BILLING_EXCLUDE
    ]
  },
  {
    code: ROLES.GRUP_YONETICISI,
    name: "Grup y\xF6neticisi",
    description: "T\xFCm birimleri g\xF6r\xFCr, hi\xE7bir veriyi de\u011Fi\u015Ftiremez.",
    defaultScopeType: SCOPE_TYPES.GROUP,
    mfaRequired: false,
    sortOrder: 80,
    permissions: [
      ...SELF_PERMISSIONS,
      P.COMPANY_READ,
      P.ORG_UNIT_READ,
      P.EMPLOYEE_READ,
      P.CREDENTIAL_READ,
      P.LEAVE_REQUEST_READ,
      P.REPORT_GROUP,
      P.REPORT_COMPANY,
      P.DASHBOARD_GROUP,
      P.DASHBOARD_TEAM,
      P.AUDIT_READ,
      P.INTEGRATION_READ,
      P.FINANCE_READ,
      P.PROJECT_READ,
      // Nakit pozisyonunu görür, tek bir hareket giremez — bu rolün tamamının
      // tarifi.
      P.TREASURY_READ,
      P.FIXED_EXPENSE_READ,
      // Filoda uçan kiralık uçakların taahhüdünü ve garanti saat kullanımını
      // görür, hiçbirini değiştirmez — rolün tarifi bu.
      P.LEASED_AIRCRAFT_READ,
      // Vergi bir yükümlülüktür, ödeme kararı değil: görür, hiçbirini
      // değiştirmez. Ödemenin onayı zaten payment.approve'da.
      P.TAX_READ,
      P.KONAKLAMA_READ,
      P.OKUL_TAHSILAT_READ,
      // Grubun ne istediğini görür, hiçbirine karar vermez — rolün tarifi bu.
      P.STAFF_REQUEST_READ,
      // Salt okuma. Bu rolün tarifi zaten bu, ve geciken faturalandırma tam da
      // grup yöneticisinin görmek isteyeceği şey.
      P.MAINTENANCE_READ,
      // "Her şeyi görür, hiçbir veriyi değiştiremez" tarifi, tam olarak "grup
      // güvenlik memorandumunu kim okumadı?" diye soran kişi. Yayımlamaz.
      P.ANNOUNCEMENT_RECEIPT_READ,
      // İletişim denetim izi: kimin kiminle yazıştığının tarihçesi bir
      // GÖZETİM yetkisidir ve moderasyondan ayrı durur. Grup yönetiminde,
      // makbuz okumanın yanında.
      P.MESSAGE_AUDIT_READ
    ]
  },
  /**
   * Şirket ortağı.
   *
   * Ödeme onay zincirinin son kapısı. Grup yöneticisi gibi her şeyi görür, ama
   * ondan bir farkla: ödemeyi yetkilendirebilir. Hassas personel verisi (maaş,
   * TCKN, IBAN) bilerek verilmedi — ortaklık, KVKK'nın özel nitelikli verisine
   * erişimi kendiliğinden gerektirmez; gerekirse rol matrisinden ayrıca
   * verilir ve o kararın bir yerde görünür olması iyidir.
   */
  {
    code: ROLES.ORTAK,
    name: "Ortak",
    description: "\u015Eirket orta\u011F\u0131: t\xFCm birimleri g\xF6r\xFCr ve \xF6deme onay zincirinde kendisine d\xFC\u015Fen a\u015Famay\u0131 onaylar.",
    defaultScopeType: SCOPE_TYPES.GROUP,
    mfaRequired: false,
    sortOrder: 85,
    permissions: [
      ...SELF_PERMISSIONS,
      P.COMPANY_READ,
      P.ORG_UNIT_READ,
      P.EMPLOYEE_READ,
      P.LEAVE_REQUEST_READ,
      // Beside LEAVE_REQUEST_READ for the same reason: this role reads other
      // people's records across the group. It used to reach the payment table
      // through SELF_PERMISSIONS, which is to say by the same accident that let
      // every employee reach it. Stated here, it is a decision.
      P.PAYMENT_READ,
      P.INVOICE_READ,
      P.INVOICE_APPROVE,
      // Masrafın grup kapısı — büyük tutarlar ortağa çıkıyor, ve bugün de
      // çıkıyordu: eski zincir o kapıyı `report.group` ile açıyordu, yani bir
      // RAPOR izniyle. Zincir veriye taşınınca kapının izni `expense.approve`
      // oldu ve bu satır aynı kişilerin aynı kapıyı geçmesini sürdürüyor —
      // farkı, artık okuma değil onaylama izni olduğunu söylemesi.
      P.EXPENSE_APPROVE,
      P.REPORT_GROUP,
      P.REPORT_COMPANY,
      P.DASHBOARD_GROUP,
      P.DASHBOARD_TEAM,
      P.AUDIT_READ,
      P.FINANCE_READ,
      P.PROJECT_READ,
      P.TREASURY_READ,
      P.FIXED_EXPENSE_READ,
      // Filoda uçan kiralık uçakların taahhüdünü ve garanti saat kullanımını
      // görür, hiçbirini değiştirmez — rolün tarifi bu.
      P.LEASED_AIRCRAFT_READ,
      // Kira artışı, ortağın onayladığı tek tek ödemelerden farklı olarak yıllara
      // yayılan bir taahhüt; onu da görmesi değil, onaylaması gerekiyor.
      P.FIXED_EXPENSE_ESCALATION_APPROVE,
      P.TAX_READ,
      P.KONAKLAMA_READ,
      P.OKUL_TAHSILAT_READ,
      // Zincirin son kapısı. Ortağın parayı onaylaması, parayı görebilmesinden
      // ayrı bir yetki; ikisi de burada.
      P.PAYMENT_APPROVE,
      P.PAYMENT_READ,
      // Personel talebinin zinciri de aynı koltuklarla kuruluyor; büyük bir
      // alım ortağın kapısına dayanıyorsa orada karar verebilmeli.
      P.STAFF_REQUEST_READ,
      P.STAFF_REQUEST_APPROVE,
      P.MAINTENANCE_READ
    ]
  },
  {
    code: ROLES.SISTEM_YONETICISI,
    name: "Sistem y\xF6neticisi",
    description: "Kullan\u0131c\u0131, rol, i\u015F ve entegrasyon y\xF6netimi. Hassas personel verisine eri\u015Femez.",
    defaultScopeType: SCOPE_TYPES.GROUP,
    mfaRequired: false,
    sortOrder: 90,
    permissions: [
      // Misafirhane kaydını silmek. Günlük işletme rolünde değil burada:
      // silme geri alınamaz, ve o rol tahsilatı da alıyor.
      P.KONAKLAMA_DELETE,
      ...SELF_PERMISSIONS,
      // Zinciri KURAN kişi. Aşamaları tanımlamak ve koltuklara isim yazmak bir
      // yapılandırma işi, finans işi değil — ama sonucu doğrudan paradır, o
      // yüzden her değişiklik denetim kaydına yazılır.
      P.PAYMENT_APPROVAL_CHAIN_MANAGE,
      // Zincirin üçüncü kapısı, grubun isteği üzerine. Şunu açıkça söylemek
      // gerekir: sistemi yönetenin aynı zamanda ödemeyi onaylaması, görevler
      // ayrılığı ilkesine göre en zayıf halkadır — çünkü bu rol kullanıcı ve rol
      // yönetimine sahiptir, yani teorik olarak kendi önüne düşen bir onayı
      // başka birine devredebilir. Karşılığı: pay_approval append-only, her
      // zincir değişikliği denetlenir ve aynı kişinin iki kapıyı doldurması
      // öntanımlı olarak reddedilir.
      P.PAYMENT_APPROVE,
      P.PAYMENT_READ,
      // Cari kaydı da burada. Ayrı tutmak bir sınır değil bir varsayılan olurdu:
      // bu rol ROLE_MANAGE taşıyor ve izni kendine tek çağrıda verebiliyor. Onu
      // dışarıda bırakmanın tek sonucu, sistemi kuran kişinin ilk cariyi
      // tanımlayamaması olurdu.
      P.INVOICE_READ,
      P.INVOICE_APPROVE,
      P.PAYEE_MANAGE,
      // Bakım takibi de aynı gerekçeyle burada, ve iki ayrı sebeple. Okuma:
      // birimin olaylarının merkeze düşüp düşmediği sorusunun cevabı bu ekranda
      // görünüyor, ve entegrasyonu yöneten rol bu. İşaretleme: PAYEE_MANAGE'in
      // gerekçesinin aynısı — bu rol ROLE_MANAGE taşıyor ve izni kendine tek
      // çağrıda verebiliyor, dışarıda bırakmanın tek sonucu sistemi kuran kişinin
      // ilk garanti işini işaretleyememesi olurdu.
      P.MAINTENANCE_READ,
      P.MAINTENANCE_BILLING_EXCLUDE,
      // Kiralık uçak da aynı gerekçeyle burada, ve gerekçe bu blokta üçüncü kez
      // tekrarlanıyor: bu rol ROLE_MANAGE taşıyor ve izni kendine tek çağrıda
      // verebiliyor, dolayısıyla dışarıda bırakmanın tek sonucu sistemi kuran
      // kişinin ilk uçağı ve ilk sözleşmeyi tanımlayamaması olurdu — yani bir
      // sınır değil, bir engel.
      //
      // HOURS_CONFIRM de veriliyor ve bunu açıkça yazmak gerekir: saati girmek
      // ile kesinleştirmek, mahsup tutarına iki ayrı elin bakması için bilerek
      // ayrılmıştı ve bu rolde ikisi birleşiyor. Görevler ayrılığı burada
      // rolün kendisiyle değil, arkasındaki kayıtla taşınıyor — fin_lease_event
      // ekleme-yalnız, her kesinleştirme sys_audit_log'a düşüyor ve mahsubun
      // ödemeye dönmesi yine ödeme zincirinden geçiyor. Ayrımı korumak isteyen
      // kurulumda çözüm bu kodu buradan almak değil, günlük işi finans
      // uzmanı/yöneticisi rolleriyle yürütmektir.
      P.LEASED_AIRCRAFT_READ,
      P.LEASED_AIRCRAFT_MANAGE,
      P.LEASED_AIRCRAFT_RECORD,
      P.LEASED_AIRCRAFT_HOURS_CONFIRM,
      P.USER_READ,
      P.USER_MANAGE,
      P.ROLE_READ,
      P.ROLE_MANAGE,
      // Destek kuyruğunun sahibi. Hata bildirimlerini bu rol görür, sıraya
      // koyar ve kapatır — modülün "destek ekibi" dediği taraf budur.
      P.SUPPORT_READ,
      P.SUPPORT_MANAGE,
      // "Sistem 22:00-23:00 arası kapalı olacak" en sık yazılan gerçek duyuru,
      // ve bu rol zaten ROLE_MANAGE taşıyor — esirgemenin tek sonucu, sistemi
      // kuran kişinin ilk duyuruyu yayımlayamaması olurdu.
      // ANNOUNCEMENT_RECEIPT_READ bilerek YOK, EMPLOYEE_READ_SENSITIVE'in
      // gerekçesiyle: sistemi yönetmek, kişi kişi okuma kaydı okumayı
      // beraberinde getirmez.
      P.ANNOUNCEMENT_PUBLISH,
      // Kaldırma bir SİLME değil: gövde yerinde kalır, gerekçe ve damga
      // yazılır. MESSAGE_AUDIT_READ bilerek YOK — sistemi yönetmek, kimin
      // kiminle yazıştığını okumayı beraberinde getirmez
      // (ANNOUNCEMENT_RECEIPT_READ'in aynı gerekçesi).
      P.MESSAGE_MODERATE,
      P.SETTINGS_MANAGE,
      // Uçuş priminin dördü de burada, ve bu bir SAPMA: bu rol maaşı okuyamaz
      // (EMPLOYEE_READ_SENSITIVE bilerek verilmedi), prim tutarını okuyabilir.
      // Grup açıkça bunu istedi — İK'nın girdiği listeyi yöneticinin de görüp
      // düzeltebilmesi için. Ayrımı yapısal kılan şey veri sınıfının AYRI
      // olması: `flight_premium.read` yalnız FLIGHT_PREMIUM sınıfını açıyor,
      // maaş tablolarının istediği SENSITIVE sınıfını değil. Yani bu satır
      // bordroyu değil, yalnız prim listesini açıyor.
      P.FLIGHT_PREMIUM_READ,
      P.FLIGHT_PREMIUM_RECORD,
      P.FLIGHT_PREMIUM_RATE_MANAGE,
      P.FLIGHT_PREMIUM_FILE_PAYMENT,
      P.JOB_READ,
      P.JOB_MANAGE,
      P.AUDIT_READ,
      P.SECURITY_READ,
      // Personel aktivitesi ekranı. Yalnızca bu role bağlandı: "kim müsait"
      // sorusunun cevabı İK'nın ya da bir müdürün de işine yarar, ama kime
      // verileceği rol matrisinden verilecek bir karar — kodda peşinen dağıtmak
      // o kararı kimseye sormadan vermek olurdu.
      P.ACTIVITY_READ,
      // Only here, deliberately: the roster is a directory, the screen name is
      // observation. Widen it by granting the permission, not by assuming it.
      P.ACTIVITY_PAGE_READ,
      P.COMPANY_READ,
      P.COMPANY_MANAGE,
      P.ORG_UNIT_READ,
      P.ORG_UNIT_MANAGE,
      // Cross-company zimmet, and ONLY here. Note what is NOT on this list:
      // ASSET_ASSIGN. This role cannot issue an ordinary handover, so the code
      // is not a back door into the daily job — it is the seat that agrees the
      // company boundary may be crossed at all, and that is the same seat that
      // draws the boundary in the first place (COMPANY_MANAGE, ORG_UNIT_MANAGE).
      //
      // It stops here on purpose, and `depo_sorumlusu` is the case that forced
      // the question. That desk issues the group's zimmets and met a dead screen
      // after the 2026-08-20 restructuring: the assets sit in TUNA_TEKNIK, the
      // 42 active people are employed by ASYA_HOLDING, so a picker scoped to the
      // asset's own company correctly returns nobody. Both cheap fixes were
      // wrong. Granting that desk EMPLOYEE_READ would widen another company's
      // entire HR roster to a warehouse terminal to solve a problem that is not
      // about reading people. Granting it THIS code would hand a standing power
      // to bind one legal entity's staff to another's property, with nobody
      // above it ever agreeing to that. So it gets neither: crossing the
      // boundary stays a decision taken at this seat.
      P.ASSET_ASSIGN_CROSS_COMPANY,
      P.INTEGRATION_READ,
      P.INTEGRATION_ADMIN
    ]
  },
  {
    code: ROLES.SUPER_ADMIN,
    name: "Grup Super Admin",
    description: "Grup genelinde t\xFCm yetkileri ta\u015F\u0131r ve veri d\xFCzeltme i\u015Flemlerini y\xFCr\xFCt\xFCr. Yetkileri rol matrisinden de\u011Fil koddan gelir; yeni eklenen her yetkiyi otomatik kapsar.",
    defaultScopeType: SCOPE_TYPES.GROUP,
    mfaRequired: false,
    sortOrder: 120,
    /**
     * LİSTE BİLEREK BOŞ — ve bu bir eksiklik değil, tasarımın kendisi.
     *
     * Buraya `ALL_PERMISSIONS` yazmak sözü TUTMAZDI: tohum bu listeyi
     * veritabanına yazar, matris ekranı onu düzenlenebilir kılar ve 137. izin
     * kodu eklendiğinde rol sessizce eksik kalır. Yetki `principal.service.ts`
     * içinde, çözümleme anında kataloğun KENDİSİNDEN veriliyor.
     *
     * Boş liste ayrıca doğruyu söylüyor: rol matrisi ekranında bu rolün yanında
     * hiçbir kutu işaretli görünmeyecek, çünkü matris bu rolü gerçekten
     * yönetmiyor. Dolu görünen ama okunmayan bir liste, ekranı yalancı yapardı.
     */
    permissions: []
  }
];
var ROLE_BY_CODE = Object.fromEntries(
  ROLE_DEFINITIONS.map((r) => [r.code, r])
);

// src/enums/super-admin.ts
var SUPER_ADMIN_ROLE = "super_admin";
function superAdminIzinleri() {
  return new Set(ALL_PERMISSIONS);
}
function superAdminMi(roleCodes) {
  return roleCodes.includes(SUPER_ADMIN_ROLE);
}
var ACILIS_ROL = "sistem_yoneticisi";
var ACILIS_IZNI = "role.manage";
function superAdminDegisimiDenetle(input) {
  const degisiyor = input.hedefteVarDi !== input.hedefteOlacak;
  if (!degisiyor) return { izin: true };
  const acilisDurumu = input.mevcutSuperAdminSayisi === 0 && !input.hedefteVarDi && input.hedefteOlacak;
  if (acilisDurumu) {
    const yeterli = input.aktorRolleri.includes(ACILIS_ROL) && input.aktorIzinleri?.includes(ACILIS_IZNI) === true && input.aktorGrupKapsami === true;
    if (!yeterli) return { izin: false, sebep: "acilis-yetersiz" };
  } else if (!superAdminMi(input.aktorRolleri)) {
    return { izin: false, sebep: "yetkisiz" };
  }
  if (!input.gerekce || input.gerekce.trim().length < 5) {
    return { izin: false, sebep: "gerekce-yok" };
  }
  const kaldiriliyor = input.hedefteVarDi && !input.hedefteOlacak;
  if (kaldiriliyor) {
    if (input.aktorUserId === input.hedefUserId) {
      return { izin: false, sebep: "kendi-yetkisi" };
    }
    if (input.mevcutSuperAdminSayisi <= 1) {
      return { izin: false, sebep: "son-super-admin" };
    }
  }
  return { izin: true };
}
var SUPER_ADMIN_RED_MESAJLARI = {
  yetkisiz: "Super Admin yetkisini yaln\u0131zca ba\u015Fka bir Super Admin verebilir veya kald\u0131rabilir.",
  "kendi-yetkisi": "Kendi Super Admin yetkinizi kald\u0131ramazs\u0131n\u0131z; bunu ba\u015Fka bir Super Admin yapmal\u0131.",
  "son-super-admin": "Sistemdeki son Super Admin kald\u0131r\u0131lamaz. \xD6nce ba\u015Fka bir kullan\u0131c\u0131ya bu yetkiyi verin.",
  "acilis-yetersiz": "\u0130lk Super Admin atamas\u0131n\u0131 yaln\u0131zca grup kapsam\u0131na ve rol y\xF6netimi yetkisine sahip bir sistem y\xF6neticisi yapabilir.",
  "gerekce-yok": "Super Admin yetkisi de\u011Fi\u015Fikli\u011Fi i\xE7in en az be\u015F karakterlik bir gerek\xE7e yaz\u0131n."
};

// src/enums/yetki-yukseltme.ts
var YETKI_VEREN_IZINLER = ["user.manage", "role.manage"];
var GEREKCE_ASGARI = 5;
function yetkiVerenIzinMi(izin) {
  return YETKI_VEREN_IZINLER.includes(izin);
}
function yetkiVermeGerekcesiGerekli(oncekiIzinler, yeniIzinler) {
  const onceki = new Set(oncekiIzinler);
  return [...new Set(yeniIzinler)].filter((izin) => !onceki.has(izin) && yetkiVerenIzinMi(izin));
}
function gerekceGecerliMi(gerekce) {
  if (!gerekce || gerekce.trim().length === 0) return { gecerli: false, sebep: "eksik" };
  if (gerekce.trim().length < GEREKCE_ASGARI) return { gecerli: false, sebep: "kisa" };
  return { gecerli: true };
}

// src/enums/units.ts
var UNIT_CODES = {
  HOLDING: "ASYA_HOLDING",
  TECHNIC: "ASYA_TECHNIC",
  ASC: "ASYA_ASC",
  ATO: "ASYA_ATO"
};
var SEED_COMPANIES = [
  {
    code: UNIT_CODES.HOLDING,
    name: "Grup Merkezi",
    legalName: "ASYA Havac\u0131l\u0131k Grup Merkezi",
    sourceSystem: null,
    sortOrder: 10
  },
  {
    code: UNIT_CODES.TECHNIC,
    name: "U\xE7ak Bak\u0131m",
    legalName: "ASYA Technic",
    sourceSystem: "asya-bakim.tech",
    sortOrder: 20
  },
  {
    code: UNIT_CODES.ASC,
    name: "Yedek Par\xE7a",
    legalName: "ASYA ASC Aviation",
    sourceSystem: "asya.click",
    sortOrder: 30
  },
  {
    code: UNIT_CODES.ATO,
    name: "U\xE7u\u015F Okulu",
    legalName: "ASYA Flight Academy (ATO)",
    sourceSystem: null,
    sortOrder: 40
  }
];

// src/dto/auth.ts
import { z as z4 } from "zod";
var loginSchema = z4.object({
  email: z4.email({ message: "Ge\xE7erli bir e-posta adresi girin." }).trim().toLowerCase(),
  password: z4.string().min(1, { message: "Parola gerekli." })
});
var mfaVerifySchema = z4.object({
  mfaToken: z4.string().min(10),
  code: z4.string().trim().regex(/^[0-9A-Za-z-]{6,14}$/, { message: "Do\u011Frulama kodu ge\xE7ersiz." })
});
var changePasswordSchema = z4.object({
  currentPassword: z4.string().min(1),
  newPassword: z4.string().min(12, { message: "Parola en az 12 karakter olmal\u0131." }).max(200)
});
var mfaChallengeSchema = z4.object({
  status: z4.literal("mfa_required"),
  mfaToken: z4.string(),
  expiresInSeconds: z4.number().int()
});
var sessionUserSchema = z4.object({
  id: z4.uuid(),
  email: z4.string(),
  fullName: z4.string(),
  employeeId: z4.uuid().nullable(),
  roles: z4.array(z4.string()),
  /**
   * Never optional and never undefined. A `string[] | undefined` permission
   * list is what let asya.click's frontend guard read "if we don't know, allow".
   * The type makes that branch unwritable.
   */
  permissions: z4.array(z4.string()),
  scopes: z4.array(scopeSchema),
  /** Companies the principal may act in, resolved from scopes. Empty = denied. */
  companies: z4.array(z4.object({ id: z4.uuid(), code: z4.string(), name: z4.string() })),
  mfaEnabled: z4.boolean(),
  mfaRequired: z4.boolean(),
  /**
   * Whether this deployment offers two-factor at all (FEATURE_MFA).
   *
   * Distinct from the two above, which describe the ACCOUNT. A frontend must
   * not offer enrolment the server will refuse, and it must not read
   * `mfaEnabled: false` as "you should turn it on" when there is nothing to
   * turn on.
   */
  mfaAvailable: z4.boolean(),
  mustChangePassword: z4.boolean(),
  locale: z4.string()
});
var loginSuccessSchema = z4.object({
  status: z4.literal("ok"),
  accessToken: z4.string(),
  expiresInSeconds: z4.number().int(),
  user: sessionUserSchema
});
var loginResponseSchema = z4.union([loginSuccessSchema, mfaChallengeSchema]);
function isMfaChallenge(res) {
  return res.status === "mfa_required";
}
var SECURITY_EVENT_LABELS = {
  refresh_token_reuse: "Kapat\u0131lm\u0131\u015F oturum jetonu yeniden kullan\u0131ld\u0131",
  refresh_token_unknown: "Tan\u0131nmayan oturum jetonu \u2014 tekrar giri\u015F istendi",
  refresh_token_expired: "Oturum s\xFCresi doldu \u2014 tekrar giri\u015F istendi",
  refresh_token_grace: "Kaybolan cevap sonras\u0131 oturum korundu (af penceresi)",
  account_locked: "Hesap ge\xE7ici olarak kilitlendi",
  ip_locked: "Ba\u011Flant\u0131 ge\xE7ici olarak kilitlendi",
  password_changed: "Parola de\u011Fi\u015Ftirildi",
  password_reset: "Parola y\xF6neticiden s\u0131f\u0131rland\u0131"
};
var LOGIN_FAILURE_REASONS = {
  bad_password: "Parola hatal\u0131",
  unknown_user: "Kullan\u0131c\u0131 bulunamad\u0131",
  disabled: "Hesap kapal\u0131",
  service_account: "Servis hesab\u0131",
  mfa_challenge: "\u0130ki ad\u0131ml\u0131 do\u011Frulama istendi",
  locked_out: "Kilitli"
};

// src/dto/mfa.ts
import { z as z5 } from "zod";
var mfaSetupSchema = z5.object({
  /** Base32 secret, shown once alongside the QR payload. */
  secret: z5.string(),
  otpauthUrl: z5.string()
});
var mfaConfirmSchema = z5.object({
  code: z5.string().trim().regex(/^[0-9]{6}$/, { message: "Do\u011Frulama kodu 6 haneli olmal\u0131." })
});
var mfaConfirmResultSchema = z5.object({
  recoveryCodes: z5.array(z5.string())
});
var mfaDisableSchema = z5.object({
  /** Disabling two-factor requires the current password, not just a session. */
  password: z5.string().min(1)
});
var sessionEntrySchema = z5.object({
  id: z5.uuid(),
  familyId: z5.uuid(),
  issuedAt: z5.string(),
  expiresAt: z5.string(),
  ip: z5.string().nullable(),
  userAgent: z5.string().nullable(),
  current: z5.boolean()
});

// src/dto/company.ts
import { z as z7 } from "zod";

// src/common/patch.ts
import { z as z6 } from "zod";
function patchSchema(source) {
  const stripped = Object.fromEntries(
    Object.entries(source.shape).map(([key, schema]) => [
      key,
      schema instanceof z6.ZodDefault ? schema.removeDefault() : schema
    ])
  );
  return z6.object(stripped);
}

// src/dto/company.ts
var companySchema = z7.object({
  id: z7.uuid(),
  code: z7.string(),
  name: z7.string(),
  legalName: z7.string(),
  taxNumber: z7.string().nullable(),
  city: z7.string().nullable(),
  country: z7.string(),
  sourceSystem: z7.string().nullable(),
  functionalCurrency: z7.string(),
  isActive: z7.boolean(),
  sortOrder: z7.number().int(),
  orgUnitCount: z7.number().int().optional(),
  /**
   * Bu şirket hangi şirkete devredildi. Doluysa kayıt bir KABUKtur: geçmişi
   * durur, yeni kayıt kabul etmez, operasyonel listelerde görünmez.
   *
   * `isActive: false` ile aynı şey değil ve ekranın ikisini ayrı etiketlemesi
   * gerekiyor: "pasif" kapandığını söyler, bu alan NEREYE gittiğini. Arşiv
   * görünümünde kullanıcının okuması gereken şey ikincisidir — çünkü asıl
   * sorusu "bu kayıt neden yok" değil, "artık nereye yazmalıyım".
   */
  mergedIntoCompanyId: z7.uuid().nullable(),
  /** Devralan şirketin adı; kimlik göstermek kullanıcıya bir şey anlatmaz. */
  mergedIntoCompanyName: z7.string().nullable()
});
var companyListQuerySchema = sortableQuerySchema.extend({
  isActive: z7.enum(["true", "false"]).optional(),
  /**
   * Absorbe edilmiş şirketleri de getir.
   *
   * Varsayılan `false`, ve varsayılanın böyle olması bir tercih değil bir
   * gereklilik: devredilmiş bir şirket seçim listesinde göründüğü an biri ona
   * masraf yazar. Yalnız "Birleştirilen/Pasif Şirketler" akışı bunu açar;
   * operasyonel formlar hiç göndermez.
   */
  includeArchived: z7.enum(["true", "false"]).optional()
});
var COMPANY_SORT_FIELDS = ["code", "name", "sortOrder", "createdAt"];
var codeField = z7.string().trim().min(2, { message: "Kod en az 2 karakter olmal\u0131." }).max(40).regex(/^[A-Z0-9_]+$/, { message: "Kod yaln\u0131zca b\xFCy\xFCk harf, rakam ve alt \xE7izgi i\xE7erebilir." });
var createCompanySchema = z7.object({
  code: codeField,
  name: z7.string().trim().min(2).max(200),
  legalName: z7.string().trim().min(2).max(300),
  taxNumber: z7.string().trim().max(30).optional(),
  city: z7.string().trim().max(100).optional(),
  country: z7.string().trim().length(2).default("TR"),
  sourceSystem: z7.string().trim().max(60).nullable().optional(),
  functionalCurrency: z7.string().trim().length(3).default("TRY"),
  sortOrder: z7.coerce.number().int().min(0).max(9999).default(0)
});
var updateCompanySchema = patchSchema(createCompanySchema).omit({ code: true }).partial().extend({ isActive: z7.boolean().optional() });

// src/dto/correction.ts
var CORRECTION_OPERATIONS = ["COMPANY_DRY_RUN", "CONTRACT_TRANSFER_DRY_RUN"];
var CORRECTION_STATUSES = ["DRY_RUN", "APPLIED", "FAILED", "REVERTED"];
var CORRECTION_OPERATION_LABELS = {
  COMPANY_DRY_RUN: "\u015Eirket etki analizi",
  CONTRACT_TRANSFER_DRY_RUN: "S\xF6zle\u015Fme aktar\u0131m\u0131 etki analizi"
};
var CORRECTION_STATUS_LABELS = {
  DRY_RUN: "Analiz",
  APPLIED: "Uyguland\u0131",
  FAILED: "Ba\u015Far\u0131s\u0131z",
  REVERTED: "Geri al\u0131nd\u0131"
};
var SIRKET_ETKI_ETIKETLERI = {
  sozlesme: "Sabit gider s\xF6zle\u015Fmesi",
  personel: "Personel",
  fatura: "Fatura",
  proje: "Proje / operasyon",
  finansKaydi: "Gelir / gider kayd\u0131",
  odemeTalebi: "\xD6deme talebi",
  masrafMerkezi: "Masraf merkezi",
  birim: "\u0130\u015F birimi",
  cari: "Cari",
  kullaniciKapsami: "Kullan\u0131c\u0131 kapsam\u0131",
  disSistem: "D\u0131\u015F sistem ba\u011F\u0131"
};
function sirketSilinebilir(sayimlar) {
  return Object.values(sayimlar).every((n) => n === 0);
}
function doluBaglar(sayimlar) {
  return Object.keys(SIRKET_ETKI_ETIKETLERI).map((alan) => ({ alan, etiket: SIRKET_ETKI_ETIKETLERI[alan], adet: sayimlar[alan] })).filter((x) => x.adet > 0);
}
function aktarimEngelleri(analiz) {
  const engeller = [];
  if (!analiz.targetCompanyId) engeller.push("hedef-yok");
  else if (analiz.targetCompanyId === analiz.sourceCompanyId) engeller.push("ayni-sirket");
  if (analiz.kodCakismasi) engeller.push("kod-cakismasi");
  return engeller;
}
var AKTARIM_ENGEL_MESAJLARI = {
  "hedef-yok": "Hedef \u015Firket se\xE7ilmeden aktar\u0131m planlanamaz.",
  "kod-cakismasi": "Hedef \u015Firkette ayn\u0131 kodla bir s\xF6zle\u015Fme var; \xF6nce kodu de\u011Fi\u015Ftirin.",
  "ayni-sirket": "Kaynak ve hedef \u015Firket ayn\u0131 olamaz."
};
function kaynaktaKalan(analiz) {
  return analiz.odenmisDonem + analiz.gecmisDonem;
}
function hedefteKurulacak(analiz) {
  return analiz.acikDonem + analiz.gelecekDonem;
}

// src/dto/job-title.ts
import { z as z8 } from "zod";
var jobTitleSchema = z8.object({
  id: z8.uuid(),
  companyId: z8.uuid(),
  code: z8.string(),
  name: z8.string(),
  /** Serbest kademe/bant — norm kadro modeli bilerek ertelendi. */
  grade: z8.string().nullable(),
  isActive: z8.boolean(),
  /** Bu ünvana bağlı görevlendirme sayısı; silinip silinemeyeceğini söyler. */
  assignmentCount: z8.number().int().nonnegative()
});
var jobTitleListQuerySchema = z8.object({
  companyId: z8.uuid().optional(),
  isActive: z8.enum(["true", "false"]).optional(),
  q: z8.string().trim().max(200).optional()
});
var createJobTitleSchema = z8.object({
  companyId: z8.uuid(),
  /**
   * Kod, addan TÜRETİLMİYOR — elle yazılıyor.
   *
   * "Şöför" ile "Şoför" aynı kodu üretirdi ya da üretmezdi; Türkçe karakterden
   * kod üretmek, iki kaydın sessizce çakışması ya da okunmaz bir kod demek.
   * Ekran öneriyor, karar kullanıcıda.
   */
  code: z8.string().trim().min(2).max(40).regex(/^[A-Z0-9_]+$/, { message: "Kod yaln\u0131zca b\xFCy\xFCk harf, rakam ve alt \xE7izgi i\xE7erebilir." }),
  name: z8.string().trim().min(2).max(200),
  grade: z8.string().trim().max(40).nullable().optional()
});
var updateJobTitleSchema = z8.object({
  name: z8.string().trim().min(2).max(200).optional(),
  grade: z8.string().trim().max(40).nullable().optional(),
  /** Pasife alınan ünvan yeni görevlendirmede seçilemez; eskiler durur. */
  isActive: z8.boolean().optional()
});

// src/dto/org-unit.ts
import { z as z9 } from "zod";
var orgUnitSchema = z9.object({
  id: z9.uuid(),
  companyId: z9.uuid(),
  parentId: z9.uuid().nullable(),
  code: z9.string(),
  name: z9.string(),
  /** Materialized path, e.g. `.a1b2.c3d4.` — maintained by a database trigger. */
  path: z9.string(),
  depth: z9.number().int(),
  isActive: z9.boolean(),
  sortOrder: z9.number().int(),
  /**
   * MERKEZ | DIREKTORLUK | DEPARTMAN | BIRIM
   *
   * Ağaçtaki bir satırın şirket mi merkez mi departman mı olduğu, bugüne kadar
   * yalnız girintisinden tahmin ediliyordu. Tür veritabanında vardı ama API
   * hiç döndürmüyordu — yani ekran gösterecek veriye sahip değildi.
   */
  unitType: z9.enum(["MERKEZ", "DIREKTORLUK", "DEPARTMAN", "BIRIM"]),
  /**
   * Birimin MÜDÜRÜ — masraf onayı buraya gidiyor.
   *
   * Kolon şemada baştan beri vardı («Head of this unit») ama hiçbir yerde
   * okunmuyor, hiçbir yerde yazılmıyordu: `unitType` ile birebir aynı durum,
   * aynı sonuçla — ekran gösterecek veriye sahip değildi.
   *
   * Masraf zincirinin ilk kapısı artık buna bakıyor. Boşsa talep bir üst
   * birimin müdürüne çıkıyor; en üstte de yoksa doğrudan üst yönetime.
   */
  managerEmployeeId: z9.uuid().nullable(),
  /** Ekranda gösterilecek ad — kimlik tek başına kimseye bir şey söylemiyor. */
  managerName: z9.string().nullable()
});
var includeArchivedField = z9.enum(["true", "false"]).optional();
var orgUnitListQuerySchema = z9.object({
  companyId: z9.uuid().optional(),
  isActive: z9.enum(["true", "false"]).optional(),
  q: z9.string().trim().max(200).optional(),
  includeArchived: includeArchivedField
});
var orgUnitTreeQuerySchema = z9.object({
  companyId: z9.uuid().optional(),
  includeArchived: includeArchivedField
});
var createOrgUnitSchema = z9.object({
  companyId: z9.uuid(),
  parentId: z9.uuid().nullable().optional(),
  code: z9.string().trim().min(2).max(40).regex(/^[A-Z0-9_]+$/, { message: "Kod yaln\u0131zca b\xFCy\xFCk harf, rakam ve alt \xE7izgi i\xE7erebilir." }),
  name: z9.string().trim().min(2).max(200),
  sortOrder: z9.coerce.number().int().min(0).max(9999).default(0),
  /** Ebeveynsiz birim MERKEZ olmak zorunda — veritabanı kısıtı da bunu zorluyor. */
  unitType: z9.enum(["MERKEZ", "DIREKTORLUK", "DEPARTMAN", "BIRIM"]).default("BIRIM")
});
var updateOrgUnitSchema = z9.object({
  name: z9.string().trim().min(2).max(200).optional(),
  /**
   * Moving a unit rewrites its whole subtree in one transaction. The database
   * rejects a move under the unit's own descendant.
   */
  parentId: z9.uuid().nullable().optional(),
  sortOrder: z9.coerce.number().int().min(0).max(9999).optional(),
  isActive: z9.boolean().optional(),
  /**
   * Müdürü atamak ve KALDIRMAK — `null` gönderilebiliyor.
   *
   * `.optional()` «dokunma», `null` «boşalt» demek. İkisi ayrı olmasaydı bir
   * müdür atandıktan sonra geri alınamazdı ve yanlış atanan müdür kalıcı
   * olurdu.
   */
  managerEmployeeId: z9.uuid().nullable().optional(),
  /**
   * Tür DÜZELTİLEBİLİR olmak zorunda. Göç mevcut satırlara bir tür atadı ve
   * atarken tahmin yürüttü (ebeveynsiz olan MERKEZ, gerisi eldeki yapıdan);
   * düzeltmenin yolu olmazsa yanlış atanan tür kalıcı olur ve rozet ekranda
   * güvenilmez bir şey söylemeye başlar.
   */
  unitType: z9.enum(["MERKEZ", "DIREKTORLUK", "DEPARTMAN", "BIRIM"]).optional()
});

// src/dto/user.ts
import { z as z10 } from "zod";
var userSummarySchema = z10.object({
  id: z10.uuid(),
  email: z10.string(),
  fullName: z10.string(),
  employeeId: z10.uuid().nullable(),
  roles: z10.array(z10.string()),
  scopes: z10.array(scopeSchema),
  companies: z10.array(z10.object({ id: z10.uuid(), code: z10.string(), name: z10.string() })),
  mfaEnabled: z10.boolean(),
  isServiceAccount: z10.boolean(),
  disabledAt: z10.string().nullable(),
  lastLoginAt: z10.string().nullable(),
  createdAt: z10.string()
});
var assignableUserSchema = z10.object({
  id: z10.uuid(),
  fullName: z10.string(),
  email: z10.string()
});
var userListQuerySchema = sortableQuerySchema.extend({
  companyId: z10.uuid().optional(),
  role: z10.string().max(60).optional(),
  status: z10.enum(["active", "disabled"]).optional(),
  /** Whether the account has a personnel card. 'false' is the fix-me list. */
  linked: z10.enum(["true", "false"]).optional()
});
var USER_SORT_FIELDS = ["fullName", "email", "createdAt", "lastLoginAt"];
var scopeAssignmentSchema = scopeSchema.superRefine((scope, ctx) => {
  const needsTarget = scope.type === "COMPANY" || scope.type === "DEPARTMENT";
  if (needsTarget && !scope.targetId) {
    ctx.addIssue({
      code: "custom",
      path: ["targetId"],
      message: "\u015Eirket veya departman kapsam\u0131 i\xE7in hedef se\xE7ilmeli."
    });
  }
  if (!needsTarget && scope.targetId) {
    ctx.addIssue({
      code: "custom",
      path: ["targetId"],
      message: "Grup ve kendisi kapsamlar\u0131nda hedef belirtilemez."
    });
  }
});
var createUserSchema = z10.object({
  email: z10.email({ message: "Ge\xE7erli bir e-posta adresi girin." }).trim().toLowerCase(),
  fullName: z10.string().trim().min(3, { message: "Ad soyad en az 3 karakter olmal\u0131." }).max(200),
  roles: z10.array(z10.string().min(1)).min(1, { message: "En az bir rol se\xE7ilmeli." }),
  /** Yeni hesaba doğrudan Super Admin veriliyorsa gerekçe. Bkz. updateUserSchema. */
  superAdminGerekce: z10.string().trim().max(500).optional(),
  scopes: z10.array(scopeAssignmentSchema).min(1, { message: "En az bir kapsam tan\u0131mlanmal\u0131." }),
  locale: z10.enum(["tr", "en"]).default("tr")
});
var updateUserSchema = z10.object({
  fullName: z10.string().trim().min(3).max(200).optional(),
  /**
   * The login address itself — editable, and deliberately so.
   *
   * It was the one field an administrator could not repair. People marry, a
   * company changes domain, and somebody's account was opened with a typo; all
   * three left the only fix in psql. On 2026-08-13 a misspelled address —
   * `asyahavacilk.com`, one `i` short — produced five failed logins and a
   * support ticket, and nobody could correct the record it came from.
   *
   * Same shape as creation, including the lowercase: the login lookup is
   * case-sensitive, so an address typed with a capital would authenticate for
   * nobody.
   */
  email: z10.email({ message: "Ge\xE7erli bir e-posta adresi girin." }).trim().toLowerCase().optional(),
  /**
   * Links the login to a personnel card. Required before the user can file
   * leave: entitlement, balance and the approval chain all hang off the
   * employee record, not off the account.
   */
  employeeId: z10.uuid().nullable().optional(),
  roles: z10.array(z10.string().min(1)).min(1).optional(),
  scopes: z10.array(scopeAssignmentSchema).min(1).optional(),
  locale: z10.enum(["tr", "en"]).optional(),
  disabled: z10.boolean().optional(),
  /**
   * Super Admin yetkisi DEĞİŞİYORSA zorunlu gerekçe.
   *
   * Şema zorunlu kılmıyor çünkü alan yalnız o değişiklikte anlamlı; adını
   * düzelten bir istekten gerekçe istemek, formu her açanı bir cümle yazmaya
   * zorlardı. Zorunluluk kuralda (`superAdminDegisimiDenetle`) ve orada test
   * ediliyor. Gerekçe `app.reason` GUC'siyle denetim satırına düşüyor.
   */
  superAdminGerekce: z10.string().trim().max(500).optional()
});
var credentialDeliverySchema = z10.object({
  status: z10.enum(["sent", "failed", "disabled"]),
  to: z10.string(),
  /** Present on `failed`: the relay's own words, shown to the administrator. */
  reason: z10.string().optional()
});
var createUserResultSchema = z10.object({
  user: userSummarySchema,
  temporaryPassword: z10.string(),
  delivery: credentialDeliverySchema
});
var resetPasswordResultSchema = z10.object({
  temporaryPassword: z10.string(),
  delivery: credentialDeliverySchema
});

// src/dto/activity.ts
import { z as z11 } from "zod";
var ACTIVITY_PRESENCE = {
  /** Son on dakika içinde görüldü. */
  ACTIVE: "ACTIVE",
  /** On ile yirmi dakika arası — masasından kalkmış olabilir. */
  RECENT: "RECENT",
  /**
   * Yirmi dakikadır iz yok.
   *
   * `IDLE` ("oturumu açık") 2026-08-13'te KALDIRILDI. Açık bir refresh token'a
   * dayanıyordu ve o token otuz gün yaşıyor — yani geçen hafta kapatılmamış bir
   * sekme, bugün "sistemde" gibi okunuyordu. Zaman damgası konuşurken bir
   * yedeğe ihtiyaç yok; konuşmuyorsa da söylenecek dürüst şey "yok".
   */
  OFFLINE: "OFFLINE",
  /**
   * ÖLÇÜLEMİYOR — "yok" değil.
   *
   * The only source for this person is a unit whose integration is paused,
   * keyless or silent. Rendering that as OFFLINE would report an absence of
   * measurement as an absence of people, and a broken integration would look
   * exactly like an empty building. Same principle as `atWork: null` below.
   */
  UNKNOWN: "UNKNOWN"
};
var activitySourceSchema = z11.object({
  /** ASYA_TECHNIC gibi. Group Hub'ın kendi sinyali de burada bir kaynak. */
  unitCode: z11.string(),
  sourceSystem: z11.string(),
  lastSeenAt: z11.string(),
  /**
   * Ekranın ADI — yol değil, kimlik değil, sorgu değil.
   *
   * Null iki ayrı sebeple olabilir ve ikisi de dürüst: birim rotayı tanımadı,
   * ya da okuyanda `activity.page_read` yok. Alanın hiç gönderilmemesi yerine
   * null gönderiliyor ki ekran "bilinmiyor" ile "yetkin yok"u aynı boşlukla
   * göstermek zorunda kalmasın.
   */
  pageLabel: z11.string().nullable()
});
var activityRowSchema = z11.object({
  /**
   * Satırın değişmeyen anahtarı. Merkez hesabı olanlarda kullanıcı kimliği,
   * yalnızca bir birimde görünenlerde `birim:dış kimlik`.
   */
  key: z11.string(),
  /**
   * Merkezde hesabı olmayan biri de bu ekranda görünebilir — o zaman null.
   *
   * Birim ERP'sinde çalışan herkesin Group Hub hesabı yok, ve "hepsi tek yerde"
   * iddiasındaki bir ekranın onları sessizce atlaması, bilmediğini itiraf
   * etmesinden kötüdür.
   */
  userId: z11.uuid().nullable(),
  fullName: z11.string(),
  email: z11.string(),
  roles: z11.array(z11.string()),
  /** Personel kartı bağlıysa; puantajın okunabildiği tek durum budur. */
  employeeId: z11.uuid().nullable(),
  /**
   * Hiçbir personel kartına bağlanamadı.
   *
   * Ad, birimin verdiği addır ve doğrulanmamıştır. Ekran bunu açıkça söyler:
   * e-posta benzerliği bu sistemde bilerek "tahmin" sayılıyor ve tahmini
   * kesinlik kılığında göstermek, eşleştirme kuyruğunun var oluş sebebini
   * ortadan kaldırır.
   */
  unmatched: z11.boolean(),
  presence: z11.enum([
    ACTIVITY_PRESENCE.ACTIVE,
    ACTIVITY_PRESENCE.RECENT,
    ACTIVITY_PRESENCE.OFFLINE,
    ACTIVITY_PRESENCE.UNKNOWN
  ]),
  /** Sunucuya en son iş yaptırdığı an. Hiç iz yoksa null. */
  lastSeenAt: z11.string().nullable(),
  /**
   * Süresi dolmamış, iptal edilmemiş refresh token sayısı.
   *
   * Sayı, adres değil. IP de burada duruyordu ve çıkarıldı: "kime ulaşabilirim"
   * sorusuna hiçbir şey katmıyor, karşılığında herkesin nereden bağlandığını
   * operasyonel bir ekrana taşıyordu. Bağlantı adresleri güvenlik olaylarının
   * konusu ve orada kalıyor.
   */
  openSessions: z11.number().int(),
  /**
   * Son eylemin BAŞLIĞI, içeriği değil: "izin talebi güncellendi" gibi.
   * Hangi alanın ne olduğu denetim kaydının işi ve audit.read ister.
   */
  lastActionAt: z11.string().nullable(),
  lastActionLabel: z11.string().nullable(),
  /**
   * Puantaja göre şu an içeride mi.
   *
   * `null` "hayır" değil "bilinmiyor" demektir — personel kartı bağlı olmayan
   * bir hesabın puantajı yoktur, ve bunu "dışarıda" diye göstermek bir yokluğu
   * ölçüme çevirir.
   */
  atWork: z11.boolean().nullable(),
  lastPunchAt: z11.string().nullable(),
  /**
   * Bu kişiyi hangi sistem, ne zaman gördü — en yenisi başta.
   *
   * Group Hub kendi sinyalini de buraya koyar, aynı sözlükle. Bu kasıtlı: hub'ın
   * kendi ölçümü on beş dakikaya kadar bayat, birimlerinki bir dakika, ve tek
   * bir yeşil nokta bunu gizlerdi. Yan yana durunca hangi kaynağın ne kadar
   * taze olduğu okunabiliyor.
   */
  seenIn: z11.array(activitySourceSchema)
});
var activityListSchema = z11.object({
  rows: z11.array(activityRowSchema),
  /** Sunucunun "şimdi"si: istemci saati kaymışsa satırlar yine doğru okunsun. */
  asOf: z11.string()
});
var activityListQuerySchema = z11.object({
  /** Hesabı kapatılmışları da göster. Öntanımlı olarak gizli. */
  includeDisabled: z11.union([z11.boolean(), z11.enum(["true", "false"])]).transform((v) => v === true || v === "true").optional()
});

// src/dto/audit.ts
import { z as z12 } from "zod";
var GROUP_LEVEL_COMPANY_ID = "00000000-0000-0000-0000-000000000000";
var auditEntrySchema = z12.object({
  id: z12.string(),
  occurredAt: z12.string(),
  actorType: z12.string(),
  actorId: z12.uuid().nullable(),
  actorLabel: z12.string().nullable(),
  companyId: z12.uuid(),
  companyName: z12.string().nullable(),
  entityType: z12.string(),
  entityId: z12.string(),
  action: z12.string(),
  changedFields: z12.array(z12.string()),
  before: z12.unknown().nullable(),
  after: z12.unknown().nullable(),
  requestId: z12.string().nullable(),
  ip: z12.string().nullable(),
  source: z12.string()
});
var auditListQuerySchema = sortableQuerySchema.extend({
  companyId: z12.uuid().optional(),
  entityType: z12.string().max(60).optional(),
  entityId: z12.string().max(64).optional(),
  actorId: z12.uuid().optional(),
  action: z12.enum(["insert", "update", "delete"]).optional(),
  from: z12.string().optional(),
  to: z12.string().optional()
});
var auditMineQuerySchema = z12.object({
  page: z12.coerce.number().int().min(1).default(1),
  perPage: z12.coerce.number().int().min(1).max(100).default(50)
});
var AUDIT_FIELD_LABELS = {
  // Her tabloda geçenler
  status: "Durum",
  name: "Ad",
  code: "Kod",
  amount: "Tutar",
  currency: "Para birimi",
  description: "A\xE7\u0131klama",
  note: "Not",
  reason: "Gerek\xE7e",
  is_active: "Aktiflik",
  is_primary: "Birincil hesap",
  label: "Etiket",
  kind: "T\xFCr",
  priority: "\xD6ncelik",
  title: "Ba\u015Fl\u0131k",
  email: "E-posta",
  phone: "Telefon",
  address: "Adres",
  city: "\u0130l",
  district: "\u0130l\xE7e",
  // Kapanış ve karar alanları. "resolved_by_user_id" ekranda ham hâliyle
  // görünüyordu: kullanıcı kendi işleminin ne olduğunu anlamak için bir sütun
  // adı okumak zorunda kalıyordu.
  resolved_at: "\xC7\xF6z\xFCld\xFC tarihi",
  resolved_by_user_id: "\xC7\xF6zen ki\u015Fi",
  closed_at: "Kapan\u0131\u015F tarihi",
  cancelled_at: "\u0130ptal tarihi",
  checked_in_at: "Giri\u015F zaman\u0131",
  checked_out_at: "\xC7\u0131k\u0131\u015F zaman\u0131",
  // İyimser kilit sayacı. Kullanıcı için bir bilgi değil ama listeden
  // gizlemiyoruz: satırın neden değiştiğini açıklayan tek alan o olabiliyor.
  version: "S\xFCr\xFCm sayac\u0131",
  // Hesap ve güvenlik — ham hâlleri kullanıcıyı gereksiz tedirgin ediyor
  password_hash: "Parola",
  password_changed_at: "Parola de\u011Fi\u015Fim tarihi",
  must_change_password: "Parola de\u011Fi\u015Ftirme zorunlulu\u011Fu",
  permission_version: "Yetki s\xFCr\xFCm\xFC",
  disabled_at: "Hesap kapatma",
  mfa_enabled: "\u0130ki ad\u0131ml\u0131 do\u011Frulama",
  last_login_at: "Son giri\u015F",
  // Para ve banka
  iban_enc: "IBAN",
  iban_last4: "IBAN son d\xF6rt hane",
  bank_name: "Banka",
  account_holder: "Hesap sahibi",
  payment_method: "\xD6deme y\xF6ntemi",
  payee_name: "Al\u0131c\u0131",
  document_type: "Belge t\xFCr\xFC",
  document_no: "Belge no",
  invoice_no: "Fatura no",
  invoice_date: "Fatura tarihi",
  tax_number: "Vergi no",
  bank_description: "Bankadaki a\xE7\u0131klama",
  // Karar ve zaman
  decided_at: "Karar tarihi",
  decided_by_user_id: "Karar veren",
  approved_at: "Onay tarihi",
  rejected_at: "Ret tarihi",
  reject_reason: "Ret gerek\xE7esi",
  paid_at: "\xD6deme tarihi",
  start_at: "Ba\u015Flang\u0131\xE7",
  due_at: "Biti\u015F",
  remind_at: "Hat\u0131rlatma",
  valid_from: "Ge\xE7erlilik ba\u015Flang\u0131c\u0131",
  valid_to: "Ge\xE7erlilik biti\u015Fi",
  assignee_user_id: "Sorumlu",
  responsible_user_id: "\u0130lgili ki\u015Fi",
  revision_no: "D\xFCzeltme no",
  require_payee_registration: "Cari defteri zorunlulu\u011Fu",
  require_iban: "IBAN zorunlulu\u011Fu",
  cutoff_day_of_week: "Kesim g\xFCn\xFC",
  cutoff_time: "Kesim saati",
  entered_at: "Muhasebeye giri\u015F",
  scan_status: "Vir\xFCs taramas\u0131",
  file_name: "Dosya ad\u0131",
  storage_key: "Dosya konumu"
};
var AUDIT_ACTION_LABELS = {
  insert: "Olu\u015Fturma",
  update: "G\xFCncelleme",
  delete: "Silme"
};
var AUDIT_ENTITY_LABELS = {
  company: "\u015Eirket",
  org_unit: "Departman",
  location: "Lokasyon",
  job_title: "Unvan",
  // Proje/operasyon boyutu — bütçesi ve müşterisi olan bir kayıt, kimin ne
  // zaman değiştirdiği sorulacak türden.
  project: "Proje / operasyon",
  user: "Kullan\u0131c\u0131",
  user_role: "Kullan\u0131c\u0131 rol\xFC",
  user_scope: "Kullan\u0131c\u0131 kapsam\u0131",
  role_permission: "Rol yetkisi",
  notification_preference: "Bildirim tercihi",
  // --- vergi ---
  // Bu dört ad "İşlem Geçmişim" ekranında kullanıcıya HAM görünüyor; tabloya
  // yazılmayan bir tür, orada `tax_payment_due` diye okunur.
  tax_period: "Vergi d\xF6nemi",
  tax_payment_due: "Vergi \xF6deme vadesi",
  tax_obligation_type: "Y\xFCk\xFCml\xFCl\xFCk t\xFCr\xFC",
  tax_obligation_instalment: "Y\xFCk\xFCml\xFCl\xFCk \xF6deme aya\u011F\u0131",
  tax_stamp_tariff: "Beyanname damga tarifesi",
  // --- kiralık uçak ---
  lease_aircraft: "Kiral\u0131k u\xE7ak",
  lease_contract: "U\xE7ak kira s\xF6zle\u015Fmesi",
  lease_period: "Kira garanti d\xF6nemi",
  lease_payment_due: "Kira \xF6deme aya\u011F\u0131",
  lease_flight_log: "U\xE7u\u015F saati kayd\u0131",
  // --- zimmet ---
  asset: "Zimmet varl\u0131\u011F\u0131",
  asset_assignment: "Zimmet sat\u0131r\u0131",
  asset_category: "Zimmet kategorisi",
  // Hareket defteri artık DENETLENİYOR: kart silinebildiği için satırları yok
  // olabiliyor, ve yok oluşun kendisi bir yere yazılmalı (DST-00091).
  asset_movement: "Zimmet hareketi",
  asset_custody: "Zimmet tutana\u011F\u0131",
  asset_custody_holder: "Zimmet teslim kayd\u0131",
  asset_calibration: "Kalibrasyon",
  asset_maintenance: "Varl\u0131k bak\u0131m\u0131",
  asset_exception: "Zimmet istisnas\u0131",
  asset_attachment: "Zimmet eki",
  // --- insan kaynakları ---
  employee: "Personel",
  employment_record: "\xD6zl\xFCk kayd\u0131",
  assignment: "G\xF6revlendirme",
  employee_document: "Personel belgesi",
  // Bordro — DST-00092. İki ad, iki kaynak: tetikleyici tabloyu (`hr_payslip`)
  // yazıyor, indirme kaydı ise varlığı (`payslip`). İkisi de "İşlem Geçmişim"
  // ekranında kullanıcıya ham hâliyle görünürdü.
  hr_payslip: "Bordro",
  payslip: "Bordro",
  // Okul tahsilatı — DST-00120. Tetikleyici adları; indirme kaydı eki adlandırıyor.
  okul_tahsilat: "Okul tahsilat\u0131",
  okul_tahsilat_satir: "Okul tahsilat\u0131 kalemi",
  okul_tahsilat_ek: "Okul tahsilat\u0131 dekontu",
  okul_tahsilat_kalem: "Okul fiyat listesi kalemi",
  okul_tahsilat_hesap: "Okul tahsilat hesab\u0131",
  seniority_adjustment: "K\u0131dem d\xFCzeltmesi",
  compensation: "\xDCcret",
  pay_component: "\xDCcret bile\u015Feni",
  advance: "Avans",
  advance_entry: "Avans hareketi",
  credential: "Yetki belgesi",
  // --- izin ve puantaj ---
  leave_request: "\u0130zin talebi",
  leave_approval_step: "\u0130zin onay ad\u0131m\u0131",
  leave_ledger: "\u0130zin bakiyesi",
  leave_period: "\u0130zin hak edi\u015F d\xF6nemi",
  leave_policy: "\u0130zin kural\u0131",
  leave_type_map: "\u0130zin t\xFCr\xFC e\u015Flemesi",
  attendance_punch: "Giri\u015F-\xE7\u0131k\u0131\u015F kayd\u0131",
  attendance_policy: "Puantaj kural\u0131",
  attendance_exemption: "Puantaj muafiyeti",
  holiday: "Resm\xEE tatil",
  // --- ödeme ve cari ---
  payment_request: "\xD6deme talebi",
  payment_request_comment: "\xD6deme talebi yaz\u0131\u015Fmas\u0131",
  payment_request_payment: "K\u0131smi \xF6deme",
  payment_week: "\xD6deme haftas\u0131",
  payment_policy: "\xD6deme kural\u0131",
  payment_approval: "\xD6deme onay\u0131",
  payment_approval_stage: "Onay kap\u0131s\u0131",
  payment_approval_assignee: "Onay koltu\u011Fu",
  payee: "Cari",
  payee_bank_account: "Cari banka hesab\u0131",
  payee_ledger: "Cari hesap hareketi",
  payee_reconciliation: "Mutabakat",
  invoice: "Fatura",
  bank_account: "Banka hesab\u0131",
  // --- finans ---
  finance_entry: "Finans kayd\u0131",
  finance_budget: "B\xFCt\xE7e",
  finance_period: "Mali d\xF6nem",
  budget_version: "B\xFCt\xE7e s\xFCr\xFCm\xFC",
  expense_request: "Masraf talebi",
  expense_decision: "Masraf onay karar\u0131",
  allocation_rule: "Da\u011F\u0131t\u0131m kural\u0131",
  accounting_profile: "Muhasebe profili",
  treasury_account: "Kasa/banka hesab\u0131",
  treasury_transaction: "Kasa hareketi",
  fixed_commitment: "Sabit gider s\xF6zle\u015Fmesi",
  fixed_occurrence: "Sabit gider d\xF6nemi",
  fixed_escalation: "Sabit gider art\u0131\u015F\u0131",
  fixed_loan: "Kredi",
  // --- misafirhane ---
  konaklama_room: "Misafirhane odas\u0131",
  konaklama_bed: "Yatak",
  konaklama_guest: "Misafir",
  konaklama_reservation: "Rezervasyon",
  konaklama_collection: "Tahsilat",
  konaklama_expense: "Misafirhane gideri",
  konaklama_ledger: "Misafirhane hesab\u0131",
  konaklama_tariff: "Konaklama tarifesi",
  konaklama_cleaning: "Temizlik kayd\u0131",
  konaklama_issue: "Eksik/ar\u0131za kayd\u0131",
  // --- doküman, duyuru, destek ---
  document_folder: "Dok\xFCman klas\xF6r\xFC",
  document_file: "Dok\xFCman",
  document_file_version: "Dok\xFCman s\xFCr\xFCm\xFC",
  announcement: "Duyuru",
  announcement_target: "Duyuru hedefi",
  // Master data: ekrandan düzenlenen kategori listesi. Pasife alınan bir
  // kategori, onu seçmeye çalışan herkes için sessiz bir davranış değişikliği.
  announcement_category: "Duyuru kategorisi",
  // Yayım ÖNCESİ verilen imza. Kaydın kendisi append-only, o yüzden denetim
  // tetikleyicisi yalnız INSERT dinliyor — `payment_approval`'ın aynı duruşu.
  announcement_approval: "Memorandum onay\u0131",
  // Ek, tebellüğün KAPSAMINDA (K1): belgenin parçası, ayrı bir dosya değil.
  // Yayımdan sonra donuyor, o yüzden denetimin anlattığı şey yalnız taslak
  // aşaması — "bu eki kim koydu, kim kaldırdı".
  announcement_attachment: "Duyuru eki",
  support_ticket: "Destek kayd\u0131",
  support_attachment: "Destek eki",
  payment_attachment: "\xD6deme belgesi",
  payment_request_iban: "\xD6deme talebi IBAN",
  invoice_decision: "Fatura karar\u0131",
  /** Faturanın EKİ. Tek eki kaldıran uç geldiğinde denetlenir oldu (DST-00114). */
  invoice_attachment: "Fatura belgesi",
  /** Faturanın «ne için alındı» sözlüğü (DST-00130). */
  invoice_purpose: "Fatura i\xE7erik t\xFCr\xFC",
  /** Proforma ↔ resmî fatura eşleşmesi (DST-00130). */
  invoice_proforma: "Proforma e\u015Fle\u015Fmesi",
  invoice_comment: "Fatura yaz\u0131\u015Fmas\u0131",
  konaklama_reservation_event: "Rezervasyon hareketi",
  konaklama_issue_attachment: "Misafirhane eki",
  // --- sabit giderler ---
  fixed_expense_commitment: "Sabit gider s\xF6zle\u015Fmesi",
  fixed_expense_occurrence: "Sabit gider d\xF6nemi",
  fixed_expense_escalation: "Sabit gider zamm\u0131",
  fixed_expense_attachment: "Sabit gider belgesi",
  fixed_expense_policy: "Sabit gider kural\u0131",
  fixed_event: "Sabit gider olay\u0131",
  fixed_attachment: "Sabit gider eki",
  // --- uçuş primi ---
  /** Ana veri: uçuş tipleri artık ekrandan tanımlanıyor ve fiyat taşıyor. */
  flight_type: "U\xE7u\u015F tipi",
  flight_pilot: "Prim alan pilot",
  flight_premium_rate: "U\xE7u\u015F prim oran\u0131",
  flight_record: "U\xE7u\u015F kayd\u0131",
  // A monthly total is the SAME table as a flight row and deliberately not the
  // same audit entity: the two are told apart by conditional triggers on
  // `entry_kind`, so that "İşlem Geçmişim" never shows a 122-hour month under
  // the same heading as a 1,4-hour flight. See 20260819180000_flight_monthly_total.
  flight_monthly_total: "Ayl\u0131k u\xE7u\u015F toplam\u0131",
  flight_premium_period: "Ayl\u0131k u\xE7u\u015F primi",
  // --- personel talepleri ---
  staff_request: "Personel talebi",
  staff_request_decision: "Personel talebi karar\u0131",
  staff_request_attachment: "Personel talebi eki",
  // --- ajanda ---
  agenda_item: "Ajanda kayd\u0131",
  agenda_comment: "Ajanda yaz\u0131\u015Fmas\u0131",
  calendar: "Takvim",
  // --- kurumsal iletişim ---
  // `message` yalnız GÜNCELLEME ve SİLME ile buraya düşer: her mesaj INSERT'i
  // denetime yazılsaydı sohbet hacmi sys_audit_log'u boğar ve asıl aranan iki
  // olay — düzenleme ve kaldırma — gürültünün içinde kaybolurdu.
  message_conversation: "Konu\u015Fma",
  message_participant: "Konu\u015Fma \xFCyeli\u011Fi",
  message: "Mesaj",
  // Yalnız İNDİRME izinde görünür: ek yükleme/silme msg_message üzerinden
  // denetleniyor, indirme ise sys_data_access_log'a kendi adıyla düşüyor.
  message_attachment: "Mesaj eki",
  // --- entegrasyon ve sistem ---
  integration_unit: "Entegrasyon birimi",
  integration_credential: "Entegrasyon anahtar\u0131",
  external_identity: "D\u0131\u015F kimlik",
  job_definition: "Zamanlanm\u0131\u015F i\u015F",
  maintenance_billing_exclusion: "Bak\u0131m faturalama istisnas\u0131",
  integration: "Entegrasyon",
  job: "Zamanlanm\u0131\u015F i\u015F",
  system: "Sistem"
};

// src/dto/employee.ts
import { z as z13 } from "zod";
var EMPLOYEE_STATUSES = ["ACTIVE", "ON_LEAVE", "TERMINATED"];
var EMPLOYEE_STATUS_LABELS = {
  ACTIVE: "Aktif",
  ON_LEAVE: "\u0130zinde",
  TERMINATED: "Ayr\u0131ld\u0131"
};
var CONTRACT_TYPES = [
  "PERMANENT",
  "FIXED_TERM",
  "PART_TIME",
  "INTERN",
  "CONTRACTOR"
];
var CONTRACT_TYPE_LABELS = {
  PERMANENT: "Belirsiz s\xFCreli",
  FIXED_TERM: "Belirli s\xFCreli",
  PART_TIME: "K\u0131smi s\xFCreli",
  INTERN: "Stajyer",
  CONTRACTOR: "S\xF6zle\u015Fmeli / d\u0131\u015F kaynak"
};
var GENDERS = ["FEMALE", "MALE", "OTHER"];
var GENDER_LABELS = {
  FEMALE: "Kad\u0131n",
  MALE: "Erkek",
  OTHER: "Di\u011Fer / belirtmek istemiyor"
};
var MATCH_METHODS = ["exact", "normalized", "national_id", "manual"];
var MATCH_METHOD_LABELS = {
  exact: "Kay\u0131tl\u0131 e\u015Fle\u015Fme",
  normalized: "E-posta benzerli\u011Fi",
  national_id: "TC kimlik numaras\u0131",
  manual: "Elle ba\u011Fland\u0131"
};
var MATCH_EVIDENCE = ["national_id", "work_email", "employee_no", "full_name"];
var MATCH_EVIDENCE_LABELS = {
  national_id: "TC kimlik numaras\u0131 e\u015Fle\u015Fti",
  work_email: "Kurumsal e-posta e\u015Fle\u015Fti",
  employee_no: "Personel numaras\u0131 e\u015Fle\u015Fti",
  full_name: "Ad soyad e\u015Fle\u015Fti"
};
var EMPLOYEE_COMPLETENESS_FIELDS = [
  "nationalId",
  "birthDate",
  "personalPhone",
  "emergencyContact",
  "jobTitle",
  "lineManager"
];
var EMPLOYEE_COMPLETENESS_LABELS = {
  nationalId: "TC kimlik numaras\u0131",
  birthDate: "Do\u011Fum tarihi",
  personalPhone: "Ki\u015Fisel telefon",
  emergencyContact: "Acil durumda aranacak ki\u015Fi",
  jobTitle: "Unvan",
  lineManager: "Ba\u011Fl\u0131 oldu\u011Fu y\xF6netici"
};
var employeeSummarySchema = z13.object({
  id: z13.uuid(),
  companyId: z13.uuid(),
  companyName: z13.string(),
  employeeNo: z13.string(),
  fullName: z13.string(),
  workEmail: z13.string().nullable(),
  status: z13.string(),
  seniorityStartDate: z13.string(),
  /** Whole years of service at today's date, after seniority adjustments. */
  seniorityYears: z13.number(),
  orgUnitName: z13.string().nullable(),
  jobTitleName: z13.string().nullable(),
  lineManagerName: z13.string().nullable(),
  /** Credentials expiring within their own warning window. */
  expiringCredentialCount: z13.number().int(),
  /**
   * Whether a login points at this card. False is a real state, not missing
   * data — field staff and service accounts legitimately have no account — so
   * the list renders "Bağlı hesap yok" and never an em-dash.
   */
  hasLogin: z13.boolean()
});
var employeeListQuerySchema = sortableQuerySchema.extend({
  companyId: z13.uuid().optional(),
  orgUnitId: z13.uuid().optional(),
  status: z13.enum(EMPLOYEE_STATUSES).optional(),
  /** Only those with a credential inside its warning window. */
  expiring: z13.enum(["true"]).optional()
});
var EMPLOYEE_SORT_FIELDS = ["employeeNo", "seniorityStartDate", "createdAt"];
var employeeNoSuggestionQuerySchema = z13.object({ companyId: z13.uuid() });
var isoDate = z13.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Tarih YYYY-AA-GG bi\xE7iminde olmal\u0131." });
var createEmployeeSchema = z13.object({
  companyId: z13.uuid(),
  /**
   * The login this card belongs to, chosen from the user picker. Optional, and
   * that is a decision rather than laziness: field staff without a work e-mail
   * and service accounts both need cards, so 'Hesap seçmeden oluştur' stays.
   *
   * Supplying it requires `employee.link_user`, and the id must be one the
   * caller can already see — the service re-checks visibility, because a
   * picker-supplied id is still a request parameter.
   */
  userId: z13.uuid().optional(),
  employeeNo: z13.string().trim().min(1).max(40),
  firstName: z13.string().trim().min(2).max(100),
  lastName: z13.string().trim().min(2).max(100),
  birthDate: isoDate.optional(),
  /** Validated with the official TCKN checksum before it is encrypted. */
  nationalId: z13.string().trim().regex(/^\d{11}$/, { message: "TC kimlik numaras\u0131 11 haneli olmal\u0131." }).optional(),
  workEmail: z13.email({ message: "Ge\xE7erli bir e-posta girin." }).optional().or(z13.literal("")),
  workPhone: z13.string().trim().max(40).optional(),
  personalPhone: z13.string().trim().max(40).optional(),
  orgUnitId: z13.uuid(),
  jobTitleId: z13.uuid().optional(),
  lineManagerEmployeeId: z13.uuid().optional(),
  /** Drives annual-leave entitlement; may predate this company's contract. */
  seniorityStartDate: isoDate,
  contractType: z13.enum(CONTRACT_TYPES).default("PERMANENT"),
  contractStartDate: isoDate,
  contractEndDate: isoDate.optional(),
  probationEndDate: isoDate.optional(),
  weeklyHours: z13.coerce.number().min(1).max(60).default(45)
});
var updateEmployeeSchema = z13.object({
  firstName: z13.string().trim().min(2).max(100).optional(),
  lastName: z13.string().trim().min(2).max(100).optional(),
  workEmail: z13.email().optional().or(z13.literal("")),
  workPhone: z13.string().trim().max(40).optional(),
  status: z13.enum(EMPLOYEE_STATUSES).optional(),
  terminationDate: isoDate.nullable().optional(),
  /** `null` TEMİZLER. `optional` tek başına yetmiyordu: boş alan gönderilmeyince
   *  dolu bir doğum tarihi ekrandan silinemiyor, kullanıcı ne hata ne değişiklik
   *  görüyordu — gürültülü bir 400 yerine sessiz bir hiçlik (DST-00112 incelemesi).
   *  `terminationDate` aynı kalıbı zaten taşıyor. */
  birthDate: isoDate.nullable().optional(),
  /**
   * WRITE-ONCE. Accepted only while the card has no TCKN; once recorded it is
   * not editable here at any permission level, and the service refuses rather
   * than ignoring the field.
   *
   * Not squeamishness about typos: the value is stored as a blind index
   * (nationalIdHash) that every unit-ERP match is keyed on. Changing it rewrites
   * the key, so yesterday's asya.click match silently stops resolving and the
   * hash may now collide with a different person's card. A genuine correction is
   * rare enough to belong with someone who can see what it will break.
   */
  nationalId: z13.string().trim().regex(/^\d{11}$/, { message: "TC kimlik numaras\u0131 11 haneli olmal\u0131." }).optional(),
  personalPhone: z13.string().trim().max(40).optional(),
  personalEmail: z13.email({ message: "Ge\xE7erli bir e-posta girin." }).optional().or(z13.literal("")),
  address: z13.string().trim().max(500).optional(),
  gender: z13.enum(GENDERS).optional(),
  /** Moving it re-derives annual-leave entitlement for every open period. */
  seniorityStartDate: isoDate.optional(),
  emergencyContactName: z13.string().trim().max(200).optional(),
  emergencyContactPhone: z13.string().trim().max(40).optional(),
  orgUnitId: z13.uuid().optional(),
  jobTitleId: z13.uuid().nullable().optional(),
  lineManagerEmployeeId: z13.uuid().nullable().optional()
});
var linkableUserSchema = z13.object({
  id: z13.uuid(),
  email: z13.string(),
  fullName: z13.string(),
  /** Non-null means this account already has a card — the picker disables it. */
  employeeId: z13.uuid().nullable(),
  // --- Aşağıdakiler EKLEMELİ ve OPSİYONEL. Eski istemci bunları görmezden
  // gelir; yeni istemci yoksa da çalışır. Sözleşme bozulmuyor.
  /**
   * Seçicide gösterilen telefon. YALNIZ düz metin saklanan iki kaynaktan
   * gelir — bağlı personel kartının iş telefonu, ya da kişinin kendi kayıt
   * talebinde verdiği numara. `hr_person.personal_phone_enc` şifreli ve kör
   * indeksi yok; ne aranır ne çözülür.
   */
  phone: z13.string().nullable().optional(),
  /**
   * Hesap zaten bağlıysa hangi personele bağlı olduğu ("A-042 · Ahmet Yılmaz").
   * Personelin şirketi çağıranın kapsamı DIŞINDAYSA verilmez: satır yine pasif
   * görünür, yalnız kime bağlı olduğu yazılmaz.
   */
  employeeLabel: z13.string().nullable().optional()
});
var employeeLinkableUserQuerySchema = sortableQuerySchema.extend({
  /** The picker's default: hide accounts that already have a card. */
  onlyUnlinked: z13.enum(["true"]).optional()
});
var LINKABLE_USER_SORT_FIELDS = ["fullName", "email"];
var employeeLinkUserSchema = z13.object({ userId: z13.uuid() });
var MATCH_CANDIDATE_STATES = ["proposed", "confirmed", "rejected"];
var MATCH_CANDIDATE_STATE_LABELS = {
  proposed: "Onay bekliyor",
  confirmed: "Onayl\u0131",
  rejected: "Reddedildi"
};
var linkExternalIdentitySchema = z13.object({
  unitId: z13.uuid(),
  externalUserId: z13.string().trim().min(1).max(64),
  employeeId: z13.uuid()
});
var matchCandidateQuerySchema = sortableQuerySchema.extend({
  companyId: z13.uuid().optional(),
  sourceSystem: z13.string().trim().max(60).optional(),
  state: z13.enum(MATCH_CANDIDATE_STATES).optional()
});
var MATCH_CANDIDATE_SORT_FIELDS = ["confidence", "firstSeenAt"];
var createCredentialSchema = z13.object({
  credentialTypeId: z13.uuid(),
  referenceNo: z13.string().trim().max(100).optional(),
  issuedBy: z13.string().trim().max(200).optional(),
  issuedAt: isoDate.optional(),
  expiresAt: isoDate.optional(),
  note: z13.string().trim().max(2e3).optional()
});

// src/dto/leave.ts
import { z as z14 } from "zod";
var LEAVE_STATUSES = [
  "DRAFT",
  "PENDING_APPROVAL",
  "REVISION_REQUESTED",
  "APPROVED",
  "REJECTED",
  "CANCELLED"
];
var LEAVE_STATUS_LABELS = {
  DRAFT: "Taslak",
  PENDING_APPROVAL: "Onay bekliyor",
  REVISION_REQUESTED: "Revizyon istendi",
  APPROVED: "Onayland\u0131",
  REJECTED: "Reddedildi",
  CANCELLED: "\u0130ptal edildi"
};
var LEAVE_BUCKETS = ["ANNUAL", "EXCUSE", "COMP"];
var LEAVE_BUCKET_LABELS = {
  ANNUAL: "Y\u0131ll\u0131k izin",
  EXCUSE: "Mazeret izni",
  COMP: "Telafi izni"
};
var STEP_LABELS = {
  LINE_MANAGER: "Y\xF6neticisi",
  ORG_UNIT_MANAGER: "Departman y\xF6neticisi",
  UNIT_MANAGER: "Birim m\xFCd\xFCr\xFC",
  HR: "\u0130nsan kaynaklar\u0131",
  HR_MANAGER: "\u0130K y\xF6neticisi",
  GROUP: "Grup y\xF6netimi"
};
var leaveAmountNf = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 });
function formatLeaveNumber(n) {
  return leaveAmountNf.format(Math.abs(n) < 5e-3 ? 0 : n);
}
function leaveDaysToHours(days, hoursPerDay) {
  return Math.round(days * hoursPerDay * 60) / 60 || 0;
}
function formatLeaveAmount(days, hoursPerDay) {
  const gun = `${formatLeaveNumber(days)} g\xFCn`;
  if (!hoursPerDay) return gun;
  return `${formatLeaveNumber(leaveDaysToHours(days, hoursPerDay))} saat (\u2248${gun})`;
}
function formatLeaveAmountShort(days, hoursPerDay) {
  if (!hoursPerDay) return `${formatLeaveNumber(days)} g\xFCn`;
  return `${formatLeaveNumber(leaveDaysToHours(days, hoursPerDay))} saat`;
}
function leaveExceeds(needed, available, hoursPerDay) {
  if (hoursPerDay) {
    return Math.round(needed * hoursPerDay * 60) > Math.round(available * hoursPerDay * 60);
  }
  return Math.round(needed * 1e4) > Math.round(available * 1e4);
}
function formatLeaveDurationText(request) {
  const gun = `${formatLeaveNumber(request.workingDays)} g\xFCn`;
  if (!(typeof request.workingHours === "number" && request.workingHours > 0)) return gun;
  return `${formatLeaveNumber(request.workingHours)} saat (\u2248${gun})`;
}
var isoDate2 = z14.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Tarih YYYY-AA-GG bi\xE7iminde olmal\u0131." });
var isoTime = z14.string().regex(/^\d{2}:\d{2}$/, { message: "Saat SS:DD bi\xE7iminde olmal\u0131." });
var leavePreviewSchema = z14.object({
  employeeId: z14.uuid().optional(),
  leaveTypeId: z14.uuid(),
  startDate: isoDate2,
  endDate: isoDate2,
  startTime: isoTime.optional(),
  endTime: isoTime.optional(),
  startHalf: z14.boolean().default(false),
  endHalf: z14.boolean().default(false)
}).refine((v) => v.endDate >= v.startDate, {
  message: "Biti\u015F tarihi ba\u015Flang\u0131\xE7tan \xF6nce olamaz.",
  path: ["endDate"]
}).refine((v) => v.startTime === void 0 === (v.endTime === void 0), {
  message: "Saatlik izinde ba\u015Flang\u0131\xE7 ve biti\u015F saatinin ikisi de girilmeli.",
  path: ["endTime"]
});
var createLeaveSchema = z14.object({
  employeeId: z14.uuid().optional(),
  leaveTypeId: z14.uuid(),
  startDate: isoDate2,
  endDate: isoDate2,
  startTime: isoTime.optional(),
  endTime: isoTime.optional(),
  startHalf: z14.boolean().default(false),
  endHalf: z14.boolean().default(false),
  reason: z14.string().trim().max(2e3).optional(),
  /** From the preview; a mismatch means the calendar changed underneath. */
  calendarVersion: z14.string().optional()
});
var leaveDecisionSchema = z14.object({
  comment: z14.string().trim().max(2e3).optional()
});
var leaveListQuerySchema = sortableQuerySchema.extend({
  employeeId: z14.uuid().optional(),
  companyId: z14.uuid().optional(),
  status: z14.enum(LEAVE_STATUSES).optional(),
  from: isoDate2.optional(),
  to: isoDate2.optional(),
  /** Only requests currently awaiting the caller's decision. */
  mine: z14.enum(["true"]).optional()
});
var LEAVE_SORT_FIELDS = ["startDate", "createdAt"];
var leaveAdjustSchema = z14.object({
  employeeId: z14.uuid(),
  bucket: z14.enum(["ANNUAL", "EXCUSE", "COMP"]),
  /** Negative reduces. Whole and half days only. */
  deltaDays: z14.number().min(-400).max(400).refine((n) => n !== 0 && Number.isInteger(n * 2), {
    message: "G\xFCn say\u0131s\u0131 s\u0131f\u0131r olamaz ve yar\u0131m g\xFCn kat\u0131 olmal\u0131d\u0131r."
  }),
  effectiveDate: z14.iso.date(),
  /** ADJUSTMENT for a correction, OPENING_IMPORT for a migrated balance. */
  entryType: z14.enum(["ADJUSTMENT", "OPENING_IMPORT"]).default("ADJUSTMENT"),
  /** Mandatory. An unexplained balance change is indistinguishable from a bug. */
  reason: z14.string().min(5).max(300)
});
var leavePeriodRecalcSchema = z14.object({
  /**
   * One service year, named by its start date.
   *
   * Omitted, every calculated period of the employee is revisited — a birth
   * date arriving on the card moves the md. 53/son floor for the year the
   * person turned 50 and every year after it, so correcting them one at a time
   * leaves the card internally inconsistent in between.
   */
  periodStart: z14.iso.date().optional()
});

// src/dto/konaklama.ts
import { z as z15 } from "zod";
var KONAKLAMA_ROOM_TYPES = ["STANDARD", "SUITE", "DORM", "STAFF"];
var KONAKLAMA_ROOM_TYPE_LABELS = {
  STANDARD: "Standart",
  SUITE: "Suit",
  DORM: "Ko\u011Fu\u015F",
  STAFF: "Personel"
};
var KONAKLAMA_BED_TYPES = ["SINGLE", "BUNK_TOP", "BUNK_BOTTOM", "DOUBLE"];
var KONAKLAMA_BED_TYPE_LABELS = {
  SINGLE: "Tek ki\u015Filik",
  BUNK_TOP: "Ranza \u2014 \xFCst",
  BUNK_BOTTOM: "Ranza \u2014 alt",
  DOUBLE: "\xC7ift ki\u015Filik"
};
var KONAKLAMA_GENDERS = ["MALE", "FEMALE", "MIXED"];
var KONAKLAMA_GENDER_LABELS = {
  MALE: "Erkek",
  FEMALE: "Kad\u0131n",
  MIXED: "Karma"
};
var KONAKLAMA_GUEST_TYPES = ["STUDENT", "EMPLOYEE", "INTERN", "VISITOR", "OTHER"];
var KONAKLAMA_GUEST_TYPE_LABELS = {
  STUDENT: "\xD6\u011Frenci",
  EMPLOYEE: "Personel",
  INTERN: "Stajyer",
  VISITOR: "Misafir",
  OTHER: "Di\u011Fer"
};
var KONAKLAMA_RESERVATION_STATUSES = [
  "RESERVED",
  "CHECKED_IN",
  "CHECKED_OUT",
  "CANCELLED",
  "NO_SHOW"
];
var KONAKLAMA_RESERVATION_STATUS_LABELS = {
  RESERVED: "Rezerve",
  CHECKED_IN: "Konakl\u0131yor",
  CHECKED_OUT: "\xC7\u0131k\u0131\u015F yapt\u0131",
  CANCELLED: "\u0130ptal edildi",
  NO_SHOW: "Gelmedi"
};
var KONAKLAMA_OCCUPYING_STATUSES = [
  "RESERVED",
  "CHECKED_IN"
];
var KONAKLAMA_RATE_PERIODS = ["MONTHLY", "NIGHTLY"];
var KONAKLAMA_RATE_PERIOD_LABELS = {
  MONTHLY: "Ayl\u0131k",
  NIGHTLY: "G\xFCnl\xFCk"
};
var KONAKLAMA_PRORATION_MODES = ["DAILY", "FULL"];
var KONAKLAMA_PRORATION_MODE_LABELS = {
  DAILY: "G\xFCn oran\u0131",
  FULL: "Tam ay"
};
var KONAKLAMA_LEDGER_ENTRY_TYPES = [
  "ACCRUAL",
  "COLLECTION",
  "DISCOUNT",
  "WRITE_OFF",
  "REFUND",
  "DEPOSIT",
  "DEPOSIT_REFUND",
  "ADJUSTMENT"
];
var KONAKLAMA_LEDGER_ENTRY_TYPE_LABELS = {
  ACCRUAL: "Tahakkuk",
  COLLECTION: "Tahsilat",
  DISCOUNT: "\u0130ndirim",
  WRITE_OFF: "Alacak silme",
  REFUND: "\u0130ade",
  DEPOSIT: "Depozito",
  DEPOSIT_REFUND: "Depozito iadesi",
  ADJUSTMENT: "D\xFCzeltme"
};
var KONAKLAMA_ADJUST_ENTRY_TYPES = [
  "DISCOUNT",
  "WRITE_OFF",
  "REFUND",
  "DEPOSIT",
  "DEPOSIT_REFUND",
  "ADJUSTMENT"
];
var KONAKLAMA_COLLECTION_METHODS = ["CASH", "BANK_TRANSFER", "CARD", "OTHER"];
var KONAKLAMA_COLLECTION_METHOD_LABELS = {
  CASH: "Nakit",
  BANK_TRANSFER: "Havale/EFT",
  CARD: "Kredi kart\u0131",
  OTHER: "Di\u011Fer"
};
var KONAKLAMA_EXPENSE_CATEGORIES = [
  "FOOD",
  "CLEANING",
  "UTILITY",
  "MAINTENANCE",
  "FURNISHING",
  "STAFF",
  "RENT",
  "OTHER"
];
var KONAKLAMA_EXPENSE_CATEGORY_LABELS = {
  FOOD: "Yiyecek ve mutfak",
  CLEANING: "Temizlik ve hijyen",
  UTILITY: "Elektrik, su, do\u011Falgaz",
  MAINTENANCE: "Bak\u0131m ve onar\u0131m",
  FURNISHING: "Demirba\u015F ve d\xF6\u015Feme",
  STAFF: "Personel gideri",
  RENT: "Kira",
  OTHER: "Di\u011Fer"
};
var KONAKLAMA_CLEANING_KINDS = ["DAILY", "CHECKOUT", "DEEP", "LINEN"];
var KONAKLAMA_CLEANING_KIND_LABELS = {
  DAILY: "G\xFCnl\xFCk",
  CHECKOUT: "\xC7\u0131k\u0131\u015F sonras\u0131",
  DEEP: "Detayl\u0131",
  LINEN: "\xC7ar\u015Faf de\u011Fi\u015Fimi"
};
var KONAKLAMA_CLEANING_STATUSES = ["DONE", "PARTIAL", "SKIPPED"];
var KONAKLAMA_CLEANING_STATUS_LABELS = {
  DONE: "Yap\u0131ld\u0131",
  PARTIAL: "K\u0131smen",
  SKIPPED: "Yap\u0131lmad\u0131"
};
var KONAKLAMA_ISSUE_KINDS = [
  "FAULT",
  "MISSING_ITEM",
  "CLEANLINESS",
  "SAFETY",
  "OTHER"
];
var KONAKLAMA_ISSUE_KIND_LABELS = {
  FAULT: "Ar\u0131za",
  MISSING_ITEM: "Eksik",
  CLEANLINESS: "Temizlik",
  SAFETY: "G\xFCvenlik",
  OTHER: "Di\u011Fer"
};
var KONAKLAMA_ISSUE_PRIORITIES = ["LOW", "NORMAL", "HIGH", "URGENT"];
var KONAKLAMA_ISSUE_PRIORITY_LABELS = {
  LOW: "D\xFC\u015F\xFCk",
  NORMAL: "Normal",
  HIGH: "Y\xFCksek",
  URGENT: "Acil"
};
var KONAKLAMA_ISSUE_PRIORITY_RANK = {
  URGENT: 4,
  HIGH: 3,
  NORMAL: 2,
  LOW: 1
};
var KONAKLAMA_ISSUE_STATUSES = [
  "OPEN",
  "IN_PROGRESS",
  "RESOLVED",
  "CLOSED",
  "CANCELLED"
];
var KONAKLAMA_ISSUE_STATUS_LABELS = {
  OPEN: "A\xE7\u0131k",
  IN_PROGRESS: "\u0130\u015Flemde",
  RESOLVED: "\xC7\xF6z\xFCld\xFC",
  CLOSED: "Kapat\u0131ld\u0131",
  CANCELLED: "\u0130ptal edildi"
};
var KONAKLAMA_OPEN_ISSUE_STATUSES = ["OPEN", "IN_PROGRESS"];
var isoDate3 = z15.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Tarih YYYY-AA-GG bi\xE7iminde olmal\u0131." });
var money = moneyText({ min: 0, max: 99999999 });
var positiveMoney = moneyText({ min: 0.01, max: 99999999 });
var writeCompanyId = z15.uuid().optional();
var createKonaklamaRoomSchema = z15.object({
  companyId: writeCompanyId,
  code: z15.string().trim().min(1).max(20),
  name: z15.string().trim().max(200).optional(),
  floor: z15.string().trim().max(20).optional(),
  roomType: z15.enum(KONAKLAMA_ROOM_TYPES).default("STANDARD"),
  gender: z15.enum(KONAKLAMA_GENDERS).optional(),
  notes: z15.string().trim().max(1e3).optional(),
  /**
   * Kolaylık: oda açılırken bu kadar yatak da açılır ('1', '2', ...).
   * Kapasite yine yatak satırlarının sayısıdır — bu alan sadece onları üretir.
   */
  bedCount: z15.coerce.number().int().min(0).max(50).default(0)
});
var updateKonaklamaRoomSchema = z15.object({
  code: z15.string().trim().min(1).max(20).optional(),
  name: z15.string().trim().max(200).nullish(),
  floor: z15.string().trim().max(20).nullish(),
  roomType: z15.enum(KONAKLAMA_ROOM_TYPES).optional(),
  gender: z15.enum(KONAKLAMA_GENDERS).nullish(),
  notes: z15.string().trim().max(1e3).nullish(),
  isActive: z15.boolean().optional()
});
var createKonaklamaBedSchema = z15.object({
  code: z15.string().trim().min(1).max(20),
  bedType: z15.enum(KONAKLAMA_BED_TYPES).default("SINGLE"),
  notes: z15.string().trim().max(500).optional()
});
var updateKonaklamaBedSchema = z15.object({
  code: z15.string().trim().min(1).max(20).optional(),
  bedType: z15.enum(KONAKLAMA_BED_TYPES).optional(),
  notes: z15.string().trim().max(500).nullish(),
  isActive: z15.boolean().optional()
});
var konaklamaRoomListQuerySchema = sortableQuerySchema.extend({
  roomType: z15.enum(KONAKLAMA_ROOM_TYPES).optional(),
  floor: z15.string().trim().max(20).optional(),
  includeInactive: z15.enum(["true"]).optional(),
  /** Bu tarihteki doluluk da dönsün. Verilmezse bugün. */
  on: isoDate3.optional()
});
var KONAKLAMA_ROOM_SORT_FIELDS = ["code", "floor", "createdAt"];
var createKonaklamaGuestSchema = z15.object({
  companyId: writeCompanyId,
  guestType: z15.enum(KONAKLAMA_GUEST_TYPES).default("STUDENT"),
  firstName: z15.string().trim().min(1).max(100),
  lastName: z15.string().trim().min(1).max(100),
  /** Boş bırakılırsa sistem üretir (MSF-2026-0001). */
  guestNo: z15.string().trim().max(40).optional(),
  employeeId: z15.uuid().optional(),
  birthDate: isoDate3.optional(),
  gender: z15.enum(["MALE", "FEMALE"]).optional(),
  nationalId: z15.string().trim().regex(/^\d{11}$/, { message: "TCKN 11 haneli olmal\u0131." }).optional(),
  phone: z15.string().trim().max(40).optional(),
  email: z15.email({ message: "Ge\xE7erli bir e-posta girin." }).max(255).optional(),
  guardianName: z15.string().trim().max(200).optional(),
  guardianPhone: z15.string().trim().max(40).optional(),
  /** Öğrencinin bağlı olduğu okul/program — serbest metin, ATO kaydı değil. */
  program: z15.string().trim().max(200).optional(),
  notes: z15.string().trim().max(2e3).optional()
});
var updateKonaklamaGuestSchema = patchSchema(createKonaklamaGuestSchema).partial().extend({ isActive: z15.boolean().optional() });
var konaklamaGuestListQuerySchema = sortableQuerySchema.extend({
  guestType: z15.enum(KONAKLAMA_GUEST_TYPES).optional(),
  includeInactive: z15.enum(["true"]).optional(),
  /** Sadece şu an konaklayanlar. */
  staying: z15.enum(["true"]).optional(),
  /** Sadece bakiyesi olanlar. */
  withDebt: z15.enum(["true"]).optional()
});
var KONAKLAMA_GUEST_SORT_FIELDS = ["lastName", "guestNo", "createdAt"];
var createKonaklamaReservationSchema = z15.object({
  bedId: z15.uuid(),
  guestId: z15.uuid(),
  checkIn: isoDate3,
  /** HARİÇ. Misafir bu tarihte yatakta değildir. */
  checkOut: isoDate3,
  /** Verilmezse tarifeden çözülür. */
  ratePeriod: z15.enum(KONAKLAMA_RATE_PERIODS).optional(),
  rateAmount: money.optional(),
  prorationMode: z15.enum(KONAKLAMA_PRORATION_MODES).default("DAILY"),
  depositAmount: money.optional(),
  notes: z15.string().trim().max(1e3).optional()
}).refine((v) => v.checkOut > v.checkIn, {
  message: "\xC7\u0131k\u0131\u015F tarihi giri\u015F tarihinden sonra olmal\u0131.",
  path: ["checkOut"]
}).refine((v) => v.rateAmount === void 0 === (v.ratePeriod === void 0), {
  message: "\xDCcret girilecekse hem d\xF6nem hem tutar verilmeli.",
  path: ["rateAmount"]
});
var updateKonaklamaReservationSchema = z15.object({
  bedId: z15.uuid().optional(),
  checkIn: isoDate3.optional(),
  checkOut: isoDate3.optional(),
  ratePeriod: z15.enum(KONAKLAMA_RATE_PERIODS).optional(),
  rateAmount: money.optional(),
  prorationMode: z15.enum(KONAKLAMA_PRORATION_MODES).optional(),
  notes: z15.string().trim().max(1e3).nullish(),
  version: z15.coerce.number().int().min(1),
  /** Yatak veya tarih değişiyorsa gerekçe: bu kayıt sonradan sorulan bir soru. */
  reason: z15.string().trim().max(300).optional()
});
var konaklamaReservationActionSchema = z15.object({
  note: z15.string().trim().max(500).optional(),
  /** Giriş/çıkışın fiilen olduğu tarih. Verilmezse bugün. */
  on: isoDate3.optional()
});
var konaklamaReservationCancelSchema = z15.object({
  reason: z15.string().trim().min(3).max(300),
  /** İptal gelmemeden mi kaynaklı? Rapor ayrımı için. */
  noShow: z15.boolean().default(false)
});
var konaklamaReservationListQuerySchema = sortableQuerySchema.extend({
  status: z15.enum(KONAKLAMA_RESERVATION_STATUSES).optional(),
  guestId: z15.uuid().optional(),
  roomId: z15.uuid().optional(),
  bedId: z15.uuid().optional(),
  from: isoDate3.optional(),
  to: isoDate3.optional(),
  /** Verilen tarihte yatağı tutan rezervasyonlar. Verilmezse süzme yok. */
  on: isoDate3.optional()
});
var KONAKLAMA_RESERVATION_SORT_FIELDS = [
  "checkIn",
  "checkOut",
  "createdAt",
  "reservationNo"
];
var createKonaklamaTariffSchema = z15.object({
  companyId: writeCompanyId,
  name: z15.string().trim().min(1).max(200),
  roomType: z15.enum(KONAKLAMA_ROOM_TYPES).optional(),
  guestType: z15.enum(KONAKLAMA_GUEST_TYPES).optional(),
  period: z15.enum(KONAKLAMA_RATE_PERIODS).default("MONTHLY"),
  amount: positiveMoney,
  currency: z15.string().trim().length(3).default("TRY"),
  validFrom: isoDate3,
  validTo: isoDate3.optional(),
  specificity: z15.coerce.number().int().min(0).max(100).default(0)
}).refine((v) => !v.validTo || v.validTo >= v.validFrom, {
  message: "Biti\u015F tarihi ba\u015Flang\u0131\xE7tan \xF6nce olamaz.",
  path: ["validTo"]
});
var updateKonaklamaTariffSchema = z15.object({
  name: z15.string().trim().min(1).max(200).optional(),
  roomType: z15.enum(KONAKLAMA_ROOM_TYPES).nullish(),
  guestType: z15.enum(KONAKLAMA_GUEST_TYPES).nullish(),
  period: z15.enum(KONAKLAMA_RATE_PERIODS).optional(),
  amount: positiveMoney.optional(),
  currency: z15.string().trim().length(3).optional(),
  validFrom: isoDate3.optional(),
  validTo: isoDate3.nullish(),
  specificity: z15.coerce.number().int().min(0).max(100).optional()
}).refine((v) => !v.validTo || !v.validFrom || v.validTo >= v.validFrom, {
  message: "Biti\u015F tarihi ba\u015Flang\u0131\xE7tan \xF6nce olamaz.",
  path: ["validTo"]
});
var createKonaklamaCollectionSchema = z15.object({
  guestId: z15.uuid(),
  reservationId: z15.uuid().optional(),
  amount: positiveMoney,
  currency: z15.string().trim().length(3).default("TRY"),
  method: z15.enum(KONAKLAMA_COLLECTION_METHODS),
  collectedOn: isoDate3,
  /** Dekont/makbuz numarası. */
  reference: z15.string().trim().max(100).optional(),
  note: z15.string().trim().max(300).optional()
});
var konaklamaCollectionListQuerySchema = sortableQuerySchema.extend({
  guestId: z15.uuid().optional(),
  method: z15.enum(KONAKLAMA_COLLECTION_METHODS).optional(),
  from: isoDate3.optional(),
  to: isoDate3.optional()
});
var KONAKLAMA_COLLECTION_SORT_FIELDS = ["collectedOn", "createdAt", "amount"];
var konaklamaLedgerAdjustSchema = z15.object({
  guestId: z15.uuid(),
  reservationId: z15.uuid().optional(),
  entryType: z15.enum(KONAKLAMA_ADJUST_ENTRY_TYPES),
  /** Her zaman POZİTİF girilir; yönü entryType belirler. */
  amount: positiveMoney,
  currency: z15.string().trim().length(3).default("TRY"),
  effectiveDate: isoDate3,
  /** Zorunlu. Gerekçesiz bakiye değişimi hatadan ayırt edilemez. */
  reason: z15.string().trim().min(5).max(300)
});
var konaklamaRefundSchema = z15.object({
  guestId: z15.uuid(),
  amount: positiveMoney,
  currency: z15.string().trim().length(3).default("TRY"),
  method: z15.enum(KONAKLAMA_COLLECTION_METHODS),
  refundedOn: isoDate3,
  /** Zorunlu, düzeltmedeki gibi: ekstrede «neden geri verildi»nin tek cevabı. */
  reason: z15.string().trim().min(5).max(300)
});
function iadeTavani(bakiye) {
  return bakiye < 0 ? Math.round(-bakiye * 100) / 100 : 0;
}
var konaklamaLedgerListQuerySchema = sortableQuerySchema.extend({
  guestId: z15.uuid().optional(),
  reservationId: z15.uuid().optional(),
  entryType: z15.enum(KONAKLAMA_LEDGER_ENTRY_TYPES).optional(),
  from: isoDate3.optional(),
  to: isoDate3.optional()
});
var konaklamaAccrualRunSchema = z15.object({
  /** Hangi ay tahakkuk edecek. Verilmezse içinde bulunulan ay. */
  year: z15.coerce.number().int().min(2020).max(2100).optional(),
  month: z15.coerce.number().int().min(1).max(12).optional()
});
var createKonaklamaExpenseSchema = z15.object({
  companyId: writeCompanyId,
  category: z15.enum(KONAKLAMA_EXPENSE_CATEGORIES),
  description: z15.string().trim().min(1).max(300),
  amount: positiveMoney,
  currency: z15.string().trim().length(3).default("TRY"),
  spentOn: isoDate3,
  /** Fatura/fiş numarası. */
  reference: z15.string().trim().max(100).optional(),
  supplierName: z15.string().trim().max(200).optional(),
  roomId: z15.uuid().optional(),
  issueId: z15.uuid().optional(),
  note: z15.string().trim().max(500).optional()
});
var updateKonaklamaExpenseSchema = patchSchema(createKonaklamaExpenseSchema).partial().extend({
  reference: z15.string().trim().max(100).nullish(),
  supplierName: z15.string().trim().max(200).nullish(),
  note: z15.string().trim().max(500).nullish(),
  roomId: z15.uuid().nullish(),
  issueId: z15.uuid().nullish()
});
var konaklamaExpenseListQuerySchema = sortableQuerySchema.extend({
  category: z15.enum(KONAKLAMA_EXPENSE_CATEGORIES).optional(),
  from: isoDate3.optional(),
  to: isoDate3.optional()
});
var KONAKLAMA_EXPENSE_SORT_FIELDS = ["spentOn", "createdAt", "amount"];
var createKonaklamaCleaningSchema = z15.object({
  roomId: z15.uuid(),
  cleanedOn: isoDate3,
  kind: z15.enum(KONAKLAMA_CLEANING_KINDS).default("DAILY"),
  status: z15.enum(KONAKLAMA_CLEANING_STATUSES).default("DONE"),
  /** Temizlik çoğu zaman dışarıdan alınır; temizleyen personel olmayabilir. */
  cleanedByEmployeeId: z15.uuid().optional(),
  cleanedByName: z15.string().trim().max(200).optional(),
  note: z15.string().trim().max(500).optional()
});
var updateKonaklamaCleaningSchema = patchSchema(createKonaklamaCleaningSchema).partial();
var konaklamaCleaningListQuerySchema = sortableQuerySchema.extend({
  roomId: z15.uuid().optional(),
  kind: z15.enum(KONAKLAMA_CLEANING_KINDS).optional(),
  status: z15.enum(KONAKLAMA_CLEANING_STATUSES).optional(),
  from: isoDate3.optional(),
  to: isoDate3.optional()
});
var KONAKLAMA_CLEANING_SORT_FIELDS = ["cleanedOn", "createdAt"];
var createKonaklamaIssueSchema = z15.object({
  /**
   * Yalnızca ortak alan bildiriminde (oda ve yatak boşken) kullanılır: aksi
   * hâlde şirket yataktan ya da odadan türetilir ve gövdeden gelen ikinci bir
   * cevap ancak yanlış olabilir.
   */
  companyId: writeCompanyId,
  /** Boş = ortak alan. */
  roomId: z15.uuid().optional(),
  bedId: z15.uuid().optional(),
  kind: z15.enum(KONAKLAMA_ISSUE_KINDS).default("FAULT"),
  priority: z15.enum(KONAKLAMA_ISSUE_PRIORITIES).default("NORMAL"),
  title: z15.string().trim().min(3).max(200),
  description: z15.string().trim().max(4e3).optional(),
  /** Açık ve bloke eden kayıt, yatağı rezervasyona kapatır. */
  blocksBed: z15.boolean().default(false),
  reportedByGuestId: z15.uuid().optional(),
  assignedToEmployeeId: z15.uuid().optional()
});
var updateKonaklamaIssueSchema = z15.object({
  kind: z15.enum(KONAKLAMA_ISSUE_KINDS).optional(),
  priority: z15.enum(KONAKLAMA_ISSUE_PRIORITIES).optional(),
  title: z15.string().trim().min(3).max(200).optional(),
  description: z15.string().trim().max(4e3).nullish(),
  status: z15.enum(KONAKLAMA_ISSUE_STATUSES).optional(),
  blocksBed: z15.boolean().optional(),
  assignedToEmployeeId: z15.uuid().nullish(),
  resolutionNote: z15.string().trim().max(500).nullish()
});
var konaklamaIssueListQuerySchema = sortableQuerySchema.extend({
  status: z15.enum(KONAKLAMA_ISSUE_STATUSES).optional(),
  kind: z15.enum(KONAKLAMA_ISSUE_KINDS).optional(),
  priority: z15.enum(KONAKLAMA_ISSUE_PRIORITIES).optional(),
  roomId: z15.uuid().optional(),
  /** Sadece açık kayıtlar (OPEN + IN_PROGRESS). */
  open: z15.enum(["true"]).optional(),
  blocking: z15.enum(["true"]).optional()
});
var KONAKLAMA_ISSUE_SORT_FIELDS = ["createdAt", "priority", "status"];
var konaklamaOccupancyQuerySchema = z15.object({
  on: isoDate3.optional()
});
var konaklamaAvailabilityQuerySchema = z15.object({
  checkIn: isoDate3,
  checkOut: isoDate3,
  roomType: z15.enum(KONAKLAMA_ROOM_TYPES).optional(),
  gender: z15.enum(KONAKLAMA_GENDERS).optional()
}).refine((v) => v.checkOut > v.checkIn, {
  message: "\xC7\u0131k\u0131\u015F tarihi giri\u015F tarihinden sonra olmal\u0131.",
  path: ["checkOut"]
});
var konaklamaSummaryQuerySchema = z15.object({
  year: z15.coerce.number().int().min(2020).max(2100).optional(),
  month: z15.coerce.number().int().min(1).max(12).optional()
});
var KONAKLAMA_RECEIVABLE_VIEWS = ["DEBTORS", "OVERPAID", "ALL"];
var konaklamaReceivableListQuerySchema = sortableQuerySchema.extend({
  /** Varsayılan `DEBTORS`. */
  view: z15.enum(KONAKLAMA_RECEIVABLE_VIEWS).optional(),
  /**
   * Eski ad: `false` = `ALL`. Dağıtım sırasında açık kalmış eski bir sekme
   * hâlâ bunu gönderiyor; `view` verilmişse yok sayılır.
   */
  onlyDebtors: z15.enum(["true", "false"]).optional()
});

// src/dto/approval.ts
var APPROVAL_DOMAINS = [
  "PAYMENT",
  "PAYMENT_URGENT",
  "INVOICE",
  "STAFF_REQUEST",
  "EXPENSE",
  /**
   * ALTINCISI, ve sınavı bu kez PARAYA HİÇ DEĞMEDEN geçti.
   *
   * Bir memorandumun onaydan geçmesi gerektiği doğru; onay için ikinci bir
   * mekanizma yazmak değil. Bu alanın eklenmesi motoru, kapı tanımını ya da
   * sayma kurallarını DEĞİŞTİRMEDİ — açılan tek yeni şey imza defteri
   * (`ann_approval`), çünkü imza, alanın kendi kaydına bağlanan tek parça.
   *
   * Diğer beşinden bir farkı var ve o fark veride değil ZAMANDA: onay
   * kesinlikle yayım ÖNCESİDİR. Yayımlanmış bir memorandum yeniden onaya
   * gönderilemez; düzeltmenin tek yolu geri çekip yeni bir revizyon
   * yayımlamaktır. Sebebi, metni yayımda donduran tetikleyicinin öncülü:
   * yayımlanmış olan artık delildir, ve bir delilin onay durumunu sonradan
   * oynatmak imzalamış herkesi başka bir şeyin tanığı yapar.
   */
  "ANNOUNCEMENT"
];
var APPROVAL_DOMAIN_LABELS = {
  PAYMENT: "\xD6deme talebi",
  PAYMENT_URGENT: "Acil \xF6deme",
  INVOICE: "Fatura",
  STAFF_REQUEST: "Personel talebi",
  EXPENSE: "Masraf",
  ANNOUNCEMENT: "Memorandum"
};
function stageAppliesToAmount(minAmount, amount) {
  return minAmount === null || amount > minAmount;
}
var APPROVAL_STAGE_MODES = ["ANY_ONE", "ALL", "QUORUM"];

// src/dto/payment.ts
import { z as z16 } from "zod";
var PAYMENT_REQUEST_STATUSES = [
  "DRAFT",
  "PENDING_APPROVAL",
  "RETURNED_TO_REQUESTER",
  "APPROVED",
  "REJECTED",
  "DEFERRED",
  "PAID",
  "CANCELLED"
];
var PAYMENT_LEGACY_STATUSES = [
  "PENDING_MANAGER",
  "MANAGER_REJECTED",
  "PENDING_EXECUTIVE",
  "EXECUTIVE_RETURNED"
];
var PAYMENT_IN_APPROVAL_STATUSES = [
  "PENDING_APPROVAL",
  "PENDING_MANAGER",
  "PENDING_EXECUTIVE"
];
var PAYMENT_STATUS_LABELS = {
  DRAFT: "Taslak",
  PENDING_APPROVAL: "Onay zincirinde",
  RETURNED_TO_REQUESTER: "Talep sahibine d\xF6nd\xFC",
  APPROVED: "\xD6demeye haz\u0131r",
  REJECTED: "Reddedildi",
  DEFERRED: "Sonraki haftaya",
  PAID: "\xD6dendi",
  CANCELLED: "\u0130ptal",
  // İki kapılı dönem:
  PENDING_MANAGER: "M\xFCd\xFCr onay\u0131nda (eski ak\u0131\u015F)",
  MANAGER_REJECTED: "M\xFCd\xFCr onaylamad\u0131 (eski ak\u0131\u015F)",
  PENDING_EXECUTIVE: "Y\xF6netici onay\u0131nda (eski ak\u0131\u015F)",
  EXECUTIVE_RETURNED: "Y\xF6neticiden d\xF6nd\xFC (eski ak\u0131\u015F)"
};
function odemeDurumEtiketi(talep) {
  if (talep.status === "APPROVED" && (talep.paidAmount ?? 0) > 0) return "K\u0131smen \xF6dendi";
  return PAYMENT_STATUS_LABELS[talep.status] ?? talep.status;
}
var PAYMENT_EVENT_LABELS = {
  SUBMITTED: "G\xF6nderildi",
  STAGE_APPROVED: "Onayland\u0131",
  STAGE_RETURNED: "Geri g\xF6nderildi",
  STAGE_RELOCATED: "Kap\u0131 de\u011Fi\u015Fti",
  CHAIN_COMPLETED: "Onay zinciri tamamland\u0131",
  CHAIN_BLOCKED: "Zincir durdu",
  CHAIN_REBASELINED: "Zincir yeniden kuruldu",
  REJECTED: "Reddedildi",
  CANCELLED: "\u0130ptal edildi",
  RESUBMITTED: "Tekrar g\xF6nderildi",
  MOVED_TO_OPEN_WEEK: "A\xE7\u0131k haftaya ta\u015F\u0131nd\u0131",
  ATTACHED: "Belge eklendi",
  CARD_FORM_VIEWED: "Kart talimat formu a\xE7\u0131ld\u0131",
  CARD_FORM_PURGED: "Kart talimat formu silindi",
  IBAN_REVEALED: "IBAN g\xF6r\xFCnt\xFClendi",
  TRANSFER_FORM_REQUESTED: "Talimat formu istendi",
  TRANSFER_FORM_READY: "Talimat formu haz\u0131r",
  PAID: "\xD6dendi",
  PARTIALLY_PAID: "K\u0131smi \xF6deme",
  REMAINDER_CLOSED: "Kalan kapat\u0131ld\u0131",
  PAYMENT_DEFERRED: "\xD6deme sonraki haftaya b\u0131rak\u0131ld\u0131",
  DUPLICATE_ACKNOWLEDGED: "Olas\u0131 tekerr\xFCr gerek\xE7esiyle kabul edildi"
};
var PAYEE_SEARCH_MIN_CHARS = 4;
var PAYEE_LEDGER_SOURCES = ["IMPORT", "PAYMENT", "MANUAL", "INVOICE", "INV_VOID"];
var PAYEE_LEDGER_SOURCE_LABELS = {
  IMPORT: "Muhasebe",
  PAYMENT: "\xD6deme",
  MANUAL: "Elle",
  INVOICE: "Fatura",
  INV_VOID: "Kesilmeyen fatura"
};
var PAYMENT_METHODS = ["TRANSFER", "CREDIT_CARD"];
var PAYMENT_METHOD_LABELS = {
  TRANSFER: "Havale / EFT",
  CREDIT_CARD: "Kredi kart\u0131"
};
function paymentMethodNeedsIban(method) {
  return method === "TRANSFER";
}
function odemeKapatmaKarari(talep, can) {
  if (can(PERMISSIONS.PAYMENT_EXECUTE)) return { izin: true };
  if (!can(PERMISSIONS.PAYMENT_CARD_SETTLE)) {
    return { izin: false, neden: "\xD6demeyi kaydetme yetkiniz yok." };
  }
  const kisim = talep.paidPartCount ?? 0;
  if (talep.receiptCount <= kisim) {
    return {
      izin: false,
      neden: kisim > 0 ? "Bu \xF6demenin dekontunu \xABBelgeler\xBBden \xABDekont\xBB t\xFCr\xFCyle ekleyin; her k\u0131smi \xF6deme kendi dekontuyla kapat\u0131l\u0131yor." : "\xD6nce dekontu \xABBelgeler\xBBden \xABDekont\xBB t\xFCr\xFCyle ekleyin; \xF6deme dekontuyla kapat\u0131l\u0131yor."
    };
  }
  return { izin: true };
}
var kurus = (n) => Math.round(n * 100);
function kalanTutar(talep) {
  return (kurus(talep.amount) - kurus(talep.paidAmount)) / 100;
}
function kismiOdemeKarari(talep, istenen, kismiKapali) {
  const kalan = kalanTutar(talep);
  if (!(kalan > 0)) {
    return { tur: "HATA", mesaj: "Bu talebin \xF6denecek tutar\u0131 kalmad\u0131." };
  }
  const tutar = istenen ?? kalan;
  if (!(kurus(tutar) > 0)) {
    return { tur: "HATA", mesaj: "\xD6denen tutar s\u0131f\u0131rdan b\xFCy\xFCk olmal\u0131." };
  }
  if (kurus(tutar) > kurus(kalan)) {
    return {
      tur: "HATA",
      mesaj: `\xD6denen tutar kalandan (${formatMoney(kalan, talep.currency)}) b\xFCy\xFCk olamaz. Fazlas\u0131 \xF6dendiyse ayr\u0131 bir talep a\xE7\u0131n.`
    };
  }
  if (kurus(tutar) === kurus(kalan)) {
    return kurus(talep.paidAmount) > 0 ? { tur: "SON_KISIM", tutar } : { tur: "TAMAMI", tutar };
  }
  if (kismiKapali) return { tur: "HATA", mesaj: kismiKapali };
  return { tur: "KISMI", tutar, kalan: (kurus(kalan) - kurus(tutar)) / 100 };
}
var closeRemainderSchema = z16.object({
  reason: z16.string().trim().min(10, "Gerek\xE7e en az 10 karakter olmal\u0131.").max(500, "Gerek\xE7e en fazla 500 karakter olabilir.")
});
var markPaymentPaidSchema = z16.object({
  /** Paranın çıktığı hesap. Seçilebilir hesap varken zorunlu (`odemeKasaKarari`). */
  treasuryAccountId: z16.uuid().optional(),
  /**
   * Paranın çıktığı gün (valör). Verilmezse bugün, İstanbul saatiyle. Banka
   * ekstresi harekete bu tarihle eşleşiyor; cuma çıkan ödemeyi pazartesi
   * işaretleyen kişi cumayı yazabilmeli.
   */
  paidOn: z16.iso.date().optional(),
  /**
   * Hesap seçilmediğinde — kart, ya da kasa defterinde hesabı olmayan şirket —
   * hangi banka ya da kartla ödendiği. Hesap seçildiyse hesabın bankası yazılır.
   */
  bankName: z16.string().trim().min(2).max(160).optional(),
  reference: z16.string().trim().max(100).optional(),
  /**
   * Bu ödemede çıkan tutar, Türkçe yazımla («690.576,48»). Verilmezse kalanın
   * tamamı — bugünkü davranış. Kalandan azsa kısmi ödeme (`kismiOdemeKarari`).
   */
  amount: moneyText({ min: 0.01, max: 1e9 }).optional()
});
function istanbulGunu(an) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(an);
}
function odemeKasaHesaplari(talep, acikHubHesaplari, zatenYazildi) {
  if (zatenYazildi) return { yazilir: false, neden: zatenYazildi };
  if (talep.paymentMethod === "CREDIT_CARD") {
    return {
      yazilir: false,
      neden: "Kartla \xF6deme kasa defterine buradan yaz\u0131lmaz: para bankadan kart ekstresi \xF6denince \xE7\u0131kar, o ayr\u0131 bir harekettir."
    };
  }
  const hesaplar = acikHubHesaplari.filter((h) => h.currency === talep.currency);
  if (hesaplar.length === 0) {
    return {
      yazilir: false,
      neden: `Bu \u015Firketin kasa defterinde a\xE7\u0131k bir ${talep.currency} hesab\u0131 yok; \xF6deme yaln\u0131z cari defterine yaz\u0131l\u0131r.`
    };
  }
  return { yazilir: true, hesaplar };
}
function odemeKasaKarari(talep, acikHubHesaplari, secim) {
  const secilenHesapId = secim.hesapId;
  const valor = secim.valor;
  const liste = odemeKasaHesaplari(talep, acikHubHesaplari, secim.zatenYazildi);
  if (secilenHesapId === void 0) {
    return liste.yazilir ? {
      tur: "HATA",
      mesaj: "\xD6demenin hangi hesaptan \xE7\u0131kt\u0131\u011F\u0131n\u0131 se\xE7in; \xF6deme o hesab\u0131n kasa/banka defterine \xE7\u0131k\u0131\u015F olarak yaz\u0131l\u0131r."
    } : { tur: "YAZMA", neden: liste.neden };
  }
  if ((talep.paymentMethod === "CREDIT_CARD" || secim.zatenYazildi) && !liste.yazilir) {
    return { tur: "HATA", mesaj: liste.neden };
  }
  const hesap = liste.yazilir ? liste.hesaplar.find((h) => h.id === secilenHesapId) : void 0;
  if (hesap) {
    if (valor !== void 0 && hesap.openingDate !== void 0 && valor < hesap.openingDate) {
      return {
        tur: "HATA",
        mesaj: `${hesap.code} hesab\u0131 kasa defterine ${hesap.openingDate} a\xE7\u0131l\u0131\u015F bakiyesiyle girdi; ondan \xF6nceki bir \xF6deme o bakiyenin i\xE7inde. \xD6deme tarihini kontrol edin.`
      };
    }
    return { tur: "YAZ", hesap };
  }
  const baskaDoviz = acikHubHesaplari.find((h) => h.id === secilenHesapId);
  return {
    tur: "HATA",
    mesaj: baskaDoviz ? `${baskaDoviz.code} hesab\u0131 ${baskaDoviz.currency} tutuyor, talep ise ${talep.currency}; kur kayd\u0131 olmadan \xE7evrilmez.` : "Se\xE7ilen hesap bu \u015Firketin kasa defterinde tutulan a\xE7\u0131k bir hesab\u0131 de\u011Fil."
  };
}
var PAYMENT_ATTACHMENT_KINDS = [
  "SOURCE_DOCUMENT",
  "RECEIPT",
  "TRANSFER_FORM",
  "CARD_INSTRUCTION"
];
var PAYMENT_ATTACHMENT_KIND_LABELS = {
  SOURCE_DOCUMENT: "Dayanak belgesi",
  RECEIPT: "Dekont",
  TRANSFER_FORM: "Havale talimat\u0131",
  CARD_INSTRUCTION: "Kredi kart\u0131 \xF6deme talimat formu"
};
var PAYMENT_ATTACHMENT_RESTRICTED_KINDS = /* @__PURE__ */ new Set([
  "CARD_INSTRUCTION"
]);
var PAYMENT_DOCUMENT_TYPES = [
  "PROFORMA",
  "E_ARCHIVE",
  "E_INVOICE",
  "STUDENT_TERMINATION",
  "INSURANCE_QUOTE",
  "INSURANCE_POLICY",
  "ACCOUNT_SETTLEMENT",
  /**
   * Trafik cezası. Bugüne kadar proforma kılığında giriliyordu ve altı ay
   * sonra tabloya bakan biri için "PROFORMA" yazan bir ceza, olmayan bir
   * belgeyi aratan bir satırdır. Belge no ZORUNLU kalıyor: tutanakta zaten bir
   * numara var, ve dayanaksız bir ödeme talebi denetimde açıklanamaz.
   */
  "TRAFFIC_FINE",
  /**
   * Cari emanet: bir cariye, henüz bir borca karşılık gelmeden yüklenen tutar.
   *
   * DHMİ ya da Petrol Ofisi gibi kurumlarda hesap önce fonlanır, harcama sonra
   * olur. Bugüne kadar bu da proforma kılığında giriliyordu ve "PROFORMA" yazan
   * bir emanet, olmayan bir belgeyi aratan bir satır — trafik cezasının aynı
   * sebeple kendi türünü almasıyla aynı gerekçe.
   *
   * BELGE NUMARASI OLMAYAN TEK TÜR, ve bu bilerek. Diğer her türde alan zorunlu
   * kalıyor: dayanaksız bir ödeme talebi denetimde açıklanamaz. Ama emanetin
   * dayanağı henüz YOKTUR — numarayı zorunlu tutmak, insanları uydurmaya iter
   * ve uydurulmuş bir referans, boş bir alandan daha kötüdür. Kural artık
   * veritabanı kısıtında yazılı (chk_pay_document_no_required).
   */
  "ACCOUNT_DEPOSIT",
  /**
   * Vergi tahakkuk fişi ve SGK tahakkuk fişi.
   *
   * İKİSİ AYRI TÜR ve bu bilinçli. Muhtasar tek beyannamedir ama iki ödemedir:
   * kesilen vergi ayın 26'sında vergi dairesine, SGK primi ayın son günü
   * SGK'ya gider. Tek türde toplansalardı haftalık tabloya bakan kişi hangi
   * kuruma ödediğini ancak açıklamayı okuyarak ayırabilirdi — ve iki ödeme beş
   * gün arayla, iki ayrı kurumun kendi gecikme zammıyla işliyor.
   *
   * Proforma kılığında girilmelerinin bedeli trafik cezasınınkiyle aynıydı:
   * "PROFORMA" yazan bir tahakkuk, olmayan bir belgeyi aratan bir satır. Belge
   * numarası ZORUNLU kalıyor ve tahakkuk fişi numarasıdır — vergi modülü zaten
   * o numarayı istemeden tahakkuk kaydını kabul etmiyor, çünkü mali müşavirden
   * fişi almaya zorlayan tek şey o.
   */
  "TAX_ASSESSMENT",
  "SGK_ACCRUAL",
  /**
   * Uçak kira sözleşmesi.
   *
   * Kiralık uçak modülü bir dönemin ödemesini haftalık tabloya iki ayak olarak
   * yazıyor ve ikincisinin dayanağı bir fatura DEĞİL: sözleşmenin kendisi.
   * Proforma kılığında girilseydi, altı ay sonra tabloya bakan biri için
   * "PROFORMA" yazan bir kira, olmayan bir belgeyi aratan bir satır olurdu —
   * trafik cezası (20260814020423) ve cari emanet (20260814200916) kendi
   * türlerini tam olarak aynı sebeple aldı.
   *
   * Belge numarası ZORUNLU kalıyor ve sözleşme numarası + dönemden türetiliyor
   * (`KUC-0001/SETTLE/2026-03`). Numaranın türetilebilir olması aynı zamanda
   * kurtarmanın da dayanağı: talep yazıldıktan sonra bağlanmadan çöken bir iş,
   * aynı numarayı `pay_request`e sorarak kaldığı yerden devam ediyor.
   */
  "LEASE_AGREEMENT",
  /**
   * Personel primi — bugün yalnız uçuş primi, ama tür ondan daha geniş adlandı.
   *
   * Kendi türünü almasının gerekçesi trafik cezasınınkiyle aynı: bir pilota
   * yapılan prim ödemesi "PROFORMA" kılığında girilseydi, tabloya bakan kişi
   * olmayan bir belgeyi arardı. Ve bu satırlar tek tek küçük, sayıca çok ve her
   * ay tekrar eden bir küme — türü olmayan bir ödeme sınıfının haftalık tabloda
   * ne kadar yer tuttuğu ancak açıklaması okunarak anlaşılırdı.
   *
   * BELGE NUMARASI ZORUNLU kalıyor ve bu bilerek: numara BELİRLENİMCİ olarak
   * üretiliyor (`UP-<yıl>-<ay>-<sicil>`), yani hem banka ekstresindeki satır
   * kişiye ve aya kadar izlenebiliyor, hem de çöken bir yazma turundan sonra
   * "bu talep açılmış mıydı" sorusu `pay_request`'e sorulabiliyor. Vergi
   * modülünün aynı kararı, aynı sebeple.
   */
  "PERSONNEL_PREMIUM",
  /**
   * Akaryakıt — DST-00105.
   *
   * Kendi türü, çünkü "ödeme taleplerinde de bu alanın açılmasını istiyorum"
   * cümlesinin karşılığı bu: istenen alan (`responsibleUserId`, aracı kullanan
   * kişi) arayüzde belge TÜRÜNE göre çiziliyor ve akaryakıtın türü yoktu —
   * PROFORMA ya da cari emanet olarak giriliyordu.
   *
   * Trafik cezası ve cari emanet de tam aynı sebeple ayrı tür olmuşlardı:
   * "PROFORMA" yazan bir satır, olmayan bir belgeyi aratıyor.
   */
  "FUEL",
  /**
   * Aidat — DST-00125.
   *
   * Site ve iş merkezi yönetimlerine ödenen aidat. Kendi türü, çünkü bugüne
   * kadar PROFORMA kılığında giriliyordu (üretimde 30.09 itibarıyla 15 talep)
   * ve "PROFORMA" yazan bir aidat, olmayan bir belgeyi aratan bir satır —
   * trafik cezası, cari emanet ve akaryakıt tam aynı sebeple ayrı tür oldu.
   *
   * Belge numarası İSTEĞE BAĞLI; gerekçesi `PAYMENT_DOCUMENT_NO_OPTIONAL`'da.
   * Sabit gider modülündeki DUES sözleşmeleri de artık bu türle açılıyor.
   */
  "DUES"
];
var PAYMENT_DOCUMENT_TYPE_LABELS = {
  PROFORMA: "Proforma",
  E_ARCHIVE: "e-Ar\u015Fiv",
  E_INVOICE: "e-Fatura",
  STUDENT_TERMINATION: "\xD6\u011Frenci fesih protokol\xFC",
  INSURANCE_QUOTE: "Poli\xE7e teklifi",
  INSURANCE_POLICY: "Poli\xE7e",
  ACCOUNT_SETTLEMENT: "Cari hesaba mahsuben",
  TRAFFIC_FINE: "Trafik cezas\u0131",
  ACCOUNT_DEPOSIT: "Cari emanet",
  TAX_ASSESSMENT: "Vergi tahakkuk fi\u015Fi",
  SGK_ACCRUAL: "SGK tahakkuk fi\u015Fi",
  LEASE_AGREEMENT: "U\xE7ak kira s\xF6zle\u015Fmesi",
  PERSONNEL_PREMIUM: "Personel primi",
  FUEL: "Akaryak\u0131t",
  DUES: "Aidat"
};
var PAYMENT_DOCUMENT_NO_LABELS = {
  PROFORMA: "Belge no",
  E_ARCHIVE: "Belge no",
  E_INVOICE: "Fatura no",
  STUDENT_TERMINATION: "Protokol no",
  INSURANCE_QUOTE: "Teklif no",
  INSURANCE_POLICY: "Poli\xE7e no",
  // Fatura no DEĞİL, ve bildirilen hata tam olarak buydu: mahsuben yapılan bir
  // ödeme birden çok faturayı kapatıyor, tek bir fatura numarası yok. Alan
  // zorunlu kalıyor — dayanaksız bir talep denetimde açıklanamaz — ama ne
  // istediğini söylüyor.
  ACCOUNT_SETTLEMENT: "Mahsup referans\u0131 (iste\u011Fe ba\u011Fl\u0131)",
  TRAFFIC_FINE: "Ceza karar tutana\u011F\u0131 no",
  // Alan bu türde EKRANDA YOK; etiket, tür sonradan değiştirilirse alanın adsız
  // kalmaması için duruyor.
  ACCOUNT_DEPOSIT: "Referans (iste\u011Fe ba\u011Fl\u0131)",
  // İkisinde de aynı ad, çünkü istenen şey aynı: fişin üstündeki numara. Türü
  // ayıran şey numaranın biçimi değil, parayı hangi kurumun beklediği.
  TAX_ASSESSMENT: "Tahakkuk fi\u015F no",
  SGK_ACCRUAL: "Tahakkuk fi\u015F no",
  // Sözleşme no + dönem. Sistem üretiyor, kimse elle yazmıyor.
  LEASE_AGREEMENT: "S\xF6zle\u015Fme ve d\xF6nem",
  // Sistemin ürettiği numara: dönem ve sicil. Elle yazılmıyor, ama adı yine de
  // ne olduğunu söylemeli — ekstredeki satırı arayan kişi bunu okuyacak.
  PERSONNEL_PREMIUM: "Prim d\xF6nem no",
  // Akaryakıtın dayanağı fiş, fatura ya da kart ekstresindeki satır olabiliyor;
  // alanın adı üçünü de karşılamalı, yoksa eldeki belgeyi "belge no" sanmayan
  // biri alanı boş bırakır.
  FUEL: "Fi\u015F / fatura no",
  // Formlar numarası isteğe bağlı türlerde alanı HİÇ çizmiyor (cari emanet
  // gibi), yani bu etiket elle açılan aidatta görünmez: dönem ve bağımsız bölüm
  // "Neden alınıyor?"a yazılıyor — elle açılan üç aidat talebinde zaten oraya
  // yazılmıştı. Etiket, numarası sözleşmeden türetilen sabit gider aidatları ve
  // tip tamlığı için burada.
  DUES: "D\xF6nem / bildirim no"
};
var PAYMENT_DOCUMENT_NO_OPTIONAL = /* @__PURE__ */ new Set([
  "ACCOUNT_DEPOSIT",
  "ACCOUNT_SETTLEMENT",
  "DUES"
]);
function paymentDocumentNoRequired(type) {
  return !PAYMENT_DOCUMENT_NO_OPTIONAL.has(type);
}
var FATURA_ICERIGI_TURLERI = /* @__PURE__ */ new Set([
  "E_INVOICE",
  "E_ARCHIVE",
  "PROFORMA"
]);
var FATURA_ICERIGI_AZAMI = 2e3;
function faturaIcerigiSorulur(type) {
  return FATURA_ICERIGI_TURLERI.has(type);
}
var PAYMENT_RESPONSIBLE_TYPES = /* @__PURE__ */ new Set([
  "TRAFFIC_FINE",
  "FUEL"
]);
function paymentAsksResponsible(type) {
  return PAYMENT_RESPONSIBLE_TYPES.has(type);
}
var PAYMENT_STAGE_MODES = APPROVAL_STAGE_MODES;
var PAYMENT_STAGE_MODE_LABELS = {
  ANY_ONE: "Herhangi biri",
  ALL: "Atananlar\u0131n tamam\u0131",
  QUORUM: "Belirli say\u0131da ki\u015Fi"
};
var PAYMENT_STAGE_MODE_HINTS = {
  ANY_ONE: "Masas\u0131nda olan karar verir. \xDC\xE7 finans uzman\u0131 olan bir birim i\xE7in do\u011Frusu bu.",
  ALL: "Atanan her isim tek tek onaylamak zorunda; biri eksikse \xF6deme yetkilendirilmemi\u015F say\u0131l\u0131r. \u0130ki orta\u011F\u0131n ikisinin de onaylamas\u0131 bu.",
  QUORUM: "Atananlardan ka\xE7 ki\u015Finin onay\u0131n\u0131n yetece\u011Fi ayr\u0131ca yaz\u0131l\u0131r."
};
var PAYMENT_APPROVAL_DECISIONS = ["APPROVED", "REJECTED", "RETURNED"];
var PAYMENT_RETURN_FEEDBACKS = ["BUY_MORE", "BUY_LESS", "REVISE"];
var PAYMENT_RETURN_FEEDBACK_LABELS = {
  BUY_MORE: "Daha fazla al\u0131m",
  BUY_LESS: "Daha az al\u0131m",
  REVISE: "Belge veya bilgi d\xFCzeltilmeli"
};
var PAYMENT_APPROVAL_FEEDBACKS = [
  "POSITIVE",
  "NEGATIVE",
  ...PAYMENT_RETURN_FEEDBACKS
];
var PAYMENT_DEFAULT_STAGES = [
  {
    seq: 10,
    code: "FINANS_UZMANI",
    name: "Finans uzman\u0131",
    mode: "ANY_ONE",
    roleCode: "finans_uzmani",
    allowRoleFallback: true
  },
  {
    seq: 20,
    code: "FINANS_YONETICISI",
    name: "Finans y\xF6neticisi",
    mode: "ANY_ONE",
    roleCode: "finans_yoneticisi",
    allowRoleFallback: true
  },
  {
    seq: 30,
    code: "SISTEM_YONETICISI",
    name: "Sistem y\xF6neticisi",
    mode: "ANY_ONE",
    roleCode: "sistem_yoneticisi",
    allowRoleFallback: true
  },
  {
    seq: 40,
    code: "ORTAKLAR",
    name: "Ortaklar",
    mode: "ALL",
    roleCode: "ortak",
    allowRoleFallback: false
  }
];
var paymentApproveSchema = z16.object({
  comment: z16.string().max(2e3).optional()
});
var paymentRejectSchema = z16.object({
  /** Gerekçesiz ret, aynı talebin haftaya aynı hâliyle tekrar girmesidir. */
  comment: z16.string().min(5).max(2e3)
});
var paymentReturnSchema = z16.object({
  feedback: z16.enum(PAYMENT_RETURN_FEEDBACKS),
  comment: z16.string().min(5).max(2e3)
});
var paymentCancelSchema = z16.object({
  comment: z16.string().min(5).max(2e3)
});
var paymentResubmitSchema = z16.object({
  /**
   * Tutar burada da metin olarak gelir; sebebi talep açmadakiyle aynı.
   *
   * Dönen bir talebi düzeltmenin en sık sebebi zaten tutarın yanlış olması, ve
   * düzeltmeyi yapan ekranın eski bir sürümü olması ihtimali burada daha da
   * yüksek — talep dönene kadar sekme açık kalmış olabilir.
   */
  amountText: z16.string().min(1).max(32).optional(),
  amountFormat: z16.enum(["tr", "en"]).optional(),
  amount: z16.number().min(0.01).max(1e9).optional(),
  documentNo: z16.string().min(1).max(100).optional(),
  documentDate: z16.iso.date().optional(),
  reason: z16.string().min(5).max(1e3).optional(),
  /**
   * Bankaya yazılacak açıklama da düzeltilebilmeli.
   *
   * Talep açarken zorunlu, dönen talebi düzeltirken hiç yoktu. Oysa geri
   * gönderilen bir talebin en sık sebebi tutarın yanlış olması ve bu metin
   * çoğu zaman tutarı ya da fatura numarasını içeriyor: tutar düzelip metin
   * eski kalırsa, bankaya gerçekte ödenenden başka bir şey anlatan bir
   * açıklama gider. Talep açmadaki kuralla aynı sınırlar — 140, kolonun ve
   * transfer formunun genişliği.
   */
  bankDescription: z16.string().min(3).max(140).optional(),
  /** DST-00087 — boş dize alanı temizler; türü sormuyorsa sunucu reddeder. */
  invoiceContent: z16.string().trim().max(FATURA_ICERIGI_AZAMI).optional(),
  comment: z16.string().max(2e3).optional()
});
var stageBase = {
  code: z16.string().min(2).max(40).regex(/^[A-Z0-9_]+$/, "Kap\u0131 kodu yaln\u0131zca b\xFCy\xFCk harf, rakam ve alt \xE7izgi i\xE7erebilir."),
  name: z16.string().min(2).max(120),
  seq: z16.number().int().min(1).max(999),
  mode: z16.enum(PAYMENT_STAGE_MODES),
  quorum: z16.number().int().min(1).max(50).nullable().optional(),
  roleCode: z16.string().max(60).nullable().optional(),
  allowRoleFallback: z16.boolean().optional(),
  /**
   * Kapının BİRİMİ — «bu kapı yalnız şu birimin (ve altlarının) kayıtlarında
   * işler». NULL: her kayda uygulanır, bugünkü bütün kapılar böyle.
   *
   * `nullable().optional()` ikisi ayrı ayrı: alan hiç gönderilmediyse birim
   * olduğu gibi kalır, açıkça `null` gönderildiyse kaldırılır. Karıştırmak,
   * kapının adını değiştiren bir isteğin birimi de silmesi demekti — ve o
   * silme, kapıyı sessizce bütün kayıtlara açardı.
   *
   * KOLTUĞUN birimiyle aynı şey DEĞİL: koltuk «bu koltuk burada geçerli» der
   * ve uymazsa kapı onaycısız kalır; bu alan «kapı bu kaydın zincirinde yok»
   * der.
   */
  orgUnitId: z16.uuid().nullable().optional(),
  /**
   * Kapının devreye girdiği tutar. NULL — her tutarda devrede.
   *
   * `nullable().optional()` ikisi ayrı ayrı gerekiyor ve fark KRİTİK: alan hiç
   * gönderilmediyse eşik olduğu gibi kalır, açıkça `null` gönderildiyse
   * kaldırılır. Kısmi bir PATCH'te ikisini birbirine karıştırmak, sıra
   * numarasını değiştiren bir isteğin eşiği de silmesi demek.
   *
   * `coerce`: zincir ekranı eşiği metin olarak tutuyor (Türkçe biçim,
   * `MoneyInput`) ve sayıya çevirip gönderiyor; `null` coerce'e hiç uğramıyor,
   * çünkü `nullable` sarmalayıcısı ondan önce dönüyor — yoksa `Number(null)`
   * eşiği sessizce 0 yapardı.
   *
   * Kolon `Decimal(18,2)`: kuruştan küçük eşik yok. Yuvarlanıp kabul edilmek
   * yerine reddediliyor — sessizce değiştirilen bir eşiğe yazan kişi bir daha
   * bakmaz.
   */
  minAmount: z16.coerce.number().min(0).max(1e9).multipleOf(0.01, "Tutar e\u015Fi\u011Fi en fazla iki ondal\u0131k basamak ta\u015F\u0131yabilir.").nullable().optional()
};
var paymentStageCreateSchema = z16.object({
  /** NULL/atlanmış: grup genelindeki zincire yazılır. */
  companyId: z16.uuid().nullable().optional(),
  /**
   * Hangi zincire — ödeme talebi mi, fatura mı.
   *
   * Atlanırsa ödeme. Varsayılan olması şart: bu uç zincir ekranından bu alan
   * yokken de çağrılıyordu ve atlanan bir alanın sessizce fatura zincirine
   * yazması, kimsenin kurmadığı bir kapı demek olurdu.
   */
  domain: z16.enum(APPROVAL_DOMAINS).optional(),
  ...stageBase
});
var paymentStageUpdateSchema = z16.object({
  seq: stageBase.seq.optional(),
  name: stageBase.name.optional(),
  mode: stageBase.mode.optional(),
  quorum: stageBase.quorum,
  roleCode: stageBase.roleCode,
  orgUnitId: stageBase.orgUnitId,
  allowRoleFallback: stageBase.allowRoleFallback,
  // Üç hâl: yok (eşiğe dokunma), null (eşiği kaldır), sayı (eşiği kur).
  minAmount: stageBase.minAmount,
  isActive: z16.boolean().optional()
}).refine((v) => Object.keys(v).length > 0, { message: "De\u011Fi\u015Ftirilecek bir alan verilmedi." });
var paymentAssigneeCreateSchema = z16.object({
  userId: z16.uuid(),
  /**
   * Koltuk HANGİ şirketin talebinde geçerli. NULL/atlanmış — hepsinde.
   *
   * Bu alan olmadığı için şikâyet doğdu: ekranda şirket seçilip kişi
   * ekleniyordu ama isteğin gövdesi düpedüz `{ userId }` idi, yani şirket
   * kutusu yalnız bir GÖRÜNTÜ seçicisiydi. Grup kapısına yazılan isim, o
   * zinciri devralan bütün şirketlerde karar veriyordu.
   *
   * Atlanmış hâli bilerek «hepsi»: bugünkü koltukların anlamı bu ve göç
   * hiçbir davranışı değiştirmiyor. Daraltma bir SEÇİM olmalı, sessiz bir
   * varsayılan değil.
   */
  companyId: z16.uuid().nullable().optional(),
  /**
   * Koltuk hangi DEPARTMANDA geçerli. NULL/atlanmış — şirketin tamamında.
   * ALT BİRİMLER DÂHİL: birim ağacında bu birimin altındaki her departman.
   *
   * Şirketsiz gönderilemez — `chk_pay_assignee_scope` de reddediyor, ama
   * sebebini Türkçe söylemek sunucunun işi: birim ağacı şirket başına ayrı ve
   * iki şirkette aynı adlı departman var, dolayısıyla şirketsiz bir departman
   * kapsamı hangi ağaçta aranacağı belli olmayan bir kapsamdır.
   */
  orgUnitId: z16.uuid().nullable().optional()
});
var paymentAssigneeUpdateSchema = z16.object({
  delegateUserId: z16.uuid().nullable(),
  delegateUntil: z16.iso.datetime().nullable().optional()
}).refine((v) => v.delegateUserId === null || Boolean(v.delegateUntil), {
  message: "Vek\xE2let verilirken biti\u015F tarihi zorunlu.",
  path: ["delegateUntil"]
});
var paymentCommentSchema = z16.object({
  body: z16.string().trim().min(2).max(2e3),
  /**
   * The approvers' own note; the requester never sees it and is never told it
   * exists. Set by anyone without approval-side visibility, it is silently
   * downgraded to a normal message rather than refused — a note its writer
   * cannot see is a note they will sit waiting for an answer to.
   */
  internal: z16.boolean().optional()
});
var PAYEE_KINDS = ["ALICI", "SATICI", "HER_IKISI"];
var PAYEE_KIND_LABELS = {
  ALICI: "Al\u0131c\u0131",
  SATICI: "Sat\u0131c\u0131",
  HER_IKISI: "Al\u0131c\u0131 ve sat\u0131c\u0131"
};
var PAYEE_CODE_SEGMENTS = [
  { prefix: "120.01", kind: "ALICI", label: "\xD6\u011Frenci hesaplar\u0131" },
  { prefix: "120.02", kind: "ALICI", label: "Yurt d\u0131\u015F\u0131 al\u0131c\u0131lar" },
  { prefix: "120.03", kind: "ALICI", label: "Yurt i\xE7i al\u0131c\u0131lar" },
  { prefix: "320.01", kind: "SATICI", label: "Yurt i\xE7i sat\u0131c\u0131lar" },
  { prefix: "320.02", kind: "SATICI", label: "Yurt d\u0131\u015F\u0131 sat\u0131c\u0131lar" },
  /**
   * İş avansları (DST-00093). «195 muhasebede iş avansları olarak takip
   * ettiğimiz hesap türü.» Personele avans ödeniyor, masraf formuyla kapanıyor:
   * para iki yönde akıyor, o yüzden «Alıcı ve satıcı». Zirve'deki kırılım
   * `195.01.001` gibi; grup yalnız `195`.
   */
  { prefix: "195", kind: "HER_IKISI", label: "\u0130\u015F avanslar\u0131" }
];
function payeeSegmentFromCode(code) {
  if (!code) return null;
  const temiz = code.trim();
  return PAYEE_CODE_SEGMENTS.find((s) => temiz.startsWith(s.prefix)) ?? null;
}
function payeeKindFromCode(code) {
  return payeeSegmentFromCode(code)?.kind ?? null;
}
var createPayeeAccountSchema = z16.object({
  iban: z16.string().min(15).max(40),
  label: z16.string().trim().max(120).optional(),
  currency: z16.string().length(3).default("TRY"),
  bankName: z16.string().trim().max(160).optional(),
  swiftCode: z16.string().trim().max(20).optional(),
  accountHolder: z16.string().trim().max(200).optional(),
  /** İlk hesap zaten birincil olur; bu bayrak sonrakileri öne almak için. */
  isPrimary: z16.boolean().optional()
});
var updatePayeeAccountSchema = z16.object({
  label: z16.string().trim().max(120).nullish(),
  currency: z16.string().length(3).optional(),
  bankName: z16.string().trim().max(160).nullish(),
  swiftCode: z16.string().trim().max(20).nullish(),
  accountHolder: z16.string().trim().max(200).nullish(),
  isPrimary: z16.boolean().optional(),
  isActive: z16.boolean().optional()
});
var VAT_RATES = [0, 1, 10, 20];
function vatFromGross(gross, rate) {
  if (!Number.isFinite(gross) || !Number.isFinite(rate) || rate < 0) {
    return { vat: 0, net: gross };
  }
  const net = gross / (1 + rate / 100);
  const vat = Math.round((gross - net) * 100) / 100;
  return { vat, net: Math.round((gross - vat) * 100) / 100 };
}
var PAYMENT_DATE_BASES = ["REQUEST", "PAYMENT", "DOCUMENT"];
var PAYMENT_DATE_BASIS_LABELS = {
  REQUEST: "Talep tarihi",
  PAYMENT: "\xD6deme tarihi",
  DOCUMENT: "Belge tarihi"
};
var PAYMENT_DATE_BASIS_HINTS = {
  REQUEST: "Talebin sisteme girildi\u011Fi an. Her talepte dolu, hi\xE7bir sat\u0131r elenmez.",
  PAYMENT: "Paran\u0131n \xE7\u0131kt\u0131\u011F\u0131 an. Yaln\u0131zca \xF6denmi\u015F talepler listelenir \u2014 \xF6denmemi\u015Flerin bu tarihi yoktur.",
  DOCUMENT: "Faturan\u0131n/proforman\u0131n \xFCst\xFCndeki tarih. Girilmemi\u015F olabilir; girilmemi\u015F olanlar listeye girmez."
};
var PAYMENT_DATE_BASIS_NULLABLE = {
  REQUEST: false,
  PAYMENT: true,
  DOCUMENT: true
};
var paymentDateRangeSchema = z16.object({
  from: z16.iso.date().optional(),
  to: z16.iso.date().optional(),
  basis: z16.enum(PAYMENT_DATE_BASES).default("REQUEST")
}).refine((v) => !v.from || !v.to || v.from <= v.to, {
  message: "Ba\u015Flang\u0131\xE7 tarihi biti\u015Ften sonra olamaz.",
  path: ["to"]
});

// src/dto/iban-dogrulama.ts
import { z as z17 } from "zod";
var IBAN_DOGRULAMA_ETIKETLERI = {
  DOGRULANDI: "Do\u011Fruland\u0131",
  // «Doğrulanmamış» gibi okunmasın diye cümlenin ikinci yarısı şart.
  GECIS_ONCESI: "Ge\xE7i\u015F \xF6ncesi \u2014 do\u011Frulanm\u0131\u015F say\u0131l\u0131r",
  BEKLIYOR: "Do\u011Frulama bekliyor"
};
function ibanDogrulamaDurumu(r) {
  if (r.verifiedAt === null) return "BEKLIYOR";
  return r.verifiedByUserId === null ? "GECIS_ONCESI" : "DOGRULANDI";
}
function ibanDogrulayabilirMi(r, kisiId, yetkili) {
  if (!yetkili) return { izin: false, neden: "IBAN do\u011Frulama yetkiniz yok." };
  if (r.verifiedAt !== null) return { izin: false, neden: "Bu IBAN zaten do\u011Frulanm\u0131\u015F." };
  if (r.pendingChangers.includes(kisiId)) {
    return {
      izin: false,
      neden: "Bu IBAN\u2019\u0131 siz girdiniz ya da de\u011Fi\u015Ftirdiniz; ikinci bir yetkili do\u011Frulamal\u0131 \u2014 \xE7ift onay\u0131n anlam\u0131 bu."
    };
  }
  return { izin: true };
}
var verifyIbanSchema = z17.object({
  changedAt: z17.iso.datetime({ offset: true }).nullable()
});
function odemeHedefiKarari(o) {
  if (o.paymentMethod !== "TRANSFER" || !o.ibanVar) return null;
  const kimDegistirdi = (ad) => ad ? `${ad} girdi ya da de\u011Fi\u015Ftirdi; ` : "";
  if (o.hesap) {
    const h = o.hesap;
    if (!h.bulundu)
      return { dogrulandi: false, neden: "Talebin ba\u011Fl\u0131 oldu\u011Fu banka hesab\u0131 bulunamad\u0131." };
    if (!h.cariEslesiyor) {
      return {
        dogrulandi: false,
        neden: "Talebin banka hesab\u0131 ba\u015Fka bir cariye ait; bu talep o hesaba \xF6denemez."
      };
    }
    if (!h.cariAktif)
      return { dogrulandi: false, neden: "Talebin carisi pasife al\u0131nm\u0131\u015F; \xF6denemez." };
    if (!h.hesapAktif) {
      return { dogrulandi: false, neden: "Talebin banka hesab\u0131 pasife al\u0131nm\u0131\u015F; \xF6denemez." };
    }
    if (!h.ibanEsit) {
      return {
        dogrulandi: false,
        neden: "Talepteki IBAN cari kayd\u0131ndaki hesapla ayn\u0131 de\u011Fil; talep do\u011Frulanm\u0131\u015F bir hesaba ba\u011Fl\u0131 de\u011Fil."
      };
    }
    if (!h.dogrulandi) {
      return {
        dogrulandi: false,
        neden: `IBAN do\u011Frulanmad\u0131: ${kimDegistirdi(h.degistirenAd)}ba\u015Fka bir yetkili cari kayd\u0131ndan do\u011Frulamadan \xF6denemez.`
      };
    }
    if (!h.swiftUyumlu && !o.gecisOncesi) {
      return {
        dogrulandi: false,
        neden: "Talepteki SWIFT hesaptakiyle ayn\u0131 de\u011Fil; hesaptaki SWIFT do\u011Frulanmadan \xF6denemez."
      };
    }
    return { dogrulandi: true };
  }
  if (o.sozlesme) {
    const s = o.sozlesme;
    if (!s.ibanEsit) {
      return {
        dogrulandi: false,
        neden: "Talepteki IBAN s\xF6zle\u015Fmedeki IBAN\u2019la ayn\u0131 de\u011Fil; talep do\u011Frulanm\u0131\u015F bir IBAN ta\u015F\u0131m\u0131yor."
      };
    }
    if (!s.dogrulandi) {
      return {
        dogrulandi: false,
        neden: `S\xF6zle\u015Fmenin IBAN\u2019\u0131 do\u011Frulanmad\u0131: ${kimDegistirdi(s.degistirenAd)}ba\u015Fka bir yetkili s\xF6zle\u015Fmeden do\u011Frulamadan \xF6denemez.`
      };
    }
    if (!s.swiftUyumlu) {
      return {
        dogrulandi: false,
        neden: "Talepteki SWIFT s\xF6zle\u015Fmedekiyle ayn\u0131 de\u011Fil; \xF6denemez."
      };
    }
    return { dogrulandi: true };
  }
  if (o.gecisOncesi) return { dogrulandi: true };
  return {
    dogrulandi: false,
    neden: "Bu talebin IBAN\u2019\u0131 cari kayd\u0131ndaki do\u011Frulanm\u0131\u015F bir hesaptan gelmiyor; \xF6denemez. Talebi iptal edip cari ve hesap se\xE7erek yeniden a\xE7\u0131n."
  };
}
function swiftUyumlu(talep, kaynak) {
  const t = (talep ?? "").replace(/\s+/g, "").toUpperCase();
  if (t === "") return true;
  const k = (kaynak ?? "").replace(/\s+/g, "").toUpperCase();
  return t === k;
}

// src/dto/belge-no.ts
import { z as z18 } from "zod";
function belgeNoNormalize(value) {
  return value.replace(/\s+/g, "").toUpperCase();
}
function belgeNolariniAyir(value) {
  if (!value) return [];
  const gorulen = /* @__PURE__ */ new Set();
  for (const parca of value.split(/[,;\n]+/)) {
    const no = belgeNoNormalize(parca);
    if (no) gorulen.add(no);
  }
  return [...gorulen];
}
var GIB_BELGE_NO_DESENI = /^[A-Z0-9]{3}20\d{2}\d{9}$/;
function gibBicimindeMi(no) {
  return GIB_BELGE_NO_DESENI.test(belgeNoNormalize(no));
}
var FATURA_BELGE_TURLERI = /* @__PURE__ */ new Set([
  "E_INVOICE",
  "E_ARCHIVE"
]);
function odemeBelgeSinifi(type) {
  if (FATURA_BELGE_TURLERI.has(type)) return "FATURA";
  return paymentDocumentNoRequired(type) ? "NUMARALI" : "NUMARASIZ";
}
var TEKERRUR_PENCERE_GUN = 30;
var TEKERRUR_NEDENLERI = ["AYNI_NUMARA", "AYNI_TUTAR", "AYNI_DOSYA"];
var TEKERRUR_NEDEN_ETIKETLERI = {
  AYNI_NUMARA: "Ayn\u0131 belge numaras\u0131",
  AYNI_TUTAR: `Ayn\u0131 cari, ayn\u0131 tutar (son ${TEKERRUR_PENCERE_GUN} g\xFCn)`,
  AYNI_DOSYA: "Ayn\u0131 dosya ba\u015Fka bir faturada"
};
var tekerrurGerekcesiSchema = z18.string().trim().min(10, "Gerek\xE7e en az 10 karakter olmal\u0131: neden tekerr\xFCr olmad\u0131\u011F\u0131n\u0131 yaz\u0131n.").max(500);

// src/dto/staff-request.ts
import { z as z19 } from "zod";
var STAFF_REQUEST_STATUSES = [
  "PENDING_APPROVAL",
  "APPROVED",
  "PURCHASED",
  "REJECTED",
  "CANCELLED"
];
var STAFF_REQUEST_STATUS_LABELS = {
  PENDING_APPROVAL: "Onay bekliyor",
  APPROVED: "Onayland\u0131 \u2014 sat\u0131n al\u0131nacak",
  PURCHASED: "Sat\u0131n al\u0131nd\u0131",
  REJECTED: "Reddedildi",
  CANCELLED: "\u0130ptal edildi"
};
var STAFF_REQUEST_CATEGORIES = [
  "MALZEME",
  "DONANIM",
  "YAZILIM",
  "HIZMET",
  "BAKIM",
  "SEYAHAT",
  "DIGER"
];
var STAFF_REQUEST_CATEGORY_LABELS = {
  MALZEME: "Sarf malzeme",
  DONANIM: "Donan\u0131m",
  YAZILIM: "Yaz\u0131l\u0131m / abonelik",
  HIZMET: "Hizmet al\u0131m\u0131",
  BAKIM: "Bak\u0131m / onar\u0131m",
  SEYAHAT: "Seyahat",
  DIGER: "Di\u011Fer"
};
var STAFF_REQUEST_URGENCIES = ["NORMAL", "URGENT"];
var STAFF_REQUEST_URGENCY_LABELS = {
  NORMAL: "Normal",
  URGENT: "Acil"
};
var STAFF_REQUEST_EVENT_LABELS = {
  SUBMITTED: "Talep a\xE7\u0131ld\u0131",
  APPROVED: "Kap\u0131 onaylad\u0131",
  CHAIN_COMPLETED: "Onay zinciri tamamland\u0131",
  REJECTED: "Reddedildi",
  CANCELLED: "\u0130ptal edildi",
  PURCHASED: "Sat\u0131n al\u0131nd\u0131, \xF6deme talebi a\xE7\u0131ld\u0131",
  RESUBMITTED: "D\xFCzeltilip yeniden sunuldu",
  ATTACHED: "Belge eklendi"
};
var STAFF_REQUEST_LIST_LIMIT = 300;
var estimated = moneyText({ min: 0.01, max: 99999999999 });
var staffRequestCreateSchema = z19.object({
  companyId: z19.uuid(),
  title: z19.string().trim().min(3).max(200),
  /**
   * Neden gerektiği. Zorunlu ve kısa değil: onaylayan kişi talebi açanla aynı
   * odada değil, ve "toner" tek başına onaylanabilir bir cümle kurmuyor.
   */
  description: z19.string().trim().min(10).max(2e3),
  category: z19.enum(STAFF_REQUEST_CATEGORIES),
  urgency: z19.enum(STAFF_REQUEST_URGENCIES).default("NORMAL"),
  quantity: z19.coerce.number().min(0.01).max(1e6).optional(),
  unit: z19.string().trim().max(20).optional(),
  estimatedAmountText: estimated.optional(),
  currency: z19.string().length(3).default("TRY"),
  /** Ne zamana lazım. Aciliyetin doğrulanabilir hâli. */
  neededBy: z19.iso.date().optional(),
  orgUnitId: z19.uuid().optional(),
  costCenterId: z19.uuid().optional()
});
var staffRequestApproveSchema = z19.object({
  comment: z19.string().trim().max(1e3).optional()
});
var staffRequestRejectSchema = z19.object({
  /**
   * Zorunlu. Reddedilen bir talebin gerekçesiz kalması, aynı talebin bir hafta
   * sonra aynı hâliyle yeniden açılması demek.
   */
  reason: z19.string().trim().min(3).max(1e3)
});
var staffRequestCancelSchema = z19.object({
  reason: z19.string().trim().max(1e3).optional()
});
var staffRequestResubmitSchema = z19.object({
  title: z19.string().trim().min(3).max(200).optional(),
  description: z19.string().trim().min(10).max(2e3).optional(),
  quantity: z19.coerce.number().min(0.01).max(1e6).nullable().optional(),
  unit: z19.string().trim().max(20).nullable().optional(),
  estimatedAmountText: moneyText({ min: 0.01, max: 99999999999 }).nullable().optional(),
  urgency: z19.enum(STAFF_REQUEST_URGENCIES).optional(),
  neededBy: z19.iso.date().nullable().optional(),
  /** Onaycıya not: "fiyatı düşürdüm", "teklifi ekledim". */
  note: z19.string().trim().max(1e3).optional()
});

// src/dto/fixed-expense.ts
import { z as z20 } from "zod";
var FIXED_EXPENSE_KINDS = [
  "RENT",
  "VEHICLE",
  "LOAN",
  "LEASING",
  "UTILITY",
  "TELECOM",
  "INSURANCE",
  "SUBSCRIPTION",
  "DUES",
  "TAX",
  "SERVICE",
  "OTHER"
];
var FIXED_EXPENSE_KIND_LABELS = {
  RENT: "Kira",
  VEHICLE: "Ara\xE7 kiralama",
  LOAN: "Kredi",
  LEASING: "Finansal kiralama (leasing)",
  UTILITY: "Elektrik, su, do\u011Falgaz",
  TELECOM: "Telefon ve internet",
  INSURANCE: "Sigorta",
  SUBSCRIPTION: "Abonelik",
  DUES: "Aidat",
  TAX: "Vergi ve har\xE7",
  SERVICE: "Hizmet s\xF6zle\u015Fmesi",
  OTHER: "Di\u011Fer"
};
var FIXED_EXPENSE_AMOUNT_BASES = ["FIXED", "VARIABLE", "AMORTISED"];
var FIXED_EXPENSE_AMOUNT_BASIS_LABELS = {
  FIXED: "Sabit tutar",
  VARIABLE: "Faturayla belli olur",
  AMORTISED: "Kredi taksit plan\u0131"
};
var FIXED_EXPENSE_STATUSES = [
  "DRAFT",
  "ACTIVE",
  "SUSPENDED",
  "ENDED",
  "CANCELLED"
];
var FIXED_EXPENSE_STATUS_LABELS = {
  DRAFT: "Taslak",
  ACTIVE: "Y\xFCr\xFCrl\xFCkte",
  SUSPENDED: "Ask\u0131da",
  ENDED: "S\xFCresi doldu",
  CANCELLED: "Feshedildi"
};
var FIXED_EXPENSE_ESCALATION_BASES = ["NONE", "TUFE", "FIXED_PERCENT", "MANUAL"];
var FIXED_EXPENSE_ESCALATION_BASIS_LABELS = {
  NONE: "Art\u0131\u015F yok",
  TUFE: "T\xDCFE oran\u0131nda",
  FIXED_PERCENT: "Sabit y\xFCzde",
  MANUAL: "Elle belirlenir"
};
var FIXED_EXPENSE_OCCURRENCE_STATUSES = [
  "PLANNED",
  "AWAITING_INVOICE",
  "READY",
  "PAYMENT_REQUESTED",
  "PAID",
  "SKIPPED",
  "CANCELLED"
];
var FIXED_EXPENSE_OCCURRENCE_STATUS_LABELS = {
  PLANNED: "Planland\u0131",
  AWAITING_INVOICE: "Fatura bekleniyor",
  READY: "\xD6demeye haz\u0131r",
  PAYMENT_REQUESTED: "\xD6deme talebi a\xE7\u0131ld\u0131",
  PAID: "\xD6dendi",
  SKIPPED: "Atland\u0131",
  CANCELLED: "\u0130ptal"
};
var FIXED_EXPENSE_OPEN_STATUSES = [
  "PLANNED",
  "AWAITING_INVOICE",
  "READY",
  "PAYMENT_REQUESTED"
];
var FIXED_EXPENSE_ESCALATION_STATUSES = ["PROPOSED", "APPLIED", "REJECTED"];
var FIXED_EXPENSE_ESCALATION_STATUS_LABELS = {
  PROPOSED: "Onay bekliyor",
  APPLIED: "Uyguland\u0131",
  REJECTED: "Reddedildi"
};
var FIXED_EXPENSE_LOAN_RATE_TYPES = ["FIXED", "FLOATING"];
var FIXED_EXPENSE_LOAN_RATE_TYPE_LABELS = {
  FIXED: "Sabit faiz",
  FLOATING: "De\u011Fi\u015Fken faiz"
};
var FIXED_EXPENSE_ATTACHMENT_KINDS = ["CONTRACT", "INVOICE", "RECEIPT", "OTHER"];
var FIXED_EXPENSE_ATTACHMENT_KIND_LABELS = {
  CONTRACT: "S\xF6zle\u015Fme",
  INVOICE: "Fatura",
  RECEIPT: "Dekont",
  OTHER: "Di\u011Fer"
};
var FIXED_EXPENSE_INTERVALS = [1, 2, 3, 4, 6, 12];
var FIXED_EXPENSE_INTERVAL_LABELS = {
  1: "Ayl\u0131k",
  2: "\u0130ki ayda bir",
  3: "\xDC\xE7 ayda bir",
  4: "D\xF6rt ayda bir",
  6: "Alt\u0131 ayda bir",
  12: "Y\u0131ll\u0131k"
};
var isoDate4 = z20.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Tarih YYYY-AA-GG bi\xE7iminde olmal\u0131." });
var money2 = moneyText({ min: 0, max: 99999999999 });
var positiveMoney2 = moneyText({ min: 0.01, max: 99999999999 });
var percent = z20.number().min(0).max(100).refine((n) => Number.isInteger(Math.round(n * 100)), {
  message: "Oran en fazla iki ondal\u0131k haneli olabilir."
});
var fixedExpenseCommitmentFields = z20.object({
  companyId: z20.uuid(),
  code: z20.string().trim().min(2).max(30),
  name: z20.string().trim().min(2).max(200),
  kind: z20.enum(FIXED_EXPENSE_KINDS),
  costCenterId: z20.uuid(),
  categoryId: z20.uuid(),
  orgUnitId: z20.uuid().optional(),
  payeeName: z20.string().trim().min(2).max(200),
  payeeTaxNumber: z20.string().trim().max(20).optional(),
  payeeContact: z20.string().trim().max(200).optional(),
  payeePhone: z20.string().trim().max(40).optional(),
  /**
   * Para nasıl çıkıyor — ve dolayısıyla IBAN'ın sorulup sorulmayacağı.
   *
   * Bir kullanıcının sözü: "Vergi ödemeleri vergi dairesinin ibanına gerek
   * olmadan yapıldığı için vergi ödemeleri seçildiğinde ibanı zorunlu kılmadan
   * yürürlüğe alabilir miyiz." Doğru gözlem, ama kural VERGİYE değil ÖDEME
   * YOLUNA ait: karşı tarafın IBAN'ı yoksa bunun sebebi verginin vergi olması
   * değil, paranın bankanın tahsilat ekranından karttan çıkmasıdır. Şirket
   * kartıyla ödenen bir abonelik de aynı durumda.
   *
   * Bu yüzden tür (`kind`) üzerinden dallanmıyoruz. `kind` bu modülde bilerek
   * yalnızca raporlama alanı — davranışı `amountBasis` ve tekrar alanları
   * belirliyor — ve "vergiyse IBAN sorma" demek, havaleyle vergi ödeyen bir
   * şirketin söyleyecek sözünü elinden alırdı.
   *
   * Katalog ödeme modülünün kendi kataloğu, kopyası değil: `pay_request` aynı
   * ayrımı `chk_pay_iban_matches_method` ile veritabanında zaten yapıyor ve
   * dönemin talebi oraya gidiyor.
   */
  paymentMethod: z20.enum(PAYMENT_METHODS).default("TRANSFER"),
  /**
   * Yürürlüğe alınırken zorunlu, taslakta değil. Kira sözleşmesi imzalanır,
   * IBAN bir hafta sonra gelir — sistemin bunu beklemek için bir hâli olmalı.
   *
   * Kartla ödenen sözleşmede hiç sorulmuyor: olmayan bir numara.
   */
  iban: z20.string().trim().min(15).max(34).optional(),
  swiftCode: z20.string().trim().max(20).optional(),
  bankName: z20.string().trim().max(160).optional(),
  accountHolder: z20.string().trim().max(200).optional(),
  contractNumber: z20.string().trim().min(1).max(100),
  startDate: isoDate4,
  endDate: isoDate4.optional(),
  noticePeriodDays: z20.coerce.number().int().min(0).max(365).default(0),
  autoRenew: z20.boolean().default(false),
  renewalAlertDays: z20.coerce.number().int().min(0).max(365).default(60),
  amountBasis: z20.enum(FIXED_EXPENSE_AMOUNT_BASES).default("FIXED"),
  baseAmount: money2,
  currency: z20.string().trim().length(3).default("TRY"),
  vatRate: percent.default(20),
  /** "12.000 TL + KDV" ile "12.000 TL KDV dahil" farklı paralardır. */
  amountIncludesVat: z20.boolean().default(false),
  /** Gerçek kişiye ödenen kirada %20. Kesilen vergi de şirketin gideridir. */
  withholdingRate: percent.default(0),
  intervalMonths: z20.literal(FIXED_EXPENSE_INTERVALS).default(1),
  dueDayOfMonth: z20.coerce.number().int().min(1).max(31).default(1),
  dueOnLastDay: z20.boolean().default(false),
  /**
   * Dönem bitince ödenir: kasım kirası 1 aralıkta (DST-00108). Dönem hizmetin
   * ayı olur, vade bir periyot sonraya düşer.
   */
  paidInArrears: z20.boolean().default(false),
  autoFileDaysBefore: z20.coerce.number().int().min(0).max(60).default(7),
  treasuryAccountId: z20.uuid().optional(),
  escalationBasis: z20.enum(FIXED_EXPENSE_ESCALATION_BASES).default("NONE"),
  escalationPercent: percent.optional(),
  escalationIntervalMonths: z20.coerce.number().int().min(1).max(60).default(12),
  escalationCapPercent: percent.optional(),
  varianceThresholdPercent: percent.default(30),
  responsibleUserId: z20.uuid().optional(),
  note: z20.string().trim().max(4e3).optional()
});
var createFixedExpenseCommitmentSchema = fixedExpenseCommitmentFields.refine((v) => v.endDate === void 0 || v.endDate >= v.startDate, {
  message: "Biti\u015F tarihi ba\u015Flang\u0131\xE7tan \xF6nce olamaz.",
  path: ["endDate"]
}).refine((v) => paymentMethodNeedsIban(v.paymentMethod) || v.iban === void 0, {
  message: "Kredi kart\u0131yla \xF6denen s\xF6zle\u015Fmede IBAN girilmez.",
  path: ["iban"]
}).refine((v) => v.escalationBasis !== "FIXED_PERCENT" || v.escalationPercent !== void 0, {
  message: "Sabit y\xFCzdeli art\u0131\u015Fta oran zorunlu.",
  path: ["escalationPercent"]
}).refine((v) => v.amountBasis !== "AMORTISED" || v.intervalMonths === 1, {
  message: "Kredi taksitleri ayl\u0131kt\u0131r; farkl\u0131 bir periyot taksit plan\u0131yla \xE7eli\u015Fir.",
  path: ["intervalMonths"]
}).refine((v) => v.amountBasis !== "AMORTISED" || !v.paidInArrears, {
  message: "Kredinin takvimi bankan\u0131n taksit tablosundan gelir; geriden \xF6deme se\xE7ilemez.",
  path: ["paidInArrears"]
});
var updateFixedExpenseCommitmentSchema = patchSchema(fixedExpenseCommitmentFields).omit({ companyId: true, amountBasis: true }).partial().extend({ version: z20.coerce.number().int().min(1) }).refine((v) => v.startDate === void 0 || v.endDate === void 0 || v.endDate >= v.startDate, {
  message: "Biti\u015F tarihi ba\u015Flang\u0131\xE7tan \xF6nce olamaz.",
  path: ["endDate"]
});
var changeFixedExpenseStatusSchema = z20.object({
  status: z20.enum(["ACTIVE", "SUSPENDED", "ENDED", "CANCELLED"]),
  /** Zorunlu. Gerekçesiz durdurulmuş bir sözleşme, unutulmuş olandan ayırt edilemez. */
  reason: z20.string().trim().min(5).max(300),
  version: z20.coerce.number().int().min(1)
});
var fixedExpenseCommitmentListQuerySchema = sortableQuerySchema.extend({
  companyId: z20.uuid().optional(),
  kind: z20.enum(FIXED_EXPENSE_KINDS).optional(),
  status: z20.enum(FIXED_EXPENSE_STATUSES).optional(),
  costCenterId: z20.uuid().optional(),
  categoryId: z20.uuid().optional(),
  /** Yenileme takvimi: bitişine bu kadar gün kalanlar. */
  endingWithinDays: z20.coerce.number().int().min(1).max(365).optional()
});
var FIXED_EXPENSE_COMMITMENT_SORT_FIELDS = [
  "code",
  "name",
  "startDate",
  "endDate",
  "baseAmount",
  "createdAt"
];
var setFixedExpenseLoanSchema = z20.object({
  principal: positiveMoney2,
  currency: z20.string().trim().length(3).default("TRY"),
  /** Yıllık nominal oran, yüzde: 43.5 → yıllık %43,5. */
  annualInterestRate: z20.number().min(0).max(500),
  rateType: z20.enum(FIXED_EXPENSE_LOAN_RATE_TYPES).default("FIXED"),
  termMonths: z20.coerce.number().int().min(1).max(480),
  drawdownOn: isoDate4,
  firstInstalmentOn: isoDate4,
  /** Ticari nakit kredide %15 ve %5; leasingde sıfır. Faiz üzerinden alınır. */
  kkdfRate: percent.default(0),
  bsmvRate: percent.default(0),
  /** Leasing kalıntı değeri veya balon ödeme. Ödünç anapara planına eklenir. */
  balloonAmount: money2.default(0)
});
var generateFixedExpenseOccurrencesSchema = z20.object({
  commitmentId: z20.uuid().optional(),
  companyId: z20.uuid().optional(),
  horizonMonths: z20.coerce.number().int().min(1).max(36).default(12)
});
var recordFixedExpenseInvoiceSchema = z20.object({
  actualAmount: money2,
  invoiceNo: z20.string().trim().max(100).optional(),
  invoiceDate: isoDate4.optional(),
  /** Beklenenden büyük sapmada zorunlu; hangi eşiği aştığını servis söyler. */
  note: z20.string().trim().max(300).optional(),
  version: z20.coerce.number().int().min(1)
});
var skipFixedExpenseOccurrenceSchema = z20.object({
  reason: z20.string().trim().min(5).max(300),
  version: z20.coerce.number().int().min(1)
});
var rescheduleFixedExpenseOccurrenceSchema = z20.object({
  dueDate: isoDate4,
  reason: z20.string().trim().min(5).max(300),
  version: z20.coerce.number().int().min(1)
});
var setFixedExpenseAttachmentValiditySchema = z20.object({
  invalid: z20.boolean(),
  reason: z20.string().trim().max(300).optional()
});
var markFixedExpensePaidSchema = z20.object({
  paidOn: isoDate4,
  paidAmount: positiveMoney2,
  /** Verilirse kasa/banka hareketi de yazılır. Boşsa nakit tarafı elle kalır. */
  treasuryAccountId: z20.uuid().optional(),
  reference: z20.string().trim().max(100).optional(),
  version: z20.coerce.number().int().min(1)
});
var fixedExpenseOccurrenceListQuerySchema = sortableQuerySchema.extend({
  companyId: z20.uuid().optional(),
  commitmentId: z20.uuid().optional(),
  status: z20.enum(FIXED_EXPENSE_OCCURRENCE_STATUSES).optional(),
  kind: z20.enum(FIXED_EXPENSE_KINDS).optional(),
  year: z20.coerce.number().int().min(2020).max(2100).optional(),
  month: z20.coerce.number().int().min(1).max(12).optional(),
  dueFrom: isoDate4.optional(),
  dueTo: isoDate4.optional(),
  /** Vadesi geçmiş ve hâlâ kapanmamış olanlar. */
  overdue: z20.enum(["true"]).optional(),
  /** Faturası gelmemiş değişken kalemler. */
  missingInvoice: z20.enum(["true"]).optional(),
  /** Beklenenden fazla sapan faturalar. */
  flagged: z20.enum(["true"]).optional()
});
var FIXED_EXPENSE_OCCURRENCE_SORT_FIELDS = [
  "dueDate",
  "expectedAmount",
  "status",
  "sequence"
];
var proposeFixedExpenseEscalationSchema = z20.object({
  effectiveFrom: isoDate4,
  /** Ya oran ya yeni tutar verilir; ikisi birden çelişki demektir. */
  ratePercent: percent.optional(),
  newAmount: money2.optional(),
  /** TÜFE için yayımlanan endeks değeri — sayı TÜİK'ten doğrulanabilsin diye. */
  indexValue: z20.number().min(0).max(1e4).optional(),
  comment: z20.string().trim().max(1e3).optional()
});
var decideFixedExpenseEscalationSchema = z20.object({
  approve: z20.boolean(),
  /** Onaylarken önerilen tutar değiştirilebilir; pazarlık böyle biter. */
  newAmount: money2.optional(),
  comment: z20.string().trim().max(1e3).optional()
}).refine((v) => v.approve || (v.comment?.trim().length ?? 0) >= 5, {
  message: "Reddediyorsan\u0131z gerek\xE7e zorunlu.",
  path: ["comment"]
});
var FIXED_EXPENSE_WITHHOLDING_CORRECTION_MODES = ["NET_ENTERED", "GROSS_ENTERED"];
var FIXED_EXPENSE_WITHHOLDING_CORRECTION_MODE_LABELS = {
  NET_ENTERED: "Tutar NET girildi \u2014 \xF6deme ayn\u0131 kals\u0131n, br\xFCte \xE7evir",
  GROSS_ENTERED: "Tutar BR\xDCT girildi \u2014 stopaj \xF6demeden d\xFC\u015F\xFCls\xFCn"
};
var correctFixedExpenseWithholdingSchema = z20.object({
  mode: z20.enum(FIXED_EXPENSE_WITHHOLDING_CORRECTION_MODES),
  withholdingRate: percent,
  version: z20.coerce.number().int().min(1),
  note: z20.string().trim().max(1e3).optional(),
  /** Önizleme: hesaplar, hiçbir şey yazmaz. Ekran önce bunu gösterir. */
  dryRun: z20.boolean().default(false)
});
var saveFixedExpensePolicySchema = z20.object({
  companyId: z20.uuid(),
  /** Kendi önerdiği zammı onaylayabilir mi? Varsayılan hayır. */
  allowSelfApprovedEscalation: z20.boolean()
});
var fixedExpenseSummaryQuerySchema = z20.object({
  companyId: z20.uuid().optional(),
  year: z20.coerce.number().int().min(2020).max(2100),
  month: z20.coerce.number().int().min(1).max(12).optional()
});
var fixedExpenseProjectionQuerySchema = z20.object({
  companyId: z20.uuid().optional(),
  year: z20.coerce.number().int().min(2020).max(2100)
});
var transferFixedExpenseBudgetSchema = z20.object({
  companyId: z20.uuid(),
  year: z20.coerce.number().int().min(2020).max(2100),
  /** Boşsa açık taslak bulunur ya da açılır. */
  versionId: z20.uuid().optional()
});

// src/dto/flight-premium.ts
import { z as z21 } from "zod";
var FLIGHT_PREMIUM_PERIOD_STATUSES = [
  "DRAFT",
  "FILED",
  "PAID",
  "REJECTED",
  "CANCELLED"
];
var FLIGHT_PREMIUM_PERIOD_STATUS_LABELS = {
  DRAFT: "Hesapland\u0131",
  FILED: "\xD6deme onay\u0131nda",
  PAID: "\xD6dendi",
  REJECTED: "Reddedildi",
  CANCELLED: "\u0130ptal"
};
var flightTypeCode = z21.string().trim().toUpperCase().regex(/^[A-Z0-9_]{2,20}$/, {
  message: "U\xE7u\u015F tipi kodu 2-20 karakter, b\xFCy\xFCk harf, rakam ve alt \xE7izgi olabilir."
});
var FLIGHT_SOURCE_TYPES = ["MANUAL", "IMPORT", "NAERON"];
var FLIGHT_SOURCE_TYPE_LABELS = {
  MANUAL: "Elle girildi",
  IMPORT: "Dosyadan aktar\u0131ld\u0131",
  NAERON: "NAERON"
};
var FLIGHT_ENTRY_KINDS = ["FLIGHT", "MONTHLY_TOTAL"];
var FLIGHT_ENTRY_KIND_LABELS = {
  FLIGHT: "Tek u\xE7u\u015F",
  MONTHLY_TOTAL: "Ayl\u0131k toplam"
};
var FLIGHT_IMPORTED_SOURCES = /* @__PURE__ */ new Set(["IMPORT", "NAERON"]);
var FLIGHT_RATE_CHANGE_REASONS = ["INITIAL", "RAISE", "CORRECTION"];
var FLIGHT_RATE_CHANGE_REASON_LABELS = {
  INITIAL: "\u0130lk tan\u0131m",
  RAISE: "Zam",
  CORRECTION: "D\xFCzeltme"
};
var isoDate5 = z21.iso.date();
var flightHours = z21.number().min(0.1, "U\xE7u\u015F s\xFCresi en az 0,1 saat olmal\u0131.").max(24, "Tek bir u\xE7u\u015F 24 saatten uzun olamaz.").refine(
  (v) => Number.isInteger(Math.round(v * 100)),
  "U\xE7u\u015F s\xFCresi en fazla iki ondal\u0131k olabilir"
);
function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
var MONTHLY_TOTAL_HOURS_PER_28_DAYS = 100;
function monthlyTotalMaxHours(year, month) {
  const raw = daysInMonth(year, month) * MONTHLY_TOTAL_HOURS_PER_28_DAYS / 28;
  return Math.round(raw * 100) / 100;
}
function monthlyTotalHoursError(year, month, hours) {
  if (!Number.isFinite(hours) || hours < 0.1) {
    return "Ayl\u0131k toplam en az 0,1 saat olmal\u0131.";
  }
  if (Math.abs(hours * 100 - Math.round(hours * 100)) > 1e-9) {
    return "Ayl\u0131k toplam en fazla iki ondal\u0131k olabilir.";
  }
  const max = monthlyTotalMaxHours(year, month);
  if (hours > max) {
    return `${year}-${String(month).padStart(2, "0")} d\xF6nemi i\xE7in ayl\u0131k toplam en fazla ${max.toFixed(2).replace(".", ",")} saat olabilir (${daysInMonth(year, month)} g\xFCn, 28 g\xFCnde 100 saat \xF6l\xE7\xFCs\xFCyle). Girilen: ${hours.toFixed(2).replace(".", ",")} saat. Bu kadar saat ger\xE7ekten u\xE7ulduysa u\xE7u\u015Flar\u0131 tek tek girin.`;
  }
  return null;
}
function bulkMonthlyTotalHoursError(year, month, rows) {
  if (rows.length === 0) {
    return "En az bir u\xE7u\u015F tipi i\xE7in saat girilmeli.";
  }
  let total = 0;
  for (const row of rows) {
    const rowError = monthlyTotalHoursError(year, month, row.hours);
    if (rowError) return `${row.flightType}: ${rowError}`;
    total += row.hours;
  }
  total = Math.round(total * 100) / 100;
  const max = monthlyTotalMaxHours(year, month);
  if (total > max) {
    return `${year}-${String(month).padStart(2, "0")} d\xF6nemi i\xE7in b\xFCt\xFCn tiplerin toplam\u0131 en fazla ${max.toFixed(2).replace(".", ",")} saat olabilir (${daysInMonth(year, month)} g\xFCn, 28 g\xFCnde 100 saat \xF6l\xE7\xFCs\xFCyle). Girilen sat\u0131rlar\u0131n toplam\u0131: ${total.toFixed(2).replace(".", ",")} saat. Bu kadar saat ger\xE7ekten u\xE7ulduysa u\xE7u\u015Flar\u0131 tek tek girin.`;
  }
  return null;
}
var periodYearBody = z21.number().int().min(2e3).max(2100);
var periodMonthBody = z21.number().int().min(1).max(12);
var periodYearQuery = z21.coerce.number().int().min(2e3).max(2100);
var periodMonthQuery = z21.coerce.number().int().min(1).max(12);
var createFlightTypeSchema = z21.object({
  code: flightTypeCode,
  name: z21.string().trim().min(2).max(120),
  /** Listedeki sıra. Aynı sırayı taşıyan iki tip ada göre diziliyor. */
  sortOrder: z21.number().int().min(0).max(9999).default(0),
  isActive: z21.boolean().default(true),
  note: z21.string().trim().max(300).optional()
});
var updateFlightTypeSchema = patchSchema(createFlightTypeSchema.omit({ code: true })).partial().extend({
  /** Notu temizlemek de bir düzenleme; `null` "sil" demek. */
  note: z21.string().trim().max(300).nullish()
});
var flightTypeListQuerySchema = z21.object({
  includeInactive: z21.enum(["true"]).optional()
});
var flightPilotCreateSchema = z21.object({
  companyId: z21.uuid(),
  employeeId: z21.uuid(),
  note: z21.string().trim().max(300).optional()
});
var flightPilotUpdateSchema = z21.object({
  isActive: z21.boolean().optional(),
  note: z21.string().trim().max(300).nullish()
});
var flightRateCreateSchema = z21.object({
  /**
   * Hangi uçuş tipinin oranı. BOŞ BIRAKILIRSA bu pilotun VARSAYILAN oranı.
   *
   * İsteğe bağlı olması kolaylık değil, kuralın kendisi: her pilot için her
   * tipe ayrı satır zorunlu olsaydı, tek oranla çalışan bir pilotta zam beş
   * satırın beşini birden güncellemek demek olurdu ve unutulan satır sessizce
   * eski fiyattan ödeme yapardı. Kural şu: tipi yazılmış bir oran varsa o
   * geçerlidir, yoksa varsayılana düşülür. Böylece "PIC farklı, gerisi aynı"
   * iki satırla — bir varsayılan, bir PIC — ifade edilebiliyor.
   */
  flightType: flightTypeCode.optional(),
  /**
   * Upper bound of 100.000 TL/saat is a typo guard, not a policy: it is two
   * orders of magnitude above any real rate, so it only ever catches a
   * misplaced separator that `moneyText` could not.
   */
  hourlyRate: moneyText({ min: 0, max: 1e5 }),
  validFrom: isoDate5,
  changeReason: z21.enum(FLIGHT_RATE_CHANGE_REASONS).default("RAISE"),
  note: z21.string().trim().max(300).optional()
});
var flightRecordCreateSchema = z21.object({
  pilotId: z21.uuid(),
  flightDate: isoDate5,
  hours: flightHours,
  aircraftRegistration: z21.string().trim().max(20).optional(),
  /**
   * Varsayılan TRAINING olarak kalıyor: ATO'nun uçuşlarının ezici çoğunluğu
   * eğitim ve seçim yapmayan formun sessizce başka bir tipe düşmesi, artık
   * fiyat taşıyan bir alanda yanlış ücretten ödeme demek. Kodun `hr_flight_type`
   * içinde gerçekten var olduğuna yabancı anahtar karar veriyor.
   */
  flightType: flightTypeCode.default("TRAINING"),
  reference: z21.string().trim().max(60).optional(),
  note: z21.string().trim().max(300).optional()
});
var flightRecordUpdateSchema = flightRecordCreateSchema.omit({ pilotId: true }).partial().extend({
  /** Optimistic lock: the row as the editor last saw it. */
  version: z21.number().int().min(1)
});
function periodAnchorOf(isoDay) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDay)) return isoDay;
  return `${isoDay.slice(0, 7)}-01`;
}
var flightRecordQuerySchema = z21.object({
  companyId: z21.uuid().optional(),
  pilotId: z21.uuid().optional(),
  /**
   * The range, and it means TWO different things because the rows do.
   *
   * For a FLIGHT it is the days flown, exactly as typed. For a MONTHLY_TOTAL it
   * is read as PERIODS: a bound anywhere inside July selects July's total, and
   * only a bound that leaves July drops it. That is not a convenience — a
   * monthly total has no day. Its `flightDate` is an anchor pinned to the 1st
   * by database CHECK, a date nobody typed, so comparing it against a day the
   * reader chose measures a storage detail. Done literally it hides a reader's
   * own row from them the moment they narrow the start past the 1st, and says
   * nothing about having done it.
   *
   * The service applies both readings, including when `entryKind` is absent and
   * the answer mixes the two kinds. The screen states which periods it is
   * showing rather than leaving the widening invisible — a filter that quietly
   * does not apply is its own trap.
   */
  from: isoDate5.optional(),
  to: isoDate5.optional(),
  sourceType: z21.enum(FLIGHT_SOURCE_TYPES).optional(),
  /** Ask for one kind only — the flight list and the monthly-total list are
   *  two different tables on the screen even though they are one in the
   *  database, and a list that mixed them would show a month beside a day. */
  entryKind: z21.enum(FLIGHT_ENTRY_KINDS).optional(),
  take: z21.coerce.number().int().min(1).max(500).default(200),
  skip: z21.coerce.number().int().min(0).default(0)
});
var flightImportRowSchema = z21.object({
  employeeNo: z21.string().trim().min(1).max(40),
  flightDate: isoDate5,
  hours: flightHours,
  aircraftRegistration: z21.string().trim().max(20).optional(),
  /**
   * Dosyadaki tip kodu. Küçük harfle gelirse büyütülüyor — dışarıdan gelen bir
   * aktarımda "training" ile "TRAINING" arasındaki fark, satırın reddedilmesi
   * için bir sebep değil.
   */
  flightType: flightTypeCode.default("TRAINING"),
  reference: z21.string().trim().max(60).optional(),
  /**
   * The row's identity in the source. When given, re-importing the same file
   * updates instead of duplicating; when absent, a second import of the same
   * file doubles the month — which is why the screen insists on it.
   */
  sourceId: z21.string().trim().max(120).optional()
});
var flightImportSchema = z21.object({
  companyId: z21.uuid(),
  sourceType: z21.enum(["IMPORT", "NAERON"]).default("IMPORT"),
  rows: z21.array(flightImportRowSchema).min(1).max(2e3)
});
var flightMonthlyTotalFields = {
  pilotId: z21.uuid(),
  periodYear: periodYearBody,
  periodMonth: periodMonthBody,
  /**
   * DELIBERATELY UNBOUNDED HERE. Every rule about this number — the floor, the
   * two decimals, and the ceiling derived from the period — lives in
   * `monthlyTotalHoursError`, and the service applies it.
   *
   * That is not where it started. It was a `superRefine` on this schema, which
   * is the obvious place, and the verification suite caught what the obvious
   * place costs: `HttpExceptionFilter` answers a ZodError with the flat
   * sentence "Gönderilen bilgiler geçersiz." and files the real message under
   * `details.issues`. So a monthly total one hundredth over its ceiling was
   * refused with no reason on it — which is DST-00068 happening AGAIN, one
   * layer further in, to the very field the ticket is about.
   *
   * The rule is still one implementation. The service calls
   * `monthlyTotalHoursError` and turns it into a BadRequestException whose
   * message survives the filter; the form calls the same function against the
   * period it is displaying, for the inline hint. What moved is where it runs,
   * not how many copies of it there are.
   */
  hours: z21.number(),
  /**
   * Hangi tipin aylık toplamı — ve artık PARA TAŞIYOR.
   *
   * DST-00068 bir ayın toplam EĞİTİM saatiyle ilgiliydi ve tip o zaman yalnızca
   * "hangi saatler bunlar" sorusunu cevaplıyordu. Tip fiyat taşımaya
   * başladığından beri aynı alan ödemeyi belirliyor: 40 saat PIC ile 40 saat
   * eğitim aynı ay içinde farklı tutar ediyor. Ay artık tip BAŞINA bir satır
   * taşıyabiliyor (veritabanındaki tekil indeks (pilot, ay, tip) üçlüsünde), ve
   * her tipin ayrı ayrı girilmesi tam olarak `bulkMonthlyTotalSchema`'nın
   * çözdüğü sorun.
   */
  flightType: flightTypeCode.default("TRAINING"),
  /** Journey-log pages, the report this figure was read off. */
  reference: z21.string().trim().max(60).optional(),
  note: z21.string().trim().max(300).optional()
};
var flightMonthlyTotalCreateSchema = z21.object(flightMonthlyTotalFields);
var flightMonthlyTotalUpdateSchema = z21.object({
  hours: z21.number().optional(),
  flightType: flightTypeCode.optional(),
  reference: z21.string().trim().max(60).nullish(),
  note: z21.string().trim().max(300).nullish(),
  /** Optimistic lock, same reason as the flight row's. */
  version: z21.number().int().min(1)
});
var bulkMonthlyTotalRowSchema = z21.object({
  flightType: flightTypeCode,
  /**
   * Tek satırlık girişteki `hours` ile aynı sebeple SINIRSIZ bırakıldı: taban,
   * iki ondalık ve tavan `bulkMonthlyTotalHoursError`'da, çünkü buraya konan
   * bir kural `HttpExceptionFilter` tarafından "Gönderilen bilgiler geçersiz."
   * cümlesine düzleştirilir ve okuyan kimseye ulaşmaz.
   */
  hours: z21.number()
});
var bulkMonthlyTotalSchema = z21.object({
  pilotId: z21.uuid(),
  /**
   * Ad `periodYear`/`periodMonth`, `year`/`month` değil — dosyanın geri
   * kalanıyla aynı. Aynı kavramın iki adı olsaydı, tek satırlık giriş ile
   * toplu giriş arasında alan kopyalayan servis sessizce `undefined` taşırdı.
   */
  periodYear: periodYearBody,
  periodMonth: periodMonthBody,
  /**
   * Bütün satırlar için ortak künye: bu rakamların okunduğu belge bir
   * tanedir (uçuş kayıt defteri sayfası, aylık rapor). Tip başına ayrı künye
   * istemek, aynı şeyi dört kez yazdırmak olurdu.
   */
  reference: z21.string().trim().max(60).optional(),
  note: z21.string().trim().max(300).optional(),
  rows: z21.array(bulkMonthlyTotalRowSchema).min(1, { message: "En az bir u\xE7u\u015F tipi i\xE7in saat girilmeli." }).max(50)
}).superRefine((value, ctx) => {
  const seen = /* @__PURE__ */ new Set();
  value.rows.forEach((row, index) => {
    if (seen.has(row.flightType)) {
      ctx.addIssue({
        code: "custom",
        path: ["rows", index, "flightType"],
        message: `Ayn\u0131 u\xE7u\u015F tipi iki kez girilemez: ${row.flightType}.`
      });
      return;
    }
    seen.add(row.flightType);
  });
});
var flightPeriodQuerySchema = z21.object({
  companyId: z21.uuid().optional(),
  periodYear: periodYearQuery,
  periodMonth: periodMonthQuery,
  status: z21.enum(FLIGHT_PREMIUM_PERIOD_STATUSES).optional()
});
var flightPeriodRecomputeSchema = z21.object({
  companyId: z21.uuid().optional(),
  periodYear: periodYearBody,
  periodMonth: periodMonthBody
});
var flightPeriodNoteSchema = z21.object({
  note: z21.string().trim().max(300).nullish(),
  version: z21.number().int().min(1)
});
var flightPeriodFileSchema = z21.object({
  version: z21.number().int().min(1)
});
var flightPeriodFileBatchSchema = z21.object({
  companyId: z21.uuid(),
  periodYear: periodYearBody,
  periodMonth: periodMonthBody,
  /** Explicit list, so "file everything" is never a mis-click. */
  periodIds: z21.array(z21.uuid()).min(1).max(200)
});

// src/dto/registration.ts
import { z as z22 } from "zod";
var isoDate6 = z22.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Tarih YYYY-AA-GG bi\xE7iminde olmal\u0131." });
var registrationApplySchema = z22.object({
  email: z22.email({ message: "Ge\xE7erli bir e-posta adresi girin." }).trim().toLowerCase().max(160),
  firstName: z22.string().trim().min(2, { message: "Ad\u0131n\u0131z\u0131 yaz\u0131n." }).max(100),
  lastName: z22.string().trim().min(2, { message: "Soyad\u0131n\u0131z\u0131 yaz\u0131n." }).max(100),
  /**
   * Biçim burada, sağlama toplamı sunucuda: `FieldCryptoService` zaten resmî
   * algoritmayı taşıyor ve doğrulamanın iki yerde yaşaması, birinin gevşediği
   * gün fark edilmeyen bir fark demek.
   */
  nationalId: z22.string().trim().regex(/^\d{11}$/, { message: "TC kimlik numaras\u0131 11 haneli olmal\u0131." }),
  birthDate: isoDate6.optional(),
  phone: z22.string().trim().max(30).optional(),
  jobTitle: z22.string().trim().max(120).optional(),
  /** Yıllık izin hakedişi (4857 md. 53) bunun üzerine kurulur. */
  seniorityStartDate: isoDate6,
  note: z22.string().trim().max(1e3).optional()
});
var REGISTRATION_STATUSES = ["PENDING", "APPROVED", "REJECTED"];
var REGISTRATION_STATUS_LABELS = {
  PENDING: "Bekliyor",
  APPROVED: "Onayland\u0131",
  REJECTED: "Reddedildi"
};
var registrationApproveSchema = z22.object({
  companyId: z22.uuid(),
  roles: z22.array(z22.string().min(1)).min(1, { message: "En az bir rol se\xE7ilmeli." }),
  scopes: z22.array(scopeAssignmentSchema).min(1, { message: "En az bir kapsam tan\u0131mlanmal\u0131." }),
  /** Var olan bir karta bağla; boşsa yeni kart açılır. */
  employeeId: z22.uuid().optional(),
  /** Yalnızca yeni kart açılırken gerekli — var olan kartın ikisi de var. */
  orgUnitId: z22.uuid().optional(),
  employeeNo: z22.string().trim().min(1).max(40).optional(),
  note: z22.string().trim().max(1e3).optional()
}).superRefine((value, ctx) => {
  if (value.employeeId) return;
  if (!value.orgUnitId) {
    ctx.addIssue({
      code: "custom",
      path: ["orgUnitId"],
      message: "Yeni personel kart\u0131 i\xE7in departman se\xE7ilmeli."
    });
  }
  if (!value.employeeNo) {
    ctx.addIssue({
      code: "custom",
      path: ["employeeNo"],
      message: "Yeni personel kart\u0131 i\xE7in personel numaras\u0131 gerekli."
    });
  }
});
var registrationRejectSchema = z22.object({
  note: z22.string().trim().max(1e3).optional()
});

// src/dto/support.ts
import { z as z23 } from "zod";
var SUPPORT_TICKET_KINDS = ["BUG", "FEATURE"];
var SUPPORT_TICKET_KIND_LABELS = {
  BUG: "Hata",
  FEATURE: "Geli\u015Ftirme talebi"
};
var SUPPORT_PRIORITIES = ["LOW", "NORMAL", "HIGH", "CRITICAL"];
var SUPPORT_PRIORITY_LABELS = {
  LOW: "D\xFC\u015F\xFCk",
  NORMAL: "Normal",
  HIGH: "Y\xFCksek",
  CRITICAL: "Kritik"
};
var SUPPORT_STATUSES = [
  "OPEN",
  "IN_PROGRESS",
  "RESOLVED",
  "CLOSED",
  "REJECTED",
  "DUPLICATE"
];
var SUPPORT_STATUS_LABELS = {
  OPEN: "A\xE7\u0131k",
  IN_PROGRESS: "\xDCzerinde \xE7al\u0131\u015F\u0131l\u0131yor",
  RESOLVED: "\xC7\xF6z\xFCld\xFC",
  CLOSED: "Kapand\u0131",
  REJECTED: "Reddedildi",
  DUPLICATE: "M\xFCkerrer"
};
var SUPPORT_CLOSED_STATUSES = [
  "CLOSED",
  "REJECTED",
  "DUPLICATE"
];
var SUPPORT_AREAS = [
  "HR",
  "LEAVE",
  "ATTENDANCE",
  "PAYMENT",
  "FINANCE",
  "TREASURY",
  "KONAKLAMA",
  "INTEGRATION",
  "AUTH",
  "OTHER"
];
var SUPPORT_AREA_LABELS = {
  HR: "Personel",
  LEAVE: "\u0130zin",
  ATTENDANCE: "Puantaj",
  PAYMENT: "\xD6deme",
  FINANCE: "Finans",
  TREASURY: "Kasa ve banka",
  KONAKLAMA: "Misafirhane",
  INTEGRATION: "Entegrasyon",
  AUTH: "Giri\u015F ve yetki",
  OTHER: "Di\u011Fer"
};
var SUPPORT_STATUS_TRANSITIONS = {
  OPEN: ["IN_PROGRESS", "RESOLVED", "REJECTED", "DUPLICATE"],
  IN_PROGRESS: ["RESOLVED", "OPEN", "REJECTED", "DUPLICATE"],
  RESOLVED: ["CLOSED", "IN_PROGRESS"],
  CLOSED: [],
  REJECTED: [],
  DUPLICATE: []
};
var SUPPORT_STATUSES_NEEDING_COMMENT = ["REJECTED", "DUPLICATE"];
var SUPPORT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
var createSupportTicketSchema = z23.object({
  kind: z23.enum(SUPPORT_TICKET_KINDS),
  title: z23.string().trim().min(5).max(200),
  /**
   * On karakterlik alt sınır bir formalite değil: "çalışmıyor" diye açılan bir
   * kayıt, açıldığı andan itibaren birinin gidip sormasını bekleyen bir iştir.
   */
  description: z23.string().trim().min(10).max(5e3),
  area: z23.enum(SUPPORT_AREAS).optional(),
  /** Bildiren öncelik ÖNERİR; bağlayıcı olanı destek ekibi belirler. */
  priority: z23.enum(SUPPORT_PRIORITIES).optional(),
  /** Hatanın göründüğü ekranın adresi — üretimde aramaktan ucuzdur. */
  pageUrl: z23.string().trim().max(500).optional(),
  /**
   * Hangi şirketin kaydı. Tek şirkete kapsamlı kullanıcı için gereksiz —
   * servis onu kapsamdan okur. Grup kapsamlı bir yönetici için gerekli, çünkü
   * onun "kendi şirketi" diye bir şeyi yok ve tahmin etmek yanlış şirketin
   * kuyruğuna kayıt düşürür.
   */
  companyId: z23.uuid().optional()
});
var updateSupportTicketSchema = z23.object({
  priority: z23.enum(SUPPORT_PRIORITIES).optional(),
  area: z23.enum(SUPPORT_AREAS).optional(),
  /** null: atamayı kaldır. */
  assigneeUserId: z23.uuid().nullable().optional()
}).refine((v) => Object.keys(v).length > 0, { message: "En az bir alan gerekli." });
var changeSupportStatusSchema = z23.object({
  status: z23.enum(SUPPORT_STATUSES),
  comment: z23.string().trim().min(3).max(2e3).optional()
});
var addSupportCommentSchema = z23.object({
  body: z23.string().trim().min(1).max(4e3),
  /**
   * Destek ekibinin kendi arasındaki not. Bildiren göremez.
   *
   * Ayrı bir tablo değil bir bayrak, çünkü iki tablo iki sorgu ve er ya da geç
   * birinin ötekini unuttuğu bir ekran demektir. Görünürlük tek yerde,
   * okunurken uygulanıyor.
   */
  internal: z23.boolean().default(false)
});
var supportTicketListQuerySchema = sortableQuerySchema.extend({
  kind: z23.enum(SUPPORT_TICKET_KINDS).optional(),
  status: z23.enum(SUPPORT_STATUSES).optional(),
  priority: z23.enum(SUPPORT_PRIORITIES).optional(),
  area: z23.enum(SUPPORT_AREAS).optional(),
  assigneeUserId: z23.uuid().optional(),
  /** Yalnızca benim açtıklarım. support.read olmayan için zaten tek seçenek. */
  mine: z23.coerce.boolean().optional(),
  /** Kapanmış ve reddedilmiş kayıtları da göster. Varsayılan: yalnızca açık olanlar. */
  includeResolved: z23.coerce.boolean().optional(),
  /**
   * YALNIZ sonuçlanmış kayıtlar (SUPPORT_CLOSED_STATUSES): ekrandaki
   * "Kapananlar" sekmesi. Üç durumu tek sorguda istemenin başka yolu yok —
   * `status` tek değer alıyor. Açıkça bir `status` verilirse o kazanır.
   */
  closed: z23.coerce.boolean().optional(),
  /**
   * Yalnız "cevap bizde" olanlar: son sözü bildirene ait kayıtlar.
   *
   * Durumdan bağımsız, ve bu kasıtlı — bu kaydın kaybolduğu yer tam olarak
   * "Çözüldü" kutusuydu: cevaptan sonra bildiren yazınca kayıt hiçbir açık iş
   * listesinde görünmüyordu.
   */
  waiting: z23.coerce.boolean().optional()
});
var SUPPORT_TICKET_SORT_FIELDS = [
  "createdAt",
  "updatedAt",
  "priority",
  "status",
  "number"
];

// src/dto/document-library.ts
import { z as z24 } from "zod";
var DOCUMENT_LIBRARY_ACCEPT = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".docx",
  ".xlsx",
  ".pptx"
].join(",");
var DOCUMENT_LIBRARY_ACCEPT_LABEL = "PDF, Word (.docx), Excel (.xlsx), PowerPoint (.pptx), JPEG, PNG";
var documentFolderPermissionSchema = z24.string().max(80).refine((code) => ALL_PERMISSIONS.includes(code), {
  message: "Bilinmeyen yetki kodu."
});
var createDocumentFolderSchema = z24.object({
  name: z24.string().trim().min(1).max(200),
  description: z24.string().trim().max(1e3).nullish(),
  parentId: z24.uuid().nullish(),
  /**
   * Klasörün ait olduğu şirket. Verilmezse çağıranın kapsamı karar verir:
   * tek şirketi varsa o, grup kapsamlıysa grup düzeyi (NULL).
   */
  companyId: z24.uuid().nullish(),
  /** NULL/boş = kütüphaneyi açabilen herkes görür. */
  requiredPermission: documentFolderPermissionSchema.nullish()
});
var updateDocumentFolderSchema = z24.object({
  name: z24.string().trim().min(1).max(200).optional(),
  description: z24.string().trim().max(1e3).nullish(),
  parentId: z24.uuid().nullish(),
  requiredPermission: documentFolderPermissionSchema.nullish()
});
var updateDocumentFileSchema = z24.object({
  name: z24.string().trim().min(1).max(255).optional(),
  description: z24.string().trim().max(2e3).nullish(),
  folderId: z24.uuid().optional()
});
var uploadDocumentMetaSchema = z24.object({
  name: z24.string().trim().min(1).max(255).optional(),
  description: z24.string().trim().max(2e3).optional()
});

// src/dto/invoice.ts
import { z as z25 } from "zod";
var INVOICE_DIRECTIONS = ["GELEN", "GIDEN"];
var INVOICE_DIRECTION_LABELS = {
  GELEN: "Bize kesilen",
  GIDEN: "Bizim kesti\u011Fimiz"
};
var INVOICE_ENTERED_LABELS = {
  GIDEN: {
    eylem: "Fatura kesildi olarak i\u015Faretle",
    durum: "Fatura kesildi",
    aciklama: "Fatura onayland\u0131. Zirve\u2019de kestikten sonra buray\u0131 i\u015Faretleyin \u2014 sistem Zirve\u2019ye kendili\u011Finden bir \u015Fey g\xF6ndermez.",
    referans: "Zirve\u2019nin verdi\u011Fi fatura no",
    suzgec: ["Kesilmemi\u015F", "Kesildi"],
    bekleyenIs: "Kesilmeyi"
  },
  GELEN: {
    eylem: "Zirve\u2019ye girildi olarak i\u015Faretle",
    durum: "Zirve\u2019ye girildi",
    aciklama: "Fatura onayland\u0131. Zirve\u2019ye girdikten sonra buray\u0131 i\u015Faretleyin \u2014 sistem Zirve\u2019ye kendili\u011Finden bir \u015Fey g\xF6ndermez.",
    referans: "Zirve fi\u015F no",
    suzgec: ["Girilmemi\u015F", "Girildi"],
    bekleyenIs: "Zirve\u2019ye girilmeyi"
  }
};
var INVOICE_STATUSES = [
  "PENDING",
  /**
   * Onaycı reddetmedi, SORDU.
   *
   * Ret ile soru aynı şey değil ve tek bir "reddet" düğmesi ikisini
   * karıştırıyordu: "bu tutar neden bu kadar" diye merak eden bir onaycının
   * elindeki tek araç, faturayı reddetmekti. Reddedilen fatura ise yanlış
   * bir faturadır — kaydı öyle okunur, sayısı öyle raporlanır, ve talebi
   * açan kişi haksız yere geri çevrilmiş olur.
   *
   * Bu durum faturayı ZİNCİRDEN ÇIKARMIYOR, tur numarasını artırmıyor ve
   * verilmiş onayları silmiyor: yalnızca topu soruyu cevaplayacak kişiye
   * atıyor. Cevap gelince fatura kaldığı yerden devam ediyor.
   */
  "INFO_REQUESTED",
  "APPROVED",
  "REJECTED"
];
var INVOICE_STATUS_LABELS = {
  PENDING: "Onay bekliyor",
  INFO_REQUESTED: "Ek a\xE7\u0131klama bekleniyor",
  APPROVED: "Onayland\u0131",
  REJECTED: "Reddedildi"
};
var INVOICE_DOCUMENT_TYPES = [
  "FATURA",
  "PROFORMA",
  "INSURANCE_POLICY",
  "INSURANCE_QUOTE",
  "CUSTOMS_DECLARATION",
  "RECEIPT",
  "OTHER"
];
var INVOICE_DOCUMENT_TYPE_LABELS = {
  FATURA: "Fatura",
  PROFORMA: "Proforma",
  INSURANCE_POLICY: "Poli\xE7e",
  INSURANCE_QUOTE: "Poli\xE7e teklifi",
  CUSTOMS_DECLARATION: "G\xFCmr\xFCk beyannamesi",
  RECEIPT: "Dekont",
  OTHER: "Di\u011Fer"
};
var faturaIcerigiAlanlari = {
  purposeId: z25.uuid().nullish(),
  purposeNote: z25.string().trim().max(300).nullish()
};
var createInvoicePurposeSchema = z25.object({
  code: z25.string().trim().toUpperCase().regex(/^[A-Z0-9_]{2,40}$/, {
    message: "Kod 2-40 karakter; b\xFCy\xFCk harf, rakam ve alt \xE7izgi olabilir (\xF6rn. MOTOR_REVIZYON)."
  }),
  name: z25.string().trim().min(2).max(120),
  sortOrder: z25.number().int().min(0).max(9999).default(0)
});
var updateInvoicePurposeSchema = z25.object({
  name: z25.string().trim().min(2).max(120).optional(),
  sortOrder: z25.number().int().min(0).max(9999).optional(),
  isActive: z25.boolean().optional()
});
var createInvoiceSchema = z25.object({
  companyId: z25.uuid(),
  /** Cari zorunlu: faturanın kime ait olduğu sorusunun tek cevabı defterdedir. */
  payeeId: z25.uuid(),
  direction: z25.enum(INVOICE_DIRECTIONS).default("GELEN"),
  /**
   * Fatura numarası isteğe bağlı ve öyle kalmalı.
   *
   * Bize kesilen faturada numara vardır; bizim henüz kesmediğimiz taslakta
   * yasal bir numara YOKTUR. Zorunlu kılmak, muhasebeyi olmayan bir numarayı
   * uydurmaya iterdi.
   */
  invoiceNo: z25.string().max(100).optional(),
  invoiceDate: z25.iso.date().optional(),
  /** Tutar metin olarak gelir ve sunucuda ayrıştırılır — deponun para kuralı. */
  amountText: z25.string().min(1).max(32),
  amountFormat: z25.enum(["tr", "en"]).optional(),
  amount: z25.number().min(0.01).max(1e9).optional(),
  currency: z25.string().length(3).default("TRY"),
  description: z25.string().min(3).max(500),
  /**
   * Reddin muhatabı — faturayı giren muhasebeci DEĞİL.
   *
   * "İlgili kişilere yönlendirebilelim" cümlesinin karşılığı bu alan. Fatura
   * başına seçiliyor, cari başına sabitlenmiyor: aynı tedarikçi iki departmana
   * hizmet verdiğinde iki faturanın muhatabı iki ayrı kişidir.
   */
  responsibleUserId: z25.uuid().optional(),
  // --- Organizasyonel ve finansal boyutlar. Sunucu, verilenlerin faturanın
  // şirketine ait ve aktif olduğunu ayrıca doğrular (invoice.service:
  // assertDimensions).
  //
  // ŞEMADA OPSİYONEL, KURALDA ZORUNLU — ve `orgUnitId` için ayrım kasıtlı.
  // Sunucu kayıtta birimi ŞART KOŞUYOR (assertDimensions, mod 'olusturma'),
  // ama şema bunu dayatmıyor: bir ZodError ekranda «Gönderilen bilgiler
  // geçersiz.» cümlesine iniyor (`http-exception.filter`), oysa servisin
  // reddi hangi alan olduğunu ve niçin gerektiğini söylüyor. Kural aynı
  // kural; fark, kullanıcının ne okuduğu.
  //
  // Diğer üçü gerçekten opsiyonel: eski istemci onları hiç göndermez, eski
  // fatura NULL taşır ve akış aynen çalışır.
  orgUnitId: z25.uuid().nullish(),
  costCenterId: z25.uuid().nullish(),
  projectId: z25.uuid().nullish(),
  /** Grup içi işlemde karşı şirket. Cari (`payeeId`) ile aynı şey DEĞİL. */
  counterpartyCompanyId: z25.uuid().nullish(),
  /** İki tarafın da taşıdığı eşleştirme referansı; şirket başına benzersiz. */
  intercompanyReference: z25.string().trim().max(60).nullish(),
  categoryId: z25.uuid().nullish(),
  ...faturaIcerigiAlanlari
});
var decideInvoiceSchema = z25.object({
  approve: z25.boolean(),
  /** Rette ZORUNLU: gerekçesiz ret, aynı faturanın yarın aynı hâliyle dönmesidir. */
  reason: z25.string().trim().min(5).max(2e3).optional(),
  /**
   * Rette «yerine ne ile tedarik edilecek» (DST-00130) — OPSİYONEL.
   *
   * «Reddedilecek ise sebebini, yenisi yerine hangisi ile tedarik edileceği
   * bilgisi verilmeli.» Her retin böyle bir cevabı yok (yanlış tutar,
   * eksik belge), o yüzden zorunlu değil; ama yazıldığında gerekçenin
   * içinde kaybolmasın diye ayrı bir alan ve bildirimde ayrı bir cümle.
   */
  alternativeSupply: z25.string().trim().max(500).optional()
}).refine((v) => v.approve || Boolean(v.reason), {
  message: "Reddin gerek\xE7esi zorunludur.",
  path: ["reason"]
}).refine((v) => !v.approve || !v.alternativeSupply, {
  message: "\xABYerine ne ile tedarik edilecek\xBB yaln\u0131z retle birlikte yaz\u0131l\u0131r.",
  path: ["alternativeSupply"]
});
var askInvoiceInfoSchema = z25.object({
  question: z25.string().trim().min(5).max(2e3)
});
var invoiceCommentSchema = z25.object({
  body: z25.string().trim().min(2).max(2e3)
});
var answerInvoiceInfoSchema = z25.object({
  answer: z25.string().trim().min(2).max(2e3)
});
var withdrawInvoiceInfoSchema = z25.object({
  reason: z25.string().trim().max(2e3).optional()
});
var markInvoiceEnteredSchema = z25.object({
  /**
   * Zirve'nin verdiği numara (#417). GİDEN faturada kayıt numarasızsa bu
   * numara faturanın YASAL numarası olur.
   *
   * Tekerrür kapısı tam burada, ve ayrı bir `invoiceNo` alanı bilerek
   * AÇILMADI: aynı numara için iki alan, ekranda iki kutu ve sunucuda iki kural
   * demek. GİDEN taslakta yasal numara yoktur (bkz. `createInvoiceSchema`),
   * numara «Fatura kesildi» işaretlenirken doğar — bu yüzden kayıt numarasızsa
   * sunucu bu alanı GİDEN'de ZORUNLU tutuyor, normalize ediyor, GİB biçimini
   * ve (şirket, numara) tekilliğini sınıyor. Zorunluluk şemada değil serviste,
   * çünkü kayda bakarak karar veriliyor: numarası önceden girilmiş bir faturada
   * bu alan gönderilmek zorunda değil.
   */
  reference: z25.string().max(100).optional()
});
var undoInvoiceEnteredSchema = z25.object({
  reason: z25.string().trim().min(5).max(1e3)
});
var resubmitInvoiceSchema = z25.object({
  /**
   * BAŞTAN ONAYA GÖNDER kipi (DST-00152/00156): onay bekleyen, bu turda onay
   * almış faturayı zincirin başına gönderir (`faturaBastanSunulabilir`).
   * Açıkça söyleniyor, durumdan tahmin edilmiyor: reddedilmiş faturanın
   * yeniden sunumu çift tıklanınca ikinci istek faturayı onay bekler bulur ve
   * «zaten onay bekliyor» cevabını almalı — baştan gönderme denemesi sanılıp
   * başka bir gerekçeyle değil.
   */
  bastan: z25.boolean().optional(),
  /**
   * İŞ BİRİMİ — yeniden sunumda da verilebiliyor, ve verilmesi gerekebiliyor.
   *
   * İki sebep. Biri: yeniden sunum zinciri İLK KAPIDAN başlatıyor, yani birim
   * burada değişirse fatura doğru müdüre gider — düzeltmenin aksine, bu yolda
   * geçmiş bir imza yok. Öteki: birimi hiç olmayan eski faturaların birim
   * kazanabileceği tek yol bu; onsuz ret→düzelt→yeniden sun döngüsünde birim
   * müdürünün kapısını sonsuza kadar atlarlardı.
   *
   * Opsiyonel çünkü birimi ZATEN olan bir fatura yeniden sunulurken onu
   * yeniden göndermek zorunda değil; sunucu sonucun boş kalmadığını ayrıca
   * doğruluyor.
   */
  orgUnitId: z25.uuid().optional(),
  invoiceNo: z25.string().max(100).optional(),
  invoiceDate: z25.iso.date().optional(),
  /** Tutar metin olarak; sunucu kendi okur — deponun para kuralı. */
  amountText: z25.string().min(1).max(32).optional(),
  amountFormat: z25.enum(["tr", "en"]).optional(),
  amount: z25.number().min(0.01).max(1e9).optional(),
  description: z25.string().trim().min(3).max(500).optional(),
  responsibleUserId: z25.uuid().nullish(),
  ...faturaIcerigiAlanlari,
  note: z25.string().trim().min(5).max(2e3)
});
var withdrawInvoiceSchema = z25.object({
  note: z25.string().trim().min(5).max(500)
});
function kesilenFaturaEklenebilir(f, belgeTuru) {
  return f.status === "APPROVED" && f.direction === "GIDEN" && belgeTuru === "FATURA";
}
function faturaListedenKaldirilabilir(f, kisiId) {
  return f.status === "REJECTED" && f.withdrawnAt === null && (f.createdByUserId === kisiId || f.responsibleUserId === kisiId);
}
function faturaKesilmeyecekKapatilabilir(f, kisiId, finansYoneticisi) {
  return f.status === "APPROVED" && f.direction === "GIDEN" && f.enteredAt === null && f.withdrawnAt === null && (finansYoneticisi || f.createdByUserId === kisiId || f.responsibleUserId === kisiId);
}
function faturaBastanSunulabilir(f, kisiId, buTurdakiOnaySayisi) {
  return (f.status === "PENDING" || f.status === "INFO_REQUESTED") && f.withdrawnAt === null && (f.createdByUserId === kisiId || f.responsibleUserId === kisiId) && buTurdakiOnaySayisi > 0;
}
var FATURA_YANIT_ASGARI = 15;
var FATURA_NOT_ASGARI = 5;
function faturaYenidenSunulabilir(input) {
  const not = input.not.trim();
  const degisiklikVar = input.degisenAlanSayisi > 0 || input.yeniBelgeSayisi > 0;
  if (not.length < FATURA_NOT_ASGARI) {
    return { izin: false, mesaj: `Not en az ${FATURA_NOT_ASGARI} karakter olmal\u0131.` };
  }
  if (!degisiklikVar && not.length < FATURA_YANIT_ASGARI) {
    return {
      izin: false,
      mesaj: `Hi\xE7bir alan\u0131 de\u011Fi\u015Ftirmeden yeniden sunuyorsunuz \u2014 bu bir yan\u0131t olarak gider. Reddedene neden d\xFCzeltme gerekmedi\u011Fini yaz\u0131n (en az ${FATURA_YANIT_ASGARI} karakter).`
    };
  }
  return { izin: true };
}
var updateInvoiceSchema = z25.object({
  payeeId: z25.uuid().optional(),
  invoiceNo: z25.string().max(100).nullish(),
  invoiceDate: z25.iso.date().nullish(),
  amountText: z25.string().min(1).max(32).optional(),
  amountFormat: z25.enum(["tr", "en"]).optional(),
  amount: z25.number().min(0.01).max(1e9).optional(),
  currency: z25.string().trim().length(3).optional(),
  description: z25.string().trim().min(3).max(500).optional(),
  responsibleUserId: z25.uuid().nullish(),
  note: z25.string().trim().min(5).max(2e3),
  // Boyutlar güncellemede de OPSİYONEL. `null` göndermek alanı temizler;
  // hiç göndermemek dokunmaz — mevcut revizyon davranışının aynısı.
  orgUnitId: z25.uuid().nullish(),
  costCenterId: z25.uuid().nullish(),
  projectId: z25.uuid().nullish(),
  /** Grup içi işlemde karşı şirket. Cari (`payeeId`) ile aynı şey DEĞİL. */
  counterpartyCompanyId: z25.uuid().nullish(),
  /** İki tarafın da taşıdığı eşleştirme referansı; şirket başına benzersiz. */
  intercompanyReference: z25.string().trim().max(60).nullish(),
  categoryId: z25.uuid().nullish(),
  ...faturaIcerigiAlanlari
});
var ONAYA_BAGLI_FATURA_ALANLARI = [
  "amount",
  "currency",
  "payeeId",
  /**
   * İŞ BİRİMİ — ve gerekçesi yukarıdaki para örneğinin BİREBİR aynısı.
   *
   * Birim, faturayı hangi kapının karşılayacağını belirliyor: «Bakım müdürü
   * onayı» kapısı yalnız Uçak Bakım'ın faturalarında işliyor. Bakım müdürü
   * imzaladıktan sonra birimi Uçuş Okulu yapmak, o imzayı artık o müdürün işi
   * OLMAYAN bir faturada saydırırdı — tıpkı onaylanmış 1.000 TL'yi 10.000
   * yapmak gibi.
   *
   * Kimse imzalamadıysa düzeltme yine serbest, ve o hâlde sunucu faturayı yeni
   * birimin zincirinin başına taşıyor: onaylanmamış bir faturayı doğru kapıya
   * götürmek kimsenin imzasını boşa çıkarmıyor.
   */
  "orgUnitId"
];
function faturaDuzeltilebilir(input) {
  if (!input.buTurdaOnayVar) return { izin: true };
  const kilitliAlanlar = ONAYA_BAGLI_FATURA_ALANLARI.filter(
    (a) => input.degisenAlanlar.includes(a)
  );
  if (kilitliAlanlar.length === 0) return { izin: true };
  const adlar = kilitliAlanlar.map((a) => (INVOICE_FIELD_LABELS[a] ?? a).toLocaleLowerCase("tr"));
  const son = adlar.pop() ?? "";
  const liste = adlar.length > 0 ? `${adlar.join(", ")} ve ${son}` : son;
  return {
    izin: false,
    kilitliAlanlar,
    mesaj: `${liste.charAt(0).toLocaleUpperCase("tr")}${liste.slice(1)} art\u0131k d\xFCzeltmeyle de\u011Fi\u015Ftirilemez: fatura onay zincirinde en az bir kap\u0131dan ge\xE7ti ve o onay faturan\u0131n \u015Fu anki h\xE2line verildi. Faturay\u0131 reddettirin, sonra \xABD\xFCzelt ya da yan\u0131tla\xBB ile yeniden sunun \u2014 o yol onay\u0131 ilk kap\u0131dan yeniden ba\u015Flat\u0131r.`
  };
}
var INVOICE_DECISIONS = [
  "APPROVED",
  "REJECTED",
  "RESUBMITTED",
  /** Onaycı sordu — reddetmedi. */
  "INFO_REQUESTED",
  /** Talebi açan cevapladı; fatura kaldığı kapıya döndü. */
  "INFO_PROVIDED",
  /** Soran soruyu geri aldı; cevap artık beklenmiyor. */
  "INFO_WITHDRAWN",
  /**
   * Karar değil, ADIM — 'RESUBMITTED' ile aynı gerekçe. Karara bağlanmamış bir
   * fatura düzeltildiğinde yazılır; olmasaydı düzeltme, zaman çizelgesinde hiç
   * görünmeyen sessiz bir yeniden yazma olurdu.
   */
  "REVISED",
  /**
   * Reddedilmiş fatura listeden kaldırıldı (DST-00122). Karar değil, ADIM:
   * ret yerinde duruyor, yalnız fatura varsayılan listeden çekiliyor.
   */
  "WITHDRAWN"
];
var INVOICE_DECISION_LABELS = {
  APPROVED: "Onayland\u0131",
  REJECTED: "Reddedildi",
  RESUBMITTED: "D\xFCzeltilip yeniden sunuldu",
  INFO_REQUESTED: "Ek a\xE7\u0131klama istendi",
  INFO_PROVIDED: "Ek a\xE7\u0131klama verildi",
  INFO_WITHDRAWN: "Ek a\xE7\u0131klama sorusu geri al\u0131nd\u0131",
  REVISED: "D\xFCzeltildi",
  WITHDRAWN: "Listeden kald\u0131r\u0131ld\u0131"
};
var INVOICE_QUESTION_TARGETS = ["OWNER", "STAGE"];
var INVOICE_QUESTION_DECISION_LABELS = {
  OWNER: {
    INFO_REQUESTED: "Ek a\xE7\u0131klama istendi",
    INFO_PROVIDED: "Ek a\xE7\u0131klama verildi",
    INFO_WITHDRAWN: "Ek a\xE7\u0131klama sorusu geri al\u0131nd\u0131"
  },
  STAGE: {
    INFO_REQUESTED: "Onay ad\u0131m\u0131na soru soruldu",
    INFO_PROVIDED: "Onay ad\u0131m\u0131 soruyu cevaplad\u0131",
    INFO_WITHDRAWN: "Onay ad\u0131m\u0131na sorulan soru geri al\u0131nd\u0131"
  }
};
var INVOICE_FIELD_LABELS = {
  invoiceNo: "Fatura no",
  invoiceDate: "Fatura tarihi",
  amount: "Tutar",
  description: "A\xE7\u0131klama",
  responsibleUserId: "\u0130lgili ki\u015Fi",
  payeeId: "Cari",
  currency: "Para birimi",
  /**
   * Boyutlar da burada olmak zorunda: `INVOICE_FIELD_LABELS[f] ?? f` düşünce
   * kullanıcı düzeltme geçmişinde ham `counterpartyCompanyId` okuyor.
   * `projectId` #344'te atlanmıştı; aynı satırda duruyorlar çünkü aynı
   * eşleyiciden geçiyorlar.
   */
  orgUnitId: "\u0130\u015F birimi",
  costCenterId: "Masraf merkezi",
  categoryId: "Kategori",
  projectId: "Proje / operasyon",
  counterpartyCompanyId: "Grup i\xE7i kar\u015F\u0131 \u015Firket",
  intercompanyReference: "E\u015Fle\u015Ftirme referans\u0131",
  purposeId: "Ne i\xE7in al\u0131nd\u0131",
  purposeNote: "Ne i\xE7in al\u0131nd\u0131 \u2014 a\xE7\u0131klama"
};
var linkInvoiceProformaSchema = z25.object({ payRequestId: z25.uuid() });
var PROFORMA_ESLESMEZ_DURUMLAR = [
  "DRAFT",
  "REJECTED",
  "CANCELLED",
  "MANAGER_REJECTED"
];
var createReturnInvoiceRequestSchema = z25.object({
  companyId: z25.uuid(),
  payeeId: z25.uuid(),
  orgUnitId: z25.uuid(),
  amountText: z25.string().trim().min(1).max(32),
  currency: z25.string().length(3).default("TRY"),
  /** İade edilen faturanın numarası — karşı tarafın bize kestiği. İsteğe bağlı. */
  returnedInvoiceNo: z25.string().trim().max(100).optional(),
  /** Neden iade: «tutara itiraz edildi», «hizmet alınmadı». */
  reason: z25.string().trim().min(5).max(400)
});
var convertToReturnInvoiceSchema = z25.object({
  payeeId: z25.uuid().optional(),
  orgUnitId: z25.uuid().optional()
});
var IADEYE_CEVRILEBILIR_DURUMLAR = [
  "DRAFT",
  "PENDING_APPROVAL",
  "RETURNED_TO_REQUESTER",
  "DEFERRED",
  "APPROVED"
];
var IADEYE_CEVRILEBILIR_BELGELER = ["E_INVOICE", "E_ARCHIVE", "PROFORMA"];
function iadeTalebineCevrilebilir(input) {
  return (input.talebiAcan || input.finansMasasi) && IADEYE_CEVRILEBILIR_DURUMLAR.includes(input.status) && IADEYE_CEVRILEBILIR_BELGELER.includes(input.documentType) && !(input.paidAmount > 0);
}
function iadeTalebiAciklamasi(input) {
  const no = input.returnedInvoiceNo?.trim();
  const metin = `\u0130ade faturas\u0131 talebi${no ? ` (${no} numaral\u0131 faturaya)` : ""} \u2014 ${input.reason.trim()}`;
  return metin.slice(0, 500);
}

// src/dto/invoice-dimensions.ts
function boyutSecilebilir(secenek, faturaCompanyId, mevcutDeger) {
  if (secenek.companyId != null && secenek.companyId !== faturaCompanyId) return false;
  if (secenek.isActive === false) return secenek.id === mevcutDeger;
  return true;
}
function secilebilirBoyutlar(secenekler, faturaCompanyId, mevcutDeger) {
  if (!secenekler) return [];
  return secenekler.filter((s) => boyutSecilebilir(s, faturaCompanyId, mevcutDeger));
}
function kategoriSecilebilir(kategori, mevcutDeger) {
  if (kategori.id === mevcutDeger) return true;
  if (kategori.isDerived) return false;
  return kategori.isActive !== false;
}
var KURUS = 5e-3;
function dagitimGecerli(satirlar, faturaTutari) {
  if (satirlar.length === 0) return { gecerli: true };
  for (const s of satirlar) {
    if (!(s.amount > 0)) return { gecerli: false, kod: "TUTAR_SIFIR_VEYA_NEGATIF" };
    if (!s.orgUnitId && !s.costCenterId && !s.categoryId) {
      return { gecerli: false, kod: "BOS_SATIR" };
    }
  }
  const toplam = satirlar.reduce((a, s) => a + s.amount, 0);
  const fark = Math.round((toplam - faturaTutari) * 100) / 100;
  if (Math.abs(fark) > KURUS) return { gecerli: false, kod: "TOPLAM_UYUSMUYOR", fark };
  return { gecerli: true };
}
function etkinBoyutlar(fatura, dagitim = []) {
  if (dagitim.length > 0) return dagitim;
  return [
    {
      amount: fatura.amount,
      orgUnitId: fatura.orgUnitId ?? null,
      costCenterId: fatura.costCenterId ?? null,
      categoryId: fatura.categoryId ?? null
    }
  ];
}
function boyutYamasi(girdi, mevcut) {
  const alanlar = [
    "orgUnitId",
    "costCenterId",
    "categoryId",
    "projectId",
    "counterpartyCompanyId"
  ];
  const sonuc = { degisen: [], yama: {} };
  for (const alan of alanlar) {
    const yeni = girdi[alan];
    if (yeni === void 0) continue;
    const normal = yeni ?? null;
    if (normal === (mevcut[alan] ?? null)) continue;
    sonuc.degisen.push(alan);
    sonuc.yama[alan] = normal;
  }
  return sonuc;
}
function aramaNormalize(metin) {
  return metin.replace(/[İIı]/g, "i").replace(/[Şş]/g, "s").replace(/[Ğğ]/g, "g").replace(/[Üü]/g, "u").replace(/[Öö]/g, "o").replace(/[Çç]/g, "c").toLocaleLowerCase("en-US").trim();
}
function secenekFiltrele(secenekler, sorgu) {
  if (!secenekler) return [];
  const q = aramaNormalize(sorgu);
  if (!q) return secenekler;
  return secenekler.filter(
    (s) => [s.name, s.code, s.description].some(
      (alan) => alan ? aramaNormalize(alan).includes(q) : false
    )
  );
}

// src/dto/intercompany.ts
function ayniKonsolidasyonAgaci(a, b) {
  const kokA = a.consolidationRootId ?? null;
  const kokB = b.consolidationRootId ?? null;
  if (kokA === null || kokB === null) return true;
  return kokA === kokB;
}
function karsiSirketSecilebilir(sirket, islemSirketi, mevcutDeger) {
  if (sirket.id === islemSirketi.id) return false;
  if (sirket.id === mevcutDeger) return true;
  if (sirket.isActive === false) return false;
  return ayniKonsolidasyonAgaci(sirket, islemSirketi);
}
function secilebilirKarsiSirketler(sirketler, islemSirketi, mevcutDeger) {
  return sirketler.filter((s) => karsiSirketSecilebilir(s, islemSirketi, mevcutDeger));
}
function karsiSirketEtiketi(sirket) {
  return sirket.isActive === false ? "Pasif \u2014 ge\xE7mi\u015F kay\u0131t" : null;
}
function grupIciIslem(kayit) {
  return Boolean(kayit.counterpartyCompanyId);
}
function grupIciReferansNormalize(ham) {
  if (ham === null || ham === void 0) return null;
  const temiz = ham.trim().toUpperCase();
  return temiz.length > 0 ? temiz : null;
}

// src/dto/project-dimension.ts
var PROJECT_STATUSES = ["DRAFT", "ACTIVE", "ON_HOLD", "COMPLETED", "CANCELLED"];
var PROJECT_STATUS_LABELS = {
  DRAFT: "Taslak",
  ACTIVE: "Aktif",
  ON_HOLD: "Beklemede",
  COMPLETED: "Tamamland\u0131",
  CANCELLED: "\u0130ptal"
};
var YENI_ISLEME_ACIK = ["DRAFT", "ACTIVE"];
function projeSecilebilir(proje, islemCompanyId, mevcutDeger) {
  if (proje.id === mevcutDeger) return true;
  if (proje.companyId !== islemCompanyId) return false;
  if (proje.isActive === false) return false;
  return YENI_ISLEME_ACIK.includes(proje.status);
}
function secilebilirProjeler(projeler, islemCompanyId, mevcutDeger) {
  if (!projeler) return [];
  return projeler.filter((p) => projeSecilebilir(p, islemCompanyId, mevcutDeger));
}
function projeDurumEtiketi(proje) {
  if (proje.isActive === false) return "Pasif \u2014 ge\xE7mi\u015F kay\u0131t";
  if (YENI_ISLEME_ACIK.includes(proje.status)) return null;
  return `${PROJECT_STATUS_LABELS[proje.status]} \u2014 ge\xE7mi\u015F kay\u0131t`;
}
function disKayitAnahtari(p) {
  const sys = p.externalSystem?.trim();
  const id = p.externalId?.trim();
  if (!sys || !id) return null;
  return `${p.companyId}:${sys}:${id}`;
}

// src/dto/project.ts
import { z as z26 } from "zod";
var createProjectTypeSchema = z26.object({
  code: z26.string().trim().min(2).max(40).toUpperCase(),
  name: z26.string().trim().min(2).max(160),
  sortOrder: z26.coerce.number().int().min(0).max(9999).optional(),
  note: z26.string().trim().max(300).nullish()
});
var updateProjectTypeSchema = z26.object({
  name: z26.string().trim().min(2).max(160).optional(),
  sortOrder: z26.coerce.number().int().min(0).max(9999).optional(),
  isActive: z26.boolean().optional(),
  note: z26.string().trim().max(300).nullish()
});
var PROJECT_SORT_FIELDS = ["code", "name", "startDate", "createdAt"];
var projectListQuerySchema = sortableQuerySchema.extend({
  companyId: z26.uuid().optional(),
  typeId: z26.uuid().optional(),
  status: z26.enum(PROJECT_STATUSES).optional(),
  responsibleUserId: z26.uuid().optional(),
  /** Başlangıcı bu tarihten sonra / bitişi bu tarihten önce olanlar. */
  startFrom: z26.iso.date().optional(),
  endTo: z26.iso.date().optional(),
  /** 'true' | 'false' — verilmezse ikisi de gelir. */
  isActive: z26.enum(["true", "false"]).optional()
});
var projectBody = {
  code: z26.string().trim().min(2).max(40).toUpperCase(),
  name: z26.string().trim().min(2).max(300),
  typeId: z26.uuid(),
  status: z26.enum(PROJECT_STATUSES).optional(),
  orgUnitId: z26.uuid().nullish(),
  costCenterId: z26.uuid().nullish(),
  responsibleUserId: z26.uuid().nullish(),
  startDate: z26.iso.date().nullish(),
  endDate: z26.iso.date().nullish(),
  payeeId: z26.uuid().nullish(),
  currency: z26.string().trim().length(3).optional(),
  budgetAmount: z26.coerce.number().min(0).max(1e12).nullish(),
  externalSystem: z26.string().trim().max(60).nullish(),
  externalId: z26.string().trim().max(64).nullish(),
  note: z26.string().trim().max(2e3).nullish()
};
var createProjectSchema = z26.object({
  companyId: z26.uuid(),
  ...projectBody
});
var updateProjectSchema = z26.object({
  code: projectBody.code.optional(),
  name: projectBody.name.optional(),
  typeId: projectBody.typeId.optional(),
  status: z26.enum(PROJECT_STATUSES).optional(),
  orgUnitId: projectBody.orgUnitId,
  costCenterId: projectBody.costCenterId,
  responsibleUserId: projectBody.responsibleUserId,
  startDate: projectBody.startDate,
  endDate: projectBody.endDate,
  payeeId: projectBody.payeeId,
  currency: projectBody.currency,
  budgetAmount: projectBody.budgetAmount,
  externalSystem: projectBody.externalSystem,
  externalId: projectBody.externalId,
  note: projectBody.note,
  isActive: z26.boolean().optional()
});

// src/dto/agenda.ts
import { z as z27 } from "zod";
var AGENDA_KINDS = ["TASK", "MEETING", "REMINDER", "NOTE"];
var AGENDA_KIND_LABELS = {
  TASK: "G\xF6rev",
  MEETING: "Toplant\u0131",
  REMINDER: "Hat\u0131rlatma",
  NOTE: "Not"
};
var AGENDA_ACTIONABLE_KINDS = ["TASK", "REMINDER"];
var AGENDA_PRIORITIES = ["LOW", "NORMAL", "HIGH", "CRITICAL"];
var AGENDA_PRIORITY_LABELS = {
  LOW: "D\xFC\u015F\xFCk",
  NORMAL: "Normal",
  HIGH: "Y\xFCksek",
  CRITICAL: "Kritik"
};
var AGENDA_STATUSES = ["PLANNED", "IN_PROGRESS", "DONE", "DECLINED", "CANCELLED"];
var AGENDA_STATUS_LABELS = {
  PLANNED: "Planland\u0131",
  IN_PROGRESS: "Devam ediyor",
  DONE: "Tamamland\u0131",
  DECLINED: "Reddedildi",
  CANCELLED: "\u0130ptal edildi"
};
var AGENDA_CLOSED_STATUSES = ["DONE", "DECLINED", "CANCELLED"];
var AGENDA_RSVPS = ["PENDING", "ACCEPTED", "TENTATIVE", "DECLINED"];
var AGENDA_RSVP_LABELS = {
  PENDING: "Yan\u0131t bekliyor",
  ACCEPTED: "Kat\u0131l\u0131yor",
  TENTATIVE: "Belki",
  DECLINED: "Kat\u0131lm\u0131yor"
};
var AGENDA_RSVP_ANSWERS = ["ACCEPTED", "TENTATIVE", "DECLINED"];
var AGENDA_RSVP_NEEDS_TELLING = ["TENTATIVE", "DECLINED"];
function closingDropsReminder(item, next) {
  if (!AGENDA_CLOSED_STATUSES.includes(next)) return false;
  if (AGENDA_CLOSED_STATUSES.includes(item.status)) return false;
  return reminderPending(item);
}
function reminderPending(item) {
  return item.remindAt !== null && item.remindedAt === null;
}
function droppedReminderNotice(when) {
  return `Bu kayd\u0131n ${when} tarihli hat\u0131rlatmas\u0131 hen\xFCz g\xF6nderilmedi; kay\u0131t kapand\u0131\u011F\u0131nda art\u0131k g\xF6nderilmeyecek.`;
}
function reminderTimeMissing(kind, remindAt) {
  return kind === "REMINDER" && !remindAt;
}
var REMINDER_TIME_REQUIRED = "Hat\u0131rlatma t\xFCr\xFCndeki bir kay\u0131t hat\u0131rlatma saati olmadan kaydedilemez.";
var ALL_DAY_REMINDER_MS = 9 * 60 * 60 * 1e3;
function suggestedRemindAt(kind, startAt, allDay) {
  if (kind !== "REMINDER") return null;
  return allDay ? new Date(startAt.getTime() + ALL_DAY_REMINDER_MS) : startAt;
}
var AGENDA_STATUS_TRANSITIONS = {
  PLANNED: ["IN_PROGRESS", "DONE", "DECLINED", "CANCELLED"],
  IN_PROGRESS: ["DONE", "PLANNED", "DECLINED", "CANCELLED"],
  DONE: ["IN_PROGRESS"],
  DECLINED: [],
  CANCELLED: []
};
var AGENDA_RECURRENCES = ["NONE", "DAILY", "WEEKLY", "MONTHLY"];
var AGENDA_RECURRENCE_LABELS = {
  NONE: "Tekrar yok",
  DAILY: "Her g\xFCn",
  WEEKLY: "Her hafta",
  MONTHLY: "Her ay"
};
function shiftedReminder(remindAt, fromStart, toStart) {
  if (remindAt === null) return null;
  const shift = toStart.getTime() - fromStart.getTime();
  return shift === 0 ? remindAt : new Date(remindAt.getTime() + shift);
}
var AGENDA_STATUSES_NEEDING_REASON = ["DECLINED", "CANCELLED"];
var AGENDA_VIEWS = ["mine", "assigned", "delegated", "oversee"];
var AGENDA_RANGE_MAX_DAYS = 400;
var isoDateTime = z27.iso.datetime({ offset: true });
var createAgendaItemSchema = z27.object({
  kind: z27.enum(AGENDA_KINDS).default("TASK"),
  title: z27.string().trim().min(3).max(200),
  description: z27.string().trim().max(4e3).optional(),
  priority: z27.enum(AGENDA_PRIORITIES).default("NORMAL"),
  startAt: isoDateTime,
  /**
   * Bitiş. Toplantı için "ne zaman bitiyor", görev için "en geç ne zaman".
   *
   * Tek sütun, iki okuma — ve bu bilerek böyle. İkiye ayırmak, her formda
   * ikisinden birinin boş bırakıldığı ve hiçbir ekranın hangisine bakacağını
   * bilemediği iki sütun demekti.
   */
  dueAt: isoDateTime.optional(),
  /** Saat taşımayan kayıt: takvimde güne yayılır, listede saat göstermez. */
  allDay: z27.boolean().default(false),
  /**
   * Ne zaman haber verilsin. Boşsa haber verilmez — sessiz bir not meşrudur.
   *
   * HATIRLATMA türünde meşru değil ve şema onu reddediyor; bkz.
   * `reminderTimeMissing`.
   */
  remindAt: isoDateTime.optional(),
  recurrence: z27.enum(AGENDA_RECURRENCES).default("NONE"),
  /**
   * Görevlendirme. Boşsa kayıt yazanın kendi ajandasına düşer.
   *
   * Ayrı bir uç değil de burada bir alan olmasının sebebi: bir işi verirken
   * onu önce kendine yazıp sonra devretmek zorunda kalmak, iki adımda tek
   * bir niyeti anlatmaktır.
   */
  assigneeUserId: z27.uuid().optional(),
  /**
   * Bildirim gidecek kişiler — kaydın SAHİBİ DEĞİL, çağrılanlar.
   *
   * Toplantının bir sahibi ve birden çok katılanı olur; görevin bir
   * sorumlusu. Sahiplik `assigneeUserId`'de kalıyor ve buraya taşınmıyor:
   * "benim ajandam" sorgusu tek indeks araması olmayı sürdürmeli.
   *
   * Yirmi kişilik üst sınır bir kapasite değil bir niyet sınırı: yirmi
   * kişiye çağrı, ajanda kaydı değil duyurudur ve bu modül duyuru
   * göndermiyor.
   */
  participantUserIds: z27.array(z27.uuid()).max(20).optional()
}).refine((v) => !v.dueAt || v.dueAt >= v.startAt, {
  message: "Biti\u015F, ba\u015Flang\u0131\xE7tan \xF6nce olamaz.",
  path: ["dueAt"]
}).refine((v) => !reminderTimeMissing(v.kind, v.remindAt), {
  message: REMINDER_TIME_REQUIRED,
  path: ["remindAt"]
});
var updateAgendaItemSchema = z27.object({
  title: z27.string().trim().min(3).max(200).optional(),
  /** null: açıklamayı sil. */
  description: z27.string().trim().max(4e3).nullable().optional(),
  kind: z27.enum(AGENDA_KINDS).optional(),
  priority: z27.enum(AGENDA_PRIORITIES).optional(),
  startAt: isoDateTime.optional(),
  dueAt: isoDateTime.nullable().optional(),
  allDay: z27.boolean().optional(),
  remindAt: isoDateTime.nullable().optional(),
  recurrence: z27.enum(AGENDA_RECURRENCES).optional()
}).refine((v) => Object.keys(v).length > 0, { message: "En az bir alan gerekli." });
var changeAgendaStatusSchema = z27.object({
  status: z27.enum(AGENDA_STATUSES),
  /** REDDEDİLDİ ve İPTAL için zorunlu; gerisi için serbest bir not. */
  reason: z27.string().trim().min(3).max(500).optional()
});
var assignAgendaItemSchema = z27.object({
  /** null: işi geri al — kayıt yazanın kendi ajandasına döner. */
  assigneeUserId: z27.uuid().nullable(),
  /** Devrederken yazılan söz; yorum olarak kaydın altına düşer. */
  note: z27.string().trim().max(1e3).optional()
});
var addAgendaCommentSchema = z27.object({
  body: z27.string().trim().min(1).max(2e3)
});
var agendaListQuerySchema = sortableQuerySchema.extend({
  view: z27.enum(AGENDA_VIEWS).default("mine"),
  /** Takvim penceresi. Verilmezse tarih sınırı uygulanmaz. */
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
  kind: z27.enum(AGENDA_KINDS).optional(),
  status: z27.enum(AGENDA_STATUSES).optional(),
  priority: z27.enum(AGENDA_PRIORITIES).optional(),
  assigneeUserId: z27.uuid().optional(),
  /**
   * Kapanmışları da göster. Varsayılan: yalnızca açık kayıtlar.
   *
   * Takvim bu bayrağı AÇIK gönderir, listeler göndermez: bir listenin ilk
   * ekranı "yapılacak olan" olmalı, ama bir takvim geçmiş bir günü boş
   * gösteremez — o gün gerçekten oldu.
   */
  includeClosed: z27.coerce.boolean().optional(),
  /**
   * Yalnızca süresi geçmiş açık kayıtlar.
   *
   * İstemcide süzmek yerine burada, çünkü gecikme sayfalama ile çakışır:
   * sayfanın ilk yirmi kaydından üçü gecikmişse, ekranda "3 geciken" yazarken
   * listede yirmi satır durur — ve sekmedeki rozet başka bir sayı söyler.
   */
  overdue: z27.coerce.boolean().optional()
});
var AGENDA_SORT_FIELDS = ["startAt", "dueAt", "priority", "createdAt", "title"];
var respondAgendaRsvpSchema = z27.object({
  rsvp: z27.enum(AGENDA_RSVP_ANSWERS),
  /** Boş dize null gibi davranır: form temizlenince not da silinmeli. */
  note: z27.string().trim().max(500).nullable().optional()
});
var setAgendaParticipantsSchema = z27.object({
  userIds: z27.array(z27.uuid()).max(20)
});

// src/dto/asset.ts
import { z as z28 } from "zod";
var ASSET_KINDS = [
  "PHONE",
  "LAPTOP",
  "DESKTOP",
  "TABLET",
  "MONITOR",
  "PERIPHERAL",
  "SIM",
  "ACCESS_CARD",
  "KEY",
  "SAFETY",
  "UNIFORM",
  "HAND_TOOL",
  "MEASURING",
  "AVIATION_TOOL",
  "VEHICLE",
  "FURNITURE",
  "TOOL",
  "OTHER"
];
var ASSET_KIND_LABELS = {
  PHONE: "Telefon",
  LAPTOP: "Diz\xFCst\xFC bilgisayar",
  DESKTOP: "Masa\xFCst\xFC bilgisayar",
  TABLET: "Tablet",
  MONITOR: "Monit\xF6r",
  PERIPHERAL: "\xC7evre birimi",
  SIM: "Hat / SIM",
  ACCESS_CARD: "Giri\u015F kart\u0131",
  KEY: "Anahtar",
  SAFETY: "Ki\u015Fisel koruyucu ekipman",
  UNIFORM: "\u0130\u015F k\u0131yafeti",
  HAND_TOOL: "El aleti",
  MEASURING: "\xD6l\xE7\xFCm / test cihaz\u0131",
  AVIATION_TOOL: "Havac\u0131l\u0131k bak\u0131m tak\u0131m\u0131",
  VEHICLE: "Ara\xE7",
  FURNITURE: "Ofis demirba\u015F\u0131",
  TOOL: "Alet / ekipman",
  OTHER: "Di\u011Fer"
};
var ASSET_STATUSES = [
  "IN_STOCK",
  "ASSIGNED",
  "UNIT_ASSIGNED",
  "AWAITING_RETURN",
  "IN_MAINTENANCE",
  "IN_CALIBRATION",
  "DAMAGED",
  "LOST",
  "SCRAPPED",
  "SOLD",
  "RETIRED"
];
var ASSET_STATUS_LABELS = {
  IN_STOCK: "Kullan\u0131labilir",
  ASSIGNED: "Zimmetli",
  UNIT_ASSIGNED: "Birime zimmetli",
  AWAITING_RETURN: "\u0130ade bekliyor",
  IN_MAINTENANCE: "Bak\u0131mda",
  IN_CALIBRATION: "Kalibrasyonda",
  DAMAGED: "Hasarl\u0131",
  LOST: "Kay\u0131p",
  SCRAPPED: "Hurda",
  SOLD: "Sat\u0131ld\u0131 / devredildi",
  RETIRED: "Pasif"
};
var ASSET_TRACKINGS = ["UNIQUE", "QUANTITY"];
var ASSET_TRACKING_LABELS = {
  UNIQUE: "Tekil (seri numaral\u0131)",
  QUANTITY: "Miktar bazl\u0131"
};
var ASSET_CONDITIONS = ["NEW", "GOOD", "FAIR", "POOR", "DAMAGED"];
var ASSET_CONDITION_LABELS = {
  NEW: "S\u0131f\u0131r",
  GOOD: "\u0130yi",
  FAIR: "Orta",
  POOR: "K\xF6t\xFC",
  DAMAGED: "Hasarl\u0131"
};
var ASSET_UNITS = ["ADET", "TAKIM", "CIFT", "KUTU", "METRE", "KG", "LITRE"];
var ASSET_UNIT_LABELS = {
  ADET: "Adet",
  TAKIM: "Tak\u0131m",
  CIFT: "\xC7ift",
  KUTU: "Kutu",
  METRE: "Metre",
  KG: "Kilogram",
  LITRE: "Litre"
};
var CALIBRATION_RESULTS = ["PASS", "FAIL", "LIMITED"];
var CALIBRATION_RESULT_LABELS = {
  PASS: "Kabul",
  FAIL: "Ret",
  LIMITED: "\u015Eartl\u0131 kabul"
};
var MAINTENANCE_KINDS = ["MAINTENANCE", "REPAIR"];
var MAINTENANCE_KIND_LABELS = {
  MAINTENANCE: "Periyodik bak\u0131m",
  REPAIR: "Onar\u0131m"
};
var CUSTODY_HOLDER_KINDS = ["EMPLOYEE", "ORG_UNIT", "LOCATION"];
var CUSTODY_HOLDER_KIND_LABELS = {
  EMPLOYEE: "Personel",
  ORG_UNIT: "Birim / tak\u0131m",
  LOCATION: "Lokasyon"
};
var CUSTODY_STATUSES = ["ACTIVE", "CLOSED", "CANCELLED"];
var CUSTODY_STATUS_LABELS = {
  ACTIVE: "A\xE7\u0131k",
  CLOSED: "Kapand\u0131",
  CANCELLED: "\u0130ptal"
};
var ASSET_MOVEMENT_KINDS = [
  "REGISTERED",
  "ASSIGN",
  "ACCEPT",
  "RETURN_REQUESTED",
  "RETURN",
  "PARTIAL_RETURN",
  "TRANSFER",
  "DAMAGE",
  "LOSS",
  "MAINTENANCE_OUT",
  "MAINTENANCE_IN",
  "CALIBRATION",
  "SCRAP",
  "SOLD",
  "EXCEPTION",
  "CANCEL"
];
var ASSET_MOVEMENT_LABELS = {
  REGISTERED: "K\xFCt\xFC\u011Fe eklendi",
  ASSIGN: "Zimmet verildi",
  ACCEPT: "Teslim al\u0131nd\u0131 beyan\u0131",
  RETURN_REQUESTED: "\u0130ade istendi",
  RETURN: "\u0130ade al\u0131nd\u0131",
  PARTIAL_RETURN: "K\u0131smi iade al\u0131nd\u0131",
  TRANSFER: "Devredildi",
  DAMAGE: "Hasar kaydedildi",
  LOSS: "Kay\u0131p bildirildi",
  MAINTENANCE_OUT: "Bak\u0131ma g\xF6nderildi",
  MAINTENANCE_IN: "Bak\u0131mdan d\xF6nd\xFC",
  CALIBRATION: "Kalibrasyon kaydedildi",
  SCRAP: "Hurdaya ayr\u0131ld\u0131",
  SOLD: "Sat\u0131ld\u0131 / devredildi",
  EXCEPTION: "\u0130stisna uyguland\u0131",
  CANCEL: "\u0130ptal edildi (ters kay\u0131t)"
};
var ASSET_ATTACHMENT_KINDS = [
  "PHOTO",
  "INVOICE",
  "CERTIFICATE",
  "HANDOVER",
  "OTHER"
];
var ASSET_ATTACHMENT_KIND_LABELS = {
  PHOTO: "Foto\u011Fraf",
  INVOICE: "Fatura",
  CERTIFICATE: "Sertifika",
  HANDOVER: "\u0130mzal\u0131 tutanak",
  OTHER: "Di\u011Fer"
};
var ASSET_ACCEPT_STATEMENT = "Yukar\u0131da listelenen \u015Firkete ait malzemeyi eksiksiz ve belirtilen durumda teslim ald\u0131\u011F\u0131m\u0131, kullan\u0131m ve iade \u015Fartlar\u0131n\u0131 kabul etti\u011Fimi beyan ederim.";
var isoDate7 = z28.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Tarih YYYY-AA-GG bi\xE7iminde olmal\u0131." });
var qty = z28.coerce.number().positive().max(1e6);
var note = z28.string().trim().max(2e3);
var reason = z28.string().trim().min(10, "Gerek\xE7e en az 10 karakter olmal\u0131.").max(2e3);
var crossReason = z28.string().trim().min(10, "\u015Eirketler aras\u0131 zimmet gerek\xE7esi en az 10 karakter olmal\u0131.").max(500);
var cost = moneyText({ min: 0, max: 99999999999 });
var assetCategoryCreateSchema = z28.object({
  companyId: z28.uuid(),
  parentId: z28.uuid().optional(),
  code: z28.string().trim().min(1).max(40).regex(/^[A-Z0-9_-]+$/, { message: "Kod yaln\u0131z b\xFCy\xFCk harf, rakam, _ ve - i\xE7erebilir." }),
  name: z28.string().trim().min(1).max(200),
  defaultRequiresCalibration: z28.boolean().optional(),
  defaultMaintenancePeriodDays: z28.coerce.number().int().positive().max(3650).optional(),
  sortOrder: z28.coerce.number().int().min(0).max(9999).optional()
});
var assetCategoryUpdateSchema = assetCategoryCreateSchema.omit({ companyId: true, parentId: true }).partial().extend({ isActive: z28.boolean().optional() });
var serialNo = z28.string().trim().min(1).max(120);
var assetCreateSchema = z28.object({
  companyId: z28.uuid(),
  kind: z28.enum(ASSET_KINDS),
  name: z28.string().trim().min(1).max(200),
  brand: z28.string().trim().max(80).optional(),
  model: z28.string().trim().max(80).optional(),
  /**
   * Seri numarası / IMEI — aynı model iki cihazı ayıran tek şey, ama ZORUNLU
   * DEĞİL: anahtar ve kart da zimmetleniyor ve onların numarası yok. Yazıldığı
   * yerde şirket içinde tekil (kısmi indeks).
   */
  serialNo: serialNo.optional(),
  note: note.optional(),
  categoryId: z28.uuid().optional(),
  orgUnitId: z28.uuid().optional(),
  locationId: z28.uuid().optional(),
  partNo: z28.string().trim().max(80).optional(),
  stockNo: z28.string().trim().max(60).optional(),
  tracking: z28.enum(ASSET_TRACKINGS).optional(),
  quantity: qty.optional(),
  unit: z28.enum(ASSET_UNITS).optional(),
  purchasedOn: isoDate7.optional(),
  supplierPayeeId: z28.uuid().optional(),
  invoiceNo: z28.string().trim().max(60).optional(),
  purchaseCost: cost.optional(),
  currency: z28.string().trim().length(3).toUpperCase().optional(),
  warrantyStart: isoDate7.optional(),
  warrantyEnd: isoDate7.optional(),
  maintenancePeriodDays: z28.coerce.number().int().positive().max(3650).optional(),
  requiresCalibration: z28.boolean().optional(),
  condition: z28.enum(ASSET_CONDITIONS).optional(),
  description: z28.string().trim().max(4e3).optional()
});
var assetUpdateSchema = assetCreateSchema.omit({ companyId: true }).partial().extend({
  version: z28.coerce.number().int().min(1),
  isActive: z28.boolean().optional()
});
var assetDeleteSchema = z28.object({
  version: z28.coerce.number().int().min(1)
});
var ASSET_SORT_FIELDS = [
  "code",
  "name",
  "status",
  "createdAt",
  "calibrationDueOn"
];
var assetListQuerySchema = sortableQuerySchema.extend({
  companyId: z28.uuid().optional(),
  status: z28.enum(ASSET_STATUSES).optional(),
  kind: z28.enum(ASSET_KINDS).optional(),
  categoryId: z28.uuid().optional(),
  orgUnitId: z28.uuid().optional(),
  locationId: z28.uuid().optional(),
  employeeId: z28.uuid().optional(),
  tracking: z28.enum(ASSET_TRACKINGS).optional(),
  /** Kalibrasyonu bu tarihe kadar dolan / dolmuş olanlar. */
  calibrationDueBefore: isoDate7.optional(),
  overdueOnly: z28.enum(["true"]).optional(),
  /** Kalibrasyonu geçmiş ya da reddedilmiş — yani zimmetlenemeyecek olanlar. */
  calibrationBlockedOnly: z28.enum(["true"]).optional(),
  includeInactive: z28.enum(["true"]).optional()
});
var custodyLineSchema = z28.object({
  assetId: z28.uuid(),
  quantity: qty.optional(),
  conditionOut: z28.enum(ASSET_CONDITIONS).optional(),
  accessoriesOut: z28.string().trim().max(1e3).optional(),
  note: note.optional()
});
var custodyCreateSchema = z28.object({
  companyId: z28.uuid(),
  holderKind: z28.enum(CUSTODY_HOLDER_KINDS),
  /** EMPLOYEE'de bir ya da daha fazla kişi; diğer kiplerde boş. */
  employeeIds: z28.array(z28.uuid()).max(50).optional(),
  orgUnitId: z28.uuid().optional(),
  locationId: z28.uuid().optional(),
  lines: z28.array(custodyLineSchema).min(1).max(200),
  issuedOn: isoDate7,
  dueOn: isoDate7.optional(),
  openEnded: z28.boolean().optional(),
  terms: z28.string().trim().max(4e3).optional(),
  note: note.optional(),
  /**
   * Kalibrasyon engelini AŞMAK için gerekçe. Yalnız
   * `asset.calibration_override` taşıyan çağıran gönderebilir, ve gönderilse
   * bile engel yoksa yok sayılır — istisna kaydı ancak gerçekten kural
   * delindiğinde düşer.
   */
  calibrationOverrideReason: reason.optional(),
  /**
   * ŞİRKET SINIRINI AŞMAK için gerekçe.
   *
   * Sunucu bunu ancak personelin şirketi varlığın şirketinden FARKLIYSA
   * istiyor; aynı şirkette gönderilirse yok sayılıyor — `hr_asset_custody`
   * üzerindeki CHECK, `cross_company = false` satırında gerekçeyi YASAKLIYOR.
   * Sınır aşılmışsa boş bırakılamaz: gerekçesiz bir istisna, kuralın hiç
   * olmamasıyla aynı şey.
   *
   * 500 karakter, `reason`ın 2000'i değil: sütun `VARCHAR(500)` ve daha uzun
   * bir metin kullanıcıya 400 yerine 500 olarak dönerdi.
   */
  crossCompanyReason: crossReason.optional()
});
var CUSTODY_CANDIDATE_REASONS = [
  /** En az bir kişi teklif edilebiliyor. */
  "OK",
  /** Şirket seçilmemiş — daha varlık seçilmeden liste sorulmuş. */
  "COMPANY_REQUIRED",
  /** Varlığın şirketinde de, görülebilen başka bir şirkette de kimse yok. */
  "NONE_IN_COMPANY",
  /**
   * Varlığın şirketinde kimse yok ama BAŞKA şirketlerde var, ve çağıranda
   * `asset.assign.cross_company` yok. Kişiler İSİMSİZ kalıyor; yalnız sayı
   * dönüyor.
   */
  "CROSS_AVAILABLE_NO_PERMISSION",
  /** Varlığın şirketi çağıranın kapsamında değil — muhatap İK değil, yönetici. */
  "NONE_IN_SCOPE"
];
var CUSTODY_CANDIDATE_REASON_MESSAGES = {
  OK: "",
  COMPANY_REQUIRED: "\xD6nce varl\u0131\u011F\u0131 (ve \u015Firketini) se\xE7in.",
  NONE_IN_COMPANY: "Bu \u015Firkette zimmet verilebilecek aktif personel yok. \u0130nsan kaynaklar\u0131 ile g\xF6r\xFC\u015F\xFCn.",
  CROSS_AVAILABLE_NO_PERMISSION: 'Bu \u015Firkette aktif personel yok; grubun ba\u015Fka \u015Firketlerinde var. \u015Eirketler aras\u0131 zimmet i\xE7in "\u015Eirketler aras\u0131 zimmet verebilir" yetkisi gerekiyor.',
  NONE_IN_SCOPE: "Bu \u015Firket kapsam\u0131n\u0131zda de\u011Fil. Sistem y\xF6neticinizle g\xF6r\xFC\u015F\xFCn."
};
var custodyCandidateQuerySchema = sortableQuerySchema.extend({
  companyId: z28.uuid().optional(),
  /**
   * Diğer şirketlerin SATIRLARINI da iste.
   *
   * Varsayılan kapalı: sınır aşma yetkisi olan biri bile, her açılışta bütün
   * grubun personel listesini bir depo terminaline dökmemeli. Yetki yoksa bu
   * bayrak hiçbir şeyi açmıyor — istemcinin seçtiği bir süzgeç sınır değildir.
   */
  includeOtherCompanies: z28.enum(["true"]).optional()
});
var custodyAcceptSchema = z28.object({
  sha256: z28.string().regex(/^[0-9a-f]{64}$/, { message: "\xD6zet ge\xE7ersiz." })
});
var custodyReturnLineSchema = z28.object({
  assignmentId: z28.uuid(),
  quantity: qty.optional(),
  conditionIn: z28.enum(ASSET_CONDITIONS).optional(),
  accessoriesIn: z28.string().trim().max(1e3).optional(),
  damageNote: z28.string().trim().max(2e3).optional(),
  lossCost: cost.optional(),
  /**
   * İade edilen kalemin gideceği yer. Boş bırakılırsa SUNUCU karar veriyor:
   * hasarlı geldiyse DAMAGED, değilse IN_STOCK. "İade edildi = kullanılabilir"
   * varsayımı, hasarlı bir cihazın ertesi gün başkasına verilmesi demekti.
   */
  targetStatus: z28.enum(["IN_STOCK", "DAMAGED", "IN_MAINTENANCE", "IN_CALIBRATION"]).optional()
});
var custodyReturnSchema = z28.object({
  returnedOn: isoDate7,
  lines: z28.array(custodyReturnLineSchema).min(1).max(200).optional(),
  note: note.optional()
});
var custodyTransferSchema = z28.object({
  transferredOn: isoDate7,
  toHolderKind: z28.enum(CUSTODY_HOLDER_KINDS),
  toEmployeeIds: z28.array(z28.uuid()).max(50).optional(),
  toOrgUnitId: z28.uuid().optional(),
  toLocationId: z28.uuid().optional(),
  /** Devredilecek satırlar; verilmezse tutanağın tamamı. */
  assignmentIds: z28.array(z28.uuid()).max(200).optional(),
  dueOn: isoDate7.optional(),
  openEnded: z28.boolean().optional(),
  note: note.optional()
});
var custodyCancelSchema = z28.object({ reason });
var custodyRequestReturnSchema = z28.object({ note: note.optional() });
var assetIncidentSchema = z28.object({
  assignmentId: z28.uuid().optional(),
  assetId: z28.uuid().optional(),
  kind: z28.enum(["DAMAGE", "LOSS"]),
  occurredOn: isoDate7,
  quantity: qty.optional(),
  cost: cost.optional(),
  note
});
var CUSTODY_SORT_FIELDS = ["no", "issuedOn", "dueOn", "status"];
var custodyListQuerySchema = sortableQuerySchema.extend({
  companyId: z28.uuid().optional(),
  status: z28.enum(CUSTODY_STATUSES).optional(),
  holderKind: z28.enum(CUSTODY_HOLDER_KINDS).optional(),
  employeeId: z28.uuid().optional(),
  orgUnitId: z28.uuid().optional(),
  locationId: z28.uuid().optional(),
  assetId: z28.uuid().optional(),
  issuedFrom: isoDate7.optional(),
  issuedTo: isoDate7.optional(),
  overdueOnly: z28.enum(["true"]).optional(),
  unacceptedOnly: z28.enum(["true"]).optional()
});
var assetCalibrationSchema = z28.object({
  performedOn: isoDate7,
  nextDueOn: isoDate7.optional(),
  certificateNo: z28.string().trim().max(80).optional(),
  provider: z28.string().trim().max(200).optional(),
  result: z28.enum(CALIBRATION_RESULTS),
  deviation: z28.string().trim().max(200).optional(),
  note: note.optional()
});
var assetMaintenanceSendSchema = z28.object({
  kind: z28.enum(MAINTENANCE_KINDS),
  sentOn: isoDate7,
  dueOn: isoDate7.optional(),
  provider: z28.string().trim().max(200).optional(),
  note: note.optional()
});
var assetMaintenanceReturnSchema = z28.object({
  returnedOn: isoDate7,
  cost: cost.optional(),
  /** Bakımdan dönen kalemin gideceği yer; verilmezse IN_STOCK. */
  targetStatus: z28.enum(["IN_STOCK", "DAMAGED", "SCRAPPED"]).optional(),
  note: note.optional()
});
var assetStatusChangeSchema = z28.object({
  status: z28.enum(["SCRAPPED", "SOLD", "RETIRED", "LOST", "IN_STOCK"]),
  occurredOn: isoDate7,
  cost: cost.optional(),
  reason
});
var ASSET_REPORT_KINDS = [
  "BY_EMPLOYEE",
  "BY_ORG_UNIT",
  "BY_LOCATION",
  "OVERDUE",
  "LOST_DAMAGED",
  "MAINTENANCE_DUE",
  "CALIBRATION_OVERDUE",
  "TRANSFERS",
  "LEAVERS_WITH_ASSETS",
  "COST_BY_CATEGORY",
  "VALUE_BY_EMPLOYEE",
  "MOVEMENTS"
];
var ASSET_REPORT_LABELS = {
  BY_EMPLOYEE: "Personel baz\u0131nda aktif zimmetler",
  BY_ORG_UNIT: "Birim / tak\u0131m baz\u0131nda zimmetler",
  BY_LOCATION: "\u015Eirket ve lokasyon baz\u0131nda varl\u0131k da\u011F\u0131l\u0131m\u0131",
  OVERDUE: "Gecikmi\u015F iadeler",
  LOST_DAMAGED: "Kay\u0131p ve hasarl\u0131 varl\u0131klar",
  MAINTENANCE_DUE: "Bak\u0131m bekleyenler",
  CALIBRATION_OVERDUE: "Kalibrasyonu ge\xE7mi\u015F ekipmanlar",
  TRANSFERS: "Zimmet devir ge\xE7mi\u015Fi",
  LEAVERS_WITH_ASSETS: "\u0130\u015Ften ayr\u0131lan personelde kalan zimmetler",
  COST_BY_CATEGORY: "Kategori baz\u0131nda varl\u0131k maliyeti",
  VALUE_BY_EMPLOYEE: "Personel ba\u015F\u0131na toplam zimmet de\u011Feri",
  MOVEMENTS: "Tarih aral\u0131\u011F\u0131ndaki t\xFCm hareketler"
};
var COST_BEARING_REPORTS = [
  "COST_BY_CATEGORY",
  "VALUE_BY_EMPLOYEE"
];
var assetReportQuerySchema = z28.object({
  kind: z28.enum(ASSET_REPORT_KINDS),
  companyId: z28.uuid().optional(),
  from: isoDate7.optional(),
  to: isoDate7.optional(),
  categoryId: z28.uuid().optional(),
  orgUnitId: z28.uuid().optional(),
  locationId: z28.uuid().optional(),
  employeeId: z28.uuid().optional()
});
var assetExitOverrideSchema = z28.object({ reason });
var assetAssignSchema = z28.object({
  employeeId: z28.uuid(),
  assignedOn: isoDate7,
  note: note.optional()
});
var assetReturnSchema = z28.object({
  returnedOn: isoDate7,
  note: note.optional()
});

// src/dto/payslip.ts
import { z as z29 } from "zod";
var PAYSLIP_MONTH_LABELS = {
  1: "Ocak",
  2: "\u015Eubat",
  3: "Mart",
  4: "Nisan",
  5: "May\u0131s",
  6: "Haziran",
  7: "Temmuz",
  8: "A\u011Fustos",
  9: "Eyl\xFCl",
  10: "Ekim",
  11: "Kas\u0131m",
  12: "Aral\u0131k"
};
var payslipPeriodLabel = (year, month) => `${PAYSLIP_MONTH_LABELS[month] ?? month} ${year}`;
var periodYear = z29.coerce.number("D\xF6nem y\u0131l\u0131 okunamad\u0131.").int("D\xF6nem y\u0131l\u0131 tam say\u0131 olmal\u0131.").min(2e3, "D\xF6nem y\u0131l\u0131 2000\u2019den \xF6nce olamaz.").max(2100, "D\xF6nem y\u0131l\u0131 2100\u2019den sonra olamaz.");
var periodMonth = z29.coerce.number("D\xF6nem ay\u0131 okunamad\u0131.").int("D\xF6nem ay\u0131 tam say\u0131 olmal\u0131.").min(1, "D\xF6nem ay\u0131 1 ile 12 aras\u0131nda olmal\u0131.").max(12, "D\xF6nem ay\u0131 1 ile 12 aras\u0131nda olmal\u0131.");
var PAYSLIP_SCAN_STATUSES = ["NOT_SCANNED", "CLEAN", "INFECTED"];
var matchPayslipFilesSchema = z29.object({
  periodYear,
  periodMonth,
  /** Yalnız adlar; bu uç dosya İÇERİĞİ almıyor. */
  fileNames: z29.array(z29.string().trim().min(1).max(255)).min(1, "En az bir dosya ad\u0131 gerekli.").max(500, "Tek seferde en fazla 500 dosya e\u015Fle\u015Ftirilebilir.")
});
var PAYSLIP_MATCH_STATUSES = [
  "MATCHED",
  "NOT_MATCHED",
  "AMBIGUOUS",
  "ALREADY_EXISTS"
];
var PAYSLIP_MATCH_STATUS_LABELS = {
  MATCHED: "E\u015Fle\u015Fti",
  NOT_MATCHED: "Personel bulunamad\u0131",
  AMBIGUOUS: "Birden fazla personele uyuyor",
  ALREADY_EXISTS: "Bu d\xF6nemde bordrosu zaten var"
};
var uploadPayslipSchema = z29.object({
  employeeId: z29.string().uuid("Personel se\xE7ilmedi."),
  periodYear,
  periodMonth
});
var payslipListQuerySchema = paginationQuerySchema.extend({
  periodYear: periodYear.optional(),
  periodMonth: periodMonth.optional(),
  employeeId: z29.string().uuid().optional()
});

// src/dto/okul-tahsilat.ts
import { z as z30 } from "zod";
var OKUL_TAHSILAT_KINDS = ["SINAV", "MATERYAL"];
var OKUL_TAHSILAT_KIND_LABELS = {
  SINAV: "EKS s\u0131nav \xF6demeleri",
  MATERYAL: "E\u011Fitim materyalleri"
};
var OKUL_TAHSILAT_UNIT_LABELS = {
  SINAV: "ders",
  MATERYAL: "adet"
};
var OKUL_TAHSILAT_HOLDER_TYPES = ["KURUM", "SAHIS"];
var OKUL_TAHSILAT_HOLDER_TYPE_LABELS = {
  KURUM: "Kurum hesab\u0131",
  SAHIS: "\u015Eahsi hesap"
};
var OKUL_TAHSILAT_STATUSES = ["ACTIVE", "CANCELLED"];
var OKUL_TAHSILAT_STATUS_LABELS = {
  ACTIVE: "Ge\xE7erli",
  CANCELLED: "\u0130ptal"
};
var OKUL_TAHSILAT_SORT_FIELDS = ["paidOn", "createdAt", "amount", "receiptNo"];
var OKUL_TAHSILAT_MAX_LINES = 20;
var isoDate8 = z30.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Tarih YYYY-AA-GG bi\xE7iminde olmal\u0131." });
var unitPrice = moneyText({ min: 0.01, max: 9999999 });
var paidAmount = moneyText({ min: 0.01, max: 99999999 });
var optionalText = (max) => z30.string().trim().max(max, `En fazla ${max} karakter yaz\u0131labilir.`).optional().transform((v) => v ? v : void 0);
var createOkulTahsilatItemSchema = z30.object({
  name: z30.string().trim().min(2, "Kalem ad\u0131 en az 2 karakter olmal\u0131.").max(200, "Kalem ad\u0131 en fazla 200 karakter olabilir."),
  kind: z30.enum(OKUL_TAHSILAT_KINDS, { error: "T\xFCr se\xE7ilmeli." }),
  unitPrice,
  currency: z30.string().trim().toUpperCase().length(3, "Para birimi \xFC\xE7 harf olmal\u0131.").default("TRY"),
  sortOrder: z30.coerce.number().int().min(0).max(9999).default(0)
});
var updateOkulTahsilatItemSchema = z30.object({
  name: createOkulTahsilatItemSchema.shape.name.optional(),
  unitPrice: unitPrice.optional(),
  sortOrder: z30.coerce.number().int().min(0).max(9999).optional(),
  isActive: z30.boolean().optional()
}).refine((v) => Object.values(v).some((x) => x !== void 0), {
  message: "De\u011Fi\u015Ftirilecek bir alan g\xF6nderilmedi."
});
var createOkulTahsilatAccountSchema = z30.object({
  name: z30.string().trim().min(2, "Hesap ad\u0131 en az 2 karakter olmal\u0131.").max(200, "Hesap ad\u0131 en fazla 200 karakter olabilir."),
  holderType: z30.enum(OKUL_TAHSILAT_HOLDER_TYPES, { error: "Hesap sahibi t\xFCr\xFC se\xE7ilmeli." }),
  bankName: optionalText(120),
  note: optionalText(300),
  sortOrder: z30.coerce.number().int().min(0).max(9999).default(0)
});
var updateOkulTahsilatAccountSchema = z30.object({
  name: createOkulTahsilatAccountSchema.shape.name.optional(),
  holderType: z30.enum(OKUL_TAHSILAT_HOLDER_TYPES).optional(),
  bankName: z30.string().trim().max(120).nullable().optional(),
  note: z30.string().trim().max(300).nullable().optional(),
  sortOrder: z30.coerce.number().int().min(0).max(9999).optional(),
  isActive: z30.boolean().optional()
}).refine((v) => Object.values(v).some((x) => x !== void 0), {
  message: "De\u011Fi\u015Ftirilecek bir alan g\xF6nderilmedi."
});
var okulTahsilatLineSchema = z30.object({
  itemId: z30.uuid({ error: "Kalem se\xE7ilmeli." }),
  quantity: z30.coerce.number({ error: "Miktar bir say\u0131 olmal\u0131." }).int("Miktar tam say\u0131 olmal\u0131.").min(1, "Miktar en az 1 olmal\u0131.").max(999, "Miktar en fazla 999 olabilir.")
});
var createOkulTahsilatSchema = z30.object({
  /** Yalnız yazarın kapsamı birden çok şirketse sorulur; bkz. `resolveWriteCompanyId`. */
  companyId: z30.uuid().optional(),
  kind: z30.enum(OKUL_TAHSILAT_KINDS, { error: "Tahsilat t\xFCr\xFC se\xE7ilmeli." }),
  studentName: z30.string().trim().min(3, "\xD6\u011Frencinin ad\u0131 ve soyad\u0131 yaz\u0131lmal\u0131.").max(200, "\xD6\u011Frenci ad\u0131 en fazla 200 karakter olabilir."),
  paidOn: isoDate8,
  accountId: z30.uuid({ error: "\xD6demenin yatt\u0131\u011F\u0131 hesap se\xE7ilmeli." }),
  /** Dekonttaki tutar. */
  amount: paidAmount,
  /** Dekont / işlem numarası. */
  reference: optionalText(100),
  note: optionalText(500),
  lines: z30.array(okulTahsilatLineSchema).min(1, "En az bir kalem se\xE7ilmeli.").max(
    OKUL_TAHSILAT_MAX_LINES,
    `Bir \xF6demede en fazla ${OKUL_TAHSILAT_MAX_LINES} kalem olabilir.`
  )
});
var cancelOkulTahsilatSchema = z30.object({
  reason: z30.string().trim().min(5, "\u0130ptal gerek\xE7esi en az 5 karakter olmal\u0131.").max(300, "\u0130ptal gerek\xE7esi en fazla 300 karakter olabilir.")
});
var okulTahsilatListQuerySchema = sortableQuerySchema.extend({
  kind: z30.enum(OKUL_TAHSILAT_KINDS).optional(),
  accountId: z30.uuid().optional(),
  /** Yokluğu "yalnız geçerliler" demek; iptaller ayrıca istenir. */
  status: z30.enum([...OKUL_TAHSILAT_STATUSES, "ALL"]).optional(),
  from: isoDate8.optional(),
  to: isoDate8.optional()
});
function okulTahsilatFark(amount, expectedAmount) {
  return Math.round((amount - expectedAmount) * 100) / 100;
}

// src/dto/tax.ts
import { z as z31 } from "zod";
var TAX_TAXPAYER_STATUSES = ["ACTIVE", "INACTIVE"];
var TAX_TAXPAYER_STATUS_LABELS = {
  ACTIVE: "Aktif",
  INACTIVE: "Pasif"
};
var TAX_PERIOD_KINDS = ["MONTH", "QUARTER", "YEAR"];
var TAX_PERIOD_KIND_LABELS = {
  MONTH: "Ayl\u0131k",
  QUARTER: "\xDC\xE7 ayl\u0131k",
  YEAR: "Y\u0131ll\u0131k"
};
var TAX_AUTHORITIES = ["GIB", "SGK", "BELEDIYE", "GUMRUK"];
var TAX_AUTHORITY_LABELS = {
  GIB: "Gelir \u0130daresi",
  SGK: "SGK",
  BELEDIYE: "Belediye",
  GUMRUK: "G\xFCmr\xFCk"
};
var TAX_REGISTRATION_STATUSES = [
  "DRAFT",
  "ACTIVE",
  "SUSPENDED",
  "ENDED",
  "EXCLUDED"
];
var TAX_REGISTRATION_STATUS_LABELS = {
  DRAFT: "Taslak",
  ACTIVE: "Aktif",
  SUSPENDED: "Ask\u0131da",
  ENDED: "Sona erdi",
  EXCLUDED: "Kapsam d\u0131\u015F\u0131"
};
var TAX_REGISTRATION_OPEN_STATUSES = [
  "ACTIVE",
  "SUSPENDED"
];
var TAX_ACCRUAL_POLICIES = ["NONE", "COST"];
var TAX_ACCRUAL_POLICY_LABELS = {
  NONE: "Gider yazmaz",
  COST: "Birim k\xE2rl\u0131l\u0131\u011F\u0131na gider"
};
var TAX_ACCRUAL_STATUSES = [
  "NOT_REQUIRED",
  "PENDING",
  "POSTED",
  "BLOCKED_CLOSED_PERIOD"
];
var TAX_ACCRUAL_STATUS_LABELS = {
  NOT_REQUIRED: "Gider yaz\u0131lm\u0131yor",
  PENDING: "Tahakkuk bekliyor",
  POSTED: "Deftere yaz\u0131ld\u0131",
  BLOCKED_CLOSED_PERIOD: "Kapal\u0131 d\xF6neme tak\u0131ld\u0131"
};
var TAX_PAYMENT_METHODS = ["TRANSFER", "CREDIT_CARD"];
var TAX_PAYMENT_METHOD_LABELS = {
  TRANSFER: "Havale / EFT",
  CREDIT_CARD: "Kredi kart\u0131 (vergi tahsilat ekran\u0131)"
};
var taxNumber = z31.string().trim().regex(/^[0-9]{10,11}$/, "Vergi numaras\u0131 10 haneli VKN ya da 11 haneli TCKN olmal\u0131.");
var dayOfMonth = z31.coerce.number().int().min(1).max(31);
var createTaxTaxpayerSchema = z31.object({
  /** Kaydın hangi birimin kapsamında görüneceği — mükellefin o birim OLDUĞU iddiası değil. */
  companyId: z31.string().uuid(),
  code: z31.string().trim().min(1).max(30),
  name: z31.string().trim().min(1).max(200),
  legalName: z31.string().trim().min(1).max(300),
  /** Kart, VKN mali müşavirden gelmeden önce açılabilsin diye isteğe bağlı. */
  taxNumber: taxNumber.optional(),
  taxOfficeName: z31.string().trim().max(160).optional(),
  taxOfficeCode: z31.string().trim().max(20).optional(),
  /** Özel hesap dönemi olan mükellefte kurumlar ve geçici vergi tarihleri kayar. */
  fiscalYearStartMonth: z31.coerce.number().int().min(1).max(12).default(1),
  orgUnitId: z31.string().uuid().optional(),
  advisorName: z31.string().trim().max(160).optional(),
  advisorEmail: z31.string().trim().email().max(200).optional(),
  advisorPhone: z31.string().trim().max(40).optional(),
  responsibleUserId: z31.string().uuid().optional(),
  notes: z31.string().trim().max(4e3).optional()
});
var updateTaxTaxpayerSchema = createTaxTaxpayerSchema.omit({ companyId: true }).partial().extend({
  status: z31.enum(TAX_TAXPAYER_STATUSES).optional(),
  version: z31.coerce.number().int().min(1)
});
var taxTaxpayerListQuerySchema = sortableQuerySchema.extend({
  companyId: z31.string().uuid().optional(),
  status: z31.enum(TAX_TAXPAYER_STATUSES).optional()
});
var TAX_TAXPAYER_SORT_FIELDS = ["code", "name", "createdAt"];
var TAX_OFFSET_FROM = ["PERIOD_END", "PERIOD_START"];
var TAX_OFFSET_FROM_LABELS = {
  PERIOD_END: "D\xF6nem bittikten sonra",
  PERIOD_START: "D\xF6nemin i\xE7inde"
};
var taxInstalmentSchema = z31.object({
  seq: z31.coerce.number().int().min(1),
  label: z31.string().trim().min(1).max(60),
  authority: z31.enum(TAX_AUTHORITIES).default("GIB"),
  /**
   * Sayacın nereden başladığı.
   *
   * Varsayılanı PERIOD_END çünkü beyanname veren vergilerin hepsi öyle. Ama
   * MTV, emlak ve ÇTV taksitleri verginin ait olduğu yılın İÇİNDE ödenir ve
   * onlarda PERIOD_START olmak zorunda.
   *
   * Bu alan sözleşmede OLMAK ZORUNDA: tür güncellemesi ayakları silip
   * yeniden yazıyor, ve alan taşınmadığı sürece yönetim ekranından MTV'ye
   * dokunan biri onu sessizce PERIOD_END'e döndürür — takvim motoru da 2026
   * MTV'si için 2027 tarihi üretmeye başlar. Bir yıl geç, ve gecikme
   * göstergesi bunu bir yıl boyunca fark etmez.
   */
  offsetFrom: z31.enum(TAX_OFFSET_FROM).default("PERIOD_END"),
  /**
   * Bu ayak birim kârlılığına gider yazıyor mu.
   *
   * `offsetFrom` ile AYNI gerekçeyle sözleşmede: tür güncellemesi ayakları
   * silip yeniden yazıyor, ve alan taşınmadığı sürece yönetim ekranından
   * damgaya dokunan biri onu sessizce NONE'a döndürür — o günden sonra damga
   * vergisi hiçbir birimin maliyetine yazılmaz ve kimse fark etmez, çünkü
   * eksilen bir gider ekranda hata gibi görünmez, kâr gibi görünür.
   *
   * Varsayılanı NONE ve bu bilinçli: yanlış tarafa düşen bir varsayılan
   * burada aynı lirayı iki kez gider yazmak demek, ve iki kez sayılan bir
   * gideri bulmak, hiç sayılmayanı bulmaktan zordur.
   */
  accrualPolicy: z31.enum(TAX_ACCRUAL_POLICIES).default("NONE"),
  offsetMonths: z31.coerce.number().int().min(0).max(24),
  dayOfMonth: dayOfMonth.optional(),
  onLastDay: z31.boolean().default(false)
}).refine((v) => v.onLastDay ? v.dayOfMonth === void 0 : v.dayOfMonth !== void 0, {
  message: 'Vade ya bir g\xFCn say\u0131s\u0131d\u0131r ya da "ay\u0131n son g\xFCn\xFC" \u2014 ikisi birden olmaz.',
  path: ["dayOfMonth"]
});
var upsertTaxObligationTypeSchema = z31.object({
  code: z31.string().trim().min(1).max(40).regex(/^[A-Z0-9_]+$/, "Kod yaln\u0131zca b\xFCy\xFCk harf, rakam ve alt \xE7izgi i\xE7erebilir."),
  name: z31.string().trim().min(1).max(200),
  /** Gelir İdaresi vergi türü kodu; tahakkuk fişinden okunur, uydurulmaz. */
  gibTaxCode: z31.string().trim().max(10).optional(),
  periodKind: z31.enum(TAX_PERIOD_KINDS).default("MONTH"),
  /** MTV, emlak ve ÇTV'de beyanname YOKTUR — yalnızca ödeme vardır. */
  hasFiling: z31.boolean().default(true),
  /**
   * Beyannamesi var ama beyanname damga vergisinden HARİÇ.
   *
   * Kanun her vergi beyannamesini maktu damgaya tabi tutuyor, bir istisnayla:
   * (1) sayılı tablo IV-2-b-be "Diğer vergi beyannameleri (damga vergisi
   * beyannameleri hariç)". Damga vergisi beyannamesinin fişinde 1048 satırı
   * yok; işaretli türde tarife girilmez, tahakkuk formu damga sormaz.
   *
   * İSTEĞE BAĞLI ve varsayılansız — `accrualPolicy`'nin dersi: güncelleme her
   * alanı yeniden yazıyor, ve alanı bilmeyen eski bir istemcinin kaydı damga
   * vergisi beyannamesini sessizce yeniden damgalı yapardı. Gönderilmezse
   * saklı değer korunuyor; yeni türde `false`.
   */
  stampExempt: z31.boolean().optional(),
  filingOffsetMonths: z31.coerce.number().int().min(0).max(24).optional(),
  filingDayOfMonth: dayOfMonth.optional(),
  legalRef: z31.string().trim().max(200).optional(),
  /** Mevzuatın en son ne zaman kontrol edildiği. Boşsa ekran "teyit bekliyor" der. */
  reviewedOn: z31.string().date().optional(),
  isActive: z31.boolean().default(true),
  sortOrder: z31.coerce.number().int().default(0),
  instalments: z31.array(taxInstalmentSchema).max(24).default([])
}).refine(
  (v) => v.hasFiling ? v.filingOffsetMonths !== void 0 && v.filingDayOfMonth !== void 0 : v.filingOffsetMonths === void 0 && v.filingDayOfMonth === void 0,
  {
    message: "Beyannamesi olan t\xFCrde beyan tarihi zorunlu; olmayanda bo\u015F kalmal\u0131 \u2014 dolu b\u0131rak\u0131lan tarih takvimde \xE7eli\u015Fki \xFCretir.",
    path: ["filingDayOfMonth"]
  }
).refine((v) => new Set(v.instalments.map((i) => i.seq)).size === v.instalments.length, {
  message: "\xD6deme ayaklar\u0131n\u0131n s\u0131ras\u0131 tekrar edemez.",
  path: ["instalments"]
});
function damgaAyagiSeq(type) {
  if (!type.hasFiling || type.stampExempt) return null;
  const gib = type.instalments.filter((i) => i.authority === "GIB").map((i) => i.seq).sort((a, b) => a - b);
  return gib[0] ?? null;
}
function odenecekTutar(amount, stampAmount) {
  if (amount === null) return null;
  return Math.round((amount + (stampAmount ?? 0)) * 100) / 100;
}
var createTaxRegistrationSchema = z31.object({
  taxpayerId: z31.string().uuid(),
  typeId: z31.string().uuid(),
  status: z31.enum(TAX_REGISTRATION_STATUSES).default("ACTIVE"),
  exclusionReason: z31.string().trim().max(2e3).optional(),
  startsOn: z31.string().date(),
  endsOn: z31.string().date().optional(),
  filingDayOverride: dayOfMonth.optional(),
  paymentDayOverride: dayOfMonth.optional(),
  filingAlertDays: z31.coerce.number().int().min(0).max(180).default(7),
  paymentAlertDays: z31.coerce.number().int().min(0).max(180).default(5),
  responsibleUserId: z31.string().uuid().optional(),
  notes: z31.string().trim().max(4e3).optional(),
  // --- ödeme ve defter tarafı ------------------------------------------------
  /**
   * Maliyet yazan ayaklar için masraf merkezi ve kategori.
   *
   * `fin_entry` ikisini de NOT NULL istiyor. Burada NULLABLE olmaları ise
   * mükellefiyetlerin çoğunun maliyet yazmamasından: KDV'de masraf merkezi
   * sormak, hiç kullanılmayacak bir alanı her kayıtta doldurtmaktır ve
   * doldurulan her gereksiz alan, gerçekten gereken alanın da gürültüye
   * karışması demek.
   *
   * Boş bırakıldığında maliyet yazan bir ayakta tahakkuk YAZILMAZ — uydurulmaz.
   * Satır `PENDING` kalır ve ekranda sayılır.
   */
  costCenterId: z31.string().uuid().optional(),
  categoryId: z31.string().uuid().optional(),
  /**
   * Ödemenin çıktığı kasa hesabı. Boşsa nakit tarafı ELLE kalır.
   *
   * Sessizce atlanmıyor: ödendi kaydında ekran "kasa hesabı tanımlı değil —
   * nakit hareketi elle işlenecek" diye yazıyor. Uydurulmuş bir hesap, bir
   * bakiyeyi kimsenin yeniden üretemeyeceği bir sayıya çevirir.
   */
  treasuryAccountId: z31.string().uuid().optional(),
  paymentMethod: z31.enum(TAX_PAYMENT_METHODS).default("CREDIT_CARD"),
  /** Ödeme talebinde görünecek alacaklı adı: "Nilüfer Vergi Dairesi", "SGK". */
  payeeName: z31.string().trim().max(200).optional(),
  /**
   * Vadesi yaklaşan ödeme talebi kendiliğinden açılsın mı.
   *
   * Varsayılan KAPALI. Vergi ödemeleri büyük ve bir kere fazladan açılan talep
   * çift ödeme riskidir; açan kişi bunu bilerek açsın.
   */
  autoFilePayment: z31.boolean().default(false),
  autoFileDaysBefore: z31.coerce.number().int().min(0).max(60).default(5)
});
var updateTaxRegistrationSchema = createTaxRegistrationSchema.omit({ taxpayerId: true, typeId: true }).partial().extend({
  version: z31.coerce.number().int().min(1),
  /**
   * Gün ezmeleri BOŞALTILABİLİR olmak zorunda, o yüzden `nullable`.
   *
   * `.partial()` yalnızca "gönderilmedi"yi (undefined) veriyor ve servis onu
   * "dokunma" diye okuyor — doğru okuma, ama tek başına bir tuzak: bir kez
   * yazılmış bir gün ezmesi bir daha hiçbir gövdeyle temizlenemezdi. Formda
   * alanı silen kişi ekranda "kaydedildi" görür, tarih ise eski ezmeyle
   * üretilmeye devam ederdi — düzenlenebilir görünüp etkisi olmayan alan.
   * `null` "türün gününe dön" demek.
   */
  filingDayOverride: dayOfMonth.nullable().optional(),
  paymentDayOverride: dayOfMonth.nullable().optional(),
  /**
   * Masraf merkezi ve kategori de BOŞALTILABİLİR — DST-00080.
   *
   * Servis `null`'ı "alanı temizle" diye yazıyordu, ama şema `null`'ı hiç
   * geçirmiyordu: iki alan ekrana hiç çıkmadığı için bu fark görünmüyordu. Alan
   * artık formda; yanlış seçilen bir masraf merkezi bir daha
   * kaldırılamasaydı yukarıdaki gün ezmesiyle aynı tuzak olurdu.
   */
  costCenterId: z31.string().uuid().nullable().optional(),
  categoryId: z31.string().uuid().nullable().optional()
});
var taxRegistrationListQuerySchema = sortableQuerySchema.extend({
  companyId: z31.string().uuid().optional(),
  taxpayerId: z31.string().uuid().optional(),
  typeId: z31.string().uuid().optional(),
  status: z31.enum(TAX_REGISTRATION_STATUSES).optional()
});
var TAX_REGISTRATION_SORT_FIELDS = ["startsOn", "createdAt"];
var TAX_FILING_STATUSES = [
  "NOT_REQUIRED",
  "PENDING",
  "FILED",
  "LATE_FILED",
  "AMENDED",
  "CANCELLED"
];
var TAX_FILING_STATUS_LABELS = {
  NOT_REQUIRED: "Beyan gerekmiyor",
  PENDING: "Beyan bekliyor",
  FILED: "Verildi",
  LATE_FILED: "Ge\xE7 verildi",
  AMENDED: "D\xFCzeltme verildi",
  CANCELLED: "\u0130ptal"
};
var TAX_FILING_CLOSED_STATUSES = [
  "NOT_REQUIRED",
  "FILED",
  "LATE_FILED",
  "AMENDED",
  "CANCELLED"
];
var TAX_PAYMENT_STATUSES = [
  "PLANNED",
  "ASSESSED",
  "PAID",
  "OFFSET",
  "WAIVED",
  "CANCELLED"
];
var TAX_PAYMENT_STATUS_LABELS = {
  PLANNED: "Planland\u0131",
  ASSESSED: "Tahakkuk etti",
  PAID: "\xD6dendi",
  OFFSET: "Mahsup edildi",
  WAIVED: "Terkin/muaf",
  CANCELLED: "\u0130ptal"
};
var TAX_PAYMENT_CLOSED_STATUSES = [
  "PAID",
  "OFFSET",
  "WAIVED",
  "CANCELLED"
];
var TAX_PAYMENT_CLOSURE_STATUSES = ["PAID", "OFFSET", "WAIVED", "CANCELLED"];
var TAX_AMOUNT_SOURCES = ["YOK", "TAHMIN", "TAHAKKUK", "ODENDI"];
var TAX_AMOUNT_SOURCE_LABELS = {
  YOK: "Tutar girilmedi",
  TAHMIN: "Tahmin",
  TAHAKKUK: "Tahakkuk fi\u015Fi",
  ODENDI: "\xD6denen tutar"
};
var TAX_SHIFT_CONFIDENCES = ["KESIN", "TAKVIM_EKSIK"];
var TAX_SHIFT_CONFIDENCE_LABELS = {
  KESIN: "Kesin",
  TAKVIM_EKSIK: "Tatil takvimi yetmiyor \u2014 kanuni tarih g\xF6steriliyor"
};
var TAX_OVERRIDE_KINDS = ["MALI_TATIL", "CIRCULAR", "ADMIN_HOLIDAY"];
var TAX_OVERRIDE_KIND_LABELS = {
  MALI_TATIL: "Mali tatil (5604)",
  CIRCULAR: "Sirk\xFCler",
  ADMIN_HOLIDAY: "\u0130dari tatil"
};
var TAX_OVERRIDE_TARGETS = ["FILING", "PAYMENT"];
var TAX_OVERRIDE_TARGET_LABELS = {
  FILING: "Beyan",
  PAYMENT: "\xD6deme"
};
var TAX_FILED_SOURCES = ["MANUAL", "ADVISOR_PORTAL"];
var TAX_FILED_SOURCE_LABELS = {
  MANUAL: "Elle girildi",
  ADVISOR_PORTAL: "M\xFC\u015Favir portal\u0131"
};
var taxMoney = moneyText({ min: 0, max: 99999999999 });
var assessmentRefText = z31.string().trim().min(1, "Tahakkuk fi\u015Fi numaras\u0131 zorunludur.").max(60, "Tahakkuk fi\u015Fi numaras\u0131 en fazla 60 karakter olabilir.");
var filingRefText = z31.string().trim().max(60, "Beyanname numaras\u0131 en fazla 60 karakter olabilir.").optional();
var notesText = z31.string().trim().max(4e3, "Not en fazla 4000 karakter olabilir.").optional();
var closureReasonText = z31.string().trim().max(4e3, "Gerek\xE7e en fazla 4000 karakter olabilir.").optional();
var rowVersion = z31.coerce.number("Kay\u0131t s\xFCr\xFCm\xFC okunamad\u0131; sayfay\u0131 yenileyip tekrar deneyin.").int("Kay\u0131t s\xFCr\xFCm\xFC tam say\u0131 olmal\u0131.").min(1, "Kay\u0131t s\xFCr\xFCm\xFC ge\xE7ersiz; sayfay\u0131 yenileyip tekrar deneyin.");
var generateTaxPeriodsSchema = z31.object({
  /** Bu tarihe kadar (dahil) BAŞLAYAN dönemler üretilir. */
  through: z31.string().date().optional(),
  companyId: z31.string().uuid().optional(),
  registrationId: z31.string().uuid().optional()
});
var recalculateTaxPeriodsSchema = z31.object({
  companyId: z31.string().uuid().optional(),
  registrationId: z31.string().uuid().optional(),
  /** Yalnız bu yükümlülük türü; katalogda tek bir türü düzelten kişinin kapsamı. */
  typeId: z31.string().uuid().optional(),
  /**
   * Bu tarihten İTİBAREN vadesi olan satırlar; verilmezse bugünden.
   *
   * Varsayılanın bugün olması bilinçli: geçmiş vadeler kapanmış işlerdir ve
   * geriye dönük oynatılan bir tarih, "zamanında mıydı" sorusunun cevabını
   * sonradan değiştirir.
   */
  from: z31.string().date().optional(),
  /** Yaz, ya da yalnızca ne değişeceğini göster. */
  dryRun: z31.coerce.boolean().default(false)
});
var recordTaxFilingSchema = z31.object({
  status: z31.enum(TAX_FILING_STATUSES),
  /** Beyannamenin verildiği an. Verildi denen bir beyanda ZORUNLU. */
  filedAt: z31.string().datetime({ offset: true }).optional(),
  /** Beyanname numarası / onay kodu. */
  filingRef: filingRefText,
  /**
   * Beyan edilen vergi. BİLGİ AMAÇLI ve ödenecek tutar DEĞİL: her beyanname
   * maktu damga vergisine de tabidir ve tahakkuk fişindeki ödenecek tutar
   * bundan farklıdır.
   */
  declaredTaxAmount: taxMoney.optional(),
  notes: notesText,
  version: rowVersion
}).refine(
  (v) => !["FILED", "LATE_FILED", "AMENDED"].includes(v.status) || v.filedAt !== void 0,
  {
    message: 'Verildi denen bir beyan\u0131n ne zaman verildi\u011Fi yaz\u0131lmal\u0131; yoksa "zaman\u0131nda m\u0131yd\u0131" sorusu cevaps\u0131z kal\u0131r.',
    path: ["filedAt"]
  }
);
var taxPeriodListQuerySchema = sortableQuerySchema.extend({
  companyId: z31.string().uuid().optional(),
  taxpayerId: z31.string().uuid().optional(),
  registrationId: z31.string().uuid().optional(),
  typeCode: z31.string().trim().max(40).optional(),
  filingStatus: z31.enum(TAX_FILING_STATUSES).optional(),
  periodKey: z31.string().trim().max(20).optional(),
  /** Beyan vadesi aralığı. */
  from: z31.string().date().optional(),
  to: z31.string().date().optional(),
  /** Yalnızca beyanı gecikenler — türetilen bir süzgeç, saklanan bir bayrak değil. */
  lateOnly: z31.coerce.boolean().optional()
});
var TAX_PERIOD_SORT_FIELDS = [
  "filingDueDate",
  "periodStartsOn",
  "periodKey",
  "createdAt"
];
var recordTaxAssessmentSchema = z31.object({
  /** VERGİNİN kendisi — damga hariç (DST-00080). */
  amount: taxMoney,
  /**
   * Beyanname damgası — fişteki 1048 kodlu satır.
   *
   * Gönderilmezse damgayı taşıyan ayakta yılın tarifesi yazılıyor; sıfır
   * "bu fişte damga yok" demek. Damga taşımayan ayakta gönderilirse ret: o
   * ayağın fişinde damga satırı yok.
   */
  stampAmount: taxMoney.optional(),
  assessmentRef: assessmentRefText,
  notes: notesText,
  version: rowVersion
});
var recordTaxEstimateSchema = z31.object({
  amount: taxMoney,
  notes: notesText,
  version: rowVersion
});
var recordTaxPaymentSchema = z31.object({
  status: z31.enum(TAX_PAYMENT_CLOSURE_STATUSES),
  paidOn: z31.string().date().optional(),
  paidAmount: taxMoney.optional(),
  closureReason: closureReasonText,
  notes: notesText,
  version: rowVersion
}).refine((v) => v.status !== "PAID" || v.paidOn !== void 0 && v.paidAmount !== void 0, {
  message: "\xD6dendi denen bir sat\u0131rda \xF6deme tarihi ve \xF6denen tutar zorunlu.",
  path: ["paidOn"]
}).refine(
  (v) => !["OFFSET", "WAIVED"].includes(v.status) || v.closureReason !== void 0 && v.closureReason.length > 0,
  {
    message: "Mahsup ve terkinde gerek\xE7e zorunlu \u2014 para \xE7\u0131kmadan kapanan borcun sebebi yaz\u0131lmal\u0131.",
    path: ["closureReason"]
  }
);
var taxDueListQuerySchema = sortableQuerySchema.extend({
  companyId: z31.string().uuid().optional(),
  taxpayerId: z31.string().uuid().optional(),
  periodId: z31.string().uuid().optional(),
  typeCode: z31.string().trim().max(40).optional(),
  authority: z31.enum(TAX_AUTHORITIES).optional(),
  paymentStatus: z31.enum(TAX_PAYMENT_STATUSES).optional(),
  amountSource: z31.enum(TAX_AMOUNT_SOURCES).optional(),
  /** Ödeme vadesi aralığı — takvim ekranının ay penceresi. */
  from: z31.string().date().optional(),
  to: z31.string().date().optional(),
  /** Yalnızca gecikenler. */
  lateOnly: z31.coerce.boolean().optional(),
  /** Yalnızca açık satırlar: kapanmış bir vade takvimde yer kaplamasın. */
  openOnly: z31.coerce.boolean().optional()
});
var TAX_DUE_SORT_FIELDS = ["dueDate", "statutoryDueDate", "createdAt"];
var fileTaxPaymentSchema = z31.object({
  version: z31.coerce.number().int().min(1)
});
var markTaxDuePaidSchema = z31.object({
  paidOn: z31.string().date(),
  paidAmount: taxMoney,
  treasuryAccountId: z31.string().uuid().optional(),
  /** Banka referansı ya da tahsilat makbuz numarası — kasa hareketine geçer. */
  reference: z31.string().trim().max(100).optional(),
  notes: z31.string().trim().max(4e3).optional(),
  version: z31.coerce.number().int().min(1)
});
var upsertTaxStampTariffSchema = z31.object({
  amount: moneyText({ min: 0.01, max: 99999999 }),
  notes: notesText,
  version: z31.coerce.number().int().min(1).optional()
});
var upsertTaxOverrideSchema = z31.object({
  typeId: z31.string().uuid().optional(),
  companyId: z31.string().uuid().optional(),
  periodKey: z31.string().trim().max(20).optional(),
  target: z31.enum(TAX_OVERRIDE_TARGETS),
  originalDate: z31.string().date(),
  shiftedDate: z31.string().date(),
  kind: z31.enum(TAX_OVERRIDE_KINDS),
  circularRef: z31.string().trim().max(120).optional(),
  notes: z31.string().trim().max(4e3).optional()
}).refine((v) => v.shiftedDate >= v.originalDate, {
  message: "Kayd\u0131r\u0131lm\u0131\u015F tarih kanuni tarihten \xF6nce olamaz \u2014 idare s\xFCre uzat\u0131r, k\u0131saltmaz.",
  path: ["shiftedDate"]
}).refine(
  (v) => v.kind !== "CIRCULAR" || v.circularRef !== void 0 && v.circularRef.length > 0,
  {
    message: "Sirk\xFClerle uzat\u0131lan bir s\xFCre, hangi sirk\xFCler oldu\u011Fu yaz\u0131lmadan iddiad\u0131r.",
    path: ["circularRef"]
  }
);
var taxOverrideListQuerySchema = sortableQuerySchema.extend({
  year: z31.coerce.number().int().min(2e3).max(2100).optional(),
  target: z31.enum(TAX_OVERRIDE_TARGETS).optional(),
  kind: z31.enum(TAX_OVERRIDE_KINDS).optional(),
  typeId: z31.string().uuid().optional(),
  companyId: z31.string().uuid().optional()
});
var TAX_OVERRIDE_SORT_FIELDS = ["originalDate", "year", "createdAt"];
var taxCalendarSummaryQuerySchema = z31.object({
  companyId: z31.string().uuid().optional(),
  taxpayerId: z31.string().uuid().optional(),
  /** Pencere; verilmezse bu ayın başından doksan gün sonrasına. */
  from: z31.string().date().optional(),
  to: z31.string().date().optional()
});

// src/dto/announcement.ts
import { z as z32 } from "zod";
var ANNOUNCEMENT_KINDS = ["DUYURU", "MEMORANDUM"];
var ANNOUNCEMENT_KIND_LABELS = {
  DUYURU: "Duyuru",
  MEMORANDUM: "Memorandum"
};
var ANNOUNCEMENT_AUDIENCE_MODES = ["FROZEN", "STANDING"];
var ANNOUNCEMENT_AUDIENCE_MODE_LABELS = {
  FROZEN: "Yay\u0131m an\u0131ndaki ki\u015Filer",
  STANDING: "Sonradan kat\u0131lanlar da"
};
var ANNOUNCEMENT_TARGET_KINDS = ["ALL", "ROLE", "USER", "ORG_UNIT", "CC"];
var ANNOUNCEMENT_TARGET_KIND_LABELS = {
  ALL: "Herkes",
  ROLE: "Rol",
  USER: "Ki\u015Fi",
  ORG_UNIT: "Departman",
  CC: "Bilgi (kopya)"
};
var ANNOUNCEMENT_RECIPIENT_ROLES = ["TO", "CC"];
var ANNOUNCEMENT_RECIPIENT_ROLE_LABELS = {
  TO: "Gere\u011Fi",
  CC: "Bilgi"
};
var ANNOUNCEMENT_ADDED_REASONS = ["PUBLISH", "EXTEND", "BACKFILL"];
var ANNOUNCEMENT_ADDED_REASON_LABELS = {
  PUBLISH: "Yay\u0131mda",
  EXTEND: "Sonradan eklendi",
  BACKFILL: "Sonradan kat\u0131ld\u0131"
};
var ANNOUNCEMENT_VIEWS = [
  "inbox",
  "published",
  "drafts",
  "scheduled",
  "archived"
];
var ANNOUNCEMENT_VIEW_LABELS = {
  inbox: "Bana gelenler",
  published: "Yay\u0131mlad\u0131klar\u0131m",
  drafts: "Taslaklar",
  scheduled: "Zamanlanm\u0131\u015F",
  archived: "S\xFCresi dolmu\u015F"
};
var ANNOUNCEMENT_ACK_MODES = ["INFO", "READ", "ACK", "ACK_BY_DATE"];
var ANNOUNCEMENT_ACK_MODE_LABELS = {
  INFO: "Bilgi ama\xE7l\u0131",
  READ: "G\xF6r\xFCnt\xFClenmesi izlenir",
  ACK: "Tebli\u011F beyan\u0131 istenir",
  ACK_BY_DATE: "Son g\xFCne kadar tebli\u011F beyan\u0131 istenir"
};
var ANNOUNCEMENT_ACK_MODES_REQUIRING_DECLARATION = [
  "ACK",
  "ACK_BY_DATE"
];
function defaultAckModeFor(kind, ackDueAt) {
  if (kind !== "MEMORANDUM") return "READ";
  return ackDueAt ? "ACK_BY_DATE" : "ACK";
}
var ANNOUNCEMENT_PRIORITIES = ["LOW", "NORMAL", "HIGH", "CRITICAL"];
var ANNOUNCEMENT_PRIORITY_LABELS = {
  LOW: "D\xFC\u015F\xFCk",
  NORMAL: "Normal",
  HIGH: "Y\xFCksek",
  CRITICAL: "Kritik"
};
var ANNOUNCEMENT_CONFIDENTIALITIES = [
  "PUBLIC",
  "INTERNAL",
  "CONFIDENTIAL",
  "RESTRICTED"
];
var ANNOUNCEMENT_CONFIDENTIALITY_LABELS = {
  PUBLIC: "A\xE7\u0131k",
  INTERNAL: "Kurum i\xE7i",
  CONFIDENTIAL: "Gizli",
  RESTRICTED: "K\u0131s\u0131tl\u0131"
};
var ANNOUNCEMENT_CATEGORY_KINDS = ["DUYURU", "MEMORANDUM", "BOTH"];
var ANNOUNCEMENT_CATEGORY_KIND_LABELS = {
  DUYURU: "Yaln\u0131z duyuru",
  MEMORANDUM: "Yaln\u0131z memorandum",
  BOTH: "\u0130kisi de"
};
var ANNOUNCEMENT_LINKED_ENTITY_TYPES = [
  "document_library",
  "payment_request",
  "staff_request",
  "leave_request",
  "asset"
];
var ANNOUNCEMENT_LINKED_ENTITY_TYPE_LABELS = {
  document_library: "Dok\xFCman",
  payment_request: "\xD6deme talebi",
  staff_request: "Personel talebi",
  leave_request: "\u0130zin talebi",
  asset: "Zimmet kayd\u0131"
};
var ANNOUNCEMENT_APPROVAL_STATES = [
  "NONE",
  "PENDING",
  "APPROVED",
  "REJECTED",
  "REVISION"
];
var ANNOUNCEMENT_APPROVAL_STATE_LABELS = {
  NONE: "Onay gerekmiyor",
  PENDING: "Onay bekliyor",
  APPROVED: "Onayland\u0131",
  REJECTED: "Reddedildi",
  REVISION: "Revizyon istendi"
};
var ANNOUNCEMENT_APPROVAL_STATES_BLOCKING_PUBLISH = [
  "PENDING",
  "REJECTED",
  "REVISION"
];
var ANNOUNCEMENT_APPROVAL_DECISIONS = ["APPROVED", "REJECTED", "REVISION"];
var ANNOUNCEMENT_APPROVAL_DECISION_LABELS = {
  APPROVED: "Onaylad\u0131",
  REJECTED: "Reddetti",
  REVISION: "Revizyon istedi"
};
var ANNOUNCEMENT_APPROVAL_COMMENT_MIN = 5;
function announcementDecisionNeedsComment(decision) {
  return decision !== "APPROVED";
}
var ANNOUNCEMENT_ACK_STATEMENT = "Bu memorandumun metnini g\xF6r\xFCnt\xFCledim ve tebli\u011F ald\u0131\u011F\u0131m\u0131 beyan ederim.";
var ANNOUNCEMENT_AUDIENCE_MAX = 2e3;
var ANNOUNCEMENT_RECEIPT_PAGE_SIZE = 100;
var ANNOUNCEMENT_ATTACHMENT_MAX = 10;
var ANNOUNCEMENT_ATTACHMENT_CAPTION_MAX = 200;
function announcementDocumentDigestSource(body, attachmentSha256s) {
  if (attachmentSha256s.length === 0) return null;
  const sorted = [...attachmentSha256s].map((s) => s.toLowerCase()).sort();
  return `${body}
${sorted.join("\n")}`;
}
var isoDateTime2 = z32.iso.datetime({ offset: true });
var createAnnouncementSchema = z32.object({
  kind: z32.enum(ANNOUNCEMENT_KINDS),
  /** null = grup geneli. Servis GRUP kapsamı arıyor; RLS de ayrıca bakıyor. */
  companyId: z32.uuid().nullable().optional(),
  title: z32.string().trim().min(3).max(200),
  /**
   * Düz metin. HTML YOK — hem bir XSS yüzeyi, hem de aynı metnin zilde,
   * ekranda ve tutanakta üç farklı biçimde görünmesi demek.
   */
  body: z32.string().trim().min(10).max(2e4),
  audienceMode: z32.enum(ANNOUNCEMENT_AUDIENCE_MODES).default("FROZEN"),
  /** Beyan için son gün. Yalnızca memorandumda anlamlı. */
  ackDueAt: isoDateTime2.nullable().optional(),
  /** Yerine geçtiği yayım. Düzeltmenin tek yolu bu. */
  supersedesId: z32.uuid().nullable().optional(),
  // --- sınıflandırma ve künye --------------------------------------------
  /**
   * Kategori KODU — id değil. Kategori pasife alındığında ya da adı
   * değiştiğinde kayıt aynı ibareyi taşımaya devam etsin.
   */
  categoryCode: z32.string().trim().min(2).max(40).nullable().optional(),
  /** "Konu" — başlıktan ayrı, resmî yazının üst bloğunda duran ibare. */
  subject: z32.string().trim().min(3).max(300).nullable().optional(),
  priority: z32.enum(ANNOUNCEMENT_PRIORITIES).default("NORMAL"),
  confidentiality: z32.enum(ANNOUNCEMENT_CONFIDENTIALITIES).default("INTERNAL"),
  /**
   * Beyan kipi. VERİLMEZSE sunucu `defaultAckModeFor(kind, ackDueAt)`
   * uygular — sabit bir varsayılan yazamayız, çünkü doğru varsayılan
   * `kind`'a bakıyor ve bu şema `kind`ı `default` çözülürken görmüyor.
   */
  ackMode: z32.enum(ANNOUNCEMENT_ACK_MODES).optional(),
  /** Metnin yürürlüğe girdiği ve geçerliliğini yitirdiği anlar. */
  effectiveAt: isoDateTime2.nullable().optional(),
  expiresAt: isoDateTime2.nullable().optional(),
  /** Başka bir defterin numarası. `number` ile karıştırılmamalı. */
  referenceNo: z32.string().trim().min(1).max(60).nullable().optional(),
  /** Hazırlayan birim. */
  preparedOrgUnitId: z32.uuid().nullable().optional(),
  /** Yumuşak işaretçi: "bu yazı ne hakkında". İkisi birlikte ya da hiç. */
  linkedEntityType: z32.enum(ANNOUNCEMENT_LINKED_ENTITY_TYPES).nullable().optional(),
  linkedEntityId: z32.string().trim().min(1).max(64).nullable().optional(),
  // --- zamanlama ve görüntüleme ------------------------------------------
  /** Yayım anı gelince gece işi yayımlar. Taslakta anlamlı. */
  scheduledPublishAt: isoDateTime2.nullable().optional(),
  /** Listenin başında kalma süresi — görüntüleme kararı, belge özelliği değil. */
  pinnedUntil: isoDateTime2.nullable().optional()
}).refine((v) => v.audienceMode !== "STANDING" || v.kind === "MEMORANDUM", {
  message: "Sonradan kat\u0131lanlara uygulama yaln\u0131zca memorandumda kullan\u0131labilir.",
  path: ["audienceMode"]
}).refine((v) => !v.ackDueAt || v.kind === "MEMORANDUM", {
  message: "Beyan son g\xFCn\xFC yaln\u0131zca memorandumda kullan\u0131labilir.",
  path: ["ackDueAt"]
}).refine(
  (v) => v.kind === "MEMORANDUM" || v.ackMode === void 0 || v.ackMode === "INFO" || v.ackMode === "READ",
  {
    message: "Bir duyuru tebli\u011F beyan\u0131 isteyemez; beyan memorandumun i\u015Fidir.",
    path: ["ackMode"]
  }
).refine((v) => v.ackMode !== "ACK_BY_DATE" || Boolean(v.ackDueAt), {
  message: "Son g\xFCnl\xFC beyan i\xE7in bir son g\xFCn verilmeli.",
  path: ["ackDueAt"]
}).refine((v) => !v.ackDueAt || v.ackMode === void 0 || v.ackMode === "ACK_BY_DATE", {
  message: 'Son g\xFCn verildiyse beyan kipi "son g\xFCne kadar" olmal\u0131.',
  path: ["ackMode"]
}).refine((v) => !v.expiresAt || !v.effectiveAt || v.expiresAt > v.effectiveAt, {
  message: "Son ge\xE7erlilik, y\xFCr\xFCrl\xFCk tarihinden sonra olmal\u0131.",
  path: ["expiresAt"]
}).refine((v) => Boolean(v.linkedEntityType) === Boolean(v.linkedEntityId), {
  message: "Ba\u011Fl\u0131 kay\u0131t i\xE7in hem t\xFCr hem kimlik gerekli.",
  path: ["linkedEntityId"]
});
var updateAnnouncementSchema = z32.object({
  kind: z32.enum(ANNOUNCEMENT_KINDS).optional(),
  companyId: z32.uuid().nullable().optional(),
  title: z32.string().trim().min(3).max(200).optional(),
  body: z32.string().trim().min(10).max(2e4).optional(),
  audienceMode: z32.enum(ANNOUNCEMENT_AUDIENCE_MODES).optional(),
  ackDueAt: isoDateTime2.nullable().optional(),
  supersedesId: z32.uuid().nullable().optional(),
  categoryCode: z32.string().trim().min(2).max(40).nullable().optional(),
  subject: z32.string().trim().min(3).max(300).nullable().optional(),
  priority: z32.enum(ANNOUNCEMENT_PRIORITIES).optional(),
  confidentiality: z32.enum(ANNOUNCEMENT_CONFIDENTIALITIES).optional(),
  ackMode: z32.enum(ANNOUNCEMENT_ACK_MODES).optional(),
  effectiveAt: isoDateTime2.nullable().optional(),
  expiresAt: isoDateTime2.nullable().optional(),
  referenceNo: z32.string().trim().min(1).max(60).nullable().optional(),
  preparedOrgUnitId: z32.uuid().nullable().optional(),
  linkedEntityType: z32.enum(ANNOUNCEMENT_LINKED_ENTITY_TYPES).nullable().optional(),
  linkedEntityId: z32.string().trim().min(1).max(64).nullable().optional(),
  scheduledPublishAt: isoDateTime2.nullable().optional(),
  pinnedUntil: isoDateTime2.nullable().optional()
}).refine((v) => Object.keys(v).length > 0, { message: "En az bir alan gerekli." });
var pinAnnouncementSchema = z32.object({
  pinnedUntil: isoDateTime2.nullable()
});
var targetSchema = z32.object({
  kind: z32.enum(ANNOUNCEMENT_TARGET_KINDS),
  roleCode: z32.string().trim().min(1).max(60).nullable().optional(),
  userId: z32.uuid().nullable().optional(),
  orgUnitId: z32.uuid().nullable().optional()
}).refine((t) => t.kind === "ROLE" === Boolean(t.roleCode), {
  message: "ROLE hedefi bir rol kodu ister; di\u011Ferleri istemez."
}).refine((t) => (t.kind === "USER" || t.kind === "CC") === Boolean(t.userId), {
  message: "USER ve CC hedefi bir ki\u015Fi ister; di\u011Ferleri istemez."
}).refine((t) => t.kind === "ORG_UNIT" === Boolean(t.orgUnitId), {
  message: "ORG_UNIT hedefi bir departman ister; di\u011Ferleri istemez."
});
var setAnnouncementTargetsSchema = z32.object({
  /** Tam değiştirme: gönderilen liste, hedeflerin tamamıdır. */
  targets: z32.array(targetSchema).min(1).max(50)
});
var acknowledgeAnnouncementSchema = z32.object({
  /**
   * Ekranda gösterilen metnin özeti.
   *
   * Sunucu kendi özetiyle karşılaştırıp uyuşmazsa 409 döner: bayat bir sekme,
   * hiç göstermediği bir metni beyan edemez.
   */
  bodySha256: z32.string().trim().length(64),
  /**
   * Ekranda gösterilen BELGENİN özeti — gövde ve ekler birlikte (K1).
   *
   * `bodySha256`'nın yerine değil YANINA gönderiliyor, ve ikisi de
   * karşılaştırılıyor. Yalnız gövdeyi karşılaştırmak, eki değişmiş bir sekmeye
   * beyan verdirir; yalnız belgeyi karşılaştırmak, eki olmayan yayımlarda
   * karşılaştıracak bir şey bırakmaz.
   *
   * Eki OLMAYAN bir belgede gönderilmez (sunucu tarafında `document_sha256`
   * NULL'dır). Eki OLAN bir belgede zorunlu, ve zorunluluğu servis uyguluyor:
   * şemada `optional`, çünkü hangi belgenin eki olduğunu istemci değil kayıt
   * bilir.
   */
  documentSha256: z32.string().trim().length(64).optional()
});
var withdrawAnnouncementSchema = z32.object({
  reason: z32.string().trim().min(3).max(500)
});
var decideAnnouncementSchema = z32.object({
  decision: z32.enum(ANNOUNCEMENT_APPROVAL_DECISIONS),
  comment: z32.string().trim().min(ANNOUNCEMENT_APPROVAL_COMMENT_MIN).max(2e3).optional()
}).refine((v) => !announcementDecisionNeedsComment(v.decision) || Boolean(v.comment), {
  message: "Reddederken ve revizyon isterken gerek\xE7e yazmak zorunlu.",
  path: ["comment"]
});
var announcementListQuerySchema = sortableQuerySchema.extend({
  view: z32.enum(ANNOUNCEMENT_VIEWS).default("inbox"),
  kind: z32.enum(ANNOUNCEMENT_KINDS).optional(),
  /** Yalnızca görüntülemediklerim. */
  unread: z32.coerce.boolean().optional(),
  /** Yalnızca beyan bekleyenler. */
  unacknowledged: z32.coerce.boolean().optional()
});
var receiptListQuerySchema = sortableQuerySchema.extend({
  /** 'all' | 'unseen' | 'unacknowledged' */
  filter: z32.enum(["all", "unseen", "unacknowledged"]).default("all")
});
var announcementAttachmentMetaSchema = z32.object({
  caption: z32.string().trim().min(1).max(ANNOUNCEMENT_ATTACHMENT_CAPTION_MAX).optional(),
  /** Belgedeki sıra. Verilmezse servis sona ekler. */
  sortOrder: z32.coerce.number().int().min(0).max(999).optional()
});
var createAnnouncementCategorySchema = z32.object({
  /**
   * Kod BÜYÜK HARF ve sabit biçimli — veritabanındaki `chk_ann_category_code`
   * ile birebir aynı desen. Küçük harfli ikinci bir "ik" kaydı sözlüğü ikiye
   * böler ve geçmiş kayıtlar kod bazında karşılaştırılamaz olur.
   */
  code: z32.string().trim().regex(/^[A-Z0-9_]{2,40}$/, "Kod yaln\u0131z b\xFCy\xFCk harf, rakam ve alt \xE7izgi i\xE7erebilir."),
  name: z32.string().trim().min(2).max(120),
  kind: z32.enum(ANNOUNCEMENT_CATEGORY_KINDS).default("BOTH"),
  /** null = grup geneli. Grup düzeyi bir kategori açmak GRUP kapsamı ister. */
  companyId: z32.uuid().nullable().optional(),
  sortOrder: z32.coerce.number().int().min(0).max(9999).default(0)
});
var updateAnnouncementCategorySchema = z32.object({
  name: z32.string().trim().min(2).max(120).optional(),
  kind: z32.enum(ANNOUNCEMENT_CATEGORY_KINDS).optional(),
  isActive: z32.boolean().optional(),
  sortOrder: z32.coerce.number().int().min(0).max(9999).optional()
}).refine((v) => Object.keys(v).length > 0, { message: "En az bir alan gerekli." });
var announcementCategoryListQuerySchema = z32.object({
  /**
   * Varsayılan `false`: yazma ekranı yalnız aktifleri gösterir, YÖNETİM ekranı
   * hepsini ister — pasife alınmış bir satırı göremeyen ekranda onu geri
   * açmanın yolu yoktur.
   */
  includeInactive: z32.coerce.boolean().default(false),
  /** Yalnız bu tipte seçilebilenler. */
  kind: z32.enum(ANNOUNCEMENT_KINDS).optional()
});

// src/dto/messaging.ts
import { z as z33 } from "zod";
var CONVERSATION_KINDS = ["DIRECT", "GROUP", "ORG_UNIT", "TOPIC"];
var CONVERSATION_KIND_LABELS = {
  DIRECT: "Birebir",
  GROUP: "Grup",
  ORG_UNIT: "Departman kanal\u0131",
  TOPIC: "Konu kanal\u0131"
};
var CONVERSATION_KIND_REQUIRED_PERMISSION = {
  DIRECT: PERMISSIONS.MESSAGE_USE,
  GROUP: PERMISSIONS.MESSAGE_GROUP_CREATE,
  ORG_UNIT: PERMISSIONS.MESSAGE_CHANNEL_MANAGE,
  TOPIC: PERMISSIONS.MESSAGE_CHANNEL_MANAGE
};
var PARTICIPANT_ROLES = ["OWNER", "ADMIN", "MEMBER"];
var PARTICIPANT_ROLE_LABELS = {
  OWNER: "Sahip",
  ADMIN: "Y\xF6netici",
  MEMBER: "\xDCye"
};
var MESSAGE_KINDS = ["TEXT", "SYSTEM"];
var MESSAGE_KIND_LABELS = {
  TEXT: "Mesaj",
  SYSTEM: "Sistem bildirimi"
};
var MESSAGE_NOTIFY_MODES = ["ALL", "MENTIONS", "NONE"];
var MESSAGE_NOTIFY_MODE_LABELS = {
  ALL: "T\xFCm mesajlar",
  MENTIONS: "Yaln\u0131zca bana etiketlendi\u011Fimde",
  NONE: "Kapal\u0131"
};
var CONVERSATION_VIEWS = ["active", "archived"];
var CONVERSATION_LINK_TYPES = [
  "support_ticket",
  "agenda_item",
  "announcement",
  "asset",
  "asset_custody",
  "lease_contract",
  "konaklama_reservation",
  "payee"
];
function isConversationLinkType(value) {
  return value !== null && CONVERSATION_LINK_TYPES.includes(value);
}
var CONVERSATION_LINK_TYPE_LABELS = {
  support_ticket: "Destek kayd\u0131",
  agenda_item: "Ajanda kayd\u0131",
  announcement: "Duyuru",
  asset: "Zimmet varl\u0131\u011F\u0131",
  asset_custody: "Zimmet tutana\u011F\u0131",
  lease_contract: "U\xE7ak kira s\xF6zle\u015Fmesi",
  konaklama_reservation: "Rezervasyon",
  payee: "Cari"
};
var PRESENCE_STATUSES = ["CEVRIMICI", "MESGUL"];
var PRESENCE_STATUS_LABELS = {
  CEVRIMICI: "\xC7evrim i\xE7i",
  MESGUL: "Me\u015Fgul"
};
var MESSAGE_DELIVERY_STATUSES = ["GONDERILDI", "ILETILDI", "OKUNDU"];
var MESSAGE_DELIVERY_STATUS_LABELS = {
  GONDERILDI: "G\xF6nderildi",
  ILETILDI: "\u0130letildi",
  OKUNDU: "Okundu"
};
var MESSAGE_BODY_MAX = 4e3;
var CONVERSATION_TITLE_MAX = 200;
var CONVERSATION_TOPIC_MAX = 300;
var MESSAGE_DELETE_REASON_MAX = 300;
var MESSAGE_ATTACHMENT_MAX = 5;
var MESSAGE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
var CONVERSATION_PARTICIPANT_MAX = 500;
var CONVERSATION_PARTICIPANTS_PER_REQUEST_MAX = 100;
var MESSAGE_MENTION_MAX = 50;
var MESSAGE_PAGE_SIZE_DEFAULT = 50;
var MESSAGE_PAGE_SIZE_MAX = 100;
var MESSAGE_RECEIPT_SEQS_MAX = MESSAGE_PAGE_SIZE_MAX;
var MESSAGE_SEARCH_MIN_LENGTH = 3;
var MESSAGE_PREVIEW_MAX = 140;
var MESSAGE_DELETED_PLACEHOLDER = "Bu mesaj kald\u0131r\u0131ld\u0131.";
var isoDateTime3 = z33.iso.datetime({ offset: true });
var messageSeqSchema = z33.string().regex(/^(0|[1-9][0-9]{0,18})$/, "S\u0131ra numaras\u0131 ge\xE7ersiz.");
var createConversationSchema = z33.object({
  kind: z33.enum(CONVERSATION_KINDS),
  /**
   * null = group-wide. Same reason as ann_announcement: staff with SELF
   * scope have an EMPTY companyIds list, so a company-stamped row would be
   * invisible to exactly the people who need it most.
   */
  companyId: z33.uuid().nullable().optional(),
  title: z33.string().trim().min(2).max(CONVERSATION_TITLE_MAX).optional(),
  topic: z33.string().trim().min(2).max(CONVERSATION_TOPIC_MAX).nullable().optional(),
  /** Only meaningful for ORG_UNIT; the channel follows this unit. */
  orgUnitId: z33.uuid().optional(),
  /**
   * The ERP record this thread is about. A pointer — never a parent.
   *
   * The type is an ENUM, not free text, and the id is a uuid: every record
   * this system can open is keyed by one. A free-text type would let a
   * caller store a pointer nothing can ever resolve, and the screen has no
   * way to tell that apart from "you may not open this".
   *
   * The server ALSO refuses a link the caller cannot resolve themselves —
   * otherwise this field would answer "does record X exist" for anyone with
   * `message.use`.
   */
  linkedEntityType: z33.enum(CONVERSATION_LINK_TYPES).nullable().optional(),
  linkedEntityId: z33.uuid().nullable().optional(),
  /**
   * Everyone in the room at creation. The caller is added as OWNER by the
   * server and must NOT be listed here; sending yourself is not an error but
   * it is not what creates your membership either.
   */
  participantUserIds: z33.array(z33.uuid()).max(CONVERSATION_PARTICIPANT_MAX).default([])
}).refine((v) => v.kind !== "DIRECT" || v.participantUserIds.length === 1, {
  message: "Birebir konu\u015Fma tek bir ki\u015Fiyle a\xE7\u0131l\u0131r.",
  path: ["participantUserIds"]
}).refine((v) => v.kind !== "DIRECT" || !v.title && !v.orgUnitId, {
  message: "Birebir konu\u015Fman\u0131n ba\u015Fl\u0131\u011F\u0131 ve departman\u0131 olmaz.",
  path: ["title"]
}).refine((v) => v.kind !== "ORG_UNIT" || Boolean(v.orgUnitId), {
  message: "Departman kanal\u0131 bir departman ister.",
  path: ["orgUnitId"]
}).refine((v) => v.kind === "DIRECT" || Boolean(v.title), {
  message: "Konu\u015Fma bir ba\u015Fl\u0131k ister.",
  path: ["title"]
}).refine((v) => Boolean(v.linkedEntityType) === Boolean(v.linkedEntityId), {
  message: "Ba\u011Fl\u0131 kay\u0131t hem t\xFCr hem kimlik ister.",
  path: ["linkedEntityId"]
});
var updateConversationSchema = z33.object({
  title: z33.string().trim().min(2).max(CONVERSATION_TITLE_MAX).optional(),
  topic: z33.string().trim().min(2).max(CONVERSATION_TOPIC_MAX).nullable().optional()
}).refine((v) => Object.keys(v).length > 0, { message: "En az bir alan gerekli." });
var setConversationLinkSchema = z33.object({
  linkedEntityType: z33.enum(CONVERSATION_LINK_TYPES),
  linkedEntityId: z33.uuid()
});
var setPresenceStatusSchema = z33.object({
  status: z33.enum(PRESENCE_STATUSES)
});
var addConversationParticipantsSchema = z33.object({
  userIds: z33.array(z33.uuid()).min(1).max(CONVERSATION_PARTICIPANTS_PER_REQUEST_MAX),
  /** OWNER is not assignable here; ownership moves, it is not handed out in bulk. */
  role: z33.enum(["ADMIN", "MEMBER"]).default("MEMBER")
});
var setConversationArchiveSchema = z33.object({
  archived: z33.boolean()
});
var setConversationMuteSchema = z33.object({
  /** null lifts the mute. Absent leaves it alone. */
  mutedUntil: isoDateTime3.nullable().optional(),
  notifyMode: z33.enum(MESSAGE_NOTIFY_MODES).optional()
}).refine((v) => v.mutedUntil !== void 0 || v.notifyMode !== void 0, {
  message: "En az bir alan gerekli."
});
var markConversationReadSchema = z33.object({
  upToSeq: messageSeqSchema
});
var markConversationDeliveredSchema = z33.object({
  upToSeq: messageSeqSchema
});
var messageReceiptsQuerySchema = z33.object({
  seqs: z33.string().optional().transform(
    (raw) => raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : []
  ).pipe(z33.array(messageSeqSchema).max(MESSAGE_RECEIPT_SEQS_MAX))
});
var sendMessageSchema = z33.object({
  /** Plain text. No HTML — an XSS surface, and one body rendered three ways. */
  body: z33.string().trim().max(MESSAGE_BODY_MAX).default(""),
  replyToId: z33.uuid().nullable().optional(),
  /**
   * Who was tagged. Sent as ids rather than parsed out of the body: parsing
   * "@Ali" server-side has to guess which Ali, and guessing wrong notifies
   * the wrong person about a message they cannot open.
   */
  mentionUserIds: z33.array(z33.uuid()).max(MESSAGE_MENTION_MAX).default([]),
  /**
   * Files uploaded ahead of the message via POST
   * /conversations/:id/attachments, bound to it here. Upload-then-send is
   * what lets the composer show progress and reject an oversized file before
   * the user commits to sending anything.
   */
  attachmentIds: z33.array(z33.uuid()).max(MESSAGE_ATTACHMENT_MAX).default([])
}).refine((v) => v.body.length > 0 || v.attachmentIds.length > 0, {
  message: "Mesaj ya bir metin ya da bir dosya i\xE7ermeli.",
  path: ["body"]
});
var editMessageSchema = z33.object({
  body: z33.string().trim().min(1).max(MESSAGE_BODY_MAX)
});
var deleteMessageSchema = z33.object({
  reason: z33.string().trim().min(3).max(MESSAGE_DELETE_REASON_MAX).nullable().optional()
});
var setMessageStarSchema = z33.object({
  starred: z33.boolean()
});
var setMessagePinSchema = z33.object({
  pinned: z33.boolean()
});
var conversationListQuerySchema = sortableQuerySchema.extend({
  view: z33.enum(CONVERSATION_VIEWS).default("active"),
  kind: z33.enum(CONVERSATION_KINDS).optional(),
  unreadOnly: z33.enum(["true"]).optional(),
  /** "Bu kayıtla ilgili konuşmalar" — both halves or neither, as on create. */
  linkedEntityType: z33.enum(CONVERSATION_LINK_TYPES).optional(),
  linkedEntityId: z33.uuid().optional()
});
var messageListQuerySchema = z33.object({
  /** A seq. String, because it is a bigint — see messageSeqSchema. */
  before: messageSeqSchema.optional(),
  limit: z33.coerce.number().int().min(1).max(MESSAGE_PAGE_SIZE_MAX).default(MESSAGE_PAGE_SIZE_DEFAULT)
});
var messageSearchQuerySchema = sortableQuerySchema.extend({
  q: z33.string().trim().min(MESSAGE_SEARCH_MIN_LENGTH).max(200),
  /** Narrow to one thread. Membership is checked regardless. */
  conversationId: z33.uuid().optional(),
  authorUserId: z33.uuid().optional(),
  from: isoDateTime3.optional(),
  to: isoDateTime3.optional()
});
var mentionListQuerySchema = sortableQuerySchema.extend({
  unreadOnly: z33.enum(["true"]).optional()
});

// src/dto/notification.ts
import { z as z34 } from "zod";
var NOTIFICATION_CODES = {
  // --- ajanda -------------------------------------------------------------
  AGENDA_ASSIGNED: "agenda.assigned",
  AGENDA_COMMENT_ADDED: "agenda.comment_added",
  /** Çağrıldığınız saatte ZATEN kaydınız var (DST-00074). */
  AGENDA_CONFLICT: "agenda.conflict",
  AGENDA_INVITED: "agenda.invited",
  AGENDA_REMINDER: "agenda.reminder",
  AGENDA_RSVP: "agenda.rsvp",
  AGENDA_STATUS_CHANGED: "agenda.status_changed",
  /** Kaydın kendisi değişti — saati, başlığı (DST-00074). */
  AGENDA_UPDATED: "agenda.updated",
  // --- duyuru ve memorandum ------------------------------------------------
  ANNOUNCEMENT_APPROVED: "announcement.approved",
  ANNOUNCEMENT_AWAITING_APPROVAL: "announcement.awaiting_approval",
  ANNOUNCEMENT_PUBLISHED: "announcement.published",
  ANNOUNCEMENT_REJECTED: "announcement.rejected",
  ANNOUNCEMENT_REMINDER: "announcement.reminder",
  ANNOUNCEMENT_REVISION_REQUESTED: "announcement.revision_requested",
  // --- zimmet --------------------------------------------------------------
  ASSET_ACCEPTANCE_PENDING: "asset.acceptance.pending",
  ASSET_ACCEPTED: "asset.accepted",
  ASSET_ASSIGNED: "asset.assigned",
  ASSET_CALIBRATION_DUE: "asset.calibration.due",
  ASSET_CALIBRATION_FAILED: "asset.calibration_failed",
  ASSET_CANCELLED: "asset.cancelled",
  ASSET_INCIDENT: "asset.incident",
  ASSET_MAINTENANCE_DUE: "asset.maintenance.due",
  ASSET_MAINTENANCE_LATE_RETURN: "asset.maintenance.late_return",
  ASSET_RETURN_OVERDUE: "asset.return.overdue",
  ASSET_RETURN_REQUESTED: "asset.return_requested",
  // --- tatil takvimi -------------------------------------------------------
  CALENDAR_EXHAUSTING: "calendar.exhausting",
  // --- masraf --------------------------------------------------------------
  EXPENSE_AWAITING_APPROVAL: "expense.awaiting_approval",
  EXPENSE_DECISION: "expense.decision",
  // --- sabit giderler ------------------------------------------------------
  FIXED_EXPENSE_ESCALATION_DECIDED: "fixed_expense.escalation_decided",
  FIXED_EXPENSE_ESCALATION_PROPOSED: "fixed_expense.escalation_proposed",
  FIXED_EXPENSE_IBAN_PENDING: "fixed_expense.iban_pending",
  FIXED_EXPENSE_MISSING_INVOICE: "fixed_expense.missing_invoice",
  FIXED_EXPENSE_OPEN_REQUESTS: "fixed_expense.open_requests",
  FIXED_EXPENSE_OVERDUE: "fixed_expense.overdue",
  FIXED_EXPENSE_PAYMENT_REJECTED: "fixed_expense.payment_rejected",
  FIXED_EXPENSE_RENEWAL: "fixed_expense.renewal",
  FIXED_EXPENSE_VARIANCE: "fixed_expense.variance",
  // --- birim entegrasyonları -----------------------------------------------
  INTEGRATION_DECISION_REFUSED: "integration.decision_refused",
  INTEGRATION_EMPLOYEE_UNMATCHED: "integration.employee_unmatched",
  INTEGRATION_POLICY_REJECTED: "integration.policy_rejected",
  INTEGRATION_UNMAPPED_LEAVE_TYPE: "integration.unmapped_leave_type",
  // --- fatura --------------------------------------------------------------
  INVOICE_APPROVED: "invoice.approved",
  INVOICE_AWAITING_APPROVAL: "invoice.awaiting_approval",
  INVOICE_COMMENT_ADDED: "invoice.comment_added",
  INVOICE_INFO_PROVIDED: "invoice.info_provided",
  INVOICE_INFO_REQUESTED: "invoice.info_requested",
  INVOICE_INFO_WITHDRAWN: "invoice.info_withdrawn",
  INVOICE_REJECTED: "invoice.rejected",
  INVOICE_RESUBMITTED: "invoice.resubmitted",
  // --- zamanlanmış işler ---------------------------------------------------
  JOBS_UNHEALTHY: "jobs.unhealthy",
  // --- uçak kirası ---------------------------------------------------------
  LEASE_SETTLEMENT_CREATED: "lease.settlement_created",
  LEASED_AIRCRAFT_HOURS_MISSING: "leased_aircraft.hours_missing",
  LEASED_AIRCRAFT_OVERDUE: "leased_aircraft.overdue",
  LEASED_AIRCRAFT_RENEWAL: "leased_aircraft.renewal",
  // --- izin ----------------------------------------------------------------
  LEAVE_DECISION: "leave.decision",
  LEAVE_PENDING_APPROVAL: "leave.pending_approval",
  // --- mesajlaşma ----------------------------------------------------------
  MESSAGING_MENTION: "messaging.mention",
  MESSAGING_UNREAD: "messaging.unread",
  // --- ödeme ---------------------------------------------------------------
  PAYMENT_APPROVED: "payment.approved",
  PAYMENT_AWAITING_APPROVAL: "payment.awaiting_approval",
  PAYMENT_COMMENT_ADDED: "payment.comment_added",
  PAYMENT_DECISION: "payment.decision",
  PAYMENT_DEFERRED: "payment.deferred",
  PAYMENT_PAYEE_ACCOUNT_PENDING: "payment.payee_account_pending",
  PAYMENT_PAYEE_RESOLVED: "payment.payee_resolved",
  PAYMENT_RETURNED: "payment.returned",
  PAYMENT_TRANSFER_FORM_NEEDED: "payment.transfer_form_needed",
  PAYMENT_WEEK_READY: "payment.week_ready",
  // --- sürüm ---------------------------------------------------------------
  RELEASE_NOTES: "release.notes",
  // --- personel talepleri --------------------------------------------------
  STAFF_REQUEST_APPROVED: "staff_request.approved",
  STAFF_REQUEST_AWAITING_APPROVAL: "staff_request.awaiting_approval",
  STAFF_REQUEST_PURCHASED: "staff_request.purchased",
  STAFF_REQUEST_REJECTED: "staff_request.rejected",
  // --- destek --------------------------------------------------------------
  SUPPORT_ASSIGNED: "support.assigned",
  SUPPORT_COMMENT_ADDED: "support.comment_added",
  SUPPORT_STATUS_CHANGED: "support.status_changed",
  SUPPORT_TICKET_CREATED: "support.ticket_created",
  // --- sistem sağlığı ------------------------------------------------------
  SYSTEM_DISK_FULL: "system.disk_full",
  SYSTEM_DEPLOY_STALLED: "system.deploy_stalled",
  /** Dağıtım anahtarının süresi doluyor — donmadan ÖNCE (DST-00133). */
  SYSTEM_DEPLOY_TOKEN_EXPIRING: "system.deploy_token_expiring",
  SYSTEM_OWNERSHIP_LEAK: "system.ownership_leak",
  // --- vergi ---------------------------------------------------------------
  VERGI_BEYAN: "vergi.beyan",
  VERGI_ODEME: "vergi.odeme"
};
var NOTIFICATION_CODE_LIST = Object.values(NOTIFICATION_CODES);
var NOTIFICATION_CODE_LABELS = {
  "agenda.assigned": "G\xF6revlendirme",
  "agenda.comment_added": "Ajanda yorumu",
  "agenda.conflict": "Takvim \xE7ak\u0131\u015Fmas\u0131",
  "agenda.invited": "Toplant\u0131 daveti",
  "agenda.reminder": "Ajanda hat\u0131rlatmas\u0131",
  "agenda.rsvp": "Toplant\u0131 yan\u0131t\u0131",
  "agenda.status_changed": "Ajanda durumu de\u011Fi\u015Fti",
  "agenda.updated": "Ajanda kayd\u0131 g\xFCncellendi",
  "announcement.approved": "Memorandum onayland\u0131",
  "announcement.awaiting_approval": "Onay\u0131n\u0131z\u0131 bekleyen memorandum",
  "announcement.published": "Yeni duyuru / memorandum",
  "announcement.rejected": "Memorandum reddedildi",
  "announcement.reminder": "Bekleyen tebli\u011F beyan\u0131",
  "announcement.revision_requested": "Memorandum i\xE7in revizyon istendi",
  "asset.acceptance.pending": "Bekleyen zimmet kabul\xFC",
  "asset.accepted": "Zimmet kabul edildi",
  "asset.assigned": "Zimmet verildi",
  "asset.calibration.due": "Kalibrasyon zaman\u0131 geldi",
  "asset.calibration_failed": "Kalibrasyon ba\u015Far\u0131s\u0131z",
  "asset.cancelled": "Zimmet iptal edildi",
  "asset.incident": "Zimmet olay\u0131",
  "asset.maintenance.due": "Varl\u0131k bak\u0131m\u0131 zaman\u0131 geldi",
  "asset.maintenance.late_return": "Bak\u0131mdan ge\xE7 d\xF6nen varl\u0131k",
  "asset.return.overdue": "Geciken zimmet iadesi",
  "asset.return_requested": "Zimmet iadesi istendi",
  "calendar.exhausting": "Tatil takvimi t\xFCkeniyor",
  "expense.awaiting_approval": "Onay bekleyen masraf",
  "expense.decision": "Masraf karar\u0131",
  "fixed_expense.escalation_decided": "Zam talebi karara ba\u011Fland\u0131",
  "fixed_expense.escalation_proposed": "Zam talebi geldi",
  "fixed_expense.iban_pending": "S\xF6zle\u015Fme IBAN\u2019\u0131 do\u011Frulama bekliyor",
  "fixed_expense.missing_invoice": "Faturas\u0131 gelmemi\u015F sabit gider",
  "fixed_expense.open_requests": "Bekleyen sabit gider talepleri",
  "fixed_expense.overdue": "Vadesi ge\xE7mi\u015F sabit gider",
  "fixed_expense.payment_rejected": "Sabit gider \xF6demesi reddedildi",
  "fixed_expense.renewal": "S\xF6zle\u015Fme yenileme zaman\u0131",
  "fixed_expense.variance": "Sabit gider sapmas\u0131",
  "integration.decision_refused": "Birim karar\u0131 geri \xE7evrildi",
  "integration.employee_unmatched": "E\u015Fle\u015Fmeyen personel",
  "integration.policy_rejected": "Politika reddi",
  "integration.unmapped_leave_type": "E\u015Flenmemi\u015F izin t\xFCr\xFC",
  "invoice.approved": "Fatura onayland\u0131",
  "invoice.awaiting_approval": "Onay bekleyen fatura",
  "invoice.comment_added": "Fatura yaz\u0131\u015Fmas\u0131",
  "invoice.info_provided": "Fatura bilgisi verildi",
  "invoice.info_requested": "Fatura bilgisi istendi",
  "invoice.info_withdrawn": "Fatura bilgi talebi geri \xE7ekildi",
  "invoice.rejected": "Fatura reddedildi",
  "invoice.resubmitted": "Fatura yeniden g\xF6nderildi",
  "jobs.unhealthy": "Zamanlanm\u0131\u015F i\u015F sorunlu",
  "lease.settlement_created": "Kira mutabakat\u0131 olu\u015Ftu",
  "leased_aircraft.hours_missing": "U\xE7u\u015F saati girilmemi\u015F",
  "leased_aircraft.overdue": "Vadesi ge\xE7mi\u015F kira \xF6demesi",
  "leased_aircraft.renewal": "Kira s\xF6zle\u015Fmesi yenileme",
  "leave.decision": "\u0130zin karar\u0131",
  "leave.pending_approval": "Onay bekleyen izin",
  "messaging.mention": "Mesajda etiketlendiniz",
  "messaging.unread": "Okunmam\u0131\u015F mesaj",
  "payment.approved": "\xD6deme onayland\u0131",
  "payment.awaiting_approval": "Onay bekleyen \xF6deme",
  "payment.comment_added": "\xD6deme talebine yorum",
  "payment.decision": "\xD6deme karar\u0131",
  "payment.deferred": "\xD6deme ertelendi",
  "payment.payee_account_pending": "Cari banka hesab\u0131 do\u011Frulama bekliyor",
  "payment.payee_resolved": "Cari e\u015Fle\u015Ftirildi",
  "payment.returned": "\xD6deme talebi iade edildi",
  "payment.transfer_form_needed": "Transfer formu gerekiyor",
  "payment.week_ready": "\xD6deme haftas\u0131 haz\u0131r",
  "release.notes": "Yeni s\xFCr\xFCm",
  "staff_request.approved": "Personel talebi onayland\u0131",
  "staff_request.awaiting_approval": "Onay bekleyen personel talebi",
  "staff_request.purchased": "Personel talebi sat\u0131n al\u0131nd\u0131",
  "staff_request.rejected": "Personel talebi reddedildi",
  "support.assigned": "Destek kayd\u0131 atand\u0131",
  "support.comment_added": "Destek kayd\u0131na yorum",
  "support.status_changed": "Destek kayd\u0131 durumu de\u011Fi\u015Fti",
  "support.ticket_created": "Yeni destek kayd\u0131",
  "system.disk_full": "Disk doluyor",
  "system.deploy_stalled": "Da\u011F\u0131t\u0131m durdu",
  "system.deploy_token_expiring": "Da\u011F\u0131t\u0131m anahtar\u0131n\u0131n s\xFCresi doluyor",
  "system.ownership_leak": "Devredilmi\u015F \u015Firkette kay\u0131t",
  "vergi.beyan": "Vergi beyan tarihi",
  "vergi.odeme": "Vergi \xF6deme tarihi"
};
function notificationCodeGroup(code) {
  const dot = code.indexOf(".");
  return dot < 0 ? code : code.slice(0, dot);
}
var NOTIFICATION_GROUPS = [
  { prefix: "leave", label: "\u0130zin" },
  { prefix: "payment", label: "\xD6deme" },
  { prefix: "expense", label: "Masraf" },
  { prefix: "invoice", label: "Fatura" },
  { prefix: "staff_request", label: "Personel talepleri" },
  { prefix: "messaging", label: "Mesajla\u015Fma" },
  { prefix: "announcement", label: "Duyuru ve memorandum" },
  { prefix: "agenda", label: "Ajanda ve g\xF6revler" },
  { prefix: "asset", label: "Zimmet" },
  { prefix: "support", label: "Destek" },
  { prefix: "fixed_expense", label: "Sabit giderler" },
  { prefix: "lease", label: "Kira mutabakat\u0131" },
  { prefix: "leased_aircraft", label: "Kiral\u0131k u\xE7ak" },
  { prefix: "vergi", label: "Vergi takvimi" },
  { prefix: "integration", label: "Birim entegrasyonlar\u0131" },
  { prefix: "calendar", label: "Tatil takvimi" },
  { prefix: "release", label: "S\xFCr\xFCm notlar\u0131" },
  { prefix: "jobs", label: "Zamanlanm\u0131\u015F i\u015Fler" },
  { prefix: "system", label: "Sistem sa\u011Fl\u0131\u011F\u0131" }
];
var NOTIFICATION_SEVERITIES = ["info", "action_required", "warning", "critical"];
var NOTIFICATION_SEVERITY_LABELS = {
  info: "Bilgi",
  action_required: "\u0130\u015Flem gerekli",
  warning: "Uyar\u0131",
  critical: "Kritik"
};
function severityIgnoresInAppPreference(severity) {
  return severity === "action_required" || severity === "critical";
}
var notificationListQuerySchema = z34.object({
  unread: z34.enum(["true", "false"]).optional(),
  code: z34.string().max(60).regex(/^[a-z0-9_.]+$/, { message: "Ge\xE7ersiz bildirim kodu." }).optional(),
  severity: z34.enum(NOTIFICATION_SEVERITIES).optional(),
  /** Inclusive, local calendar days in Europe/Istanbul terms; the server widens `to` to end-of-day. */
  from: z34.iso.date().optional(),
  to: z34.iso.date().optional(),
  limit: z34.coerce.number().int().min(1).max(100).default(50),
  cursor: z34.string().max(120).optional()
});
var notificationMarkSchema = z34.object({
  notificationId: z34.uuid().nullable().optional()
});
var notificationPreferenceUpdateSchema = z34.object({
  group: z34.string().min(1).max(40).regex(/^[a-z0-9_]+$/, { message: "Ge\xE7ersiz bildirim grubu." }),
  inApp: z34.boolean(),
  email: z34.boolean()
});

// src/dto/deploy-status.ts
var DEPLOY_STATUS_PATH = "/yonetim/dagitim";
var REGISTRY_TOKEN_PROBE_PATTERN = /^(expires|no_expiry|rejected|unreachable|no_plain_auth|no_login|no_config|http_\d{3})$/;
function registryTokenIsDead(probe) {
  return probe === "rejected" || probe === "no_login";
}
function registryTokenProbeText(probe) {
  if (probe === "expires") return null;
  const bilinmiyor = {
    label: "Bilinmiyor",
    detail: "Sunucu tarihi GitHub'dan hen\xFCz okumad\u0131 ya da okuyamad\u0131. Okunam\u0131yorsa uyar\u0131 da gelmez \u2014 da\u011F\u0131t\u0131m dururken gelen alarm yine gelir.",
    tone: null
  };
  if (probe == null) return bilinmiyor;
  switch (probe) {
    case "no_expiry":
      return {
        label: "S\xFCresiz",
        detail: "GitHub bu token i\xE7in bir biti\u015F tarihi bildirmiyor: s\xFCresi dolmayacak. \u0130ptal edilir ya da silinirse sunucu bunu en ge\xE7 on iki saat i\xE7inde g\xF6r\xFCr ve uyar\u0131 verir.",
        tone: null
      };
    case "no_login":
      return {
        label: "Giri\u015F yok",
        detail: "Sunucu imaj deposuna (ghcr.io) hi\xE7 giri\u015F yapmam\u0131\u015F: s\u0131radaki s\xFCr\xFCm \xE7ekilemeyecek. Sunucuda grouphub kullan\u0131c\u0131s\u0131 olarak `docker login ghcr.io` ile yaln\u0131z read:packages kapsaml\u0131 bir token girin.",
        tone: "danger"
      };
    case "rejected":
      return {
        label: "Reddediliyor",
        detail: "GitHub sunucunun token'\u0131n\u0131 reddediyor: s\xFCresi dolmu\u015F ya da iptal edilmi\u015F. S\u0131radaki s\xFCr\xFCm canl\u0131ya inmeyecek. Sunucuda grouphub kullan\u0131c\u0131s\u0131 olarak `docker login ghcr.io` ile yaln\u0131z read:packages kapsaml\u0131 yeni bir token girin.",
        tone: "danger"
      };
    case "unreachable":
      return {
        label: "Okunamad\u0131",
        detail: "Sunucu api.github.com'a ula\u015Famad\u0131; on iki saat sonra yeniden soracak. Bu s\xFCrede biti\u015F tarihi uyar\u0131s\u0131 gelmez.",
        tone: "warning"
      };
    case "no_plain_auth":
      return {
        label: "Okunamad\u0131",
        detail: "Sunucu token'\u0131 bir kimlik yard\u0131mc\u0131s\u0131nda tutuyor; tarih buradan okunam\u0131yor ve biti\u015F uyar\u0131s\u0131 gelmez. Biti\u015F tarihini GitHub'daki token sayfas\u0131ndan izleyin.",
        tone: "warning"
      };
    case "no_config":
      return {
        label: "Okunamad\u0131",
        detail: "Sunucuda Docker giri\u015F bilgisi bulunamad\u0131. \u0130maj deposuna girilmemi\u015Fse s\u0131radaki s\xFCr\xFCm de canl\u0131ya inmez.",
        tone: "warning"
      };
    default:
      if (typeof probe !== "string" || !probe.startsWith("http_")) return bilinmiyor;
      return {
        label: "Okunamad\u0131",
        detail: `GitHub beklenmeyen bir cevap verdi (${probe.replace("http_", "HTTP ")}); on iki saat sonra yeniden sorulacak.`,
        tone: "warning"
      };
  }
}
function deployStateText(state, reason2) {
  switch (state) {
    case "ok":
      return "Canl\u0131, yay\u0131na haz\u0131r en yeni s\xFCr\xFCmde.";
    case "waiting":
      return reason2 === "images" ? "Yeni s\xFCr\xFCm\xFCn imajlar\u0131 hen\xFCz depoda de\u011Fil; normalde birka\xE7 dakika s\xFCrer." : "Yay\u0131na haz\u0131r i\u015Faretli bir s\xFCr\xFCm yok.";
    case "deploying":
      if (reason2 === "lock") return "Ba\u015Fka bir da\u011F\u0131t\u0131m s\xFCr\xFCyor; bitince s\u0131radaki denenecek.";
      if (reason2 === "retry") return "Da\u011F\u0131t\u0131m ba\u015Far\u0131s\u0131z oldu; bir dakika sonra yeniden denenecek.";
      return "Da\u011F\u0131t\u0131m s\xFCr\xFCyor.";
    case "blocked":
      switch (reason2) {
        case "registry":
          return "Sunucu imaj deposuna giri\u015F yapam\u0131yor \u2014 yeni s\xFCr\xFCmler canl\u0131ya inmiyor.";
        case "git_fetch":
          return "Sunucu GitHub'dan s\xFCr\xFCm etiketlerini okuyam\u0131yor \u2014 yeni s\xFCr\xFCmler canl\u0131ya inmiyor.";
        case "release_abandoned":
          return "En yeni s\xFCr\xFCm \xFC\xE7 kez da\u011F\u0131t\u0131lamad\u0131 ve b\u0131rak\u0131ld\u0131; canl\u0131 eski s\xFCr\xFCmde.";
        case "state_unwritable":
          return "Da\u011F\u0131t\u0131m deneme sayac\u0131 yaz\u0131lam\u0131yor; bozuk bir s\xFCr\xFCm s\xFCrekli denenebilir.";
        case "repo_missing":
          return "Sunucuda da\u011F\u0131t\u0131m klas\xF6r\xFC bulunamad\u0131.";
        default:
          return "Da\u011F\u0131t\u0131m durdu.";
      }
    default:
      return "Da\u011F\u0131t\u0131m bilgisi yok \u2014 denetleyici bu ortamda \xE7al\u0131\u015Fm\u0131yor ya da hen\xFCz yazmad\u0131.";
  }
}
function deployStateTone(state, stale) {
  if (stale) return "danger";
  if (state === "ok") return "success";
  if (state === "blocked") return "danger";
  if (state === "unknown") return "neutral";
  return "warning";
}
var DEPLOY_STATE_LABELS = {
  ok: "G\xFCncel",
  waiting: "Bekliyor",
  deploying: "Da\u011F\u0131t\u0131l\u0131yor",
  blocked: "Durdu",
  unknown: "Bilinmiyor"
};
function deployVerdict(view) {
  if (view.stale) return { label: "Bayat", tone: "danger" };
  if (view.state === "blocked") return { label: DEPLOY_STATE_LABELS.blocked, tone: "danger" };
  const lag = view.alerts.find((a) => a.kind.startsWith("lag"));
  if (lag) return { label: "Geride", tone: lag.severity === "critical" ? "danger" : "warning" };
  if (view.state === "ok" && view.behind !== null && view.behind > 0) {
    return { label: "Yolda", tone: "warning" };
  }
  return { label: DEPLOY_STATE_LABELS[view.state], tone: deployStateTone(view.state, false) };
}

// src/dto/search.ts
import { z as z35 } from "zod";
var SEARCH_SOURCES = [
  "employee",
  "payment_request",
  "payee",
  "fixed_commitment",
  "expense_request",
  "leave_request",
  "treasury_transaction",
  "support_ticket",
  "agenda_item",
  "announcement",
  "konaklama_guest",
  "konaklama_reservation",
  "org_unit"
];
function isSearchSource(value) {
  return SEARCH_SOURCES.includes(value);
}
var SEARCH_SOURCE_LABELS = {
  employee: "Personel",
  payment_request: "\xD6deme talepleri",
  payee: "Cari hesaplar",
  fixed_commitment: "Sabit gider s\xF6zle\u015Fmeleri",
  expense_request: "Masraf talepleri",
  leave_request: "\u0130zin talepleri",
  treasury_transaction: "Kasa/banka hareketleri",
  support_ticket: "Destek kay\u0131tlar\u0131",
  agenda_item: "Ajanda",
  announcement: "Duyurular",
  konaklama_guest: "Misafirler",
  konaklama_reservation: "Rezervasyonlar",
  org_unit: "Birimler"
};
var SEARCH_SOURCE_PERMISSIONS = {
  employee: PERMISSIONS.EMPLOYEE_READ,
  payment_request: PERMISSIONS.PAYMENT_REQUEST_CREATE,
  payee: PERMISSIONS.PAYMENT_REQUEST_CREATE,
  fixed_commitment: PERMISSIONS.FIXED_EXPENSE_READ,
  expense_request: PERMISSIONS.EXPENSE_CREATE,
  leave_request: PERMISSIONS.LEAVE_REQUEST_CREATE,
  treasury_transaction: PERMISSIONS.TREASURY_READ,
  support_ticket: PERMISSIONS.SUPPORT_READ_OWN,
  agenda_item: PERMISSIONS.AGENDA_USE,
  announcement: PERMISSIONS.ANNOUNCEMENT_READ,
  konaklama_guest: PERMISSIONS.KONAKLAMA_READ,
  konaklama_reservation: PERMISSIONS.KONAKLAMA_READ,
  org_unit: PERMISSIONS.ORG_UNIT_READ
};
var SEARCH_MATCH_KINDS = [
  "identifier_exact",
  "identifier_prefix",
  "identifier_contains",
  "name_prefix",
  "name_contains",
  "text_contains"
];
var SEARCH_WORDS_MIN = 3;
var searchQuerySchema = z35.object({
  /**
   * min(2), not 3: people type `42` for DST-00042 and `AHM-01` for a unit.
   * The ceiling reuses sortableQuerySchema's 200 — a longer term is not a
   * search, it is a paste.
   */
  q: z35.string().trim().min(2).max(200),
  perSource: z35.coerce.number().int().min(1).max(10).default(5),
  /** CSV of SearchSource. An unknown value is a 400, never a silent drop. */
  sources: z35.string().optional().transform(
    (raw) => raw === void 0 ? void 0 : raw.split(",").map((part) => part.trim()).filter((part) => part.length > 0)
  ).refine((list) => list === void 0 || list.every((s) => isSearchSource(s)), {
    message: "Bilinmeyen arama kayna\u011F\u0131."
  })
});
var IDENTIFIER_RE = /^[A-Za-zÇĞİÖŞÜçğıöşü]{2,4}[-/][A-Za-z0-9-]{2,}$/;
var PREFIX_SOURCE = {
  DST: "support_ticket",
  ODM: "payment_request",
  IZN: "leave_request",
  MSR: "expense_request",
  RZV: "konaklama_reservation",
  MSF: "konaklama_guest"
};
function recognize(raw) {
  const q = raw.trim().toLocaleUpperCase("tr");
  if (/^\d{11}$/.test(q)) return { kind: "NATIONAL_ID" };
  const dst = /^DST-?(\d{1,5})$/.exec(q);
  if (dst?.[1]) {
    return { kind: "NUMBER", source: "support_ticket", value: `DST-${dst[1].padStart(5, "0")}` };
  }
  const b36 = /^(ODM|IZN|MSR)-([0-9A-Z]{4,14})$/.exec(q);
  const b36Source = b36?.[1] ? PREFIX_SOURCE[b36[1]] : void 0;
  if (b36Source) return { kind: "NUMBER", source: b36Source, value: q };
  const doc = /^(RZV|MSF)-(\d{4})-(\d{1,4})$/.exec(q);
  const docSource = doc?.[1] ? PREFIX_SOURCE[doc[1]] : void 0;
  if (docSource && doc?.[2] && doc?.[3]) {
    return {
      kind: "NUMBER",
      source: docSource,
      value: `${doc[1]}-${doc[2]}-${doc[3].padStart(4, "0")}`
    };
  }
  if (/^\d{4}$/.test(q)) return { kind: "IBAN_LAST4", value: q };
  return null;
}
function classify(raw) {
  const trimmed = raw.trim();
  const upper = trimmed.toLocaleUpperCase("tr");
  if (IDENTIFIER_RE.test(trimmed)) return { raw: trimmed, upper, shape: "identifier" };
  if (/^\d{2,11}$/.test(trimmed)) return { raw: trimmed, upper, shape: "numeric" };
  return { raw: trimmed, upper, shape: "words" };
}
function normalizeTerm(raw, token) {
  return token?.kind === "NUMBER" ? token.value : raw.trim();
}

// src/dto/maintenance.ts
import { z as z36 } from "zod";
var MAINTENANCE_BILLING_STATES = [
  "FATURA_BEKLIYOR",
  "FATURALANDI",
  "FATURALANMAYACAK",
  "CIKIS_BEKLIYOR"
];
var MAINTENANCE_BILLING_STATE_LABELS = {
  FATURA_BEKLIYOR: "Fatura bekliyor",
  FATURALANDI: "Faturaland\u0131",
  FATURALANMAYACAK: "Faturalanmayacak",
  CIKIS_BEKLIYOR: "Bak\u0131m \xE7\u0131k\u0131\u015F\u0131 bekliyor"
};
var UNBILLED_INVOICE_STATUSES = ["DRAFT", "PROFORMA", "CANCELLED"];
var maintenanceJobQuerySchema = paginationQuerySchema.extend({
  unitId: z36.uuid().optional(),
  state: z36.enum(MAINTENANCE_BILLING_STATES).optional(),
  /** Work order number, customer name or aircraft registration. */
  q: z36.string().trim().max(120).optional(),
  releasedFrom: z36.iso.date().optional(),
  releasedTo: z36.iso.date().optional(),
  /**
   * Default false. The excluded rows are the ones a person has already answered
   * for; showing them by default would put the answered work back in front of
   * the person who answered it.
   */
  includeExcluded: z36.coerce.boolean().optional()
});
var maintenanceExcludeSchema = z36.object({
  /**
   * Required. "Why was this one written off" is the question a month later, and
   * a mandatory sentence answers it better than an optional dropdown.
   */
  reason: z36.string().trim().min(3).max(300)
});

// src/dto/leased-aircraft.ts
import { z as z37 } from "zod";
var LEASED_AIRCRAFT_STATUSES = ["PLANNED", "ACTIVE", "RETURNED", "OWNED"];
var LEASED_AIRCRAFT_STATUS_LABELS = {
  PLANNED: "Planlan\u0131yor",
  ACTIVE: "Filoda",
  RETURNED: "\u0130ade edildi",
  OWNED: "M\xFClkiyetimizde"
};
var LEASE_TYPES = ["DRY", "WET", "ACMI", "OTHER"];
var LEASE_TYPE_LABELS = {
  DRY: "Kuru kiralama (dry lease)",
  WET: "Islak kiralama (wet lease)",
  ACMI: "ACMI",
  OTHER: "Di\u011Fer"
};
var LEASE_CHARGE_MODELS = ["GUARANTEED_HOURS", "FIXED_PLUS_EXCESS", "FIXED_ONLY"];
var LEASE_CHARGE_MODEL_LABELS = {
  GUARANTEED_HOURS: "Saat \xD7 \xFCcret, garanti taban",
  FIXED_PLUS_EXCESS: "Sabit kira + a\u015F\u0131m \xFCcreti",
  FIXED_ONLY: "Sabit kira (saat yaln\u0131zca takip)"
};
var LEASE_CHARGE_MODEL_HINTS = {
  GUARANTEED_HOURS: "U\xE7ulan saat garantinin alt\u0131ndaysa garanti kadar, \xFCst\xFCndeyse u\xE7ulan kadar \xF6denir. Ayr\u0131ca sabit kira yoktur.",
  FIXED_PLUS_EXCESS: "Her taksitte sabit kira \xF6denir; garanti saati a\u015Fan saatler ayr\u0131ca a\u015F\u0131m \xFCcretiyle faturalan\u0131r.",
  FIXED_ONLY: "Tutar hi\xE7 de\u011Fi\u015Fmez. Saatler yine kaydedilir ve garantiyle kar\u015F\u0131la\u015Ft\u0131r\u0131l\u0131r, ama faturay\u0131 etkilemez."
};
var LEASE_GUARANTEE_PERIOD_KINDS = ["MONTH", "YEAR"];
var LEASE_GUARANTEE_PERIOD_KIND_LABELS = {
  MONTH: "Ayl\u0131k",
  YEAR: "Y\u0131ll\u0131k"
};
var LEASE_SPLIT_MODES = ["NONE", "RATIO", "FIXED_OFFICIAL"];
var LEASE_SPLIT_MODE_LABELS = {
  NONE: "Tamam\u0131 resm\xEE",
  RATIO: "Oransal (y\xFCzde)",
  FIXED_OFFICIAL: "Sabit resm\xEE tutar, kalan\u0131 gayriresm\xEE"
};
var LEASE_CONTRACT_STATUSES = [
  "DRAFT",
  "ACTIVE",
  "SUSPENDED",
  "ENDED",
  "TERMINATED"
];
var LEASE_CONTRACT_STATUS_LABELS = {
  DRAFT: "Taslak",
  ACTIVE: "Y\xFCr\xFCrl\xFCkte",
  SUSPENDED: "Ask\u0131da",
  ENDED: "S\xFCresi doldu",
  TERMINATED: "Feshedildi"
};
var LEASE_PERIOD_STATUSES = ["OPEN", "HOURS_CONFIRMED", "SETTLED", "CANCELLED"];
var LEASE_PERIOD_STATUS_LABELS = {
  OPEN: "A\xE7\u0131k",
  HOURS_CONFIRMED: "Saatler kesinle\u015Fti",
  SETTLED: "Mahsupla\u015Ft\u0131",
  CANCELLED: "\u0130ptal"
};
var LEASE_HOURS_SOURCES = ["DIRECT", "LOG"];
var LEASE_HOURS_SOURCE_LABELS = {
  DIRECT: "Tek rakam girildi",
  LOG: "U\xE7u\u015F kay\u0131tlar\u0131ndan topland\u0131"
};
var LEASE_DUE_KINDS = ["SCHEDULED", "SETTLEMENT", "ONE_OFF"];
var LEASE_DUE_KIND_LABELS = {
  SCHEDULED: "Taksit",
  SETTLEMENT: "Garanti mahsubu",
  ONE_OFF: "Tek seferlik"
};
var LEASE_DUE_LEGS = ["OFFICIAL", "UNOFFICIAL"];
var LEASE_DUE_LEG_LABELS = {
  OFFICIAL: "Resm\xEE",
  UNOFFICIAL: "Gayriresm\xEE"
};
var LEASE_DUE_STATUSES = [
  "PLANNED",
  "READY",
  "PAYMENT_REQUESTED",
  "PAID",
  "CANCELLED"
];
var LEASE_DUE_STATUS_LABELS = {
  PLANNED: "Planland\u0131",
  READY: "\xD6demeye haz\u0131r",
  PAYMENT_REQUESTED: "\xD6deme talebi a\xE7\u0131ld\u0131",
  PAID: "\xD6dendi",
  CANCELLED: "\u0130ptal"
};
var LEASE_FLIGHT_LOG_SOURCES = ["MANUAL", "IMPORT"];
var LEASE_ATTACHMENT_KINDS = [
  "CONTRACT",
  "INVOICE",
  "RECEIPT",
  "HOURS_REPORT",
  "OTHER"
];
var LEASE_ATTACHMENT_KIND_LABELS = {
  CONTRACT: "S\xF6zle\u015Fme",
  INVOICE: "Fatura",
  RECEIPT: "Dekont",
  HOURS_REPORT: "U\xE7u\u015F saati raporu",
  OTHER: "Di\u011Fer"
};
var LEASE_EVENTS = [
  "CONTRACT_CREATED",
  "CONTRACT_UPDATED",
  "CONTRACT_ACTIVATED",
  "CONTRACT_SUSPENDED",
  "CONTRACT_ENDED",
  "CONTRACT_TERMINATED",
  "PERIODS_GENERATED",
  "HOURS_RECORDED",
  "HOURS_CONFIRMED",
  "HOURS_REOPENED",
  "SETTLEMENT_CREATED",
  "DUE_CREATED",
  "DUE_READY",
  "DUE_FILED",
  "DUE_FILE_FAILED",
  "DUE_PAID",
  "DUE_CANCELLED"
];
var LEASE_OFFICIAL_DOCUMENT_TYPES = ["E_INVOICE", "E_ARCHIVE", "PROFORMA"];
var money3 = moneyText({ min: 0, max: 99999999999 });
var positiveMoney3 = moneyText({ min: 0.01, max: 99999999999 });
var hoursText = (opts = {}) => z37.string().min(1, "Saat bo\u015F.").max(16).transform((raw, ctx) => {
  const parsed = parseMoney(raw.trim(), "tr");
  if (!parsed.ok || parsed.value === null) {
    ctx.addIssue({ code: "custom", message: parsed.reason ?? "Saat okunamad\u0131." });
    return z37.NEVER;
  }
  const min = opts.min ?? 0;
  const max = opts.max ?? 1e5;
  if (parsed.value < min) {
    ctx.addIssue({ code: "custom", message: `Saat en az ${min} olabilir.` });
    return z37.NEVER;
  }
  if (parsed.value > max) {
    ctx.addIssue({ code: "custom", message: `Saat en fazla ${max} olabilir.` });
    return z37.NEVER;
  }
  return parsed.value;
});
var percent2 = z37.number().min(0).max(100).refine((n) => Number.isInteger(Math.round(n * 100)), {
  message: "Oran en fazla iki ondal\u0131k haneli olabilir."
});
var currency = z37.string().trim().length(3, "Para birimi \xFC\xE7 harf olmal\u0131.").transform((v) => v.toUpperCase());
var isoDate9 = z37.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Tarih GG.AA.YYYY bi\xE7iminde se\xE7ilmeli.");
var leasedAircraftFields = z37.object({
  companyId: z37.uuid(),
  /** Kuyruk numarası boyandığı gibi: TC-ABC. Büyük harfe çevriliyor. */
  registration: z37.string().trim().min(3).max(12).transform((v) => v.toUpperCase()),
  aircraftType: z37.string().trim().min(2).max(80),
  serialNumber: z37.string().trim().max(40).optional(),
  manufactureYear: z37.coerce.number().int().min(1930).max(2100).optional(),
  homeBase: z37.string().trim().max(60).optional(),
  status: z37.enum(LEASED_AIRCRAFT_STATUSES).default("ACTIVE"),
  notes: z37.string().trim().max(4e3).optional()
});
var createLeasedAircraftSchema = leasedAircraftFields;
var updateLeasedAircraftSchema = patchSchema(leasedAircraftFields).omit({ companyId: true }).partial().extend({ version: z37.coerce.number().int().min(1) });
var leasedAircraftListQuerySchema = sortableQuerySchema.extend({
  companyId: z37.uuid().optional(),
  status: z37.enum(LEASED_AIRCRAFT_STATUSES).optional()
});
var LEASED_AIRCRAFT_SORT_FIELDS = [
  "registration",
  "aircraftType",
  "status",
  "createdAt"
];
var leaseContractFields = z37.object({
  companyId: z37.uuid(),
  aircraftId: z37.uuid(),
  contractNo: z37.string().trim().min(2).max(40),
  title: z37.string().trim().min(2).max(200),
  lessorPayeeId: z37.uuid().optional(),
  /**
   * Kiraya verenin hangi banka hesabına ödeneceği.
   *
   * Cari birden çok hesap taşıyorsa ZORUNLU hâle geliyor — ödeme modülü
   * kendiliğinden seçmiyor ve seçmemeli. Tek hesaplı bir caride boş bırakılır.
   */
  lessorAccountId: z37.uuid().optional(),
  lessorName: z37.string().trim().min(2).max(200),
  leaseType: z37.enum(LEASE_TYPES).default("DRY"),
  startDate: isoDate9,
  endDate: isoDate9.optional(),
  noticePeriodDays: z37.coerce.number().int().min(0).max(365).default(0),
  autoRenew: z37.boolean().default(false),
  renewalAlertDays: z37.coerce.number().int().min(0).max(365).default(60),
  chargeModel: z37.enum(LEASE_CHARGE_MODELS).default("GUARANTEED_HOURS"),
  guaranteePeriodKind: z37.enum(LEASE_GUARANTEE_PERIOD_KINDS).default("MONTH"),
  guaranteedHours: hoursText({ min: 0, max: 1e5 }).default(0),
  hourlyRate: positiveMoney3.optional(),
  excessHourlyRate: positiveMoney3.optional(),
  fixedAmount: positiveMoney3.optional(),
  currency: currency.default("TRY"),
  vatRate: percent2.default(20),
  amountIncludesVat: z37.boolean().default(false),
  splitMode: z37.enum(LEASE_SPLIT_MODES).default("NONE"),
  officialRatio: percent2.optional(),
  officialFixedAmount: money3.optional(),
  splitCurrency: currency.optional(),
  paymentIntervalMonths: z37.coerce.number().int().min(1).max(24).default(1),
  dueDayOfMonth: z37.coerce.number().int().min(1).max(31).default(1),
  dueOnLastDay: z37.boolean().default(false),
  settlementDueDays: z37.coerce.number().int().min(0).max(180).default(15),
  autoFileDaysBefore: z37.coerce.number().int().min(0).max(60).default(7),
  autoFile: z37.boolean().default(true),
  officialDocumentType: z37.enum(LEASE_OFFICIAL_DOCUMENT_TYPES).default("E_INVOICE"),
  orgUnitId: z37.uuid().optional(),
  costCenterId: z37.uuid().optional(),
  responsibleUserId: z37.uuid().optional(),
  notes: z37.string().trim().max(4e3).optional()
});
var withLeaseContractRules = (schema) => schema.refine((v) => v.endDate === void 0 || v.endDate >= v.startDate, {
  message: "Biti\u015F tarihi ba\u015Flang\u0131\xE7tan \xF6nce olamaz.",
  path: ["endDate"]
}).refine((v) => v.chargeModel !== "GUARANTEED_HOURS" || v.hourlyRate !== void 0, {
  message: "Saat ba\u015F\u0131 \xFCcret olmadan garanti saat \xFCcrete d\xF6n\xFC\u015Femez.",
  path: ["hourlyRate"]
}).refine((v) => v.chargeModel === "GUARANTEED_HOURS" || v.fixedAmount !== void 0, {
  message: "Sabit kira tutar\u0131 zorunlu.",
  path: ["fixedAmount"]
}).refine((v) => v.chargeModel !== "FIXED_PLUS_EXCESS" || v.excessHourlyRate !== void 0, {
  message: "A\u015F\u0131m \xFCcreti olmadan garanti saati a\u015Fan saatler faturalanamaz.",
  path: ["excessHourlyRate"]
}).refine((v) => v.splitMode !== "RATIO" || v.officialRatio !== void 0, {
  message: "Resm\xEE oran girilmeli.",
  path: ["officialRatio"]
}).refine((v) => v.splitMode !== "FIXED_OFFICIAL" || v.officialFixedAmount !== void 0, {
  message: "Resm\xEE tutar girilmeli.",
  path: ["officialFixedAmount"]
}).refine((v) => v.splitMode !== "NONE" || v.splitCurrency === void 0, {
  message: "Ayr\u0131m yokken ikinci bir para birimi se\xE7ilemez.",
  path: ["splitCurrency"]
});
var createLeaseContractSchema = withLeaseContractRules(leaseContractFields);
var updateLeaseContractSchema = patchSchema(leaseContractFields).omit({ companyId: true, aircraftId: true }).partial().extend({ version: z37.coerce.number().int().min(1) });
var leaseContractStatusSchema = z37.object({
  status: z37.enum(LEASE_CONTRACT_STATUSES),
  /** TERMINATED için zorunlu; gerekçesiz bir fesih kayıtta bir boşluktur. */
  note: z37.string().trim().max(4e3).optional(),
  terminatedOn: isoDate9.optional(),
  version: z37.coerce.number().int().min(1)
});
var leaseContractListQuerySchema = sortableQuerySchema.extend({
  companyId: z37.uuid().optional(),
  aircraftId: z37.uuid().optional(),
  status: z37.enum(LEASE_CONTRACT_STATUSES).optional(),
  chargeModel: z37.enum(LEASE_CHARGE_MODELS).optional(),
  /** Süresi bu kadar gün içinde dolacak sözleşmeler. */
  endingWithinDays: z37.coerce.number().int().min(1).max(365).optional()
});
var LEASE_CONTRACT_SORT_FIELDS = [
  "contractNo",
  "title",
  "startDate",
  "endDate",
  "status",
  "createdAt"
];
var leaseGenerateSchema = z37.object({
  contractId: z37.uuid().optional(),
  companyId: z37.uuid().optional(),
  /** Kaç ay ileriye dönem üretilecek. */
  horizonMonths: z37.coerce.number().int().min(1).max(36).default(12)
});
var setLeasePeriodHoursSchema = z37.object({
  hours: hoursText({ min: 0, max: 1e5 }),
  note: z37.string().trim().max(300).optional(),
  version: z37.coerce.number().int().min(1)
});
var confirmLeasePeriodHoursSchema = z37.object({
  version: z37.coerce.number().int().min(1)
});
var reopenLeasePeriodSchema = z37.object({
  reason: z37.string().trim().min(5).max(300),
  version: z37.coerce.number().int().min(1)
});
var leasePeriodListQuerySchema = sortableQuerySchema.extend({
  companyId: z37.uuid().optional(),
  contractId: z37.uuid().optional(),
  aircraftId: z37.uuid().optional(),
  status: z37.enum(LEASE_PERIOD_STATUSES).optional(),
  year: z37.coerce.number().int().min(2020).max(2100).optional(),
  /** Bitmiş ama saati hâlâ girilmemiş dönemler — asıl sorulan liste. */
  missingHours: z37.coerce.boolean().optional()
});
var LEASE_PERIOD_SORT_FIELDS = [
  "periodStartsOn",
  "periodEndsOn",
  "periodKey",
  "status"
];
var leaseFlightLogFields = z37.object({
  periodId: z37.uuid(),
  flightDate: isoDate9,
  hours: hoursText({ min: 0.01, max: 1e3 }),
  reference: z37.string().trim().max(60).optional(),
  note: z37.string().trim().max(300).optional()
});
var createLeaseFlightLogSchema = leaseFlightLogFields;
var updateLeaseFlightLogSchema = patchSchema(leaseFlightLogFields).omit({ periodId: true }).partial().extend({ version: z37.coerce.number().int().min(1) });
var createLeaseOneOffDueSchema = z37.object({
  contractId: z37.uuid(),
  label: z37.string().trim().min(2).max(80),
  dueDate: isoDate9,
  amount: positiveMoney3,
  currency: currency.optional(),
  leg: z37.enum(LEASE_DUE_LEGS).default("OFFICIAL"),
  /** Resmî ayakta KDV; gayriresmî ayakta yok sayılır. */
  vatRate: percent2.optional(),
  note: z37.string().trim().max(300).optional()
});
var updateLeaseDueSchema = z37.object({
  label: z37.string().trim().min(2).max(80).optional(),
  dueDate: isoDate9.optional(),
  amount: positiveMoney3.optional(),
  currency: currency.optional(),
  note: z37.string().trim().max(300).optional(),
  version: z37.coerce.number().int().min(1)
});
var leaseDueActionSchema = z37.object({
  version: z37.coerce.number().int().min(1)
});
var cancelLeaseDueSchema = z37.object({
  reason: z37.string().trim().min(5).max(300),
  version: z37.coerce.number().int().min(1)
});
var markLeaseDuePaidSchema = z37.object({
  paidOn: isoDate9,
  paidAmount: positiveMoney3.optional(),
  version: z37.coerce.number().int().min(1)
});
var leaseDueListQuerySchema = sortableQuerySchema.extend({
  companyId: z37.uuid().optional(),
  contractId: z37.uuid().optional(),
  periodId: z37.uuid().optional(),
  status: z37.enum(LEASE_DUE_STATUSES).optional(),
  leg: z37.enum(LEASE_DUE_LEGS).optional(),
  kind: z37.enum(LEASE_DUE_KINDS).optional(),
  dueFrom: isoDate9.optional(),
  dueTo: isoDate9.optional(),
  /** Vadesi geçmiş ve hâlâ ödenmemiş — bir bayrak değil, okuma anında hesap. */
  overdue: z37.coerce.boolean().optional()
});
var LEASE_DUE_SORT_FIELDS = ["dueDate", "status", "amount", "createdAt"];

// src/integration/v1/events.ts
import { z as z38 } from "zod";
var INTEGRATION_SCHEMA_VERSION = "1.0";
var INTEGRATION_HEADERS = {
  unit: "x-asya-unit",
  keyId: "x-asya-key-id",
  timestamp: "x-asya-timestamp",
  nonce: "x-asya-nonce",
  signature: "x-asya-signature"
};
var INTEGRATION_EVENT_TYPES = [
  "leave.request.created",
  "leave.request.updated",
  "leave.request.cancelled",
  "work.record.created",
  "employee.upserted",
  "attendance.day.recorded",
  "presence.snapshot",
  "maintenance.released",
  "maintenance.invoiced"
];
var externalEmployeeSchema = z38.object({
  externalUserId: z38.string().min(1).max(64),
  externalProfileId: z38.string().max(64).optional(),
  externalEmployeeNo: z38.string().max(40).optional(),
  email: z38.email().max(160).optional(),
  firstName: z38.string().max(80).optional(),
  lastName: z38.string().max(80).optional(),
  /** Blind-index matching only; the hub never stores the plaintext from here. */
  nationalId: z38.string().regex(/^\d{11}$/).optional()
});
var leaveEventPayloadSchema = z38.object({
  /** The unit's own vocabulary. Canonicalisation happens in the hub. */
  leaveTypeCode: z38.string().min(1).max(100),
  leaveTypeLabel: z38.string().max(200).optional(),
  startDate: z38.iso.date(),
  endDate: z38.iso.date(),
  startTime: z38.string().regex(/^\d{2}:\d{2}$/).nullish(),
  endTime: z38.string().regex(/^\d{2}:\d{2}$/).nullish(),
  startHalf: z38.boolean().default(false),
  endHalf: z38.boolean().default(false),
  reason: z38.string().max(2e3).nullish(),
  /** SUBMITTED | APPROVED | REJECTED | CANCELLED, as the unit sees it. */
  sourceStatus: z38.enum(["SUBMITTED", "APPROVED", "REJECTED", "CANCELLED"]),
  /**
   * Gates already passed in the unit. The hub marks the matching chain steps
   * as decided EXTERNAL instead of asking the same human twice.
   */
  passedGates: z38.array(z38.string().max(30)).default([]),
  employee: externalEmployeeSchema,
  /** Whatever else the unit wants preserved for the audit trail. */
  extra: z38.record(z38.string(), z38.unknown()).optional()
});
var attendanceEventPayloadSchema = z38.object({
  /** The business day as the unit filed it. */
  workDate: z38.iso.date(),
  clockIn: z38.iso.datetime().nullish(),
  clockOut: z38.iso.datetime().nullish(),
  /** The unit's own word for the day. Carried for the audit trail, never applied. */
  sourceStatus: z38.string().max(30).nullish(),
  /** Entered by a human in the unit rather than punched. */
  manual: z38.boolean().default(false),
  note: z38.string().max(2e3).nullish(),
  employee: externalEmployeeSchema,
  extra: z38.record(z38.string(), z38.unknown()).optional()
});
var PATH_SHAPED = /^\s*\/|:\/\/|\?|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|(?<![a-z0-9_-])(?=[a-z0-9_-]*[0-9])(?=[a-z0-9_-]*[a-z])[a-z0-9_-]{20,}(?![a-z0-9_-])/i;
function shapedLikeAPath(value) {
  return PATH_SHAPED.test(value);
}
var presenceSnapshotPayloadSchema = z38.object({
  /** When the unit took the reading, not when it sent it. */
  observedAt: z38.iso.datetime(),
  /**
   * How far back the unit looked. The hub does not act on it, but it is what
   * makes a snapshot self-describing: "nobody active" from a 30-second window
   * and from an hour-long one are different claims.
   */
  windowSeconds: z38.number().int().positive().max(3600),
  people: z38.array(
    z38.object({
      employee: externalEmployeeSchema,
      lastSeenAt: z38.iso.datetime(),
      /**
       * Blanked when it is shaped like an address. See `shapedLikeAPath`.
       *
       * A transform rather than a refine: the envelope must still land. One
       * person on an unrecognised route is not a reason to refuse a unit's
       * whole snapshot, and 400 here is permanent — the batch would never be
       * retried.
       */
      pageLabel: z38.string().max(80).nullish().transform((value) => value && shapedLikeAPath(value) ? null : value)
    })
  ).max(500)
});
var maintenanceCustomerSchema = z38.object({
  externalId: z38.string().min(1).max(64),
  /** The unit's cari code, when it has one. Matches `fin_payee.code` if ever needed. */
  code: z38.string().max(60).nullish(),
  name: z38.string().min(1).max(200)
});
var maintenanceReleasedPayloadSchema = z38.object({
  workOrderNumber: z38.string().min(1).max(60),
  crsNumber: z38.string().max(60).nullish(),
  /** The release moment — the unit's own, not when we heard about it. */
  releasedAt: z38.iso.datetime(),
  /**
   * When the job was opened. Without it the hub cannot tell a package that ran
   * from March to August from one opened and released the same week, and the
   * ageing column would call both of them equally fresh.
   */
  openedAt: z38.iso.datetime().nullish(),
  customer: maintenanceCustomerSchema,
  aircraftRegistration: z38.string().max(20).nullish(),
  title: z38.string().max(300).nullish(),
  /** The unit's own billable figure. Null until the unit has frozen its cost. */
  billableTotal: z38.string().max(24).nullish(),
  currency: z38.string().length(3).nullish(),
  /**
   * Whether the unit considers the figures above frozen. False (or absent) means
   * the screen must say so rather than show a number as if it were settled.
   */
  totalsAreFinal: z38.boolean().optional()
});
var maintenanceInvoicedPayloadSchema = z38.object({
  workOrderNumber: z38.string().min(1).max(60),
  customer: maintenanceCustomerSchema,
  invoiceNumber: z38.string().min(1).max(60),
  /** The unit's own vocabulary, e.g. DRAFT | PROFORMA | INVOICED | CANCELLED | PAID. */
  invoiceStatus: z38.string().min(1).max(30),
  issuedAt: z38.iso.datetime().nullish(),
  total: z38.string().max(24).nullish(),
  currency: z38.string().length(3).nullish()
});
var integrationEventSchema = z38.object({
  schemaVersion: z38.string().max(10),
  eventId: z38.uuid(),
  eventType: z38.enum(INTEGRATION_EVENT_TYPES),
  occurredAt: z38.iso.datetime(),
  sentAt: z38.iso.datetime().optional(),
  unitCode: z38.string().min(1).max(40),
  sourceSystem: z38.string().min(1).max(60),
  sourceRecordId: z38.string().min(1).max(64),
  /**
   * Monotone per source record. The hub applies with
   * `WHERE source_record_version < n`, which turns out-of-order delivery into a
   * no-op instead of corruption — so events may be fat and unordered.
   */
  sourceRecordVersion: z38.number().int().nonnegative(),
  idempotencyKey: z38.string().min(1).max(300),
  correlationId: z38.string().max(64).optional(),
  producer: z38.object({
    app: z38.string().max(60),
    version: z38.string().max(40).optional(),
    contractSha: z38.string().max(64).optional()
  }).optional(),
  /**
   * Validated in the worker, NOT at the HTTP edge: a malformed payload should
   * become a visible failed row in the hub console, where there is an admin
   * screen and a human, rather than a 400 the unit retries seven times and
   * dead-letters in a system with no such screen.
   */
  payload: z38.unknown()
});
var integrationEventBatchSchema = z38.object({
  events: z38.array(integrationEventSchema).min(1).max(100)
});
var decisionEventSchema = z38.object({
  schemaVersion: z38.string().max(10),
  eventId: z38.uuid(),
  eventType: z38.literal("leave.decision.made"),
  occurredAt: z38.iso.datetime(),
  sourceRecordId: z38.string().max(64),
  decision: z38.enum(["APPROVED", "REJECTED"]),
  gates: z38.array(z38.string().max(30)),
  decidedBy: z38.object({
    name: z38.string().max(160),
    role: z38.string().max(80),
    email: z38.string().max(160).optional()
  }),
  comment: z38.string().max(2e3).nullish(),
  hubRequestId: z38.uuid(),
  hubRequestNumber: z38.string().max(40)
});
var decisionAckSchema = z38.object({
  applied: z38.boolean(),
  sourceStatus: z38.string().max(40).optional(),
  sourceRecordVersion: z38.number().int().optional(),
  /** e.g. LEAD_APPROVAL_REQUIRED — neither transient nor permanent. */
  reasonCode: z38.string().max(60).optional(),
  message: z38.string().max(500).optional()
});
var BLOCKING_REASON_CODES = {
  LEAD_APPROVAL_REQUIRED: "leave.request.updated"
};
export {
  ACILIS_IZNI,
  ACILIS_ROL,
  ACTIVITY_PRESENCE,
  AGENDA_ACTIONABLE_KINDS,
  AGENDA_CLOSED_STATUSES,
  AGENDA_KINDS,
  AGENDA_KIND_LABELS,
  AGENDA_PRIORITIES,
  AGENDA_PRIORITY_LABELS,
  AGENDA_RANGE_MAX_DAYS,
  AGENDA_RECURRENCES,
  AGENDA_RECURRENCE_LABELS,
  AGENDA_RSVPS,
  AGENDA_RSVP_ANSWERS,
  AGENDA_RSVP_LABELS,
  AGENDA_RSVP_NEEDS_TELLING,
  AGENDA_SORT_FIELDS,
  AGENDA_STATUSES,
  AGENDA_STATUSES_NEEDING_REASON,
  AGENDA_STATUS_LABELS,
  AGENDA_STATUS_TRANSITIONS,
  AGENDA_VIEWS,
  AKTARIM_ENGEL_MESAJLARI,
  ALL_PERMISSIONS,
  ANNOUNCEMENT_ACK_MODES,
  ANNOUNCEMENT_ACK_MODES_REQUIRING_DECLARATION,
  ANNOUNCEMENT_ACK_MODE_LABELS,
  ANNOUNCEMENT_ACK_STATEMENT,
  ANNOUNCEMENT_ADDED_REASONS,
  ANNOUNCEMENT_ADDED_REASON_LABELS,
  ANNOUNCEMENT_APPROVAL_COMMENT_MIN,
  ANNOUNCEMENT_APPROVAL_DECISIONS,
  ANNOUNCEMENT_APPROVAL_DECISION_LABELS,
  ANNOUNCEMENT_APPROVAL_STATES,
  ANNOUNCEMENT_APPROVAL_STATES_BLOCKING_PUBLISH,
  ANNOUNCEMENT_APPROVAL_STATE_LABELS,
  ANNOUNCEMENT_ATTACHMENT_CAPTION_MAX,
  ANNOUNCEMENT_ATTACHMENT_MAX,
  ANNOUNCEMENT_AUDIENCE_MAX,
  ANNOUNCEMENT_AUDIENCE_MODES,
  ANNOUNCEMENT_AUDIENCE_MODE_LABELS,
  ANNOUNCEMENT_CATEGORY_KINDS,
  ANNOUNCEMENT_CATEGORY_KIND_LABELS,
  ANNOUNCEMENT_CONFIDENTIALITIES,
  ANNOUNCEMENT_CONFIDENTIALITY_LABELS,
  ANNOUNCEMENT_KINDS,
  ANNOUNCEMENT_KIND_LABELS,
  ANNOUNCEMENT_LINKED_ENTITY_TYPES,
  ANNOUNCEMENT_LINKED_ENTITY_TYPE_LABELS,
  ANNOUNCEMENT_PRIORITIES,
  ANNOUNCEMENT_PRIORITY_LABELS,
  ANNOUNCEMENT_RECEIPT_PAGE_SIZE,
  ANNOUNCEMENT_RECIPIENT_ROLES,
  ANNOUNCEMENT_RECIPIENT_ROLE_LABELS,
  ANNOUNCEMENT_TARGET_KINDS,
  ANNOUNCEMENT_TARGET_KIND_LABELS,
  ANNOUNCEMENT_VIEWS,
  ANNOUNCEMENT_VIEW_LABELS,
  APPROVAL_DOMAINS,
  APPROVAL_DOMAIN_LABELS,
  APPROVAL_STAGE_MODES,
  ASSET_ACCEPT_STATEMENT,
  ASSET_ATTACHMENT_KINDS,
  ASSET_ATTACHMENT_KIND_LABELS,
  ASSET_CONDITIONS,
  ASSET_CONDITION_LABELS,
  ASSET_KINDS,
  ASSET_KIND_LABELS,
  ASSET_MOVEMENT_KINDS,
  ASSET_MOVEMENT_LABELS,
  ASSET_REPORT_KINDS,
  ASSET_REPORT_LABELS,
  ASSET_SORT_FIELDS,
  ASSET_STATUSES,
  ASSET_STATUS_LABELS,
  ASSET_TRACKINGS,
  ASSET_TRACKING_LABELS,
  ASSET_UNITS,
  ASSET_UNIT_LABELS,
  AUDIT_ACTION_LABELS,
  AUDIT_ENTITY_LABELS,
  AUDIT_FIELD_LABELS,
  BLOCKING_REASON_CODES,
  CALIBRATION_RESULTS,
  CALIBRATION_RESULT_LABELS,
  COMPANY_SORT_FIELDS,
  CONTRACT_TYPES,
  CONTRACT_TYPE_LABELS,
  CONVERSATION_KINDS,
  CONVERSATION_KIND_LABELS,
  CONVERSATION_KIND_REQUIRED_PERMISSION,
  CONVERSATION_LINK_TYPES,
  CONVERSATION_LINK_TYPE_LABELS,
  CONVERSATION_PARTICIPANTS_PER_REQUEST_MAX,
  CONVERSATION_PARTICIPANT_MAX,
  CONVERSATION_TITLE_MAX,
  CONVERSATION_TOPIC_MAX,
  CONVERSATION_VIEWS,
  CORRECTION_OPERATIONS,
  CORRECTION_OPERATION_LABELS,
  CORRECTION_STATUSES,
  CORRECTION_STATUS_LABELS,
  COST_BEARING_REPORTS,
  CUSTODY_CANDIDATE_REASONS,
  CUSTODY_CANDIDATE_REASON_MESSAGES,
  CUSTODY_HOLDER_KINDS,
  CUSTODY_HOLDER_KIND_LABELS,
  CUSTODY_SORT_FIELDS,
  CUSTODY_STATUSES,
  CUSTODY_STATUS_LABELS,
  DEPLOY_STATE_LABELS,
  DEPLOY_STATUS_PATH,
  DOCUMENT_LIBRARY_ACCEPT,
  DOCUMENT_LIBRARY_ACCEPT_LABEL,
  EMPLOYEE_COMPLETENESS_FIELDS,
  EMPLOYEE_COMPLETENESS_LABELS,
  EMPLOYEE_SORT_FIELDS,
  EMPLOYEE_STATUSES,
  EMPLOYEE_STATUS_LABELS,
  ERROR_CODES,
  FATURA_BELGE_TURLERI,
  FATURA_ICERIGI_AZAMI,
  FATURA_ICERIGI_TURLERI,
  FATURA_NOT_ASGARI,
  FATURA_YANIT_ASGARI,
  FIXED_EXPENSE_AMOUNT_BASES,
  FIXED_EXPENSE_AMOUNT_BASIS_LABELS,
  FIXED_EXPENSE_ATTACHMENT_KINDS,
  FIXED_EXPENSE_ATTACHMENT_KIND_LABELS,
  FIXED_EXPENSE_COMMITMENT_SORT_FIELDS,
  FIXED_EXPENSE_ESCALATION_BASES,
  FIXED_EXPENSE_ESCALATION_BASIS_LABELS,
  FIXED_EXPENSE_ESCALATION_STATUSES,
  FIXED_EXPENSE_ESCALATION_STATUS_LABELS,
  FIXED_EXPENSE_INTERVALS,
  FIXED_EXPENSE_INTERVAL_LABELS,
  FIXED_EXPENSE_KINDS,
  FIXED_EXPENSE_KIND_LABELS,
  FIXED_EXPENSE_LOAN_RATE_TYPES,
  FIXED_EXPENSE_LOAN_RATE_TYPE_LABELS,
  FIXED_EXPENSE_OCCURRENCE_SORT_FIELDS,
  FIXED_EXPENSE_OCCURRENCE_STATUSES,
  FIXED_EXPENSE_OCCURRENCE_STATUS_LABELS,
  FIXED_EXPENSE_OPEN_STATUSES,
  FIXED_EXPENSE_STATUSES,
  FIXED_EXPENSE_STATUS_LABELS,
  FIXED_EXPENSE_WITHHOLDING_CORRECTION_MODES,
  FIXED_EXPENSE_WITHHOLDING_CORRECTION_MODE_LABELS,
  FLIGHT_ENTRY_KINDS,
  FLIGHT_ENTRY_KIND_LABELS,
  FLIGHT_IMPORTED_SOURCES,
  FLIGHT_PREMIUM_PERIOD_STATUSES,
  FLIGHT_PREMIUM_PERIOD_STATUS_LABELS,
  FLIGHT_RATE_CHANGE_REASONS,
  FLIGHT_RATE_CHANGE_REASON_LABELS,
  FLIGHT_SOURCE_TYPES,
  FLIGHT_SOURCE_TYPE_LABELS,
  GENDERS,
  GENDER_LABELS,
  GEREKCE_ASGARI,
  GIB_BELGE_NO_DESENI,
  GROUP_LEVEL_COMPANY_ID,
  IADEYE_CEVRILEBILIR_BELGELER,
  IADEYE_CEVRILEBILIR_DURUMLAR,
  IBAN_DOGRULAMA_ETIKETLERI,
  INTEGRATION_EVENT_TYPES,
  INTEGRATION_HEADERS,
  INTEGRATION_SCHEMA_VERSION,
  INVOICE_DECISIONS,
  INVOICE_DECISION_LABELS,
  INVOICE_DIRECTIONS,
  INVOICE_DIRECTION_LABELS,
  INVOICE_DOCUMENT_TYPES,
  INVOICE_DOCUMENT_TYPE_LABELS,
  INVOICE_ENTERED_LABELS,
  INVOICE_FIELD_LABELS,
  INVOICE_QUESTION_DECISION_LABELS,
  INVOICE_QUESTION_TARGETS,
  INVOICE_STATUSES,
  INVOICE_STATUS_LABELS,
  KONAKLAMA_ADJUST_ENTRY_TYPES,
  KONAKLAMA_BED_TYPES,
  KONAKLAMA_BED_TYPE_LABELS,
  KONAKLAMA_CLEANING_KINDS,
  KONAKLAMA_CLEANING_KIND_LABELS,
  KONAKLAMA_CLEANING_SORT_FIELDS,
  KONAKLAMA_CLEANING_STATUSES,
  KONAKLAMA_CLEANING_STATUS_LABELS,
  KONAKLAMA_COLLECTION_METHODS,
  KONAKLAMA_COLLECTION_METHOD_LABELS,
  KONAKLAMA_COLLECTION_SORT_FIELDS,
  KONAKLAMA_EXPENSE_CATEGORIES,
  KONAKLAMA_EXPENSE_CATEGORY_LABELS,
  KONAKLAMA_EXPENSE_SORT_FIELDS,
  KONAKLAMA_GENDERS,
  KONAKLAMA_GENDER_LABELS,
  KONAKLAMA_GUEST_SORT_FIELDS,
  KONAKLAMA_GUEST_TYPES,
  KONAKLAMA_GUEST_TYPE_LABELS,
  KONAKLAMA_ISSUE_KINDS,
  KONAKLAMA_ISSUE_KIND_LABELS,
  KONAKLAMA_ISSUE_PRIORITIES,
  KONAKLAMA_ISSUE_PRIORITY_LABELS,
  KONAKLAMA_ISSUE_PRIORITY_RANK,
  KONAKLAMA_ISSUE_SORT_FIELDS,
  KONAKLAMA_ISSUE_STATUSES,
  KONAKLAMA_ISSUE_STATUS_LABELS,
  KONAKLAMA_LEDGER_ENTRY_TYPES,
  KONAKLAMA_LEDGER_ENTRY_TYPE_LABELS,
  KONAKLAMA_OCCUPYING_STATUSES,
  KONAKLAMA_OPEN_ISSUE_STATUSES,
  KONAKLAMA_PRORATION_MODES,
  KONAKLAMA_PRORATION_MODE_LABELS,
  KONAKLAMA_RATE_PERIODS,
  KONAKLAMA_RATE_PERIOD_LABELS,
  KONAKLAMA_RECEIVABLE_VIEWS,
  KONAKLAMA_RESERVATION_SORT_FIELDS,
  KONAKLAMA_RESERVATION_STATUSES,
  KONAKLAMA_RESERVATION_STATUS_LABELS,
  KONAKLAMA_ROOM_SORT_FIELDS,
  KONAKLAMA_ROOM_TYPES,
  KONAKLAMA_ROOM_TYPE_LABELS,
  LEASED_AIRCRAFT_SORT_FIELDS,
  LEASED_AIRCRAFT_STATUSES,
  LEASED_AIRCRAFT_STATUS_LABELS,
  LEASE_ATTACHMENT_KINDS,
  LEASE_ATTACHMENT_KIND_LABELS,
  LEASE_CHARGE_MODELS,
  LEASE_CHARGE_MODEL_HINTS,
  LEASE_CHARGE_MODEL_LABELS,
  LEASE_CONTRACT_SORT_FIELDS,
  LEASE_CONTRACT_STATUSES,
  LEASE_CONTRACT_STATUS_LABELS,
  LEASE_DUE_KINDS,
  LEASE_DUE_KIND_LABELS,
  LEASE_DUE_LEGS,
  LEASE_DUE_LEG_LABELS,
  LEASE_DUE_SORT_FIELDS,
  LEASE_DUE_STATUSES,
  LEASE_DUE_STATUS_LABELS,
  LEASE_EVENTS,
  LEASE_FLIGHT_LOG_SOURCES,
  LEASE_GUARANTEE_PERIOD_KINDS,
  LEASE_GUARANTEE_PERIOD_KIND_LABELS,
  LEASE_HOURS_SOURCES,
  LEASE_HOURS_SOURCE_LABELS,
  LEASE_OFFICIAL_DOCUMENT_TYPES,
  LEASE_PERIOD_SORT_FIELDS,
  LEASE_PERIOD_STATUSES,
  LEASE_PERIOD_STATUS_LABELS,
  LEASE_SPLIT_MODES,
  LEASE_SPLIT_MODE_LABELS,
  LEASE_TYPES,
  LEASE_TYPE_LABELS,
  LEAVE_BUCKETS,
  LEAVE_BUCKET_LABELS,
  LEAVE_SORT_FIELDS,
  LEAVE_STATUSES,
  LEAVE_STATUS_LABELS,
  LINKABLE_USER_SORT_FIELDS,
  LOGIN_FAILURE_REASONS,
  MAINTENANCE_BILLING_STATES,
  MAINTENANCE_BILLING_STATE_LABELS,
  MAINTENANCE_KINDS,
  MAINTENANCE_KIND_LABELS,
  MATCH_CANDIDATE_SORT_FIELDS,
  MATCH_CANDIDATE_STATES,
  MATCH_CANDIDATE_STATE_LABELS,
  MATCH_EVIDENCE,
  MATCH_EVIDENCE_LABELS,
  MATCH_METHODS,
  MATCH_METHOD_LABELS,
  MAX_PER_PAGE,
  MESSAGE_ATTACHMENT_MAX,
  MESSAGE_ATTACHMENT_MAX_BYTES,
  MESSAGE_BODY_MAX,
  MESSAGE_DELETED_PLACEHOLDER,
  MESSAGE_DELETE_REASON_MAX,
  MESSAGE_DELIVERY_STATUSES,
  MESSAGE_DELIVERY_STATUS_LABELS,
  MESSAGE_KINDS,
  MESSAGE_KIND_LABELS,
  MESSAGE_MENTION_MAX,
  MESSAGE_NOTIFY_MODES,
  MESSAGE_NOTIFY_MODE_LABELS,
  MESSAGE_PAGE_SIZE_DEFAULT,
  MESSAGE_PAGE_SIZE_MAX,
  MESSAGE_PREVIEW_MAX,
  MESSAGE_RECEIPT_SEQS_MAX,
  MESSAGE_SEARCH_MIN_LENGTH,
  MONTHLY_TOTAL_HOURS_PER_28_DAYS,
  NOTIFICATION_CODES,
  NOTIFICATION_CODE_LABELS,
  NOTIFICATION_CODE_LIST,
  NOTIFICATION_GROUPS,
  NOTIFICATION_SEVERITIES,
  NOTIFICATION_SEVERITY_LABELS,
  OKUL_TAHSILAT_HOLDER_TYPES,
  OKUL_TAHSILAT_HOLDER_TYPE_LABELS,
  OKUL_TAHSILAT_KINDS,
  OKUL_TAHSILAT_KIND_LABELS,
  OKUL_TAHSILAT_MAX_LINES,
  OKUL_TAHSILAT_SORT_FIELDS,
  OKUL_TAHSILAT_STATUSES,
  OKUL_TAHSILAT_STATUS_LABELS,
  OKUL_TAHSILAT_UNIT_LABELS,
  ONAYA_BAGLI_FATURA_ALANLARI,
  PARTICIPANT_ROLES,
  PARTICIPANT_ROLE_LABELS,
  PAYEE_CODE_SEGMENTS,
  PAYEE_KINDS,
  PAYEE_KIND_LABELS,
  PAYEE_LEDGER_SOURCES,
  PAYEE_LEDGER_SOURCE_LABELS,
  PAYEE_SEARCH_MIN_CHARS,
  PAYMENT_APPROVAL_DECISIONS,
  PAYMENT_APPROVAL_FEEDBACKS,
  PAYMENT_ATTACHMENT_KINDS,
  PAYMENT_ATTACHMENT_KIND_LABELS,
  PAYMENT_ATTACHMENT_RESTRICTED_KINDS,
  PAYMENT_DATE_BASES,
  PAYMENT_DATE_BASIS_HINTS,
  PAYMENT_DATE_BASIS_LABELS,
  PAYMENT_DATE_BASIS_NULLABLE,
  PAYMENT_DEFAULT_STAGES,
  PAYMENT_DOCUMENT_NO_LABELS,
  PAYMENT_DOCUMENT_NO_OPTIONAL,
  PAYMENT_DOCUMENT_TYPES,
  PAYMENT_DOCUMENT_TYPE_LABELS,
  PAYMENT_EVENT_LABELS,
  PAYMENT_IN_APPROVAL_STATUSES,
  PAYMENT_LEGACY_STATUSES,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_REQUEST_STATUSES,
  PAYMENT_RESPONSIBLE_TYPES,
  PAYMENT_RETURN_FEEDBACKS,
  PAYMENT_RETURN_FEEDBACK_LABELS,
  PAYMENT_STAGE_MODES,
  PAYMENT_STAGE_MODE_HINTS,
  PAYMENT_STAGE_MODE_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYSLIP_MATCH_STATUSES,
  PAYSLIP_MATCH_STATUS_LABELS,
  PAYSLIP_MONTH_LABELS,
  PAYSLIP_SCAN_STATUSES,
  PERMISSIONS,
  PERMISSION_LABELS,
  PRESENCE_STATUSES,
  PRESENCE_STATUS_LABELS,
  PROFORMA_ESLESMEZ_DURUMLAR,
  PROJECT_SORT_FIELDS,
  PROJECT_STATUSES,
  PROJECT_STATUS_LABELS,
  REGISTRATION_STATUSES,
  REGISTRATION_STATUS_LABELS,
  REGISTRY_TOKEN_PROBE_PATTERN,
  REMINDER_TIME_REQUIRED,
  ROLES,
  ROLE_BY_CODE,
  ROLE_DEFINITIONS,
  SCOPE_LABELS,
  SCOPE_TYPES,
  SEARCH_MATCH_KINDS,
  SEARCH_SOURCES,
  SEARCH_SOURCE_LABELS,
  SEARCH_SOURCE_PERMISSIONS,
  SEARCH_WORDS_MIN,
  SECURITY_EVENT_LABELS,
  SEED_COMPANIES,
  SIRKET_ETKI_ETIKETLERI,
  STAFF_REQUEST_CATEGORIES,
  STAFF_REQUEST_CATEGORY_LABELS,
  STAFF_REQUEST_EVENT_LABELS,
  STAFF_REQUEST_LIST_LIMIT,
  STAFF_REQUEST_STATUSES,
  STAFF_REQUEST_STATUS_LABELS,
  STAFF_REQUEST_URGENCIES,
  STAFF_REQUEST_URGENCY_LABELS,
  STEP_LABELS,
  SUPER_ADMIN_RED_MESAJLARI,
  SUPER_ADMIN_ROLE,
  SUPPORT_AREAS,
  SUPPORT_AREA_LABELS,
  SUPPORT_ATTACHMENT_MAX_BYTES,
  SUPPORT_CLOSED_STATUSES,
  SUPPORT_PRIORITIES,
  SUPPORT_PRIORITY_LABELS,
  SUPPORT_STATUSES,
  SUPPORT_STATUSES_NEEDING_COMMENT,
  SUPPORT_STATUS_LABELS,
  SUPPORT_STATUS_TRANSITIONS,
  SUPPORT_TICKET_KINDS,
  SUPPORT_TICKET_KIND_LABELS,
  SUPPORT_TICKET_SORT_FIELDS,
  TAX_ACCRUAL_POLICIES,
  TAX_ACCRUAL_POLICY_LABELS,
  TAX_ACCRUAL_STATUSES,
  TAX_ACCRUAL_STATUS_LABELS,
  TAX_AMOUNT_SOURCES,
  TAX_AMOUNT_SOURCE_LABELS,
  TAX_AUTHORITIES,
  TAX_AUTHORITY_LABELS,
  TAX_DUE_SORT_FIELDS,
  TAX_FILED_SOURCES,
  TAX_FILED_SOURCE_LABELS,
  TAX_FILING_CLOSED_STATUSES,
  TAX_FILING_STATUSES,
  TAX_FILING_STATUS_LABELS,
  TAX_OFFSET_FROM,
  TAX_OFFSET_FROM_LABELS,
  TAX_OVERRIDE_KINDS,
  TAX_OVERRIDE_KIND_LABELS,
  TAX_OVERRIDE_SORT_FIELDS,
  TAX_OVERRIDE_TARGETS,
  TAX_OVERRIDE_TARGET_LABELS,
  TAX_PAYMENT_CLOSED_STATUSES,
  TAX_PAYMENT_CLOSURE_STATUSES,
  TAX_PAYMENT_METHODS,
  TAX_PAYMENT_METHOD_LABELS,
  TAX_PAYMENT_STATUSES,
  TAX_PAYMENT_STATUS_LABELS,
  TAX_PERIOD_KINDS,
  TAX_PERIOD_KIND_LABELS,
  TAX_PERIOD_SORT_FIELDS,
  TAX_REGISTRATION_OPEN_STATUSES,
  TAX_REGISTRATION_SORT_FIELDS,
  TAX_REGISTRATION_STATUSES,
  TAX_REGISTRATION_STATUS_LABELS,
  TAX_SHIFT_CONFIDENCES,
  TAX_SHIFT_CONFIDENCE_LABELS,
  TAX_TAXPAYER_SORT_FIELDS,
  TAX_TAXPAYER_STATUSES,
  TAX_TAXPAYER_STATUS_LABELS,
  TEKERRUR_NEDENLERI,
  TEKERRUR_NEDEN_ETIKETLERI,
  TEKERRUR_PENCERE_GUN,
  UNBILLED_INVOICE_STATUSES,
  UNIT_CODES,
  USER_SORT_FIELDS,
  VAT_RATES,
  YETKI_VEREN_IZINLER,
  acknowledgeAnnouncementSchema,
  activityListQuerySchema,
  activityListSchema,
  activityRowSchema,
  activitySourceSchema,
  addAgendaCommentSchema,
  addConversationParticipantsSchema,
  addSupportCommentSchema,
  agendaListQuerySchema,
  aktarimEngelleri,
  announcementAttachmentMetaSchema,
  announcementCategoryListQuerySchema,
  announcementDecisionNeedsComment,
  announcementDocumentDigestSource,
  announcementListQuerySchema,
  answerInvoiceInfoSchema,
  aramaNormalize,
  askInvoiceInfoSchema,
  assetAssignSchema,
  assetCalibrationSchema,
  assetCategoryCreateSchema,
  assetCategoryUpdateSchema,
  assetCreateSchema,
  assetDeleteSchema,
  assetExitOverrideSchema,
  assetIncidentSchema,
  assetListQuerySchema,
  assetMaintenanceReturnSchema,
  assetMaintenanceSendSchema,
  assetReportQuerySchema,
  assetReturnSchema,
  assetStatusChangeSchema,
  assetUpdateSchema,
  assignAgendaItemSchema,
  assignableUserSchema,
  attendanceEventPayloadSchema,
  auditEntrySchema,
  auditListQuerySchema,
  auditMineQuerySchema,
  ayniKonsolidasyonAgaci,
  belgeNoNormalize,
  belgeNolariniAyir,
  boyutSecilebilir,
  boyutYamasi,
  bulkMonthlyTotalHoursError,
  bulkMonthlyTotalRowSchema,
  bulkMonthlyTotalSchema,
  cancelLeaseDueSchema,
  cancelOkulTahsilatSchema,
  changeAgendaStatusSchema,
  changeFixedExpenseStatusSchema,
  changePasswordSchema,
  changeSupportStatusSchema,
  classify,
  closeRemainderSchema,
  closingDropsReminder,
  companyListQuerySchema,
  companySchema,
  confirmLeasePeriodHoursSchema,
  conversationListQuerySchema,
  convertToReturnInvoiceSchema,
  correctFixedExpenseWithholdingSchema,
  createAgendaItemSchema,
  createAnnouncementCategorySchema,
  createAnnouncementSchema,
  createCompanySchema,
  createConversationSchema,
  createCredentialSchema,
  createDocumentFolderSchema,
  createEmployeeSchema,
  createFixedExpenseCommitmentSchema,
  createFlightTypeSchema,
  createInvoicePurposeSchema,
  createInvoiceSchema,
  createJobTitleSchema,
  createKonaklamaBedSchema,
  createKonaklamaCleaningSchema,
  createKonaklamaCollectionSchema,
  createKonaklamaExpenseSchema,
  createKonaklamaGuestSchema,
  createKonaklamaIssueSchema,
  createKonaklamaReservationSchema,
  createKonaklamaRoomSchema,
  createKonaklamaTariffSchema,
  createLeaseContractSchema,
  createLeaseFlightLogSchema,
  createLeaseOneOffDueSchema,
  createLeasedAircraftSchema,
  createLeaveSchema,
  createOkulTahsilatAccountSchema,
  createOkulTahsilatItemSchema,
  createOkulTahsilatSchema,
  createOrgUnitSchema,
  createPayeeAccountSchema,
  createProjectSchema,
  createProjectTypeSchema,
  createReturnInvoiceRequestSchema,
  createSupportTicketSchema,
  createTaxRegistrationSchema,
  createTaxTaxpayerSchema,
  createUserResultSchema,
  createUserSchema,
  credentialDeliverySchema,
  custodyAcceptSchema,
  custodyCancelSchema,
  custodyCandidateQuerySchema,
  custodyCreateSchema,
  custodyLineSchema,
  custodyListQuerySchema,
  custodyRequestReturnSchema,
  custodyReturnLineSchema,
  custodyReturnSchema,
  custodyTransferSchema,
  dagitimGecerli,
  damgaAyagiSeq,
  daysInMonth,
  decideAnnouncementSchema,
  decideFixedExpenseEscalationSchema,
  decideInvoiceSchema,
  decisionAckSchema,
  decisionEventSchema,
  defaultAckModeFor,
  deleteMessageSchema,
  deployStateText,
  deployStateTone,
  deployVerdict,
  disKayitAnahtari,
  documentFolderPermissionSchema,
  doluBaglar,
  droppedReminderNotice,
  editMessageSchema,
  employeeLinkUserSchema,
  employeeLinkableUserQuerySchema,
  employeeListQuerySchema,
  employeeNoSuggestionQuerySchema,
  employeeSummarySchema,
  etkinBoyutlar,
  externalEmployeeSchema,
  faturaBastanSunulabilir,
  faturaDuzeltilebilir,
  faturaIcerigiSorulur,
  faturaKesilmeyecekKapatilabilir,
  faturaListedenKaldirilabilir,
  faturaYenidenSunulabilir,
  fileTaxPaymentSchema,
  fixedExpenseCommitmentListQuerySchema,
  fixedExpenseOccurrenceListQuerySchema,
  fixedExpenseProjectionQuerySchema,
  fixedExpenseSummaryQuerySchema,
  flightImportRowSchema,
  flightImportSchema,
  flightMonthlyTotalCreateSchema,
  flightMonthlyTotalUpdateSchema,
  flightPeriodFileBatchSchema,
  flightPeriodFileSchema,
  flightPeriodNoteSchema,
  flightPeriodQuerySchema,
  flightPeriodRecomputeSchema,
  flightPilotCreateSchema,
  flightPilotUpdateSchema,
  flightRateCreateSchema,
  flightRecordCreateSchema,
  flightRecordQuerySchema,
  flightRecordUpdateSchema,
  flightTypeCode,
  flightTypeListQuerySchema,
  formatLeaveAmount,
  formatLeaveAmountShort,
  formatLeaveDurationText,
  formatLeaveNumber,
  formatMoney,
  generateFixedExpenseOccurrencesSchema,
  generateTaxPeriodsSchema,
  gerekceGecerliMi,
  gibBicimindeMi,
  grupIciIslem,
  grupIciReferansNormalize,
  hedefteKurulacak,
  iadeTalebiAciklamasi,
  iadeTalebineCevrilebilir,
  iadeTavani,
  ibanDogrulamaDurumu,
  ibanDogrulayabilirMi,
  integrationEventBatchSchema,
  integrationEventSchema,
  invoiceCommentSchema,
  isApiErrorBody,
  isConversationLinkType,
  isMfaChallenge,
  isSearchSource,
  istanbulGunu,
  jobTitleListQuerySchema,
  jobTitleSchema,
  kalanTutar,
  karsiSirketEtiketi,
  karsiSirketSecilebilir,
  kategoriSecilebilir,
  kaynaktaKalan,
  kesilenFaturaEklenebilir,
  kismiOdemeKarari,
  konaklamaAccrualRunSchema,
  konaklamaAvailabilityQuerySchema,
  konaklamaCleaningListQuerySchema,
  konaklamaCollectionListQuerySchema,
  konaklamaExpenseListQuerySchema,
  konaklamaGuestListQuerySchema,
  konaklamaIssueListQuerySchema,
  konaklamaLedgerAdjustSchema,
  konaklamaLedgerListQuerySchema,
  konaklamaOccupancyQuerySchema,
  konaklamaReceivableListQuerySchema,
  konaklamaRefundSchema,
  konaklamaReservationActionSchema,
  konaklamaReservationCancelSchema,
  konaklamaReservationListQuerySchema,
  konaklamaRoomListQuerySchema,
  konaklamaSummaryQuerySchema,
  leaseContractListQuerySchema,
  leaseContractStatusSchema,
  leaseDueActionSchema,
  leaseDueListQuerySchema,
  leaseGenerateSchema,
  leasePeriodListQuerySchema,
  leasedAircraftListQuerySchema,
  leaveAdjustSchema,
  leaveDaysToHours,
  leaveDecisionSchema,
  leaveEventPayloadSchema,
  leaveExceeds,
  leaveListQuerySchema,
  leavePeriodRecalcSchema,
  leavePreviewSchema,
  linkExternalIdentitySchema,
  linkInvoiceProformaSchema,
  linkableUserSchema,
  loginResponseSchema,
  loginSchema,
  loginSuccessSchema,
  maintenanceCustomerSchema,
  maintenanceExcludeSchema,
  maintenanceInvoicedPayloadSchema,
  maintenanceJobQuerySchema,
  maintenanceReleasedPayloadSchema,
  markConversationDeliveredSchema,
  markConversationReadSchema,
  markFixedExpensePaidSchema,
  markInvoiceEnteredSchema,
  markLeaseDuePaidSchema,
  markPaymentPaidSchema,
  markTaxDuePaidSchema,
  matchCandidateQuerySchema,
  matchPayslipFilesSchema,
  mentionListQuerySchema,
  messageListQuerySchema,
  messageReceiptsQuerySchema,
  messageSearchQuerySchema,
  messageSeqSchema,
  mfaChallengeSchema,
  mfaConfirmResultSchema,
  mfaConfirmSchema,
  mfaDisableSchema,
  mfaSetupSchema,
  mfaVerifySchema,
  moneyText,
  monthlyTotalHoursError,
  monthlyTotalMaxHours,
  normalizeTerm,
  notificationCodeGroup,
  notificationListQuerySchema,
  notificationMarkSchema,
  notificationPreferenceUpdateSchema,
  odemeBelgeSinifi,
  odemeDurumEtiketi,
  odemeHedefiKarari,
  odemeKapatmaKarari,
  odemeKasaHesaplari,
  odemeKasaKarari,
  odenecekTutar,
  okulTahsilatFark,
  okulTahsilatLineSchema,
  okulTahsilatListQuerySchema,
  orgUnitListQuerySchema,
  orgUnitSchema,
  orgUnitTreeQuerySchema,
  pageMeta,
  paginationQuerySchema,
  parseMoney,
  parseSort,
  payeeKindFromCode,
  payeeSegmentFromCode,
  paymentAmountFrom,
  paymentApproveSchema,
  paymentAsksResponsible,
  paymentAssigneeCreateSchema,
  paymentAssigneeUpdateSchema,
  paymentCancelSchema,
  paymentCommentSchema,
  paymentDateRangeSchema,
  paymentDocumentNoRequired,
  paymentMethodNeedsIban,
  paymentRejectSchema,
  paymentResubmitSchema,
  paymentReturnSchema,
  paymentStageCreateSchema,
  paymentStageUpdateSchema,
  payslipListQuerySchema,
  payslipPeriodLabel,
  periodAnchorOf,
  pinAnnouncementSchema,
  presenceSnapshotPayloadSchema,
  projeDurumEtiketi,
  projeSecilebilir,
  projectListQuerySchema,
  proposeFixedExpenseEscalationSchema,
  recalculateTaxPeriodsSchema,
  receiptListQuerySchema,
  recognize,
  recordFixedExpenseInvoiceSchema,
  recordTaxAssessmentSchema,
  recordTaxEstimateSchema,
  recordTaxFilingSchema,
  recordTaxPaymentSchema,
  registrationApplySchema,
  registrationApproveSchema,
  registrationRejectSchema,
  registryTokenIsDead,
  registryTokenProbeText,
  reminderPending,
  reminderTimeMissing,
  reopenLeasePeriodSchema,
  rescheduleFixedExpenseOccurrenceSchema,
  resetPasswordResultSchema,
  respondAgendaRsvpSchema,
  resubmitInvoiceSchema,
  saveFixedExpensePolicySchema,
  scopeAssignmentSchema,
  scopeSchema,
  searchQuerySchema,
  secenekFiltrele,
  secilebilirBoyutlar,
  secilebilirKarsiSirketler,
  secilebilirProjeler,
  sendMessageSchema,
  sessionEntrySchema,
  sessionUserSchema,
  setAgendaParticipantsSchema,
  setAnnouncementTargetsSchema,
  setConversationArchiveSchema,
  setConversationLinkSchema,
  setConversationMuteSchema,
  setFixedExpenseAttachmentValiditySchema,
  setFixedExpenseLoanSchema,
  setLeasePeriodHoursSchema,
  setMessagePinSchema,
  setMessageStarSchema,
  setPresenceStatusSchema,
  severityIgnoresInAppPreference,
  shapedLikeAPath,
  shiftedReminder,
  sirketSilinebilir,
  skipFixedExpenseOccurrenceSchema,
  sortableQuerySchema,
  staffRequestApproveSchema,
  staffRequestCancelSchema,
  staffRequestCreateSchema,
  staffRequestRejectSchema,
  staffRequestResubmitSchema,
  stageAppliesToAmount,
  suggestedRemindAt,
  superAdminDegisimiDenetle,
  superAdminIzinleri,
  superAdminMi,
  supportTicketListQuerySchema,
  swiftUyumlu,
  taxCalendarSummaryQuerySchema,
  taxDueListQuerySchema,
  taxInstalmentSchema,
  taxMoney,
  taxOverrideListQuerySchema,
  taxPeriodListQuerySchema,
  taxRegistrationListQuerySchema,
  taxTaxpayerListQuerySchema,
  tekerrurGerekcesiSchema,
  transferFixedExpenseBudgetSchema,
  undoInvoiceEnteredSchema,
  updateAgendaItemSchema,
  updateAnnouncementCategorySchema,
  updateAnnouncementSchema,
  updateCompanySchema,
  updateConversationSchema,
  updateDocumentFileSchema,
  updateDocumentFolderSchema,
  updateEmployeeSchema,
  updateFixedExpenseCommitmentSchema,
  updateFlightTypeSchema,
  updateInvoicePurposeSchema,
  updateInvoiceSchema,
  updateJobTitleSchema,
  updateKonaklamaBedSchema,
  updateKonaklamaCleaningSchema,
  updateKonaklamaExpenseSchema,
  updateKonaklamaGuestSchema,
  updateKonaklamaIssueSchema,
  updateKonaklamaReservationSchema,
  updateKonaklamaRoomSchema,
  updateKonaklamaTariffSchema,
  updateLeaseContractSchema,
  updateLeaseDueSchema,
  updateLeaseFlightLogSchema,
  updateLeasedAircraftSchema,
  updateOkulTahsilatAccountSchema,
  updateOkulTahsilatItemSchema,
  updateOrgUnitSchema,
  updatePayeeAccountSchema,
  updateProjectSchema,
  updateProjectTypeSchema,
  updateSupportTicketSchema,
  updateTaxRegistrationSchema,
  updateTaxTaxpayerSchema,
  updateUserSchema,
  uploadDocumentMetaSchema,
  uploadPayslipSchema,
  upsertTaxObligationTypeSchema,
  upsertTaxOverrideSchema,
  upsertTaxStampTariffSchema,
  userListQuerySchema,
  userSummarySchema,
  vatFromGross,
  verifyIbanSchema,
  withdrawAnnouncementSchema,
  withdrawInvoiceInfoSchema,
  withdrawInvoiceSchema,
  yetkiVerenIzinMi,
  yetkiVermeGerekcesiGerekli
};
//# sourceMappingURL=index.js.map